import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    getEventDateForRecovery,
    moveEventToRecoveryDate,
} from "../shadowGrove/recovery/ScheduleAdjustmentEngine";
import { useNavigate } from "react-router-dom";
import { api, APP_NAME } from "@/lib/api";
import { setParentUnlocked } from "@/lib/parentSession";
import { PinGate } from "@/components/PinGate";
import AbsenceRecoveryModal from "../components/AbsenceRecoveryModal";
import { groupLessonCalendarEvents } from "../lib/lessonStage";
import { LessonCalendarDetails } from "../components/LessonResources";
import SchoolDaySettings from "../components/SchoolDaySettings";
import Homeroom from "../components/Homeroom";
import HouseholdGadget from "../components/HouseholdGadget";
import useClassroomDashboard from "../lib/useClassroomDashboard";
import { classroomEventOnDate, localDay, normalizeDashboardEvent, readDashboardStorage, reconcileClassroomEvents } from "../lib/dashboardGadgets";
import ReportsPanel from "../components/ReportsPanel";
import { attendanceByDate } from "../lib/academicReports";
import { calendarTimelineBounds, selectCalendarContent } from "../lib/calendarView";
import DeleteAssignmentDialog from "../components/DeleteAssignmentDialog";
import { assignmentSeriesIdentity, deleteSharedAssignment } from "../lib/assignmentDeletion";
import { queueAttendanceSync, queueSchoolCalendarSync, saveClassroomEvents, waitForClassroomSync } from "../lib/classroomEventSync";
import {
    purpleActionBtn,
    shadowInput,
    purpleActionRow,
    shadowCalendarShell,
    shadowCalendarToolbar,
    shadowCalendarWeekHeader,
    shadowCalendarGrid,
    shadowCalendarCell,
    shadowCalendarCellMuted,
    shadowSelectedGold,
    shadowTextarea,
} from "../shadowGrove/ui/shadowStyles";
import {
    ArrowLeft,
    BookOpen,
    Hourglass,
    Sparkles,
    User as UserIcon,
    ChevronDown,
    Wand2,
    Gamepad2,
    Zap,
    Upload,
    CalendarDays,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BRANDING } from "../shadowGrove/config/branding";

const PanelButton = ({ id, icon, title, subtitle, openPanel, setOpenPanel }) => (
    <button
        type="button"
        onClick={() => setOpenPanel(openPanel === id ? null : id)}
        className={`
            rounded-2xl
            border
            px-3
            py-2
            text-left
            bg-black
            transition-all
            duration-200
            shadow-[0_0_14px_rgba(124,58,237,0.14)]
            hover:border-[#ffd166]/70
            hover:shadow-[0_0_22px_rgba(255,209,102,0.20)]
            ${
                openPanel === id
                    ? "border-[#ffd166]/80 bg-[#7c3aed]/20 shadow-[0_0_24px_rgba(255,209,102,0.25)]"
                    : "border-[#ffd166]/25"
            }
        `}
    >
        <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg border border-[#ffd166]/25 bg-[#7c3aed]/15 flex items-center justify-center text-[#ffd166] shrink-0">
                {React.cloneElement(icon, { size: 15 })}
            </div>

            <div className="flex-1">
                <h3 className="font-display text-sm text-[#ffd166] leading-tight whitespace-normal">
                    {title}
                </h3>

                <p className="text-[10px] text-[#b0b8d6] mt-0.5 leading-tight whitespace-normal">
                    {subtitle}
                </p>
            </div>

            <ChevronDown
                size={14}
                className={`shrink-0 transition ${
                    openPanel === id
                        ? "rotate-180 text-[#ffd166]"
                        : "text-[#6c7a9c]"
                }`}
            />
        </div>
    </button>
);

const Panel = ({ id, openPanel, children }) =>
    openPanel === id ? (
        <section className="w-full rounded-[32px] border border-[#ffd166]/35 bg-black p-6 shadow-[0_0_35px_rgba(255,209,102,0.10)] backdrop-blur-sm">
            {children}
        </section>
    ) : null;


const fileToDataUrl = (file, callback) => {
    if (!file) return;


    if (file.size > 500_000) {
        toast.error("Image must be under 500KB.");
        return;
    }


    const reader = new FileReader();


    reader.onload = () => callback(reader.result);
    reader.readAsDataURL(file);
};

const ensureChildDefaults = (child) => {
    const profileColor =
        child.profileColor ||
        child.calendarColor ||
        "#c084fc";

    return {
        ...child,
        profile_picture:
            child.profile_picture ||
            child.profilePicture ||
            child.picture ||
            "",
        profileColor,
        calendarColor: profileColor,
        gradeLevel: child.gradeLevel || "",
    };
};



const ParentProfile = () => {
    const navigate = useNavigate();


    const [unlocked, setUnlocked] = useState(true);
    const [pinOpen, setPinOpen] = useState(false);
    const [openPanel, setOpenPanel] = useState("");
    const [arcadeScope, setArcadeScope] = useState("all");
    const [arcadeChildId, setArcadeChildId] = useState("");
    const [showBillingSection, setShowBillingSection] = useState(false);
    const [showSpellingConfig, setShowSpellingConfig] = useState(false);
    const [showSpellingVoiceManager, setShowSpellingVoiceManager] = useState(false);
    const [showReusableSpellingLists, setShowReusableSpellingLists] = useState(false);
    const [spellingRecordingWord, setSpellingRecordingWord] = useState("");
    const [spellingRecordingActive, setSpellingRecordingActive] = useState(false);
    const [spellingAudioMap, setSpellingAudioMap] = useState({});

    const spellingMediaRecorderRef = React.useRef(null);
    const spellingRecordedChunksRef = React.useRef([]);
    const spellingRecordingStreamRef = React.useRef(null);
    const [spellingMode, setSpellingMode] = useState("practice");
    const [spellingAttempts, setSpellingAttempts] = useState("unlimited");
    const [spellingWordsText, setSpellingWordsText] = useState("");
    const saveSpellingAudioMap = (nextAudioMap) => {
        setSpellingAudioMap(nextAudioMap);

        const targetChildIds =
            arcadeScope === "all"
                ? children.map((child) => child.child_id)
                : [arcadeChildId];

        targetChildIds.forEach((childId) => {
            if (!childId) return;

            localStorage.setItem(
                `shadowGroveSpellingAudio_${childId}`,
                JSON.stringify(nextAudioMap)
            );
        });
    };
    const saveArcadeSpellingListToReuse = () => {
        const words = spellingWordsText
            .split("\n")
            .map((word) => word.trim().toUpperCase())
            .filter(Boolean);

        if (words.length === 0) {
            toast.error("Add at least one spelling word first.");
            return;
        }

        const savedList = {
            id: Date.now().toString(),
            title:
                words.slice(0, 3).join(", ") ||
                "Reusable Spelling List",
            words,
            audio: spellingAudioMap,
            graded: spellingMode === "graded",
            mode: spellingMode,
            attempts: spellingAttempts,
            created_at: new Date().toISOString(),
        };

        const updated = [
            savedList,
            ...reusableSpellingLists,
        ];

        localStorage.setItem(
            "shadowGroveReusableSpellingLists",
            JSON.stringify(updated)
        );

        setReusableSpellingLists(updated);

        toast.success(
            "Saved to reusable spelling library."
        );
    };
    const startSpellingRecording = async (word) => {
        if (spellingRecordingActive) return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });

            spellingRecordingStreamRef.current = stream;
            spellingRecordedChunksRef.current = [];

            const recorder = new MediaRecorder(stream);

            spellingMediaRecorderRef.current = recorder;

            recorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    spellingRecordedChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = () => {
                const blob = new Blob(
                    spellingRecordedChunksRef.current,
                    {
                        type:
                            recorder.mimeType ||
                            "audio/webm",
                    }
                );

                const reader = new FileReader();

                reader.onloadend = () => {
                    const audioDataUrl = reader.result;

                    const nextAudioMap = {
                        ...spellingAudioMap,
                        [word]: audioDataUrl,
                    };

                    saveSpellingAudioMap(nextAudioMap);

                    spellingRecordingStreamRef.current
                        ?.getTracks()
                        .forEach((track) => track.stop());

                    spellingRecordingStreamRef.current = null;
                    spellingMediaRecorderRef.current = null;
                    spellingRecordedChunksRef.current = [];

                    setSpellingRecordingActive(false);
                    setSpellingRecordingWord("");

                    toast.success(
                        `Voice prompt saved for ${word}.`
                    );
                };

                reader.readAsDataURL(blob);
            };

            recorder.start();

            setSpellingRecordingWord(word);
            setSpellingRecordingActive(true);
        } catch (error) {
            console.error(
                "Unable to record spelling prompt:",
                error
            );

            toast.error(
                "Microphone access was not available."
            );
        }
    };

    const stopSpellingRecording = () => {
        const recorder = spellingMediaRecorderRef.current;

        if (
            !recorder ||
            recorder.state === "inactive"
        ) {
            return;
        }

        recorder.stop();
    };

    const [user, setUser] = useState(null);
    const [parentName, setParentName] = useState(
        localStorage.getItem("shadowGroveParentName") || "Parent"
    );
    
    const [parentPicture, setParentPicture] = useState(
        localStorage.getItem("shadowGroveParentPicture") || ""
        
    );
    const [schoolBannerImage, setSchoolBannerImage] = useState(
    localStorage.getItem("shadowGroveSchoolBannerImage") || ""
);


    const [children, setChildren] = useState([]);
    const [pendingByChild, setPendingByChild] = useState({});
        React.useEffect(() => {
            const targetChildId =
                arcadeScope === "child"
                    ? arcadeChildId
                    : children[0]?.child_id;

            if (!targetChildId) {
                setSpellingAudioMap({});
                return;
            }

            const savedAudio = JSON.parse(
                localStorage.getItem(
                    `shadowGroveSpellingAudio_${targetChildId}`
                ) || "{}"
            );

            setSpellingAudioMap(
                savedAudio &&
                typeof savedAudio === "object"
                    ? savedAudio
                    : {}
            );
        }, [arcadeScope, arcadeChildId, children]);

    const [schoolYearSettings, setSchoolYearSettings] = useState(() =>
        JSON.parse(localStorage.getItem("shadowGroveSchoolYearSettings") || "null") ||{
    schoolName: "",
    academicYearLabel: "",
    motto: "",
    startDate: "",
    endDate: "",
    division: "semesters",
}
    );
const schoolCalendarKey = "shadowGroveSchoolCalendar";

const [schoolCalendar, setSchoolCalendar] = useState(() =>
    JSON.parse(
        localStorage.getItem(schoolCalendarKey) || "null"
    ) || {
        holidays: [],
        vacations: [],
        daysOff: [],
    }
);
const notifySchoolCalendarUpdated = () => {
    queueSchoolCalendarSync(children.map((child) => child.child_id));
    window.dispatchEvent(
        new Event(
            "shadowGroveSchoolCalendarUpdated"
        )
    );
};
            const [calendarUndoStack, setCalendarUndoStack] = React.useState([]);

const rememberCalendarBeforeChange = () => {
    const currentCalendar =
        JSON.parse(localStorage.getItem(schoolCalendarKey) || "null") || {
            holidays: [],
            vacations: [],
            daysOff: [],
            items: [],
        };

    setCalendarUndoStack((prev) => [
        currentCalendar,
        ...prev,
    ].slice(0, 10));
};

const undoLastCalendarChange = () => {
    setCalendarUndoStack((prev) => {
        if (prev.length === 0) {
            toast.error("Nothing to undo.");
            return prev;
        }

        const [lastCalendar, ...rest] = prev;

        localStorage.setItem(
            schoolCalendarKey,
            JSON.stringify(lastCalendar)
        );

        setSchoolCalendar(lastCalendar);

        toast.success("Calendar change undone.");

        return rest;
    });
};

    const [attendanceChild, setAttendanceChild] = useState("");
    const [attendanceDate, setAttendanceDate] = useState(
        new Date().toISOString().slice(0, 10)
    );
    const [attendanceStatus, setAttendanceStatus] = useState("present");
    const [savedAttendanceMap, setSavedAttendanceMap] = useState({});
    const [absenceActionPopup, setAbsenceActionPopup] = useState(null);
    const [absenceInfoOpen, setAbsenceInfoOpen] = useState("");
    const [absenceRecoveryForm, setAbsenceRecoveryForm] = useState({
    reason: "sick",
    note: "",
    grading: "leave_ungraded",
    recoveryPlan: "absent_only",
    multiDay: false,
    endDate: "",
});
    const [attendanceRefreshKey, setAttendanceRefreshKey] = useState(0);
    const [vacationStart, setVacationStart] = useState("");
    const [calendarLayoutMode, setCalendarLayoutMode] = React.useState("balanced");
    const [calendarProfileView, setCalendarProfileView] = React.useState("school");
    const [pendingAssignmentDelete, setPendingAssignmentDelete] = React.useState(null);
    const [calendarClassView, setCalendarClassView] = React.useState("all");
    const [calendarItemProfileView, setCalendarItemProfileView] =
    React.useState("school");
    const [calendarItemCustomDays, setCalendarItemCustomDays] = React.useState([]);

    const [calendarItemType, setCalendarItemType] = React.useState("appointment");
    const [calendarItemName, setCalendarItemName] = React.useState("");
    const [calendarItemDescription, setCalendarItemDescription] = React.useState("");
    const [calendarItemLocation, setCalendarItemLocation] = React.useState("");
    const [calendarItemStartDate, setCalendarItemStartDate] = React.useState("");
    const [calendarItemEndDate, setCalendarItemEndDate] = React.useState("");
    const [calendarItemFrequency, setCalendarItemFrequency] = React.useState("once");
    const [calendarItemTime, setCalendarItemTime] = React.useState("");
    const [calendarItemEndTime, setCalendarItemEndTime] = React.useState("");
    const [calendarItemBlocksSchool, setCalendarItemBlocksSchool] = React.useState(false);
    const [calendarItemColor, setCalendarItemColor] = React.useState("#7c3aed");
    const [calendarItemEmoji, setCalendarItemEmoji] = React.useState("");
    const [editingCalendarItemId, setEditingCalendarItemId] = React.useState(null);
    const [calendarEditPopupItem, setCalendarEditPopupItem] = React.useState(null);
    const [draggedCalendarItemId, setDraggedCalendarItemId] = React.useState(null);
    const [draggedCalendarSourceDate, setDraggedCalendarSourceDate] =
        React.useState("");
    const [calendarDragPreview, setCalendarDragPreview] =
        React.useState(null);
    const [recurringMovePopup, setRecurringMovePopup] = React.useState(null);
    const [recurringDeletePopup, setRecurringDeletePopup] = React.useState(null);
    const [recurringEditPopup, setRecurringEditPopup] = React.useState(null);

    const [sessionDeletePopup, setSessionDeletePopup] = React.useState(null);
    const [sessionMovePopup, setSessionMovePopup] = React.useState(null);
    const [autoAddUSHolidays, setAutoAddUSHolidays] = React.useState(false);
    const [vacationEnd, setVacationEnd] = useState("");
    const [advanceChildId, setAdvanceChildId] = useState("");
    const [advanceType, setAdvanceType] = useState("semester");
    const [advancePeriodKey, setAdvancePeriodKey] = useState("");


    const [requestedDeskGadget, setRequestedDeskGadget] = useState(null);

    const [quizTitle, setQuizTitle] = useState("");
    const [selectedQuizChild, setSelectedQuizChild] = useState("");
    const [lightningGraded, setLightningGraded] = useState(false);
    const [quizQuestions, setQuizQuestions] = useState([
        {
            question: "",
            options: ["", "", "", ""],
            correct: 0,
        },
    ]);


    const [showCurrentQuizzes, setShowCurrentQuizzes] = useState(false);


    const [reusableQuizzes, setReusableQuizzes] = useState(() =>
        JSON.parse(localStorage.getItem("shadowGroveReusableQuizzes") || "[]")
    );


    const [reusableSpellingLists, setReusableSpellingLists] = useState(() =>
        JSON.parse(localStorage.getItem("shadowGroveReusableSpellingLists") || "[]")
    );


    const [selectedSpellingChild, setSelectedSpellingChild] = useState("");
    const [spellingWords, setSpellingWords] = useState(["", "", "", "", ""]);
    const [spellingAudio, setSpellingAudio] = useState({});
    const [spellingGraded, setSpellingGraded] = useState(false);
    const [recordingIndex, setRecordingIndex] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);




    const [editingChild, setEditingChild] = useState(null);
    const [editName, setEditName] = useState("");
    const [editPicture, setEditPicture] = useState("");
    const [editBirthday, setEditBirthday] = useState("");
    const [editGradeLevel, setEditGradeLevel] = useState("");
    const [editProfileColor, setEditProfileColor] = useState("#c084fc");
    const [editChildUsername, setEditChildUsername] = useState("");
    const [editChildPassword, setEditChildPassword] = useState("");
    const [showEditChildPassword, setShowEditChildPassword] = useState(false);
    const [editChildLoginEnabled, setEditChildLoginEnabled] = useState(false);
    const [editBlueberryEnabled, setEditBlueberryEnabled] = useState(false);
    const [editShowAcademicRecord, setEditShowAcademicRecord] = useState(false);
    const [editShowCurrentGrades, setEditShowCurrentGrades] = useState(false);
    const [editShowRubricResults, setEditShowRubricResults] = useState(false);
    const [editShowProgressReports, setEditShowProgressReports] = useState(false);
    const [editShowReportCards, setEditShowReportCards] = useState(false);
    const [editShowParentComments, setEditShowParentComments] = useState(false);
    const [savingChildAccess, setSavingChildAccess] = useState(false);

    const [addChildOpen, setAddChildOpen] = useState(false);
    const [newChildName, setNewChildName] = useState("");
    const [newChildBirthday, setNewChildBirthday] = useState("");
    const [newChildGradeLevel, setNewChildGradeLevel] = useState("");
    const [newChildColor, setNewChildColor] = useState("#c084fc");
    const [newChildUsername, setNewChildUsername] = useState("");
    const [newChildPassword, setNewChildPassword] = useState("");
    const [newChildLoginEnabled, setNewChildLoginEnabled] = useState(true);
    const [newChildPicture, setNewChildPicture] = useState("");
    const [newShowAcademicRecord, setNewShowAcademicRecord] = useState(false);
    const [newShowCurrentGrades, setNewShowCurrentGrades] = useState(false);
    const [newShowRubricResults, setNewShowRubricResults] = useState(false);
    const [newShowProgressReports, setNewShowProgressReports] = useState(false);
    const [newShowReportCards, setNewShowReportCards] = useState(false);
    const [newShowParentComments, setNewShowParentComments] = useState(false);
    const [savingNewChild, setSavingNewChild] = useState(false);
    const [archiveChildTarget, setArchiveChildTarget] = useState(null);
    const [deleteChildTarget, setDeleteChildTarget] = useState(null);
    const [archivingChildId, setArchivingChildId] = useState("");
    const [deletingChildId, setDeletingChildId] = useState("");

    const [archivedChildren, setArchivedChildren] = useState([]);
    const [archivedChildrenOpen, setArchivedChildrenOpen] = useState(false);
    const [restoringChildId, setRestoringChildId] = useState("");
    const [subjects, setSubjects] = useState(() =>
    JSON.parse(localStorage.getItem("shadowGroveSubjects") || "[]")
);


const [subjectName, setSubjectName] = useState("");
const [showAddClassPopup, setShowAddClassPopup] = useState(false);
const [classStudentFilter, setClassStudentFilter] = useState("all");
const [showClassCreatedPopup, setShowClassCreatedPopup] = useState(false);
const [subjectColor, setSubjectColor] = useState("#7c3aed");
const [subjectChildId, setSubjectChildId] = useState("");

const [subjectScheduleMode, setSubjectScheduleMode] =
    useState("days");

const [subjectScheduleDays, setSubjectScheduleDays] =
    useState([]);

const [subjectScheduleStartTime, setSubjectScheduleStartTime] =
    useState("");

const [subjectScheduleEndTime, setSubjectScheduleEndTime] =
    useState("");

const [subjectScheduleBlock, setSubjectScheduleBlock] =
    useState("morning");

const [subjectScheduleWeeklyHours, setSubjectScheduleWeeklyHours] =
    useState("");

const [
    subjectScheduleCustomBlock,
    setSubjectScheduleCustomBlock,
] = useState("");

const [
    editingSubjectId,
    setEditingSubjectId,
] = useState("");
const [
    subjectCurriculumSources,
    setSubjectCurriculumSources,
] = useState([]);
const [
    curriculumBuilderResourceId,
    setCurriculumBuilderResourceId,
] = useState("");
const [
    resourceDeleteConfirm,
    setResourceDeleteConfirm,
] = useState(null);
const [
    resourceStructureModal,
    setResourceStructureModal,
] = useState(null);
const [
    classScheduleModal,
    setClassScheduleModal,
] = useState(null);

const [
    classScheduleDraft,
    setClassScheduleDraft,
] = useState({
    mode: "scheduled",

    days: [],

    start_time: "",
    end_time: "",

    weekly_hours: "",

    preferred_block: "morning",

    custom_block_label: "",
});
const [
    linkedResourcesModal,
    setLinkedResourcesModal,
] = useState(null);
const [
    curriculumPlanningModal,
    setCurriculumPlanningModal,
] = useState(null);
const [
    curriculumOwnerSubjectId,
    setCurriculumOwnerSubjectId,
] = useState("");
const [
    curriculumPlanningWizard,
    setCurriculumPlanningWizard,
] = useState({
    step: 1,

    planningStyle: "assisted",

    planningRange: "year",

    questMode: "parent_choice",

    sessionMode: "automatic",
});
const [
    linkedResourceDraft,
    setLinkedResourceDraft,
] = useState({
    name: "",
    resource_type: "workbook",
    has_structure: false,
    structure_type: "chapter",
    total_pages: "",
    content_start_page: "",
    structure: [],
});
const [
    deleteSubjectTarget,
    setDeleteSubjectTarget,
] = useState(null);

const [
    deletingSubjectId,
    setDeletingSubjectId,
] = useState("");

    const [calendarSidebarWidth, setCalendarSidebarWidth] = React.useState(360);
    const [calendarDrawerOpen, setCalendarDrawerOpen] = React.useState(false);
    const [isResizingCalendar, setIsResizingCalendar] = React.useState(false);
    
        const today = new Date();

const [calendarViewMode, setCalendarViewMode] = React.useState("month");

const [calendarContentView, setCalendarContentView] = React.useState("classes");

const [selectedCalendarDate, setSelectedCalendarDate] = React.useState(today);
const [schoolSetupOpen, setSchoolSetupOpen] = React.useState(false);
const [calendarEventOpen, setCalendarEventOpen] = React.useState(false);
const [periodAdvanceOpen, setPeriodAdvanceOpen] = React.useState(false);
const [shiftScheduleOpen, setShiftScheduleOpen] = React.useState(false);

const [shiftScheduleForm, setShiftScheduleForm] = React.useState({
    startDate: new Date().toISOString().slice(0, 10),
    dayOffset: 1,
    dateMode: "instructional",
    scope: "school",
    childId: "",
    subjectId: "",
    itemScope: "both",
    includeCompleted: false,
});
const getStartOfWeek = (date) => {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
};

const addDays = (date, amount) => {
    const d = new Date(date);
    d.setDate(d.getDate() + amount);
    return d;
};

const addMonths = (date, amount) => {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth() + amount, 1);
};

const parseLocalDate = (dateString) => {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
};
const getAgeFromBirthday = (birthday) => {
    if (!birthday) return null;

    const today = new Date();
    const birthDate = new Date(`${birthday}T00:00:00`);

    if (Number.isNaN(birthDate.getTime())) {
        return null;
    }

    let age =
        today.getFullYear() -
        birthDate.getFullYear();

    const monthDifference =
        today.getMonth() -
        birthDate.getMonth();

    if (
        monthDifference < 0 ||
        (monthDifference === 0 &&
            today.getDate() < birthDate.getDate())
    ) {
        age -= 1;
    }

    return age;
};
const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};
const getShadowGroveEvents = () => {
    try {
        const events = JSON.parse(localStorage.getItem("shadowGroveEvents") || "[]");
        return Array.isArray(events) ? events : [];
    } catch {
        return [];
    }
};
const saveShadowGroveEvents = (nextEvents) => {
    return saveClassroomEvents(nextEvents);
};
const getShadowGroveEventIdFromCalendarItem = (itemId) => {
    const calendarItemId = String(itemId ?? "");

    return calendarItemId.startsWith("class-")
        ? calendarItemId.slice("class-".length)
        : calendarItemId;
};

const getShadowGroveEventForCalendarItem = (itemId) => {
    const originalEventId =
        getShadowGroveEventIdFromCalendarItem(itemId);

    return (
        getShadowGroveEvents().find(
            (event) =>
                String(event.id) ===
                String(originalEventId)
        ) || null
    );
};

const updateShadowGroveEventDate = (
    event,
    targetDate
) => {
    const updatedEvent = {
        ...event,
        updated_at: new Date().toISOString(),
    };

    const primaryDateFields = [
        "date_start",
        "date",
        "due_date",
        "scheduled_date",
    ];

    let updatedExistingDate = false;

    primaryDateFields.forEach((fieldName) => {
        if (
            Object.prototype.hasOwnProperty.call(
                event,
                fieldName
            ) &&
            event[fieldName]
        ) {
            updatedEvent[fieldName] =
                targetDate;

            updatedExistingDate = true;
        }
    });

    if (
        Object.prototype.hasOwnProperty.call(
            event,
            "date_end"
        ) &&
        event.date_end
    ) {
        updatedEvent.date_end = targetDate;
    }

    if (!updatedExistingDate) {
        updatedEvent.date_start = targetDate;
    }

    return updatedEvent;
};

const updateShadowGroveEventTime = (
    event,
    targetTime
) => {
    const currentStartTime =
        event.scheduled_start_time ||
        event.start_time ||
        event.due_time ||
        "";

    const currentEndTime =
        event.scheduled_end_time ||
        event.end_time ||
        "";

    const targetEndTime = targetTime
        ? shiftEndTimeByDuration(
              currentStartTime,
              currentEndTime,
              targetTime
          )
        : "";

    const updatedEvent = {
        ...event,
        updated_at: new Date().toISOString(),
    };

    const startTimeFields = [
        "scheduled_start_time",
        "start_time",
        "due_time",
    ];

    const endTimeFields = [
        "scheduled_end_time",
        "end_time",
    ];

    let updatedExistingStartTime = false;
    let updatedExistingEndTime = false;

    startTimeFields.forEach((fieldName) => {
        if (
            Object.prototype.hasOwnProperty.call(
                event,
                fieldName
            )
        ) {
            updatedEvent[fieldName] =
                targetTime;

            updatedExistingStartTime = true;
        }
    });

    endTimeFields.forEach((fieldName) => {
        if (
            Object.prototype.hasOwnProperty.call(
                event,
                fieldName
            )
        ) {
            updatedEvent[fieldName] =
                targetEndTime;

            updatedExistingEndTime = true;
        }
    });

    if (!updatedExistingStartTime) {
        updatedEvent.scheduled_start_time =
            targetTime;
    }

    if (
        !updatedExistingEndTime &&
        targetEndTime
    ) {
        updatedEvent.scheduled_end_time =
            targetEndTime;
    }

    return updatedEvent;
};
const isShadowGroveSessionEvent = (event) => {
    return Boolean(
        event?.is_calendar_session ||
        event?.is_timed_session ||
        event?.created_from ===
            "class_timer_lesson_flow" ||
        event?.session_flow_id
    );
};

const getShadowGroveEventDate = (event) => {
    return (
        event?.date_start ||
        event?.date ||
        event?.due_date ||
        event?.scheduled_date ||
        ""
    );
};

const getShadowGroveSessionFlowEvents = (
    sourceEvent,
    allEvents = getShadowGroveEvents()
) => {
    if (
        !sourceEvent ||
        !isShadowGroveSessionEvent(sourceEvent)
    ) {
        return [];
    }

    const sourceDate =
        getShadowGroveEventDate(sourceEvent);

    if (sourceEvent.session_flow_id) {
        return allEvents.filter(
            (event) =>
                String(event.session_flow_id || "") ===
                    String(sourceEvent.session_flow_id) &&
                event.child_id === sourceEvent.child_id &&
                event.subject_id === sourceEvent.subject_id
        );
    }

    return allEvents.filter(
        (event) =>
            isShadowGroveSessionEvent(event) &&
            event.child_id === sourceEvent.child_id &&
            event.subject_id === sourceEvent.subject_id &&
            getShadowGroveEventDate(event) === sourceDate
    );
};
const isProfileShiftableSession = (event) =>
    Boolean(
        event?.is_calendar_session ||
        event?.is_timed_session ||
        event?.created_from === "class_timer_lesson_flow"
    );

const isProfileShiftableQuest = (event) => !isProfileShiftableSession(event);

const getProfileEventDate = (event) =>
    event.date_start ||
    event.date ||
    event.due_date ||
    event.scheduled_date;

const shiftDateString = (dateString, dayOffset) => {
    if (!dateString) return dateString;

    const date = parseLocalDate(dateString);
    date.setDate(date.getDate() + dayOffset);

    return formatLocalDate(date);
};

const shouldProfileShiftEvent = (event) => {
    const eventDate = getProfileEventDate(event);

    if (!eventDate) return false;
    if (parseLocalDate(eventDate) < parseLocalDate(shiftScheduleForm.startDate)) return false;

    if (!shiftScheduleForm.includeCompleted && event.completed) return false;

    if (shiftScheduleForm.scope === "child" && event.child_id !== shiftScheduleForm.childId) {
        return false;
    }

    if (shiftScheduleForm.scope === "class" && event.subject_id !== shiftScheduleForm.subjectId) {
        return false;
    }

    if (shiftScheduleForm.itemScope === "quests" && !isProfileShiftableQuest(event)) return false;
    if (shiftScheduleForm.itemScope === "sessions" && !isProfileShiftableSession(event)) return false;

    return true;
};

const getProfileShiftOffset = () => Number(shiftScheduleForm.dayOffset) || 1;

const getProfileShiftPreviewCount = () =>
    getShadowGroveEvents().filter(shouldProfileShiftEvent).length;

const applyProfileShiftSchedule = () => {
    if (!shiftScheduleForm.startDate) {
        toast.error("Choose a start date first.");
        return;
    }

    if (shiftScheduleForm.scope === "child" && !shiftScheduleForm.childId) {
        toast.error("Choose a child first.");
        return;
    }

    if (shiftScheduleForm.scope === "class" && !shiftScheduleForm.subjectId) {
        toast.error("Choose a class first.");
        return;
    }

    const storedEvents = getShadowGroveEvents();
    const dayOffset = getProfileShiftOffset();

    const shiftedCount = storedEvents.filter(shouldProfileShiftEvent).length;

    const updatedEvents = storedEvents.map((event) =>
        shouldProfileShiftEvent(event)
            ? {
                  ...event,
                  date: shiftDateString(event.date, dayOffset),
                  date_start: shiftDateString(event.date_start, dayOffset),
                  date_end: shiftDateString(event.date_end, dayOffset),
                  due_date: shiftDateString(event.due_date, dayOffset),
                  scheduled_date: shiftDateString(event.scheduled_date, dayOffset),
                  updated_at: new Date().toISOString(),
              }
            : event
    );

    saveShadowGroveEvents(updatedEvents);

    toast.success(`${shiftedCount} item${shiftedCount === 1 ? "" : "s"} shifted.`);
};
const getPreviousDateString = (dateString) => {
    const date = parseLocalDate(dateString);
    date.setDate(date.getDate() - 1);

    return formatLocalDate(date);
};
const visibleMonth = selectedCalendarDate.getMonth();
const visibleYear = selectedCalendarDate.getFullYear();

const firstDayOfMonth = new Date(visibleYear, visibleMonth, 1);
const lastDayOfMonth = new Date(visibleYear, visibleMonth + 1, 0);

const daysInMonth = lastDayOfMonth.getDate();
const startingDay = firstDayOfMonth.getDay();

const weekStart = getStartOfWeek(selectedCalendarDate);

const weekCells = Array.from({ length: 7 }, (_, index) =>
    addDays(weekStart, index)
);

const dayCell = selectedCalendarDate;
const getCalendarTimedLayout = (items = []) => {
    const normalizedItems = items
        .map((item) => {
            const startMinutes =
                timeToMinutes(item.time);

            if (startMinutes === null) {
                return null;
            }

            const parsedEndMinutes =
                timeToMinutes(item.endTime);

            const endMinutes =
                parsedEndMinutes !== null &&
                parsedEndMinutes > startMinutes
                    ? parsedEndMinutes
                    : startMinutes +
                      calendarTimelineSlotMinutes;

            return {
                item,
                startMinutes,
                endMinutes,
            };
        })
        .filter(Boolean)
        .sort((a, b) => {
            if (
                a.startMinutes !==
                b.startMinutes
            ) {
                return (
                    a.startMinutes -
                    b.startMinutes
                );
            }

            return (
                a.endMinutes -
                b.endMinutes
            );
        });

    const overlapGroups = [];
    let currentGroup = [];
    let currentGroupEnd = -1;

    normalizedItems.forEach((entry) => {
        const overlapsCurrentGroup =
            currentGroup.length > 0 &&
            entry.startMinutes <
                currentGroupEnd;

        if (
            currentGroup.length > 0 &&
            !overlapsCurrentGroup
        ) {
            overlapGroups.push(
                currentGroup
            );

            currentGroup = [];
            currentGroupEnd = -1;
        }

        currentGroup.push(entry);

        currentGroupEnd = Math.max(
            currentGroupEnd,
            entry.endMinutes
        );
    });

    if (currentGroup.length > 0) {
        overlapGroups.push(currentGroup);
    }

    return overlapGroups.flatMap(
        (group) => {
            const laneEndTimes = [];

            const assignedItems =
                group.map((entry) => {
                    let laneIndex =
                        laneEndTimes.findIndex(
                            (laneEnd) =>
                                laneEnd <=
                                entry.startMinutes
                        );

                    if (laneIndex === -1) {
                        laneIndex =
                            laneEndTimes.length;
                    }

                    laneEndTimes[laneIndex] =
                        entry.endMinutes;

                    return {
                        ...entry,
                        laneIndex,
                    };
                });

            const laneCount = Math.max(
                laneEndTimes.length,
                1
            );

            return assignedItems.map(
                (entry) => ({
                    ...entry,
                    laneCount,
                })
            );
        }
    );
};    
const calendarCells = [];

for (let i = 0; i < startingDay; i++) {
    calendarCells.push(null);
}

for (let day = 1; day <= daysInMonth; day++) {
    calendarCells.push(day);
}
        const timeToMinutes = (time) => {
    if (!time) return null;

    const [hours, minutes] = time.split(":").map(Number);

    return hours * 60 + minutes;
};

const minutesToTime = (totalMinutes) => {
    const safeMinutes = Math.max(0, Math.min(1439, totalMinutes));
    const hours = Math.floor(safeMinutes / 60);
    const minutes = safeMinutes % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const shiftEndTimeByDuration = (oldStart, oldEnd, newStart) => {
    const oldStartMinutes = timeToMinutes(oldStart);
    const oldEndMinutes = timeToMinutes(oldEnd);
    const newStartMinutes = timeToMinutes(newStart);

    if (
        oldStartMinutes === null ||
        oldEndMinutes === null ||
        newStartMinutes === null
    ) {
        return oldEnd || "";
    }

    const duration = Math.max(0, oldEndMinutes - oldStartMinutes);

    return minutesToTime(newStartMinutes + duration);
};

        const formatCalendarTime = (time) => {
    if (!time) return "";

    const [hourText, minute] = time.split(":");
    const hour = Number(hourText);

    const suffix = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;

    return `${displayHour}:${minute} ${suffix}`;
};

const getChildCalendarColor = (profileView) => {
    if (!profileView || profileView === "school") {
        return "#ffd166";
    }

    const child = children.find(
        (c) => c.child_id === profileView
    );

    return child?.calendarColor || "#7c3aed";
};
const attendanceLoadRevision = useRef(0);
const loadSavedAttendanceForChild = async (childId) => {
    const revision = ++attendanceLoadRevision.current;
    if (!childId || childId === "school") {
        setSavedAttendanceMap({});
        return;
    }

    const key = `shadowGroveAttendance_${childId}`;
    const records = JSON.parse(localStorage.getItem(key) || "[]");

    setSavedAttendanceMap(attendanceByDate(records));
    try {
        if (records.length) await api.post(`/children/${childId}/attendance/sync`, { records });
        const response = await api.get(`/children/${childId}/attendance`);
        if (revision !== attendanceLoadRevision.current || !Array.isArray(response.data)) return;
        localStorage.setItem(key, JSON.stringify(response.data));
        setSavedAttendanceMap(attendanceByDate(response.data));
    } catch { /* Keep the calendar's saved attendance available offline. */ }
};

const getSavedAttendanceForDate = (dateString) => {
    if (!dateString || calendarProfileView === "school") return null;

    return savedAttendanceMap[dateString] || null;
};
const getCalendarItemBorderColor = (item) => {
    if (item?.isVacationRecord) {
        return item?.color || "#ffd166";
    }

    if (item?.isClassCalendarItem) {
        return (
            item?.childColor ||
            item?.child_color ||
            getChildCalendarColor(item?.profileView) ||
            "#7c3aed"
        );
    }

    if (item?.profileView && item.profileView !== "school") {
        return getChildCalendarColor(item.profileView);
    }

    return item?.color || "#ffd166";
};

const getCalendarItemFillColor = (item) => {
    if (item?.isVacationRecord) {
        return item?.color || "#ffd166";
    }

    if (item?.isClassCalendarItem) {
        return (
            item?.classColor ||
            item?.class_color ||
            item?.calendar_color ||
            item?.color ||
            "#ffd166"
        );
    }

    return item?.color || "#ffd166";
};
   

   const getCalendarItemsForDay = (day, exactDate = null) => {
    if (!day) return [];

    const date = exactDate || new Date(visibleYear, visibleMonth, day);
    const dateString = localDay(date);

    const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
    ];

    const weekdayName = dayNames[date.getDay()];

    const regularItems = (schoolCalendar.items || []).filter((item) => {
        if (
            calendarProfileView !== "school" &&
            item.profileView !== calendarProfileView &&
            item.profileView !== "school"
        ) {
            return false;
        }

        if (item.frequency === "once") return dateString === item.start;

        if (item.frequency === "through" || item.frequency === "daily") {
            return dateString >= item.start && dateString <= item.end;
        }

        if (item.frequency === "weekly") {
            const itemStart = parseLocalDate(item.start);
            if ((item.skippedDates || []).includes(dateString)) return false;
            if (item.end && item.end !== item.start && dateString > item.end) return false;

            return dateString >= item.start && date.getDay() === itemStart.getDay();
        }

        if (item.frequency === "biweekly") {
            if ((item.skippedDates || []).includes(dateString)) return false;
            if (item.end && item.end !== item.start && dateString > item.end) return false;

            const startOfItem = parseLocalDate(item.start);
            const startOfCurrent = parseLocalDate(dateString);
            const diffDays = Math.round(
                (startOfCurrent.getTime() - startOfItem.getTime()) /
                    (1000 * 60 * 60 * 24)
            );

            return diffDays >= 0 && diffDays % 14 === 0;
        }

        if (item.frequency === "monthly") {
            if ((item.skippedDates || []).includes(dateString)) return false;
            if (item.end && item.end !== item.start && dateString > item.end) return false;

            const itemStart = parseLocalDate(item.start);

            return dateString >= item.start && date.getDate() === itemStart.getDate();
        }

        if (item.frequency === "custom") {
            return item.customDays?.includes(weekdayName);
        }

        if (item.frequency === "annual") {
            const itemDate = new Date(item.start);

            return (
                date.getMonth() === itemDate.getMonth() &&
                date.getDate() === itemDate.getDate()
            );
        }

        return dateString === item.start;
    });

    const vacationItems = (schoolCalendar.vacations || [])
        .filter((vacation) => {
            if (
                calendarProfileView !== "school" &&
                vacation.child_id !== calendarProfileView
            ) {
                return false;
            }

            const vacationStart = vacation.start || vacation.date_start;
            const vacationEnd = vacation.end || vacation.date_end || vacationStart;

            return dateString >= vacationStart && dateString <= vacationEnd;
        })
        .map((vacation) => {
            const vacationStart = vacation.start || vacation.date_start;
            const vacationEnd = vacation.end || vacation.date_end || vacationStart;

            return {
                id: `vacation-${vacation.id}`,
                title: `Vacation until ${vacationEnd}`,
                type: "vacation",
                start: vacationStart,
                end: vacationEnd,
                color: "#f59e0b",
                emoji: "⭐",
                time: "",
                endTime: "",
                blocksSchool: true,
                isVacationRecord: true,
            };
        });

    const savedTodos = readDashboardStorage("shadowGroveScheduledTodos", []);
    const classItems = groupLessonCalendarEvents([...getShadowGroveEvents(), ...(Array.isArray(savedTodos) ? savedTodos : [])].map(normalizeDashboardEvent))
        .map((event) => ({ ...event, ...event.assignment_time_slots?.[dateString] }))
        .filter((event) => {
            if (event.archived) return false;

            if (!classroomEventOnDate(event, dateString)) return false;

            if (
    calendarProfileView !== "school" &&
    String(event.child_id) !== String(calendarProfileView)
) {
    return false;
}

if (
    calendarClassView !== "all" &&
    String(event.subject_id) !== String(calendarClassView)
) {
    return false;
}

return true;
        })
        .map((event) => {
            const child = children.find((c) => c.child_id === event.child_id);
            const subject = subjects.find((s) => String(s.subject_id ?? s.id) === String(event.subject_id));

            const isSession =
                event.is_calendar_session ||
                event.is_timed_session ||
                event.created_from === "class_timer_lesson_flow";

            return {
                ...event,
                id: `class-${event.id}`,
                originalEventId: event.id,
                title: isSession ? event.session_lesson_name || event.title || "Class lesson" : `${event.is_todo_calendar_item ? "To do" : "Quest"}: ${event.title || "Untitled"}`,
                isTodoCalendarItem: Boolean(event.is_todo_calendar_item),
                type: isSession ? "class_session" : event.type || "quest",
                start:
                    event.date_start ||
                    event.date ||
                    event.due_date ||
                    event.scheduled_date,
                end:
                    event.date_end ||
                    event.date_start ||
                    event.date ||
                    event.due_date ||
                    event.scheduled_date,
                time:
                    event.scheduled_start_time ||
                    event.start_time ||
                    event.due_time ||
                    "",
                endTime:
                    event.scheduled_end_time ||
                    event.end_time ||
                    "",
                color:
                    subject?.color ||
                    event.class_color ||
                    event.calendar_color ||
                    event.color ||
                    "#ffd166",

                classColor:
                    subject?.color ||
                    event.class_color ||
                    event.calendar_color ||
                    event.color ||
                    "#ffd166",

                childColor:
                    event.child_color ||
                    child?.calendarColor ||
                    "#7c3aed",

                childName:
                    event.child_name ||
                    child?.name ||
                    "Child",

emoji: isSession ? "⏱️" : "📜",
                profileView: event.child_id,
                isClassCalendarItem: true,
                blocksSchool: false,
            };
        });

    const scheduledClassItems = subjects
    .filter((subject) => {
        const schedule = subject.class_schedule || {};

        if (schedule.mode === "block" || schedule.mode === "flexible") {
            return false;
        }

        if (!Array.isArray(schedule.days)) return false;
        if (!schedule.days.includes(weekdayName)) return false;

        if (
            calendarProfileView !== "school" &&
            String(subject.child_id) !== String(calendarProfileView)
        ) {
            return false;
        }

        if (
            calendarClassView !== "all" &&
            String(subject.subject_id || subject.id) !== String(calendarClassView)
        ) {
            return false;
        }

        return true;
    })
    .map((subject) => {
        const schedule = subject.class_schedule || {};
        const subjectId = subject.subject_id || subject.id;

        return {
            id: `scheduled-class-${subjectId}-${subject.child_id}-${dateString}`,
            title: `${subject.name} — Class`,
            type: "scheduled_class",
            start: dateString,
            end: dateString,
            time: schedule.start_time || "",
            endTime: schedule.end_time || "",
            color: subject.color || "#ffd166",
            classColor: subject.color || "#ffd166",
            profileView: subject.child_id,
            child_id: subject.child_id,
            subject_id: subjectId,
            emoji: "📚",
            isClassCalendarItem: true,
            isScheduledClass: true,
            blocksSchool: false,
        };
    });

return selectCalendarContent({ regularItems, vacationItems, scheduledClassItems, classItems, view: calendarContentView });
};
const calendarTimelineDates = calendarViewMode === "week" ? weekCells : [dayCell];
const calendarTimelineSlotMinutes = calendarViewMode === "day" && calendarContentView === "assignments" ? 5 : 15;
const calendarTimelineRange = calendarTimelineBounds({
    subjects, dates: calendarTimelineDates.map(localDay), profileView: calendarProfileView, classView: calendarClassView,
    items: calendarTimelineDates.flatMap((date) => getCalendarItemsForDay(date.getDate(), date)),
    slotMinutes: calendarTimelineSlotMinutes,
});
const calendarTimelineStartMinutes = calendarTimelineRange.start;
const calendarTimelineEndMinutes = calendarTimelineRange.end;
const calendarTimelineSlotHeight = 28;

const calendarTimelineSlots = Array.from(
    {
        length:
            (calendarTimelineEndMinutes -
                calendarTimelineStartMinutes) /
            calendarTimelineSlotMinutes,
    },
    (_, index) => {
        const totalMinutes =
            calendarTimelineStartMinutes +
            index * calendarTimelineSlotMinutes;

        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;
        const suffix = hour >= 12 ? "PM" : "AM";
        const displayHour = hour % 12 || 12;

        return {
            totalMinutes,
            hour,
            minute,
            time: `${String(hour).padStart(2, "0")}:${String(
                minute
            ).padStart(2, "0")}`,
            label: `${displayHour}:${String(minute).padStart(
                2,
                "0"
            )} ${suffix}`,
            isHour: minute === 0,
        };
    }
);

const calendarTimelineHeight =
    calendarTimelineSlots.length *
    calendarTimelineSlotHeight;

const getMonthCalendarItemsForDay = (day, exactDate = null) => {
    const items = getCalendarItemsForDay(day, exactDate);

    return [...items].sort((a, b) => {
        const aMinutes = timeToMinutes(a.time);
        const bMinutes = timeToMinutes(b.time);

        // All-day and unscheduled items stay at the top.
        if (aMinutes === null && bMinutes === null) {
            return 0;
        }

        if (aMinutes === null) return -1;
        if (bMinutes === null) return 1;

        // Timed items appear from earliest to latest.
        return aMinutes - bMinutes;
    });
};
    


    useClassroomDashboard({ role: "parent", children: unlocked ? children : [], subjects });

    const load = useCallback(async () => {
        try {
            const me = await api.get("/auth/me");

        setUser(me.data);

        const accessStatus =
            me.data?.access_status;

        const hasFullAccess =
            accessStatus === "active" ||
            accessStatus === "tester";

        if (!hasFullAccess) {
            // Keep the parent inside Account/Profile so they can
            // manage billing, reactivate, and see retention dates,
            // but do not load paid school data.
            setChildren([]);
            setSubjects([]);
            setPendingByChild({});

            return;
        }

        const [
            kids,
            subjectResponse,
        ] = await Promise.all([
            api.get("/children"),
            api.get("/subjects"),
        ]);

        const localChildren = JSON.parse(

    localStorage.getItem(
        "shadowGroveChildren"
    ) || "[]"
).map(ensureChildDefaults);

const localChildrenById =
    Object.fromEntries(
        localChildren.map((localChild) => [
            String(localChild.child_id),
            localChild,
        ])
    );

const actualChildren =
    kids.data.map((serverChild) =>
        ensureChildDefaults({
            ...localChildrenById[
                String(
                    serverChild.child_id
                )
            ],
            ...serverChild,
        })
    );

localStorage.setItem(
    "shadowGroveChildren",
    JSON.stringify(actualChildren)
);

setChildren(actualChildren);

const actualSubjects = Array.isArray(
    subjectResponse.data
)
    ? subjectResponse.data
    : [];

const priorSubjects = JSON.parse(localStorage.getItem("shadowGroveSubjects") || "[]");
const legacySubjects = JSON.parse(localStorage.getItem("shadowGroveLegacySubjects") || "[]");
localStorage.setItem("shadowGroveLegacySubjects", JSON.stringify([...new Map([...legacySubjects, ...priorSubjects].map((item) => [`${item.child_id}:${item.subject_id ?? item.id}`, item])).values()]));

localStorage.setItem(
    "shadowGroveSubjects",
    JSON.stringify(actualSubjects)
);

setSubjects(actualSubjects);
// Preserve the old IDs until all classes and children have been matched together.
saveClassroomEvents(reconcileClassroomEvents(getShadowGroveEvents(), actualSubjects,
    JSON.parse(localStorage.getItem("shadowGroveLegacySubjects") || "[]"), actualChildren, localChildren));

            const savedQuests = JSON.parse(
                localStorage.getItem("shadowGroveQuests") || "[]"
            );


            const map = {};


            kids.data.forEach((c) => {
                map[c.child_id] = savedQuests.filter(
                    (q) => q.child_id === c.child_id && q.status === "pending"
                ).length;
            });


            setPendingByChild(map);
        } catch {
            const localChildren = JSON.parse(
                localStorage.getItem("shadowGroveChildren") || "[]"
            ).map(ensureChildDefaults);


            localStorage.setItem(
                "shadowGroveChildren",
                JSON.stringify(localChildren)
            );


            setUser({
                email: "local@test.com",
                title: "Parent",
            });


            setChildren(localChildren);
            setPendingByChild({});
        }
    }, []);


    useEffect(() => {
        if (unlocked) load();
    }, [unlocked, load]);
    useEffect(() => {
    loadSavedAttendanceForChild(calendarProfileView);
}, [calendarProfileView]);

    const handleLogout = async () => {
    setParentUnlocked(false);

    await api.post("/auth/logout").catch(() => {});

    navigate("/login");
    };

    const startBillingCheckout = async (interval) => {
        try {
            const response = await api.post(
                "/billing/checkout",
                {
                    interval,
                }
            );

            const checkoutUrl =
                response.data?.checkout_url;

            if (!checkoutUrl) {
                toast.error(
                    "Stripe Checkout did not return a payment link."
                );
                return;
            }

            window.location.href = checkoutUrl;
        } catch (error) {
            console.error(
                "Unable to start billing checkout:",
                error
            );

            toast.error(
                error?.response?.data?.detail ||
                    "Unable to open Stripe Checkout."
            );
        }
    };

    const saveParentIdentity = () => {
        localStorage.setItem("shadowGroveParentName", parentName);
        localStorage.setItem("shadowGroveParentPicture", parentPicture);


        setUser({
            ...user,
            name: parentName,
            picture: parentPicture,
        });


        toast.success("Parent profile updated.");
    };


    const saveSchoolYearSettings = () => {
        localStorage.setItem(
            "shadowGroveSchoolYearSettings",
            JSON.stringify(schoolYearSettings)

        );
        localStorage.setItem("shadowGroveSchoolBannerImage", schoolBannerImage);


        toast.success("School year settings saved.");
    };

      const saveAttendance = () => {
    if (!attendanceChild) {
        toast.error("Choose a child first.");
        return;
    }

    if (!attendanceDate) {
        toast.error("Choose a date first.");
        return;
    }

    saveAttendanceForDate(attendanceChild, attendanceDate, attendanceStatus);
};

const saveAttendanceForDate = (childId, dateString, status) => {
    if (!childId || childId === "school") {
        toast.error("Choose one child in Calendar View before marking attendance.");
        return;
    }

    if (!dateString) {
        toast.error("Missing attendance date.");
        return;
    }

    const key = `shadowGroveAttendance_${childId}`;
    const oldRecords = JSON.parse(localStorage.getItem(key) || "[]");
    attendanceLoadRevision.current += 1;

    const record = {
        id: `${childId}_${dateString}_${Date.now()}`,
        child_id: childId,
        date: dateString,
        status,
        hours: 0,
        source: "calendar_button",
        codex_ready: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
    };

    const nextRecords = [
        record,
        ...oldRecords.filter((oldRecord) => oldRecord.date !== dateString),
    ];

    localStorage.setItem(key, JSON.stringify(nextRecords));
    queueAttendanceSync(childId, nextRecords);

    setAttendanceChild(childId);
    setAttendanceDate(dateString);
    setAttendanceStatus(status);
    setSavedAttendanceMap((prev) => ({
    ...prev,
    [dateString]: record,
}));
   

    toast.success(status === "present" ? "Marked present." : "Marked absent.");
};

    const openAbsenceActionPopup = (childId, dateString) => {
    if (!childId || childId === "school") {
        toast.error("Choose one child in Calendar View before marking absent.");
        return;
    }

    if (!dateString) {
        toast.error("Missing absence date.");
        return;
    }

    setAbsenceActionPopup({
        childId,
        dateString,
    });
};
const getAbsenceChildName = () => {
    if (!absenceActionPopup?.childId) return "Selected Child";

    const child = children.find(
        (c) => c.child_id === absenceActionPopup.childId
    );

    return child?.name || "Selected Child";
};
const getAbsenceDateLabel = () => {
    if (!absenceActionPopup?.dateString) return "";

    if (absenceRecoveryForm.multiDay && absenceRecoveryForm.endDate) {
        return `${absenceActionPopup.dateString} to ${absenceRecoveryForm.endDate}`;
    }

    return absenceActionPopup.dateString;
};
const getAbsencePreview = () => {
    const dates = getAbsenceDateRange();

    const events = getShadowGroveEvents().filter((event) => {
        if (event.archived) return false;
        if (event.child_id !== absenceActionPopup?.childId) return false;

        const eventDate =
            event.date_start ||
            event.date ||
            event.due_date ||
            event.scheduled_date;

        return dates.includes(eventDate);
    });

    const sessions = events.filter(
        (event) =>
            event.is_calendar_session ||
            event.is_timed_session ||
            event.created_from === "class_timer_lesson_flow"
    );

    const quests = events.filter(
        (event) =>
            !event.is_calendar_session &&
            !event.is_timed_session &&
            event.created_from !== "class_timer_lesson_flow"
    );

    return {
        dates,
        sessions,
        quests,
    };
};
const childHasClassOnDate = (childId, dateString) => {
    if (!childId || !dateString) return false;

    const date = parseLocalDate(dateString);

    const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
    ];

    const weekdayName = dayNames[date.getDay()];

    const childSubjects = subjects.filter(
        (subject) => subject.child_id === childId
    );

    const schedules = childSubjects
        .map((subject) => ({
            subject,
            schedule: getClassScheduleForSubject(subject.id),
        }))
        .filter(({ schedule }) => schedule?.days?.length);

    if (schedules.length === 0) {
        return date.getDay() !== 0 && date.getDay() !== 6;
    }

    return schedules.some(({ schedule }) =>
        schedule.days.includes(weekdayName)
    );
};
    const getAbsenceDateRange = () => {
    if (!absenceActionPopup?.dateString) return [];

    const startDate = parseLocalDate(absenceActionPopup.dateString);
    const endDate =
        absenceRecoveryForm.multiDay && absenceRecoveryForm.endDate
            ? parseLocalDate(absenceRecoveryForm.endDate)
            : startDate;

    if (endDate < startDate) {
        return [absenceActionPopup.dateString];
    }

    const dates = [];
    const current = new Date(startDate);

    while (current <= endDate) {
        const dateString = formatLocalDate(current);

        const isBlocked = isBlockedSchoolDate(dateString);
        const hasClass = childHasClassOnDate(
            absenceActionPopup.childId,
            dateString
        );

        if (!isBlocked && hasClass) {
            dates.push(dateString);
        }

        current.setDate(current.getDate() + 1);
    }

    return dates;
};
const confirmAbsenceOnly = () => {
    if (!absenceActionPopup) return;

    const datesToMark = getAbsenceDateRange();

    if (datesToMark.length === 0) {
        toast.error("No class days found in that absence range.");
        return;
    }

    datesToMark.forEach((dateString) => {
        saveAttendanceForDate(
            absenceActionPopup.childId,
            dateString,
            "absent"
        );
    });

    loadSavedAttendanceForChild(absenceActionPopup.childId);

    toast.success(
        datesToMark.length === 1
            ? "Absence recorded."
            : `${datesToMark.length} absence days recorded.`
    );

    setAbsenceActionPopup(null);
};
    
const getClassScheduleForSubject = (subjectId) => {
    if (!subjectId) return null;

    return JSON.parse(
        localStorage.getItem(`shadowGroveClassSchedule_${subjectId}`) || "null"
    );
};

const isBlockedSchoolDate = (dateString) => {
    const date = parseLocalDate(dateString);
    const day = date.getDate();

    const vacationBlocks = (schoolCalendar.vacations || []).some((vacation) => {
        const start = vacation.start || vacation.date_start;
        const end = vacation.end || vacation.date_end || start;

        return dateString >= start && dateString <= end;
    });

    if (vacationBlocks) return true;

    const blockedItems = (schoolCalendar.items || []).some((item) => {
        if (!item.blocksSchool) return false;

        const itemsForDate = getCalendarItemsForDay(day, date);

        return itemsForDate.some(
            (calendarItem) =>
                calendarItem.id === item.id ||
                calendarItem.parentRecurringId === item.id
        );
    });

    return blockedItems;
};

const isValidInstructionalDateForEvent = (event, dateString) => {
    if (isBlockedSchoolDate(dateString)) return false;

    const date = parseLocalDate(dateString);
    const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
    ];

    const weekdayName = dayNames[date.getDay()];

    const classSchedule = getClassScheduleForSubject(event.subject_id);

    if (classSchedule?.days?.length) {
        return classSchedule.days.includes(weekdayName);
    }

    return date.getDay() !== 0 && date.getDay() !== 6;
};

const getNextInstructionalDateForEvent = (event, afterDateString) => {
    let nextDate = parseLocalDate(afterDateString);

    for (let i = 0; i < 90; i += 1) {
        nextDate.setDate(nextDate.getDate() + 1);

        const nextDateString = formatLocalDate(nextDate);

        if (isValidInstructionalDateForEvent(event, nextDateString)) {
            return nextDateString;
        }
    }

    return afterDateString;
};



const moveTodayLessonsForAbsence = () => {
    if (!absenceActionPopup) return;

    const absenceDates = getAbsenceDateRange();
    const lastAbsenceDate = absenceDates[absenceDates.length - 1];

    const storedEvents = getShadowGroveEvents();

    let movedCount = 0;

    const updatedEvents = storedEvents.map((event) => {
        if (event.archived) return event;
        if (event.child_id !== absenceActionPopup.childId) return event;
        if (event.completed) return event;

        const eventDate = getEventDateForRecovery(event);

        if (!absenceDates.includes(eventDate)) return event;

        const nextDate = getNextInstructionalDateForEvent(
            event,
            lastAbsenceDate
        );

        movedCount += 1;

        return moveEventToRecoveryDate({
    event,
    nextDateString: nextDate,
    reason: absenceRecoveryForm.reason,
    note: absenceRecoveryForm.note,
    recoveryPlan: absenceRecoveryForm.recoveryPlan,
});
    });

    saveShadowGroveEvents(updatedEvents);

    return movedCount;
};
    const applyAbsenceRecoveryPlan = () => {
    if (!absenceActionPopup) return;

    if (absenceRecoveryForm.recoveryPlan === "absent_only") {
        confirmAbsenceOnly();
        return;
    }

   if (absenceRecoveryForm.recoveryPlan === "move_today") {
    confirmAbsenceOnly();

    const movedCount = moveTodayLessonsForAbsence();

    toast.success(
        movedCount === 1
            ? "1 lesson or quest moved to the next instructional day."
            : `${movedCount} lessons and quests moved to the next instructional day.`
    );

    setAbsenceActionPopup(null);
    return;
}

    if (absenceRecoveryForm.recoveryPlan === "shift_future") {
        saveAttendanceForDate(
            absenceActionPopup.childId,
            absenceActionPopup.dateString,
            "absent"
        );

        toast.info("Shift the Entire Schedule will be connected next.");
        setAbsenceActionPopup(null);
    }
};
    const saveVacationDays = () => {
        if (!attendanceChild) {
            toast.error("Choose a child first.");
            return;
        }

       
        if (!vacationStart || !vacationEnd) {
            toast.error("Choose start and end dates.");
            return;
        }

        toast.error("Choose a child first.");
        const key = `shadowGroveAttendance_${attendanceChild}`;
        const oldRecords = JSON.parse(localStorage.getItem(key) || "[]");
        const updatedCalendar = {
    ...schoolCalendar,
    vacations: [
        ...schoolCalendar.vacations,
        {
            id: Date.now().toString(),
            start: vacationStart,
            end: vacationEnd,
            child_id: attendanceChild,
        },
    ],
};

localStorage.setItem(
    schoolCalendarKey,
    JSON.stringify(updatedCalendar)
);

setSchoolCalendar(updatedCalendar);

notifySchoolCalendarUpdated();

        const record = {
            id: Date.now().toString(),
            child_id: attendanceChild,
            date_start: vacationStart,
            date_end: vacationEnd,
            status: "vacation",
            created_at: new Date().toISOString(),
        };

        localStorage.setItem(key, JSON.stringify([record, ...oldRecords]));
        attendanceLoadRevision.current += 1;
        queueAttendanceSync(attendanceChild, [record, ...oldRecords]);


        toast.success("Vacation days recorded.");
    };

    const getNextGradeLevel = (currentGrade) => {
    const gradeOrder = [
        "Pre-K",
        "Kindergarten",
        "1st Grade",
        "2nd Grade",
        "3rd Grade",
        "4th Grade",
        "5th Grade",
        "6th Grade",
        "7th Grade",
        "8th Grade",
        "9th Grade",
        "10th Grade",
        "11th Grade",
        "12th Grade",
        "Graduated",
    ];

    const index = gradeOrder.indexOf(currentGrade);

    if (index === -1) return currentGrade || "";
    return gradeOrder[Math.min(index + 1, gradeOrder.length - 1)];
};

const confirmAdvancePeriod = async () => {
    if (!advanceChildId) {
        toast.error("Choose a child first.");
        return;
    }

    if (!advancePeriodKey.trim()) {
        toast.error("Enter a period label first, like Fall 2026 or Quarter 1.");
        return;
    }

    const lockKey = `shadowGroveAdvanceLock_${advanceChildId}_${advancePeriodKey}`;

    if (localStorage.getItem(lockKey) === "true") {
        toast.error("This child has already advanced for this period.");
        return;
    }

    const shouldPromoteGrade =
        advanceType === "promote_grade" ||
        advanceType === "promote_grade_and_period";


    const savedChildren = JSON.parse(
        localStorage.getItem("shadowGroveChildren") || "[]"
    ).map(ensureChildDefaults);

    const updatedChildren = savedChildren.map((child) => {
        if (child.child_id !== advanceChildId) return child;

       

        return {
            ...child,
            gradeLevel: shouldPromoteGrade
                ? getNextGradeLevel(child.gradeLevel)
                : child.gradeLevel,
        };
    });

    if (shouldPromoteGrade) {
        try {
            const records = JSON.parse(localStorage.getItem(`shadowGroveAttendance_${advanceChildId}`) || "[]");
            if (records.length) queueAttendanceSync(advanceChildId, records);
            await waitForClassroomSync();
            await api.put(`/children/${advanceChildId}`, { gradeLevel: updatedChildren.find((child) => child.child_id === advanceChildId).gradeLevel });
        } catch {
            toast.error("The new grade level could not be saved. Please try again.");
            return;
        }
    }
    localStorage.setItem("shadowGroveChildren", JSON.stringify(updatedChildren));

    localStorage.setItem(lockKey, "true");

    setChildren(updatedChildren);

    const advancedChild = updatedChildren.find(
        (child) => child.child_id === advanceChildId
    );

    toast.success(
        `${advancedChild.name} updated: ${advancedChild.gradeLevel || "No grade set"}.`
    );
};
 const saveCalendarItem = () => {
    if (!calendarItemName.trim()) {
        toast.error("Name this calendar item first.");
        return;
    }

    if (!calendarItemStartDate) {
        toast.error("Choose a start date.");
        return;
    }
    rememberCalendarBeforeChange();
    const currentCalendar =
        JSON.parse(localStorage.getItem("shadowGroveSchoolCalendar") || "null") || {
            holidays: [],
            vacations: [],
            daysOff: [],
            items: [],
        };

    const newItem = {
        id: Date.now().toString(),
        type: calendarItemType,
        title: calendarItemName.trim(),
        description: calendarItemDescription.trim(),
        location: calendarItemLocation.trim(),
        start: calendarItemStartDate,
        end: calendarItemEndDate || calendarItemStartDate,
        frequency: calendarItemFrequency,
        customDays: calendarItemCustomDays,
        time: calendarItemTime,
        endTime: calendarItemEndTime,
        blocksSchool: calendarItemBlocksSchool,
        color: calendarItemColor,
        emoji: calendarItemEmoji,
        profileView:
    calendarItemProfileView || "school",
        created_at: new Date().toISOString(),
    };

    const updatedCalendar = {
    ...currentCalendar,
    items: editingCalendarItemId
        ? (currentCalendar.items || []).map((item) =>
              item.id === editingCalendarItemId ? newItem : item
          )
        : [newItem, ...(currentCalendar.items || [])],
};

    localStorage.setItem(
        "shadowGroveSchoolCalendar",
        JSON.stringify(updatedCalendar)
    );
    setSchoolCalendar(updatedCalendar);
    setCalendarItemName("");
    notifySchoolCalendarUpdated();
    setCalendarItemDescription("");
    setCalendarItemLocation("");
    setCalendarItemStartDate("");
    setCalendarItemEndDate("");
    setCalendarItemFrequency("once");
    setCalendarItemTime("");
    setCalendarItemBlocksSchool(false);
    setCalendarItemColor("#7c3aed");
    setCalendarItemCustomDays([]);
    toast.success("Calendar item saved.");
    setCalendarItemCustomDays([]);
setEditingCalendarItemId(null);
toast.success(editingCalendarItemId ? "Calendar item updated." : "Calendar item saved.");
};

const editCalendarItem = (item) => {
    setEditingCalendarItemId(item.id);
    setCalendarItemType(item.type || "appointment");
    setCalendarItemName(item.title || "");
    setCalendarItemDescription(item.description || "");
    setCalendarItemLocation(item.location || "");
    setCalendarItemStartDate(item.start || "");
    setCalendarItemEndDate(item.end || "");
    setCalendarItemFrequency(item.frequency || "once");
    setCalendarItemTime(item.time || "");
    setCalendarItemBlocksSchool(!!item.blocksSchool);
    setCalendarItemCustomDays(item.customDays || []);
    setCalendarItemColor(item.color || "#7c3aed");

    toast.success("Calendar item loaded for editing.");
};

const deleteCalendarItem = (itemId, occurrenceDate = "") => {
    if (String(itemId).startsWith("class-todo:")) { toast.error("Archive or edit this scheduled item in its dashboard to-do list."); return; }
const isShadowGroveCalendarItem =
    String(itemId).startsWith("class-");

if (isShadowGroveCalendarItem) {
    const sourceEvent =
        getShadowGroveEventForCalendarItem(itemId);

    if (!sourceEvent) {
        toast.error(
            "Could not find the class calendar item."
        );
        return;
    }

    if (
        isShadowGroveSessionEvent(
            sourceEvent
        )
    ) {
        setSessionDeletePopup({
            itemId,
            originalEventId:
                sourceEvent.id,
            sourceEvent,
        });

        return;
    }

    if (assignmentSeriesIdentity(sourceEvent)) { setPendingAssignmentDelete(sourceEvent); return; }

    const storedEvents =
        getShadowGroveEvents();

    const updatedEvents =
        storedEvents.filter(
            (event) =>
                String(event.id) !==
                String(sourceEvent.id)
        );

    saveShadowGroveEvents(
        updatedEvents
    );

    setSchoolCalendar(
        (currentCalendar) => ({
            ...currentCalendar,
        })
    );

    setDraggedCalendarItemId(null);
    setDraggedCalendarSourceDate("");

    toast.success(
        "Class calendar item deleted."
    );

    return;
}

       if (
    itemToDelete &&
    (
        ["weekly", "biweekly", "monthly"].includes(
            itemToDelete.frequency
        ) ||
        itemToDelete.isRecurringOverride
    )
) {
        setRecurringDeletePopup({
            itemId,
            occurrenceDate:
                occurrenceDate ||
                itemToDelete.originalRecurringDate ||
                itemToDelete.start,
        });

        return;
    }

    rememberCalendarBeforeChange();

    const currentCalendar =
        JSON.parse(localStorage.getItem("shadowGroveSchoolCalendar") || "null") || {
            holidays: [],
            vacations: [],
            daysOff: [],
            items: [],
        };

    const updatedCalendar = {
        ...currentCalendar,
        items: (currentCalendar.items || []).filter(
            (item) => item.id !== itemId
        ),
    };

    localStorage.setItem(
        "shadowGroveSchoolCalendar",
        JSON.stringify(updatedCalendar)
    );

    setSchoolCalendar(updatedCalendar);
    notifySchoolCalendarUpdated();
    toast.success("Calendar item deleted.");
};

    const applyRecurringDeleteChoice = (choice) => {
    if (!recurringDeletePopup) return;

    rememberCalendarBeforeChange();

    const currentCalendar =
        JSON.parse(localStorage.getItem("shadowGroveSchoolCalendar") || "null") || {
            holidays: [],
            vacations: [],
            daysOff: [],
            items: [],
        };

    const sourceItem = (currentCalendar.items || []).find(
        (item) => item.id === recurringDeletePopup.itemId
    );

    if (!sourceItem) {
        toast.error("Could not find recurring item.");
        setRecurringDeletePopup(null);
        return;
    }

    const originalSeriesId = sourceItem.parentRecurringId || sourceItem.id;

    let updatedItems = currentCalendar.items || [];

    if (choice === "one_time") {
        if (sourceItem.isRecurringOverride) {
            updatedItems = updatedItems.filter(
                (item) => item.id !== sourceItem.id
            );
        } else {
            updatedItems = updatedItems.map((item) =>
                item.id === originalSeriesId
                    ? {
                          ...item,
                          skippedDates: Array.from(
                              new Set([
                                  ...(item.skippedDates || []),
                                  recurringDeletePopup.occurrenceDate,
                              ])
                          ),
                      }
                    : item
            );
        }
    }

    if (choice === "all") {
        updatedItems = updatedItems.filter(
            (item) =>
                item.id !== originalSeriesId &&
                item.parentRecurringId !== originalSeriesId
        );
    }

    const updatedCalendar = {
        ...currentCalendar,
        items: updatedItems,
    };

    localStorage.setItem(
        "shadowGroveSchoolCalendar",
        JSON.stringify(updatedCalendar)
    );

    setSchoolCalendar(updatedCalendar);
    notifySchoolCalendarUpdated();
    setRecurringDeletePopup(null);

    toast.success(
        choice === "one_time"
            ? "Recurring occurrence deleted."
            : "Recurring schedule deleted."
    );
};
 const updateCalendarPopupItem = (field, value) => {
    setCalendarEditPopupItem((prev) => ({
        ...prev,
        [field]: value,
    }));
};
            const saveCalendarPopupItem = () => {
    if (!calendarEditPopupItem?.title?.trim()) {
        toast.error("Name this calendar item first.");
        return;
    }

    const isRecurringEdit =
        ["weekly", "biweekly", "monthly"].includes(
            calendarEditPopupItem.frequency
        ) ||
        calendarEditPopupItem.isRecurringOverride;

    if (isRecurringEdit) {
        setRecurringEditPopup({
    item: calendarEditPopupItem,
    occurrenceDate:
        calendarEditPopupItem.visibleOccurrenceDate ||
        calendarEditPopupItem.originalRecurringDate ||
        calendarEditPopupItem.start,
});

setCalendarEditPopupItem(null);

return;
    }

    rememberCalendarBeforeChange();

    const currentCalendar =
        JSON.parse(localStorage.getItem("shadowGroveSchoolCalendar") || "null") || {
            holidays: [],
            vacations: [],
            daysOff: [],
            items: [],
        };

    const updatedCalendar = {
        ...currentCalendar,
        items: (currentCalendar.items || []).map((item) =>
            item.id === calendarEditPopupItem.id
                ? calendarEditPopupItem
                : item
        ),
    };

    localStorage.setItem(
        "shadowGroveSchoolCalendar",
        JSON.stringify(updatedCalendar)
    );

setSchoolCalendar(updatedCalendar);
notifySchoolCalendarUpdated();
setCalendarEditPopupItem(null);

toast.success("Calendar item updated.");
};

const applyRecurringEditChoice = (choice) => {
    if (!recurringEditPopup?.item) return;

    rememberCalendarBeforeChange();

    const editedItem = recurringEditPopup.item;

    const currentCalendar =
        JSON.parse(localStorage.getItem("shadowGroveSchoolCalendar") || "null") || {
            holidays: [],
            vacations: [],
            daysOff: [],
            items: [],
        };

    const sourceItem = (currentCalendar.items || []).find(
        (item) => item.id === editedItem.id
    );

    if (!sourceItem) {
        toast.error("Could not find recurring item.");
        setRecurringEditPopup(null);
        return;
    }

    const originalSeriesId = sourceItem.parentRecurringId || sourceItem.id;
    const occurrenceDate =
        recurringEditPopup.occurrenceDate ||
        sourceItem.originalRecurringDate ||
        sourceItem.start;

    let updatedItems = currentCalendar.items || [];

    if (choice === "one_time") {
        if (sourceItem.isRecurringOverride) {
            updatedItems = updatedItems.map((item) =>
                item.id === sourceItem.id ? editedItem : item
            );
        } else {
            const overrideItem = {
                ...editedItem,
                id: `${sourceItem.id}-edit-${occurrenceDate}`,
                start: occurrenceDate,
                end: occurrenceDate,
                frequency: "once",
                parentRecurringId: originalSeriesId,
                originalRecurringDate: occurrenceDate,
                isRecurringOverride: true,
            };

            updatedItems = updatedItems.map((item) =>
                item.id === originalSeriesId
                    ? {
                          ...item,
                          skippedDates: Array.from(
                              new Set([
                                  ...(item.skippedDates || []),
                                  occurrenceDate,
                              ])
                          ),
                      }
                    : item
            );

            updatedItems = updatedItems.filter(
                (item) => item.id !== overrideItem.id
            );

            updatedItems = [overrideItem, ...updatedItems];
        }
    }

    if (choice === "future") {
        const oldSeriesEnd = getPreviousDateString(occurrenceDate);

        const futureSeries = {
            ...editedItem,
            id: `${originalSeriesId}-edited-future-${occurrenceDate}`,
            start: occurrenceDate,
            end: "",
            frequency:
                sourceItem.frequency === "once" && sourceItem.parentRecurringId
                    ? "weekly"
                    : sourceItem.frequency,
            skippedDates: [],
            parentRecurringId: "",
            originalRecurringDate: "",
            isRecurringOverride: false,
        };

        updatedItems = updatedItems
            .filter(
                (item) =>
                    item.id !== sourceItem.id &&
                    item.parentRecurringId !== originalSeriesId
            )
            .map((item) =>
                item.id === originalSeriesId
                    ? {
                          ...item,
                          end: oldSeriesEnd,
                      }
                    : item
            );

        updatedItems = [futureSeries, ...updatedItems];
    }

    if (choice === "all") {
        updatedItems = updatedItems
            .filter((item) => item.parentRecurringId !== originalSeriesId)
            .map((item) =>
                item.id === originalSeriesId
                    ? {
                          ...editedItem,
                          id: originalSeriesId,
                          skippedDates: [],
                          parentRecurringId: "",
                          originalRecurringDate: "",
                          isRecurringOverride: false,
                      }
                    : item
            );
    }

    const updatedCalendar = {
        ...currentCalendar,
        items: updatedItems,
    };

    localStorage.setItem(
        "shadowGroveSchoolCalendar",
        JSON.stringify(updatedCalendar)
    );

    setSchoolCalendar(updatedCalendar);
    notifySchoolCalendarUpdated();
    setRecurringEditPopup(null);
    setCalendarEditPopupItem(null);

    toast.success("Recurring event updated.");
};

    const addQuizQuestion = () => {
        if (quizQuestions.length >= 10) return;


        setQuizQuestions([
            ...quizQuestions,
            {
                question: "",
                options: ["", "", "", ""],
                correct: 0,
            },
        ]);
    };
        const moveCalendarItemToDate = (itemId, targetDate) => {
    if (
        String(itemId).startsWith("vacation-") ||
        String(itemId).includes("vacation")
    ) {
        toast.error("Vacation records must be edited, not dragged.");
        setDraggedCalendarItemId(null);
        return;
    }
    const isShadowGroveCalendarItem =
    String(itemId).startsWith("class-");

if (isShadowGroveCalendarItem) {
    const sourceEvent =
        getShadowGroveEventForCalendarItem(
            itemId
        );

    if (!sourceEvent) {
        toast.error(
            "Could not find the class calendar item."
        );

        setDraggedCalendarItemId(null);
        setDraggedCalendarSourceDate("");

        return;
    }

    const sourceTime =
        sourceEvent.scheduled_start_time ||
        sourceEvent.start_time ||
        sourceEvent.due_time ||
        "";

    if (
        isShadowGroveSessionEvent(
            sourceEvent
        )
    ) {
        setSessionMovePopup({
            itemId,
            sourceEvent,
            targetDate,
            targetTime: sourceTime,
            mode: "date",
        });

        setDraggedCalendarItemId(null);
        setDraggedCalendarSourceDate("");

        return;
    }

    const storedEvents =
        getShadowGroveEvents();

    const updatedEvents =
        storedEvents.map((event) => {
            if (
                String(event.id) !==
                String(sourceEvent.id)
            ) {
                return event;
            }

            return updateShadowGroveEventDate(
                event,
                targetDate
            );
        });

    saveShadowGroveEvents(
        updatedEvents
    );

    setSchoolCalendar(
        (currentCalendar) => ({
            ...currentCalendar,
        })
    );

    setDraggedCalendarItemId(null);
    setDraggedCalendarSourceDate("");

    toast.success(
        "Class calendar item moved."
    );

    return;
}
    const itemBeingMoved = (schoolCalendar.items || []).find(
        (item) => item.id === itemId
    );

    if (itemBeingMoved?.type === "vacation") {
        toast.error("Vacation records must be edited, not dragged.");
        setDraggedCalendarItemId(null);
        return;
    }
if (
    ["weekly", "biweekly", "monthly"].includes(itemBeingMoved?.frequency) ||
    itemBeingMoved?.isRecurringOverride
) {
    setRecurringMovePopup({
        itemId,
        targetDate,
        originalDate:
            itemBeingMoved?.isRecurringOverride
                ? itemBeingMoved.start
                : draggedCalendarSourceDate,
        mode: "date",
    });

    setDraggedCalendarItemId(null);
    setDraggedCalendarSourceDate("");
    return;
}
    rememberCalendarBeforeChange();

    const currentCalendar =
        JSON.parse(localStorage.getItem("shadowGroveSchoolCalendar") || "null") || {
            holidays: [],
            vacations: [],
            daysOff: [],
            items: [],
        };

    const updatedCalendar = {
        ...currentCalendar,
        items: (currentCalendar.items || []).map((item) => {
            if (item.id !== itemId) return item;

            return {
                ...item,
                start: targetDate,
                end: targetDate,
                frequency: "once",
                customDays: [],
            };
        }),
    };

    localStorage.setItem(
        "shadowGroveSchoolCalendar",
        JSON.stringify(updatedCalendar)
    );

    setSchoolCalendar(updatedCalendar);
    notifySchoolCalendarUpdated();
    setDraggedCalendarItemId(null);

    toast.success("Calendar item moved.");
};
     const applyRecurringMoveChoice = (choice) => {
    if (!recurringMovePopup) return;

    rememberCalendarBeforeChange();

    const currentCalendar =
        JSON.parse(localStorage.getItem("shadowGroveSchoolCalendar") || "null") || {
            holidays: [],
            vacations: [],
            daysOff: [],
            items: [],
        };

    const sourceItem = (currentCalendar.items || []).find(
        (item) => item.id === recurringMovePopup.itemId
    );

    if (!sourceItem) {
        toast.error("Could not find recurring item.");
        setRecurringMovePopup(null);
        return;
    }

    const originalSeriesId = sourceItem.parentRecurringId || sourceItem.id;

    const originalDate =
        recurringMovePopup.originalDate ||
        sourceItem.originalRecurringDate ||
        sourceItem.start;
        const nextTime =
    recurringMovePopup.targetTime !== undefined
        ? recurringMovePopup.targetTime
        : sourceItem.time;

const nextEndTime =
    recurringMovePopup.targetEndTime !== undefined
        ? recurringMovePopup.targetEndTime
        : sourceItem.endTime;

    let updatedItems = currentCalendar.items || [];

    if (choice === "one_time") {
        if (sourceItem.isRecurringOverride) {
            updatedItems = updatedItems.map((item) =>
                item.id === sourceItem.id
                    ? {
                          ...item,
                          start: recurringMovePopup.targetDate,
                        end: recurringMovePopup.targetDate,
                        time: nextTime,
                        endTime: nextEndTime,
                        frequency: "once",
                          originalRecurringDate: originalDate,
                          parentRecurringId: originalSeriesId,
                          isRecurringOverride: true,
                      }
                    : item
            );
        } else {
            const oneTimeItem = {
                ...sourceItem,
                id: `${sourceItem.id}-override-${originalDate}-to-${recurringMovePopup.targetDate}`,
                start: recurringMovePopup.targetDate,
                end: recurringMovePopup.targetDate,
                time: nextTime,
                endTime: nextEndTime,
                frequency: "once",
                customDays: [],
                parentRecurringId: originalSeriesId,
                originalRecurringDate: originalDate,
                isRecurringOverride: true,
            };

            updatedItems = updatedItems.map((item) =>
                item.id === originalSeriesId
                    ? {
                          ...item,
                          skippedDates: Array.from(
                              new Set([
                                  ...(item.skippedDates || []),
                                  originalDate,
                              ])
                          ),
                      }
                    : item
            );

            updatedItems = updatedItems.filter(
                (item) => item.id !== oneTimeItem.id
            );

            updatedItems = [oneTimeItem, ...updatedItems];
        }
    }

    if (choice === "weekly") {
    const splitDate = originalDate;
    const oldSeriesEnd = getPreviousDateString(splitDate);

    const futureSeries = {
        ...sourceItem,
        id: `${originalSeriesId}-future-${recurringMovePopup.targetDate}`,
        start: recurringMovePopup.targetDate,
        end: "",
        time: nextTime,
        endTime: nextEndTime,
        frequency:
            sourceItem.frequency === "once" && sourceItem.parentRecurringId
                ? "weekly"
                : sourceItem.frequency,
        skippedDates: [],
        parentRecurringId: "",
        originalRecurringDate: "",
        isRecurringOverride: false,
    };

    updatedItems = updatedItems
        .filter(
            (item) =>
                item.id !== sourceItem.id &&
                item.parentRecurringId !== originalSeriesId
        )
        .map((item) =>
            item.id === originalSeriesId
                ? {
                      ...item,
                      end: oldSeriesEnd,
                  }
                : item
        );

    updatedItems = [futureSeries, ...updatedItems];
}

    const updatedCalendar = {
        ...currentCalendar,
        items: updatedItems,
    };

    localStorage.setItem(
        "shadowGroveSchoolCalendar",
        JSON.stringify(updatedCalendar)
    );

    setSchoolCalendar(updatedCalendar);
    notifySchoolCalendarUpdated();
    setRecurringMovePopup(null);
    setDraggedCalendarItemId(null);
    setDraggedCalendarSourceDate("");

    toast.success(
        choice === "one_time"
            ? "One-time occurrence moved."
            : "Weekly schedule rescheduled."
    );
};
const getCalendarDragTimeFromPointer = (event) => {
    const timelineRect =
        event.currentTarget.getBoundingClientRect();

    const pointerY = Math.max(
        0,
        Math.min(
            event.clientY - timelineRect.top,
            timelineRect.height
        )
    );

    const timelineMinuteRange =
        calendarTimelineEndMinutes -
        calendarTimelineStartMinutes;

    const rawMinutes =
        calendarTimelineStartMinutes +
        (pointerY / timelineRect.height) *
            timelineMinuteRange;

    const snappedMinutes =
        Math.round(rawMinutes / 5) * 5;

    const finalMinutes = Math.max(
        calendarTimelineStartMinutes,
        Math.min(
            snappedMinutes,
            calendarTimelineEndMinutes - 5
        )
    );

    const hours =
        Math.floor(finalMinutes / 60);

    const minutes =
        finalMinutes % 60;

    return {
        minutes: finalMinutes,
        time: `${String(hours).padStart(
            2,
            "0"
        )}:${String(minutes).padStart(
            2,
            "0"
        )}`,
    };
};
const moveCalendarItemToTime = (itemId, targetDate, targetTime) => {
    if (
        String(itemId).startsWith("vacation-") ||
        String(itemId).includes("vacation")
    ) {
        toast.error("Vacation records must be edited, not dragged.");
        setDraggedCalendarItemId(null);
        return;
    }

    const isShadowGroveCalendarItem =
        String(itemId).startsWith("class-");

    if (isShadowGroveCalendarItem) {
        const sourceEvent =
            getShadowGroveEventForCalendarItem(itemId);

        if (!sourceEvent) {
            toast.error(
                "Could not find the class calendar item."
            );

            setDraggedCalendarItemId(null);
            setDraggedCalendarSourceDate("");
            return;
        }

        if (isShadowGroveSessionEvent(sourceEvent)) {
            setSessionMovePopup({
                itemId,
                sourceEvent,
                targetDate,
                targetTime,
                mode: "time",
            });

            setDraggedCalendarItemId(null);
            setDraggedCalendarSourceDate("");
            return;
        }

        const storedEvents =
            getShadowGroveEvents();

        const updatedEvents =
            storedEvents.map((event) => {
                if (
                    String(event.id) !==
                    String(sourceEvent.id)
                ) {
                    return event;
                }

                return updateShadowGroveEventTime(
                    updateShadowGroveEventDate(
                        event,
                        targetDate
                    ),
                    targetTime
                );
            });

        saveShadowGroveEvents(updatedEvents);

        setSchoolCalendar(
            (currentCalendar) => ({
                ...currentCalendar,
            })
        );

        setDraggedCalendarItemId(null);
        setDraggedCalendarSourceDate("");

        toast.success(
            "Class calendar item moved."
        );

        return;
    }

    const itemBeingMoved = (schoolCalendar.items || []).find(
        (item) => item.id === itemId
    );

    if (itemBeingMoved?.type === "vacation") {
        toast.error("Vacation records must be edited, not dragged.");
        setDraggedCalendarItemId(null);
        return;
    }

    if (
    ["weekly", "biweekly", "monthly"].includes(itemBeingMoved?.frequency) ||
    itemBeingMoved?.isRecurringOverride
) {
        setRecurringMovePopup({
            itemId,
            targetDate,
            targetTime,
            targetEndTime: shiftEndTimeByDuration(
             itemBeingMoved?.time,
             itemBeingMoved?.endTime,
             targetTime
),
            originalDate:
                itemBeingMoved?.isRecurringOverride
                    ? itemBeingMoved.originalRecurringDate || itemBeingMoved.start
                    : draggedCalendarSourceDate || targetDate,
            mode: "time",
        });

        setDraggedCalendarItemId(null);
        setDraggedCalendarSourceDate("");
        return;
    }

    rememberCalendarBeforeChange();

    const currentCalendar =
        JSON.parse(localStorage.getItem("shadowGroveSchoolCalendar") || "null") || {
            holidays: [],
            vacations: [],
            daysOff: [],
            items: [],
        };

    const updatedCalendar = {
        ...currentCalendar,
        items: (currentCalendar.items || []).map((item) => {
            if (item.id !== itemId) return item;

            return {
                ...item,
                start: targetDate,
                end: targetDate,
                time: targetTime,
                 endTime: shiftEndTimeByDuration(
                 item.time,
                item.endTime,
                targetTime
),
frequency: "once",
                customDays: [],
            };
        }),
    };

    localStorage.setItem(
        "shadowGroveSchoolCalendar",
        JSON.stringify(updatedCalendar)
    );

    setSchoolCalendar(updatedCalendar);
    notifySchoolCalendarUpdated();
    setDraggedCalendarItemId(null);
    setDraggedCalendarSourceDate("");

    toast.success("Calendar item scheduled.");
};

const moveCalendarItemToAllDay = (itemId, targetDate) => {
    if (
        String(itemId).startsWith("vacation-") ||
        String(itemId).includes("vacation")
    ) {
        toast.error("Vacation records must be edited, not dragged.");
        setDraggedCalendarItemId(null);
        return;
    }

    const itemBeingMoved = (schoolCalendar.items || []).find(
        (item) => item.id === itemId
    );

    if (itemBeingMoved?.type === "vacation") {
        toast.error("Vacation records must be edited, not dragged.");
        setDraggedCalendarItemId(null);
        return;
    }

    if (
    ["weekly", "biweekly", "monthly"].includes(itemBeingMoved?.frequency) ||
    itemBeingMoved?.isRecurringOverride
) {
        setRecurringMovePopup({
            itemId,
            targetDate,
            targetTime: "",
            targetEndTime: "",
            originalDate:
                itemBeingMoved?.isRecurringOverride
                    ? itemBeingMoved.originalRecurringDate || itemBeingMoved.start
                    : draggedCalendarSourceDate || targetDate,
            mode: "allDay",
        });

        setDraggedCalendarItemId(null);
        setDraggedCalendarSourceDate("");
        return;
    }

    rememberCalendarBeforeChange();

    const currentCalendar =
        JSON.parse(localStorage.getItem("shadowGroveSchoolCalendar") || "null") || {
            holidays: [],
            vacations: [],
            daysOff: [],
            items: [],
        };

    const updatedCalendar = {
        ...currentCalendar,
        items: (currentCalendar.items || []).map((item) => {
            if (item.id !== itemId) return item;

            return {
                ...item,
                start: targetDate,
                end: targetDate,
                time: "",
                endTime: "",
                frequency: "once",
                customDays: [],
            };
        }),
    };

    localStorage.setItem(
        "shadowGroveSchoolCalendar",
        JSON.stringify(updatedCalendar)
    );

    setSchoolCalendar(updatedCalendar);
    notifySchoolCalendarUpdated();

    setDraggedCalendarItemId(null);
    setDraggedCalendarSourceDate("");

    toast.success("Calendar item moved to all day.");
};

const saveLightningTrial = () => {
    if (!selectedQuizChild) {
            toast.error("Select a child first");
            return;
        }


        localStorage.setItem(
            `shadowGroveLightningQuiz_${selectedQuizChild}`,
            JSON.stringify({
                title: quizTitle.trim() || "Lightning Trial",
                questions: quizQuestions,
                graded: lightningGraded,
            })
        );


        toast.success("Lightning Trial saved for this child");
    };


    const loadLightningTrialForChild = (childId) => {
        setSelectedQuizChild(childId);


        const saved = JSON.parse(
            localStorage.getItem(`shadowGroveLightningQuiz_${childId}`) || "null"
        );


        if (!saved) {
            setQuizTitle("");
            setQuizQuestions([
                {
                    question: "",
                    options: ["", "", "", ""],
                    correct: 0,
                },
            ]);
            setLightningGraded(false);
            return;
        }


        setQuizTitle(saved.title || "");
        setQuizQuestions(
            saved.questions?.length
                ? saved.questions
                : [
                      {
                          question: "",
                          options: ["", "", "", ""],
                          correct: 0,
                      },
                  ]
        );
        setLightningGraded(!!saved.graded);


        toast.success("Current Lightning Trial loaded.");
    };

    const saveLightningTrialToReuse = () => {
        const savedQuiz = {
            id: Date.now().toString(),
            title: quizTitle.trim() || "Reusable Lightning Trial",
            questions: quizQuestions,
            graded: lightningGraded,
            created_at: new Date().toISOString(),
        };


        const updated = [savedQuiz, ...reusableQuizzes];


        localStorage.setItem("shadowGroveReusableQuizzes", JSON.stringify(updated));
        setReusableQuizzes(updated);


        toast.success("Saved to reusable quiz library.");
    };


    const loadReusableQuiz = (quiz) => {
        setQuizTitle(quiz.title || "");
        setQuizQuestions(quiz.questions || []);
        setLightningGraded(!!quiz.graded);


        toast.success("Reusable quiz loaded.");
    };


    const saveSpellingListToReuse = () => {
        const cleanedWords = spellingWords
            .map((w) => w.trim().toUpperCase())
            .filter(Boolean);


        if (cleanedWords.length < 5 || cleanedWords.length > 10) {
            toast.error("Choose 5 to 10 spelling words.");
            return;
        }


        const savedList = {
            id: Date.now().toString(),
            title: cleanedWords.slice(0, 3).join(", "),
            words: cleanedWords,
            audio: spellingAudio,
            graded: spellingGraded,
            created_at: new Date().toISOString(),
        };


        const updated = [savedList, ...reusableSpellingLists];


        localStorage.setItem(
            "shadowGroveReusableSpellingLists",
            JSON.stringify(updated)
        );


        setReusableSpellingLists(updated);


        toast.success("Saved to reusable spelling library.");
    };


    const loadReusableSpellingList = (list) => {
        setSpellingWords(list.words || ["", "", "", "", ""]);
        setSpellingAudio(list.audio || {});
        setSpellingGraded(!!list.graded);


        toast.success("Reusable spelling list loaded.");
    };


    const startRecording = async (index) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });


            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];


            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };


            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, {
                    type: "audio/webm",
                });


                const reader = new FileReader();


                reader.onloadend = () => {
                    setSpellingAudio((prev) => ({
                        ...prev,
                        [index]: reader.result,
                    }));
                };


                reader.readAsDataURL(audioBlob);
                stream.getTracks().forEach((track) => track.stop());
            };


            mediaRecorder.start();
            setRecordingIndex(index);
            toast.success(`Recording Word ${index + 1}...`);
        } catch (err) {
            console.error(err);
            toast.error("Microphone permission was blocked.");
        }
    };


    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current = null;
        }


        setRecordingIndex(null);
    };


    const saveSpellingWords = () => {
        const cleanedWords = spellingWords
            .map((w) => w.trim().toUpperCase())
            .filter(Boolean);


        if (!selectedSpellingChild) {
            toast.error("Choose a child first.");
            return;
        }


        if (cleanedWords.length < 5 || cleanedWords.length > 10) {
            toast.error("Choose 5 to 10 spelling words.");
            return;
        }


        localStorage.setItem(
            `shadowGroveSpellingWords_${selectedSpellingChild}`,
            JSON.stringify(cleanedWords)
        );


        localStorage.setItem(
            `shadowGroveSpellingAudio_${selectedSpellingChild}`,
            JSON.stringify(spellingAudio)
        );


        localStorage.setItem(
            `shadowGroveSpellingMeta_${selectedSpellingChild}`,
            JSON.stringify({
                graded: spellingGraded,
            })
        );


        toast.success("Spelling practice saved.");
    };


    const loadSpellingForChild = (childId) => {
        setSelectedSpellingChild(childId);


        const words = JSON.parse(
            localStorage.getItem(`shadowGroveSpellingWords_${childId}`) || "null"
        );


        const audio = JSON.parse(
            localStorage.getItem(`shadowGroveSpellingAudio_${childId}`) || "{}"
        );


        const meta = JSON.parse(
            localStorage.getItem(`shadowGroveSpellingMeta_${childId}`) || "{}"
        );


        setSpellingWords(words?.length ? words : ["", "", "", "", ""]);
        setSpellingAudio(audio || {});
        setSpellingGraded(!!meta.graded);
    };

    const saveChildEdit = async () => {
    if (!editingChild || savingChildAccess) return;

    const childId = editingChild.child_id;
    const username = editChildUsername.trim().toLowerCase();
    const password = editChildPassword;

    const currentUsername = (
        editingChild.child_username || ""
    )
        .trim()
        .toLowerCase();

    const usernameChanged = username !== currentUsername;

    const needsCredentialSave =
        !editingChild.child_login_configured ||
        usernameChanged ||
        password.length > 0;

    if (
        usernameChanged &&
        editingChild.child_login_configured &&
        password.length === 0
    ) {
        toast.error(
            "Enter a new password when changing the child username."
        );
        return;
    }

    if (
        needsCredentialSave &&
        (username.length > 0 || password.length > 0)
    ) {
        if (username.length < 3) {
            toast.error(
                "The child username must contain at least 3 characters."
            );
            return;
        }

        if (password.length < 6) {
            toast.error(
                "The child password must contain at least 6 characters."
            );
            return;
        }
    }

    if (
        editChildLoginEnabled &&
        !editingChild.child_login_configured &&
        (!username || password.length < 6)
    ) {
        toast.error(
            "Create a username and password before allowing child sign-in."
        );
        return;
    }

    setSavingChildAccess(true);

    try {
        if (
            needsCredentialSave &&
            username.length >= 3 &&
            password.length >= 6
        ) {
            await api.put(
                `/children/${childId}/login-credentials`,
                {
                    username,
                    password,
                }
            );
        }

        await api.put(
            `/children/${childId}/login-enabled`,
            {
                enabled: editChildLoginEnabled,
            }
        );

await api.put(
    `/children/${childId}/dashboard-settings`,
    {
        blueberry_behavior_enabled:
            editBlueberryEnabled,
        show_academic_record:
            editShowAcademicRecord,
        show_current_grades:
            editShowCurrentGrades,
        show_rubric_results:
            editShowRubricResults,
        show_progress_reports:
            editShowProgressReports,
        show_report_cards:
            editShowReportCards,
        show_parent_comments:
            editShowParentComments,
    }
);
await api.put(
    `/children/${childId}`,
    {
        name: editName.trim(),
        birthday: editBirthday || null,
        gradeLevel: editGradeLevel || null,
        profileColor: editProfileColor,
        profilePicture: editPicture || null,
    }
);
        const childResponse = await api.get(
            `/children/${childId}`
        );

        const serverChild = ensureChildDefaults(
            childResponse.data?.child || childResponse.data
        );

        const finalPicture =
            editPicture ||
            editingChild.profile_picture ||
            editingChild.picture ||
            "";

        const savedChildren = JSON.parse(
            localStorage.getItem(
                "shadowGroveChildren"
            ) || "[]"
        ).map(ensureChildDefaults);

        const updatedChildren = savedChildren.map((child) =>
            child.child_id === childId
                ? {
                    ...child,
                    ...serverChild,
                    name:
                        serverChild.name ||
                        editName.trim() ||
                        child.name,
                    birthday:
                        serverChild.birthday ||
                        editBirthday ||
                        child.birthday ||
                        null,
                    gradeLevel:
                        serverChild.gradeLevel ||
                        editGradeLevel ||
                        child.gradeLevel ||
                        "",
                    profileColor:
                        serverChild.profileColor ||
                        editProfileColor ||
                        child.profileColor ||
                        "#c084fc",
                    profile_picture:
                        serverChild.profile_picture ||
                        finalPicture,
                    picture:
                        serverChild.profile_picture ||
                        finalPicture,
                    calendarColor:
                        serverChild.profileColor ||
                        editProfileColor ||
                        child.calendarColor ||
                        "#c084fc",
                }
                : child
        );

        localStorage.setItem(
            "shadowGroveChildren",
            JSON.stringify(updatedChildren)
        );

        setChildren(updatedChildren);
        setEditingChild(null);
        setEditPicture("");
        setEditChildPassword("");
        setShowEditChildPassword(false);
        setEditChildUsername("");
        setEditChildLoginEnabled(false);
        setEditBlueberryEnabled(false);

        toast.success(
            "Child profile and sign-in settings updated."
        );
    } catch (error) {
        console.error(
            "Failed to save child access settings:",
            error
        );

        toast.error(
            error?.response?.data?.detail ||
                "Could not save the child sign-in settings."
        );
    } finally {
        setSavingChildAccess(false);
    }
};

const createNewChild = async () => {
    if (savingNewChild) return;

    const name = newChildName.trim();
    const username = newChildUsername.trim().toLowerCase();
    const password = newChildPassword;

    if (!name) {
        toast.error("Enter the child’s name.");
        return;
    }
if (newChildLoginEnabled) {
    if (username.length < 3) {
        toast.error(
            "The child username must contain at least 3 characters."
        );
        return;
    }

    if (password.length < 6) {
        toast.error(
            "The child password must contain at least 6 characters."
        );
        return;
    }
}

    const birthday = newChildBirthday || null;

if (birthday) {
    const birthDate = new Date(`${birthday}T00:00:00`);

    if (Number.isNaN(birthDate.getTime())) {
        toast.error("Enter a valid birthday.");
        return;
    }

    if (birthDate > new Date()) {
        toast.error("Birthday cannot be in the future.");
        return;
    }
}
if (newChildLoginEnabled) {
    if (username.length < 3) {
        toast.error(
            "The child username must contain at least 3 characters."
        );
        return;
    }

    if (password.length < 6) {
        toast.error(
            "The child password must contain at least 6 characters."
        );
        return;
    }
}
    setSavingNewChild(true);

    try {
        const response = await api.post(
            "/children",
            {
                name,
                birthday,
                gradeLevel: newChildGradeLevel || null,
                profileColor: newChildColor,
                profilePicture: newChildPicture || null,
            }
        );
        if (newChildLoginEnabled) {
            await api.put(
                `/children/${response.data.child_id}/login-credentials`,
                {
                    username,
                    password,
                }
            );

            await api.put(
                `/children/${response.data.child_id}/login-enabled`,
                {
                    enabled: true,
                }
            );
        }

        await api.put(
            `/children/${response.data.child_id}/dashboard-settings`,
            {
                show_academic_record:
                    newShowAcademicRecord,
                show_current_grades:
                    newShowCurrentGrades,
                show_rubric_results:
                    newShowRubricResults,
                show_progress_reports:
                    newShowProgressReports,
                show_report_cards:
                    newShowReportCards,
                show_parent_comments:
                    newShowParentComments,
            }
        );
        const childResponse = await api.get(
            `/children/${response.data.child_id}`
        );

        const createdChild = ensureChildDefaults(
            childResponse.data?.child || childResponse.data
);

        const updatedChildren = [
            ...children,
            createdChild,
        ];

        setChildren(updatedChildren);

        localStorage.setItem(
            "shadowGroveChildren",
            JSON.stringify(updatedChildren)
        );

        setNewChildName("");
        setNewChildBirthday("");
        setNewChildGradeLevel("");
        setNewChildColor("#c084fc");
        setNewChildUsername("");
        setNewChildPassword("");
        setNewChildLoginEnabled(true);
        setNewChildPicture("");
        setNewShowAcademicRecord(false);
        setNewShowCurrentGrades(false);
        setNewShowRubricResults(false);
        setNewShowProgressReports(false);
        setNewShowReportCards(false);
        setNewShowParentComments(false);
        setNewShowAcademicRecord(false);
        setNewShowCurrentGrades(false);
        setNewShowRubricResults(false);
        setNewShowProgressReports(false);
        setNewShowReportCards(false);
        setNewShowParentComments(false);
        setAddChildOpen(false);

        toast.success(
            `${createdChild.name} was added.`
        );
    } catch (error) {
        console.error(
            "Failed to add child:",
            error
        );

        toast.error(
            error?.response?.data?.detail ||
                "Could not add the child profile."
        );
    } finally {
        setSavingNewChild(false);
    }
};  

const archiveChild = async () => {
    if (
        !archiveChildTarget ||
        archivingChildId
    ) {
        return;
    }

    const childId =
        archiveChildTarget.child_id;

    setArchivingChildId(childId);

    try {
        await api.post(
            `/children/${childId}/archive`
        );

        const updatedChildren = children.filter(
            (child) =>
                child.child_id !== childId
        );

        setChildren(updatedChildren);

        localStorage.setItem(
            "shadowGroveChildren",
            JSON.stringify(updatedChildren)
        );

        if (
            editingChild?.child_id === childId
        ) {
            setEditingChild(null);
            setEditPicture("");
            setEditChildPassword("");
            setShowEditChildPassword(false);
            setEditChildUsername("");
            setEditChildLoginEnabled(false);
            setEditBlueberryEnabled(false);
        }

        toast.success(
            `${archiveChildTarget.name} was archived.`
        );

        setArchiveChildTarget(null);
    } catch (error) {
        console.error(
            "Failed to archive child:",
            error
        );

        toast.error(
            error?.response?.data?.detail ||
                "Could not archive the child profile."
        );
    } finally {
        setArchivingChildId("");
    }
};

const loadArchivedChildren = async () => {
    try {
        const response = await api.get(
            "/children/archived"
        );

        setArchivedChildren(
            (response.data || []).map(
                ensureChildDefaults
            )
        );
    } catch (error) {
        console.error(
            "Failed to load archived children:",
            error
        );

        toast.error(
            error?.response?.data?.detail ||
                "Could not load archived child profiles."
        );
    }
};

const restoreArchivedChild = async (child) => {
    if (!child || restoringChildId) {
        return;
    }

    const childId = child.child_id;

    setRestoringChildId(childId);

    try {
        const response = await api.post(
            `/children/${childId}/restore`
        );

        const restoredChild = ensureChildDefaults(
            response.data
        );

        const updatedChildren = [
            ...children.filter(
                (currentChild) =>
                    currentChild.child_id !== childId
            ),
            restoredChild,
        ];

        setChildren(updatedChildren);

        localStorage.setItem(
            "shadowGroveChildren",
            JSON.stringify(updatedChildren)
        );

        setArchivedChildren((current) =>
            current.filter(
                (archivedChild) =>
                    archivedChild.child_id !== childId
            )
        );

        toast.success(
            `${restoredChild.name} was restored.`
        );
    } catch (error) {
        console.error(
            "Failed to restore child:",
            error
        );

        toast.error(
            error?.response?.data?.detail ||
                "Could not restore the child profile."
        );
    } finally {
        setRestoringChildId("");
    }
};

const updateLinkedWork = (
    curriculumId,
    materialId,
    groupId,
    itemId,
    linkedWorkId,
    updates
) => {
    setSubjectCurriculumSources((current) =>
        current.map((curriculumItem) =>
            curriculumItem.id === curriculumId
                ? {
                      ...curriculumItem,
                      materials: curriculumItem.materials.map((resource) =>
                          resource.id === materialId
                              ? {
                                    ...resource,
                                    structure: resource.structure.map(
                                        (structureGroup) =>
                                            structureGroup.id === groupId
                                                ? {
                                                      ...structureGroup,
                                                      items: structureGroup.items.map(
                                                          (structureItem) =>
                                                              structureItem.id === itemId
                                                                  ? {
                                                                        ...structureItem,
                                                                        linked_resource_work:
                                                                            (
                                                                                structureItem.linked_resource_work ||
                                                                                []
                                                                            ).map(
                                                                                (entry) =>
                                                                                    entry.id ===
                                                                                    linkedWorkId
                                                                                        ? {
                                                                                              ...entry,
                                                                                              ...updates,
                                                                                          }
                                                                                        : entry
                                                                            ),
                                                                    }
                                                                  : structureItem
                                                      ),
                                                  }
                                                : structureGroup
                                    ),
                                }
                              : resource
                      ),
                  }
                : curriculumItem
        )
    );
};

const addSubject = async () => {
    if (!subjectName.trim()) {
        toast.error(
            "Enter a subject name."
        );
        return;
    }

    if (!subjectChildId) {
        toast.error(
            "Choose a child."
        );
        return;
    }

    try {
        let savedSubject;

        if (editingSubjectId) {
            const response = await api.put(
                `/subjects/${editingSubjectId}`,
{
    name: subjectName.trim(),
    color: subjectColor,

    class_schedule: {
        mode: subjectScheduleMode,

        days:
            subjectScheduleMode === "days"
                ? subjectScheduleDays
                : [],

        start_time:
            subjectScheduleMode === "days"
                ? subjectScheduleStartTime
                : "",

        end_time:
            subjectScheduleMode === "days"
                ? subjectScheduleEndTime
                : "",

        weekly_hours:
            subjectScheduleMode === "block"
                ? subjectScheduleWeeklyHours
                : "",

        preferred_block:
            subjectScheduleMode === "block"
                ? subjectScheduleBlock
                : "",

        custom_block_label:
            subjectScheduleMode === "block" &&
            subjectScheduleBlock === "custom"
                ? subjectScheduleCustomBlock
                : "",
    },

    curriculum_sources:
        subjectCurriculumSources,
}
            );

            savedSubject = response.data;
        } else {
            const response = await api.post(
                "/subjects",
                {
                    child_id: subjectChildId,
                    name: subjectName.trim(),
                    color: subjectColor,

                    class_schedule: {
                        mode: subjectScheduleMode,

                        days:
                            subjectScheduleMode === "days"
                                ? subjectScheduleDays
                                : [],

                        start_time:
                            subjectScheduleMode === "days"
                                ? subjectScheduleStartTime
                                : "",

                        end_time:
                            subjectScheduleMode === "days"
                                ? subjectScheduleEndTime
                                : "",

                        weekly_hours:
                            subjectScheduleMode === "block"
                                ? subjectScheduleWeeklyHours
                                : "",

                        preferred_block:
                            subjectScheduleMode === "block"
                                ? subjectScheduleBlock
                                : "",

                        custom_block_label:
                            subjectScheduleMode === "block" &&
                            subjectScheduleBlock === "custom"
                                ? subjectScheduleCustomBlock
                                : "",
                    },

                    curriculum_sources:
                        subjectCurriculumSources,
                }
            );

            savedSubject = response.data;
        }
        
        const updatedSubjects =
            editingSubjectId
                ? subjects.map((subject) =>
                      subject.subject_id ===
                          editingSubjectId ||
                      subject.id ===
                          editingSubjectId
                          ? savedSubject
                          : subject
                  )
                : [
                      ...subjects,
                      savedSubject,
                  ];

        localStorage.setItem(
            "shadowGroveSubjects",
            JSON.stringify(
                updatedSubjects
            )
        );

        setSubjects(updatedSubjects);
        setCurriculumOwnerSubjectId(
            savedSubject.subject_id ||
            savedSubject.id ||
            ""
        );
        setSubjectName("");
        setSubjectColor("#7c3aed");
        setSubjectChildId("");
        setEditingSubjectId("");
        setSubjectCurriculumSources([]);
        setSubjectScheduleMode("days");
        setSubjectScheduleDays([]);
        setSubjectScheduleStartTime("");
        setSubjectScheduleEndTime("");
        setSubjectScheduleBlock("morning");
        setSubjectScheduleWeeklyHours("");
        setSubjectScheduleCustomBlock("");
    
    if (!editingSubjectId) {
        setShowAddClassPopup(false);
        setShowClassCreatedPopup(true);
    } else {
        setShowAddClassPopup(false);
    }

        toast.success(
            editingSubjectId
                ? "Subject updated."
                : "Subject added."
        );
    } catch (error) {
        console.error(
            editingSubjectId
                ? "Failed to update subject:"
                : "Failed to add subject:",
            error
        );

        toast.error(
            error?.response?.data?.detail ||
                (editingSubjectId
                    ? "Could not update the subject."
                    : "Could not add the subject.")
        );
    }
};

const confirmDeleteSubject = async () => {
    if (
        !deleteSubjectTarget ||
        deletingSubjectId
    ) {
        return;
    }

    const subjectId =
        deleteSubjectTarget.subject_id ||
        deleteSubjectTarget.id;

    if (!subjectId) {
        toast.error(
            "This subject could not be identified."
        );
        return;
    }

    setDeletingSubjectId(subjectId);

    try {
        await api.delete(
            `/subjects/${subjectId}`
        );

        const updatedSubjects =
            subjects.filter(
                (currentSubject) =>
                    (
                        currentSubject.subject_id ||
                        currentSubject.id
                    ) !== subjectId
            );

        localStorage.setItem(
            "shadowGroveSubjects",
            JSON.stringify(
                updatedSubjects
            )
        );

        setSubjects(updatedSubjects);

        if (
            editingSubjectId ===
            subjectId
        ) {
            setEditingSubjectId("");
            setSubjectChildId("");
            setSubjectName("");
            setSubjectColor("#7c3aed");
        }

        toast.success(
            "Subject deleted."
        );

        setDeleteSubjectTarget(null);
    } catch (error) {
        console.error(
            "Failed to delete subject:",
            error
        );

        toast.error(
            error?.response?.data?.detail ||
                "Could not delete the subject."
        );
    } finally {
        setDeletingSubjectId("");
    }
};   
React.useEffect(() => {
    const handleMouseMove = (e) => {
        if (!isResizingCalendar) return;

        const nextWidth = Math.min(
            650,
            Math.max(240, e.clientX - 24)
        );

        setCalendarSidebarWidth(nextWidth);
    };

    const handleMouseUp = () => {
        setIsResizingCalendar(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
    };
}, [isResizingCalendar]);

       return (
        <div
    className="min-h-screen bg-[#050711] text-white"
    style={{
        backgroundImage:
            "radial-gradient(circle at top, rgba(124,58,237,0.16), transparent 38%), radial-gradient(circle at bottom right, rgba(255,209,102,0.08), transparent 32%)",
    }}
>

{deleteSubjectTarget && (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 px-4 backdrop-blur-sm">
        <div className="w-full max-w-lg overflow-hidden rounded-[32px] border border-red-400/45 bg-black shadow-[0_0_45px_rgba(248,113,113,0.22)]">
            <div className="border-b border-red-400/25 px-6 py-5 text-center">
                <h3 className="font-display text-3xl text-red-300">
                    Delete Subject?
                </h3>

                <p className="mt-2 text-sm text-[#b0b8d6]">
                    Remove{" "}
                    <span className="font-semibold text-[#ffd166]">
                        {deleteSubjectTarget.name}
                    </span>
                    ?
                </p>
            </div>

            <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
                <div className="rounded-2xl border border-red-400/30 bg-red-950/20 p-4">
                    <p className="text-sm text-[#fff3c4]">
                        This subject will disappear from the
                        child’s active classroom list.
                    </p>

                    <p className="mt-3 text-xs leading-relaxed text-[#b0b8d6]">
                        The subject is archived rather than
                        immediately erased from the database.
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3 border-t border-red-400/25 px-6 py-5">
                <button
                    type="button"
                    onClick={() =>
                        setDeleteSubjectTarget(null)
                    }
                    disabled={Boolean(
                        deletingSubjectId
                    )}
                    className={`${purpleActionBtn} disabled:cursor-not-allowed disabled:opacity-50`}
                >
                    Cancel
                </button>

                <button
                    type="button"
                    onClick={
                        confirmDeleteSubject
                    }
                    disabled={Boolean(
                        deletingSubjectId
                    )}
                    className="rounded-2xl border border-red-400/55 bg-red-950/45 px-5 py-2.5 text-sm font-semibold text-red-100 shadow-[0_0_18px_rgba(248,113,113,0.14)] transition hover:border-red-300 hover:bg-red-950/70 hover:shadow-[0_0_24px_rgba(248,113,113,0.25)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {deletingSubjectId
                        ? "Deleting..."
                        : `Delete ${deleteSubjectTarget.name}`}
                </button>
            </div>
        </div>
    </div>
)}

           <header className="relative z-10 border-b border-[#ffd166]/25 bg-black/70 shadow-[0_0_25px_rgba(255,209,102,0.08)] backdrop-blur-sm">
    <div className="w-full max-w-[1900px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
        <button
            type="button"
            onClick={() => navigate("/profile")}
            className={purpleActionBtn}
        >
            <ArrowLeft size={16} /> Dashboard
        </button>

        <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-[#7c3aed]/25 border border-[#ffd166]/50 overflow-hidden flex items-center justify-center shrink-0">
                {parentPicture ? (
                    <img
                        src={parentPicture}
                        alt="Parent"
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <UserIcon size={18} className="text-[#ffd166]" />
                )}
            </div>

            <div className="min-w-0">
                <h1 className="font-display text-lg text-[#ffd166] truncate">
                    {parentName}
                </h1>

                <p className="text-[10px] text-[#b0b8d6] truncate">
                    Parent Profile
                </p>
            </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-[#6c7a9c] hidden sm:block">
                {BRANDING.appName}
            </span>

            <button
                type="button"
                onClick={handleLogout}
                className="rounded-xl border border-[#ffd166]/30 bg-black px-3 py-2 text-xs font-semibold text-[#fff3c4] transition hover:border-[#ffd166]/70 hover:text-[#ffd166]"
            >
                Log Out
            </button>
        </div>
    </div>
</header>

            <PinGate
                open={pinOpen}
                onOpenChange={(o) => {
                    setPinOpen(o);
                    if (!o && !unlocked) navigate("/profile");
                }}
                onSuccess={() => {
                    setUnlocked(true);
                    setPinOpen(false);
                }}
                title="Parent profile"
            />


            {unlocked && user && (
                <main className="w-full max-w-[1900px] mx-auto px-4 py-6">
                       

                   <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2 mt-10 mb-10 clear-both">
                        <PanelButton
                            id="homeroom"
                            icon={<BookOpen />}
                            title="Teacher's Lounge"
                            subtitle="Quick day view, gadgets, messages and more"
                            openPanel={!openPanel && !requestedDeskGadget ? "homeroom" : openPanel}
                            setOpenPanel={() => { setRequestedDeskGadget(null); setOpenPanel(null); }}
                        />



                        <PanelButton
                            id="quests"
                            icon={<BookOpen />}
                            title="Quest Desk"
                            subtitle="Create and enter classrooms"
                            openPanel={openPanel}
                            setOpenPanel={setOpenPanel}
                        />



                        <PanelButton
                            id="calendar"
                            icon={<CalendarDays />}
                            title="Calendar & Attendance"
                            subtitle="School year and attendance records"
                            openPanel={openPanel}
                            setOpenPanel={setOpenPanel}
                        />

                        <PanelButton
                            id="chores"
                            icon={<BookOpen />}
                            title="Household Duties"
                            subtitle="Chores, points and rewards"
                            openPanel={!openPanel && requestedDeskGadget?.type === "chores" ? "chores" : openPanel}
                            setOpenPanel={(panel) => {
                                setRequestedDeskGadget(panel ? { type: "chores" } : null);
                                setOpenPanel(null);
                            }}
                        />


                        <PanelButton
                            id="reports"
                            icon={<BookOpen />}
                            title="Reports"
                            subtitle="GPA, report cards, history & attendance"
                            openPanel={openPanel}
                            setOpenPanel={setOpenPanel}
                        />
                        <PanelButton
                            id="arcade"
                            icon={<Gamepad2 />}
                            title="Arcade"
                            subtitle="Learning trials and leisure games"
                            openPanel={openPanel}
                            setOpenPanel={setOpenPanel}
                        />
                        <PanelButton
                            id="identity"
                            icon={<UserIcon />}
                            title="Profiles"
                            subtitle="Names, pictures, and account settings"
                            openPanel={openPanel}
                            setOpenPanel={setOpenPanel}
                        />
                        
                    </div>

                    {!openPanel && (
                        requestedDeskGadget ? <details open id="working-desk-household" className={`${shadowCalendarShell} p-5`}><summary className="mb-4 cursor-pointer font-display text-2xl text-[#ffd166]">Household Duties · Munera Domus</summary><HouseholdGadget key={requestedDeskGadget.childId || "all"} type="chores" children={children} childId={requestedDeskGadget.childId || ""} /></details> : <Homeroom key={user.user_id || "local"} userId={user.user_id} children={children} subjects={subjects} />
                    )}

                    <Panel id="reports" openPanel={openPanel}>
                        <ReportsPanel children={children} />
                    </Panel>
                    <Panel id="arcade" openPanel={openPanel}>
                        <div className={`${shadowCalendarShell} flex flex-col gap-6`}>
                            <div>
                                <h2 className="font-display text-3xl text-[#ffd166]">
                                    Arcade Control Center
                                </h2>

                                <p className="mt-2 text-sm text-[#b0b8d6]">
                                    Configure learning trials, family challenges, and child Arcade access.
                                </p>
                            </div>

                            <div className={`${shadowCalendarToolbar} p-5`}>
                                <h3 className="font-display text-xl text-[#d8b4fe] mb-3">
                                    Assignment Scope
                                </h3>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setArcadeScope("all");
                                            setArcadeChildId("");
                                        }}
                                        className={`rounded-2xl border px-4 py-3 text-left text-[#fff3c4] transition ${
                                            arcadeScope === "all"
                                                ? "border-[#ffd166]/80 bg-black shadow-[0_0_22px_rgba(255,209,102,0.18)]"
                                                : "border-[#ffd166]/25 bg-black hover:border-[#ffd166]/60"
                                        }`}
                                    >

                                        <div className="mt-1 text-xs text-[#b0b8d6]">
                                            Configure one shared trial for the whole family.
                                        </div>
                                    </button>

                                   <button
                                        type="button"
                                        onClick={() => setArcadeScope("child")}
                                        className={`rounded-2xl border px-4 py-3 text-left text-[#fff3c4] transition ${
                                            arcadeScope === "child"
                                                ? "border-[#ffd166]/80 bg-black shadow-[0_0_22px_rgba(255,209,102,0.18)]"
                                                : "border-[#ffd166]/25 bg-black hover:border-[#ffd166]/60"
                                        }`}
                                    >
                                        <div className="font-semibold">
                                            Individual Child
                                        </div>

                                        <div className="mt-1 text-xs text-[#b0b8d6]">
                                            Configure a trial for one child only.
                                        </div>
                                    </button>
                                {arcadeScope === "child" && (
                                    <div className="mt-4">
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                                            Choose Child
                                        </label>

                                        <select
                                            value={arcadeChildId}
                                            onChange={(e) => setArcadeChildId(e.target.value)}
                                            className="w-full rounded-2xl border border-[#c084fc]/30 bg-black px-4 py-3 text-[#fff3c4] outline-none transition focus:border-[#ffd166]/70"
                                        >
                                            <option value="">
                                                Select a child
                                            </option>

                                            {children.map((child) => (
                                                <option
                                                    key={child.child_id}
                                                    value={child.child_id}
                                                >
                                                    {child.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                <div className="rounded-[28px] border border-[#ffd166]/35 bg-black p-5 shadow-[0_0_28px_rgba(255,209,102,0.10)] backdrop-blur-sm">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <h3 className="font-display text-2xl text-[#ffd166]">
                                                Spelling Trials
                                            </h3>

                                            <p className="mt-2 text-sm text-[#b0b8d6]">
                                                Create word lists, record spoken prompts, set practice or graded modes, and review results.
                                            </p>
                                        </div>

                                        <Wand2
                                            size={24}
                                            className="text-[#ffd166] shrink-0"
                                        />
                                    </div>

                                    

                                    <button
                                        type="button"
                                        onClick={() => setShowSpellingConfig((prev) => !prev)}
                                        className="mt-5 inline-flex items-center gap-2 rounded-2xl border border-[#ffd166]/45 bg-black px-5 py-3 font-semibold text-[#fff3c4] transition hover:border-[#ffd166] hover:text-[#ffd166]"
                                    >
                                        Configure Spelling Trials
                                    </button>
                                {showSpellingConfig && (
                                    <div className="mt-5 border-t border-[#ffd166]/20 pt-5">
                                        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                                            <div>
                                                <h4 className="font-display text-xl text-[#ffd166]">
                                                    Spelling Trial Setup
                                                </h4>

                                                <p className="mt-1 text-xs text-[#b0b8d6]">
                                                    {arcadeScope === "all"
                                                        ? "Editing a shared spelling trial for all children."
                                                        : arcadeChildId
                                                        ? "Editing this child's spelling trial."
                                                        : "Choose a child above before configuring an individual trial."}
                                                </p>
                                            </div>
                                        </div>

                                        {arcadeScope === "child" && !arcadeChildId ? (
                                            <div className="rounded-2xl border border-[#ffd166]/20 bg-black px-4 py-4 text-sm text-[#b0b8d6]">
                                                Select a child in Assignment Scope first.
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                                <div>
                                                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                                                        Trial Mode
                                                    </label>

                                                    <select
                                                        className="w-full rounded-2xl border border-[#ffd166]/25 bg-black px-4 py-3 text-[#fff3c4] outline-none focus:border-[#ffd166]/70"
                                                        value={spellingMode}
                                                        onChange={(e) => setSpellingMode(e.target.value)}
                                                    >
                                                        <option value="practice">
                                                            Practice
                                                        </option>
                                                        <option value="graded">
                                                            Graded Test
                                                        </option>
                                                        <option value="competition">
                                                            Competition
                                                        </option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                                                        Attempts Per Word
                                                    </label>

                                                    <select
                                                        className="w-full rounded-2xl border border-[#ffd166]/25 bg-black px-4 py-3 text-[#fff3c4] outline-none focus:border-[#ffd166]/70"
                                                        value={spellingAttempts}
                                                        onChange={(e) => setSpellingAttempts(e.target.value)}
                                                    >
                                                        <option value="unlimited">
                                                            Unlimited
                                                        </option>
                                                        <option value="1">
                                                            1
                                                        </option>
                                                        <option value="2">
                                                            2
                                                        </option>
                                                        <option value="3">
                                                            3
                                                        </option>
                                                    </select>
                                                </div>

                                                <div className="lg:col-span-2">
                                                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                                                        Spelling Words
                                                    </label>

                                                    <textarea
                                                        rows={7}
                                                        value={spellingWordsText}
                                                        onChange={(e) => setSpellingWordsText(e.target.value)}
                                                        placeholder={`One word per line

                                CAT
                                SUN
                                MOON
                                BAT
                                SPELL`}
                                                        className="w-full rounded-2xl border border-[#ffd166]/25 bg-black px-4 py-3 text-[#fff3c4] outline-none focus:border-[#ffd166]/70"
                                                    />
                                                </div>

                                                <div className="lg:col-span-2">
                                                    <div className="flex flex-wrap gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const words = spellingWordsText
                                                                    .split("\n")
                                                                    .map((word) => word.trim().toUpperCase())
                                                                    .filter(Boolean);

                                                                if (words.length === 0) {
                                                                    toast.error("Add at least one spelling word.");
                                                                    return;
                                                                }

                                                                const meta = {
                                                                    graded: spellingMode === "graded",
                                                                    mode: spellingMode,
                                                                    attempts: spellingAttempts,
                                                                };

                                                                const targetChildIds =
                                                                    arcadeScope === "all"
                                                                        ? children.map((child) => child.child_id)
                                                                        : [arcadeChildId];

                                                                targetChildIds.forEach((childId) => {
                                                                    if (!childId) return;

                                                                    localStorage.setItem(
                                                                        `shadowGroveSpellingWords_${childId}`,
                                                                        JSON.stringify(words)
                                                                    );

                                                                    localStorage.setItem(
                                                                        `shadowGroveSpellingMeta_${childId}`,
                                                                        JSON.stringify(meta)
                                                                    );
                                                                });

                                                                toast.success(
                                                                    arcadeScope === "all"
                                                                        ? "Spelling trial saved for all children."
                                                                        : "Spelling trial saved."
                                                                );
                                                            }}
                                                            className="rounded-2xl border border-[#ffd166]/45 bg-black px-4 py-3 font-semibold text-[#fff3c4] transition hover:border-[#ffd166]"
                                                        >
                                                            Save Spelling Trial
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={saveArcadeSpellingListToReuse}
                                                            className="rounded-xl border border-[#ffd166]/30 bg-black px-3 py-1.5 text-xs font-semibold text-[#fff3c4] transition hover:border-[#ffd166]/70"
                                                        >
                                                            Save Current to Library
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setShowSpellingVoiceManager((prev) => !prev)
                                                            }
                                                            className="rounded-2xl border border-[#c084fc]/25 bg-black px-4 py-3 text-[#d8b4fe] transition hover:border-[#ffd166]/50"
                                                        >
                                                            Manage Voice Recordings
                                                        </button>
                                                        
                                                    {showSpellingVoiceManager && (
                                                        <div className="lg:col-span-2 w-full min-w-0 mt-5 border-t border-[#ffd166]/20 pt-5">
                                                            <div className="mb-4">
                                                                <h4 className="font-display text-xl text-[#ffd166]">
                                                                    Voice Prompts
                                                                </h4>

                                                                <p className="mt-1 text-xs text-[#b0b8d6]">
                                                                    Record a spoken prompt for each spelling word.
                                                                </p>
                                                            </div>

                                                            {spellingWordsText
                                                                .split("\n")
                                                                .map((word) => word.trim().toUpperCase())
                                                                .filter(Boolean)
                                                                .map((word, index) => (
                                                                    <div
                                                                        key={`${word}-${index}`}
                                                                        className="grid w-full min-w-0 grid-cols-1 gap-3 border-b border-[#ffd166]/10 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                                                                    >
                                                                        <div>
                                                                            <div className="font-display text-lg text-[#fff3c4]">
                                                                                {word}
                                                                            </div>

                                                                            <div className="text-xs text-[#b0b8d6]">
                                                                                Word {index + 1}
                                                                            </div>
                                                                        </div>

                                                                        <div className="flex flex-nowrap items-center gap-2 shrink-0">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    if (
                                                                                        spellingRecordingActive &&
                                                                                        spellingRecordingWord === word
                                                                                    ) {
                                                                                        stopSpellingRecording();
                                                                                    } else {
                                                                                        startSpellingRecording(word);
                                                                                    }
                                                                                }}
                                                                                className="w-[64px] shrink-0 rounded-lg border border-[#ffd166]/30 bg-black px-2 py-1 text-[11px] font-semibold text-center text-[#fff3c4] transition hover:border-[#ffd166]/70"
                                                                            >
                                                                                {spellingRecordingActive &&
                                                                                spellingRecordingWord === word
                                                                                    ? "Stop"
                                                                                    : "Record"}
                                                                            </button>

                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    const audio = spellingAudioMap[word];

                                                                                    if (!audio) {
                                                                                        toast.error(
                                                                                            `No voice recording saved for ${word}.`
                                                                                        );
                                                                                        return;
                                                                                    }

                                                                                    new Audio(audio).play();
                                                                                }}
                                                                                className="w-[64px] shrink-0 rounded-lg border border-[#c084fc]/25 bg-black px-2 py-1 text-[11px] font-semibold text-center text-[#d8b4fe] transition hover:border-[#ffd166]/50"
                                                                            >
                                                                                Play
                                                                            </button>

                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    const nextAudioMap = {
                                                                                        ...spellingAudioMap,
                                                                                    };

                                                                                    delete nextAudioMap[word];

                                                                                    saveSpellingAudioMap(nextAudioMap);

                                                                                    toast.success(
                                                                                        `Voice prompt removed for ${word}.`
                                                                                    );
                                                                                }}
                                                                                className="w-[64px] shrink-0 rounded-lg border border-[#ff8fa3]/20 bg-black px-2 py-1 text-[11px] font-semibold text-center text-[#ffb3c1] transition hover:border-[#ff8fa3]/50"
                                                                            >
                                                                                Delete
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))}

                                                            {spellingWordsText
                                                                .split("\n")
                                                                .map((word) => word.trim())
                                                                .filter(Boolean).length === 0 && (
                                                                <p className="text-sm text-[#9ca3b7]">
                                                                    Add spelling words above first.
                                                                </p>
                                                            )}
                                                        </div>
                                                            )}
                                                        </div>

                                                        <div className="lg:col-span-2 mt-5 border-t border-[#ffd166]/20 pt-5">
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setShowReusableSpellingLists((prev) => !prev)
                                                                }
                                                                className="inline-flex items-center gap-2 rounded-xl border border-[#ffd166]/30 bg-black px-3 py-1.5 text-xs font-semibold text-[#fff3c4] transition hover:border-[#ffd166]/70"
                                                            >
                                                                Reusable Spelling Lists
                                                                <span className="text-[#d8b4fe]">
                                                                    ({reusableSpellingLists.length})
                                                                </span>
                                                            </button>

                                                            {showReusableSpellingLists && (
                                                                <div className="mt-3 max-h-[260px] overflow-y-auto pr-2">
                                                                    {reusableSpellingLists.length === 0 ? (
                                                                        <p className="text-sm text-[#7f7188]">
                                                                            No reusable spelling lists saved yet.
                                                                        </p>
                                                                    ) : (
                                                                        <div className="space-y-2">
                                                                            {reusableSpellingLists.map((list) => (
                                                                                <div
                                                                                    key={list.id}
                                                                                    className="flex flex-col gap-2 border-b border-[#ffd166]/10 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
                                                                                >
                                                                                    <div className="min-w-0">
                                                                                        <div className="font-semibold text-[#fff3c4]">
                                                                                            {list.title || "Reusable Spelling List"}
                                                                                        </div>

                                                                                        <div className="mt-1 text-xs text-[#b0b8d6]">
                                                                                            {(list.words || []).length} words
                                                                                        </div>
                                                                                    </div>

                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            const words = Array.isArray(list.words)
                                                                                                ? list.words
                                                                                                : [];

                                                                                            setSpellingWordsText(words.join("\n"));
                                                                                            setSpellingAudioMap(list.audio || {});

                                                                                            setSpellingMode(
                                                                                                list.mode ||
                                                                                                    (list.graded
                                                                                                        ? "graded"
                                                                                                        : "practice")
                                                                                            );

                                                                                            setSpellingAttempts(
                                                                                                list.attempts || "unlimited"
                                                                                            );

                                                                                            toast.success(
                                                                                                "Reusable spelling list loaded."
                                                                                            );
                                                                                        }}
                                                                                        className="shrink-0 rounded-lg border border-[#ffd166]/30 bg-black px-3 py-1 text-[11px] font-semibold text-[#fff3c4] transition hover:border-[#ffd166]/70"
                                                                                    >
                                                                                        Load
                                                                                    </button>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                    </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                </div>

                               <div className="p-2">
                                    <h3 className="font-display text-2xl text-[#d8b4fe]">
                                        Future Games
                                    </h3>

                                    <p className="mt-2 text-sm text-[#b0b8d6]">
                                        Additional learning games and family competitions can be configured here as they are added.
                                    </p>

                                    <div className="mt-5 rounded-2xl border border-dashed border-[#c084fc]/25 px-4 py-5 text-center text-sm text-[#7f7188]">
                                        More Arcade controls coming soon
                                    </div>
                                </div>
                            </div>

                            <div className="pt-2">
                                <h3 className="font-display text-xl text-[#ffd166] mb-4">
                                    Open Child Arcade
                                </h3>

                                {children.length === 0 ? (
                                    <p className="text-sm text-[#9ca3b7]">
                                        Add a child profile before opening the Arcade.
                                    </p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {children.map((child) => (
                                            <button
                                                key={child.child_id}
                                                type="button"
                                                onClick={() =>
                                                    navigate(`/arcade/${child.child_id}`)
                                                }
                                                className="rounded-2xl border border-[#ffd166]/25 bg-black px-4 py-4 text-left transition shadow-[0_0_14px_rgba(124,58,237,0.10)] hover:border-[#ffd166]/65 hover:shadow-[0_0_20px_rgba(255,209,102,0.14)]"
                                            >
                                                <div className="font-display text-xl text-[#ffd166]">
                                                    {child.name}
                                                </div>

                                                <div className="mt-1 text-xs text-[#b0b8d6]">
                                                    Open Arcade
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Panel>
                    <Panel id="identity" openPanel={openPanel}>
                        <h2 className="font-display text-3xl text-[#ffd166] mb-5">
                            Profiles
                        </h2>


                        <div className="rounded-[32px] border border-[#ffd166]/35 bg-black p-6 mb-8 space-y-5 shadow-[0_0_35px_rgba(255,209,102,0.10)] backdrop-blur-sm">
                            <h3 className="font-display text-3xl text-[#ffd166] text-center">
                            Parent Profile
                            </h3>


                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <Label>Parent name</Label>
                                    <Input
                                    value={parentName}
                                    onChange={(e) => setParentName(e.target.value)}
                                    className={shadowInput}
                                            />
                                </div>

                            <div>
                                <Label>Signed-in email</Label>

                                <div className="mt-2 rounded-2xl border border-[#ffd166]/25 bg-black px-4 py-3 text-sm text-[#fff3c4]">
                                    {user?.email || "No email available"}
                                </div>

                                <p className="mt-1 text-[10px] text-[#7f7188]">
                                    Managed through your sign-in provider.
                                </p>
                            </div>
                                <div>
                                    <Label>Parent picture</Label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) =>
                                            fileToDataUrl(e.target.files?.[0], setParentPicture)
                                        }
                                       className="mt-2 w-full text-sm text-[#b0b8d6]
                                        file:mr-3
                                        file:rounded-2xl
                                        file:border
                                        file:border-[#ffd166]/30
                                        file:bg-[#7c3aed]/20
                                        file:text-[#fff3c4]
                                        file:px-4
                                        file:py-2
                                        file:font-semibold
                                        file:shadow-[0_0_14px_rgba(124,58,237,0.18)]
                                        hover:file:border-[#ffd166]/75
                                        hover:file:bg-[#7c3aed]/35
                                        "
                                    />
                                </div>
                            </div>


                            <button
                                type="button"
                                onClick={saveParentIdentity}
                                className={purpleActionBtn}
                            >
                                Save Parent Profile
                            </button>
                        </div>

<div className="mb-8 rounded-[32px] border border-[#ffd166]/35 bg-black shadow-[0_0_35px_rgba(255,209,102,0.10)]">
    <button
        type="button"
        onClick={() =>
            setShowBillingSection((prev) => !prev)
        }
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
    >
        <div>
            <h3 className="font-display text-xl text-[#ffd166]">
                Billing & Subscription
            </h3>

            <p className="mt-1 text-xs text-[#b0b8d6]">
                View your plan, billing status, and subscription details.
            </p>
        </div>

        <span className="shrink-0 text-lg text-[#d8b4fe]">
            {showBillingSection ? "▲" : "▼"}
        </span>
    </button>

    {showBillingSection && (
        <div className="border-t border-[#ffd166]/20 px-5 pb-5 pt-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                    <Label>Account Email</Label>

                    <div className="mt-2 rounded-2xl border border-[#ffd166]/25 bg-black px-4 py-3 text-sm text-[#fff3c4]">
                        {user?.email || "No email available"}
                    </div>
                </div>

                <div>
                    <Label>Subscription Status</Label>

                    <div className="mt-2 rounded-2xl border border-[#ffd166]/25 bg-black px-4 py-3 text-sm text-[#fff3c4]">
                        {user?.access_status === "tester"
                            ? "Tester Access - No subscription required"
                            : user?.trial_ends_at &&
                            new Date(user.trial_ends_at) > new Date()
                            ? "Free Trial"
                            : user?.access_status === "active"
                            ? "Active"
                            : user?.access_status === "past_due"
                            ? "Past Due"
                            : user?.access_status === "canceled"
                            ? "Canceled"
                            : user?.access_status === "pending"
                            ? "Pending"
                            : "Unknown"}
                    </div>
                </div>

                <div>
                    <Label>Current Plan</Label>

                    <div className="mt-2 rounded-2xl border border-[#ffd166]/25 bg-black px-4 py-3 text-sm text-[#fff3c4]">
                        {user?.access_status === "tester"
                            ? "Tester Access"
                            : user?.billing_interval === "monthly"
                            ? user?.trial_ends_at &&
                            new Date(user.trial_ends_at) > new Date()
                                ? "Monthly - $15 after trial"
                                : "Monthly - $15/month"
                            : user?.billing_interval === "annual"
                            ? user?.trial_ends_at &&
                            new Date(user.trial_ends_at) > new Date()
                                ? "Annual - $150 after trial"
                                : "Annual - $150/year"
                            : "Not set"}
                    </div>
                </div>

                <div>
                    <Label>
                        {user?.cancel_at_period_end
                            ? "Access Until"
                            : user?.trial_ends_at &&
                            new Date(user.trial_ends_at) > new Date()
                            ? "Trial Ends"
                            : "Next Renewal"}
                    </Label>

                    <div className="mt-2 rounded-2xl border border-[#ffd166]/25 bg-black px-4 py-3 text-sm text-[#fff3c4]">
                        {user?.access_status === "tester"
                            ? "Not applicable"
                            : user?.trial_ends_at &&
                            new Date(user.trial_ends_at) > new Date()
                            ? new Date(
                                user.trial_ends_at
                            ).toLocaleDateString()
                            : user?.current_period_end
                            ? new Date(
                                user.current_period_end
                            ).toLocaleDateString()
                            : "Not available"}
                    </div>
                        </div>
                    </div>
                
                {user?.trial_ends_at &&
                new Date(user.trial_ends_at) > new Date() &&
                (() => {
                    const now = new Date();
                    const trialEnd = new Date(user.trial_ends_at);

                    const msRemaining =
                        trialEnd.getTime() - now.getTime();

                    const daysRemaining =
                        Math.ceil(
                            msRemaining /
                                (1000 * 60 * 60 * 24)
                        );

                    if (daysRemaining > 3) {
                        return null;
                    }

                    const planLabel =
                        user?.billing_interval === "annual"
                            ? "$150/year"
                            : "$15/month";

                    return (
                        <div className="mt-5 rounded-2xl border border-[#ffd166]/40 bg-[#2b1548]/45 p-4 shadow-[0_0_20px_rgba(255,209,102,0.08)]">
                            <h4 className="font-display text-lg text-[#ffd166]">
                                {daysRemaining <= 1
                                    ? "Your Free Trial Ends Today"
                                    : `Your Free Trial Ends in ${daysRemaining} Days`}
                            </h4>

                            <p className="mt-2 text-sm text-[#fff3c4]">
                                Your{" "}
                                {user?.billing_interval === "annual"
                                    ? "Annual"
                                    : "Monthly"}{" "}
                                plan is scheduled to begin on{" "}
                                {trialEnd.toLocaleDateString()} at{" "}
                                {planLabel}.
                            </p>

                            <p className="mt-2 text-xs text-[#b0b8d6]">
                                Cancel before the trial ends if you do not want the paid subscription to begin.
                            </p>
                        </div>
                    );
                })()}

                    {user?.cancel_at_period_end && (
                        <div className="mt-5 rounded-2xl border border-[#ffd166]/35 bg-[#160d26]/45 p-5 shadow-[0_0_20px_rgba(255,209,102,0.08)]">
                            <div>
                                <h4 className="font-display text-lg text-[#ffd166]">
                                    Cancellation Scheduled
                                </h4>

                                <p className="mt-1 text-sm text-[#b0b8d6]">
                                    {user?.trial_ends_at &&
                                    new Date(user.trial_ends_at) > new Date()
                                        ? "Your subscription will end when your free trial ends."
                                        : "Your subscription will not renew after the current billing period."}
                                </p>
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div className="rounded-xl border border-[#ffd166]/20 bg-black/60 p-3">
                                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                                        Access Available Through
                                    </div>

                                    <div className="mt-1 font-semibold text-[#fff3c4]">
                                        {user?.current_period_end
                                            ? new Date(
                                                user.current_period_end
                                            ).toLocaleDateString()
                                            : "End of current billing period"}
                                    </div>
                                </div>

                                <div className="rounded-xl border border-[#ffd166]/20 bg-black/60 p-3">
                                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                                        Family Data Retained Through
                                    </div>

                                    <div className="mt-1 font-semibold text-[#fff3c4]">
                                        {user?.data_retention_until
                                            ? new Date(
                                                user.data_retention_until
                                            ).toLocaleDateString()
                                            : "30 days after access ends"}
                                    </div>
                                </div>
                            </div>

                            <p className="mt-4 text-xs leading-relaxed text-[#b0b8d6]">
                                Your family information will remain stored for 30 days after your
                                access ends. Resume your subscription before the retention deadline
                                to keep your account and saved information available.
                            </p>
                        </div>
                    )}

                    <div className="mt-5 border-t border-[#ffd166]/20 pt-4">
                        {user?.access_status !== "tester" && (
                        <>
                            {user?.stripe_subscription_id &&
                            user?.access_status === "active" ? (
                                <div className="mb-3">
                                    <h4 className="font-display text-lg text-[#ffd166]">
                                        Current Subscription
                                    </h4>

                                    <p className="mt-1 text-xs text-[#b0b8d6]">
                                        Your existing Stripe subscription is already connected to this account.
                                    </p>

                                    <div className="mt-3 rounded-2xl border border-[#ffd166]/25 bg-black/60 p-4">
                                        <p className="text-sm font-semibold text-[#fff3c4]">
                                            {user?.billing_interval === "annual"
                                                ? "Annual - $150/year"
                                                : "Monthly - $15/month"}
                                        </p>

                                        {user?.trial_ends_at &&
                                            new Date(user.trial_ends_at) > new Date() && (
                                                <p className="mt-1 text-xs text-[#7dffb2]">
                                                    Free trial active until{" "}
                                                    {new Date(
                                                        user.trial_ends_at
                                                    ).toLocaleDateString()}
                                                </p>
                                            )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="mb-3">
                                        <h4 className="font-display text-lg text-[#ffd166]">
                                            Choose a Plan
                                        </h4>

                                        <p className="mt-1 text-xs text-[#b0b8d6]">
                                            Select a subscription plan to continue securely through Stripe.
                                        </p>
                                    </div>

                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                startBillingCheckout("monthly")
                                            }
                                            className="rounded-xl border border-[#ffd166]/35 bg-black px-4 py-2 text-sm font-semibold text-[#fff3c4] transition hover:border-[#ffd166]/75 hover:text-[#ffd166]"
                                        >
                                            Monthly - $15
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                startBillingCheckout("annual")
                                            }
                                            className="rounded-xl border border-[#ffd166]/35 bg-black px-4 py-2 text-sm font-semibold text-[#fff3c4] transition hover:border-[#ffd166]/75 hover:text-[#ffd166]"
                                        >
                                            Annual - $150
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                      
                    {user?.stripe_customer_id && (
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    const response = await api.post(
                                        "/billing/portal"
                                    );

                                    const portalUrl =
                                        response.data?.portal_url;

                                    if (!portalUrl) {
                                        toast.error(
                                            "Unable to open subscription management."
                                        );
                                        return;
                                    }

                                    window.location.href =
                                        portalUrl;
                                } catch (error) {
                                    console.error(
                                        "Unable to open billing portal:",
                                        error
                                    );

                                    toast.error(
                                        error?.response?.data?.detail ||
                                            "Unable to open subscription management."
                                    );
                                }
                            }}
                            className="rounded-xl border border-[#d8b4fe]/35 bg-black px-4 py-2 text-sm font-semibold text-[#e9d5ff] transition hover:border-[#d8b4fe]/75 hover:text-white"
                        >
                            Manage Subscription
                        </button>
                    )}   
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    await api.post("/billing/refresh");

                                    const me = await api.get("/auth/me");
                                    setUser(me.data);

                                    toast.success("Billing information refreshed.");
                                } catch (error) {
                                    console.error(
                                        "Unable to refresh billing:",
                                        error
                                    );

                                    toast.error(
                                        error?.response?.data?.detail ||
                                            "Unable to refresh billing information."
                                    );
                                }
                            }}
                            className="mt-3 rounded-xl border border-[#ffd166]/30 bg-black px-3 py-2 text-xs font-semibold text-[#fff3c4] transition hover:border-[#ffd166]/70"
                        >
                            Refresh Billing
                        </button>
                        </div>

                    </div>
                )}
            </div>              
<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
    <div>
        <h3 className="font-display text-2xl text-[#7dffb2]">
            Children
        </h3>

        <p className="mt-1 text-xs text-[#b0b8d6]">
            Create and manage each child’s profile and Atrium access.
        </p>
    </div>

    <button
        type="button"
        onClick={() =>
            setAddChildOpen((current) => !current)
        }
        disabled={children.length >= 25}
        className={`${purpleActionBtn} disabled:cursor-not-allowed disabled:opacity-50`}
    >
        {addChildOpen ? "Close" : "Add Child"}
    </button>
</div>

{addChildOpen && (
    <div className="mb-5 rounded-[32px] border border-[#ffd166]/35 bg-black p-5 shadow-[0_0_24px_rgba(255,209,102,0.12)]">
        <h4 className="font-display text-xl text-[#ffd166]">
            Add Child
        </h4>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
                <Label>Child Name</Label>

                <Input
                    value={newChildName}
                    onChange={(e) =>
                        setNewChildName(e.target.value)
                    }
                    placeholder="Enter the child’s name"
                    className={shadowInput}
                />
            </div>

            <div>
    <Label>Birthday — Optional</Label>

    <Input
        type="date"
        value={newChildBirthday}
        onChange={(e) =>
            setNewChildBirthday(e.target.value)
        }
        max={new Date().toISOString().split("T")[0]}
        className={shadowInput}
    />

    <p className="mt-1 text-xs text-[#8f98b5]">
        Age will update automatically from the birthday.
    </p>
</div>
<div>
    <Label>Grade Level — Optional</Label>

    <select
        value={newChildGradeLevel}
        onChange={(e) =>
            setNewChildGradeLevel(e.target.value)
        }
        className={shadowInput}
    >
        <option value="">
            Choose grade level
        </option>
        <option value="Pre-K">Pre-K</option>
        <option value="Kindergarten">Kindergarten</option>
        <option value="1st Grade">1st Grade</option>
        <option value="2nd Grade">2nd Grade</option>
        <option value="3rd Grade">3rd Grade</option>
        <option value="4th Grade">4th Grade</option>
        <option value="5th Grade">5th Grade</option>
        <option value="6th Grade">6th Grade</option>
        <option value="7th Grade">7th Grade</option>
        <option value="8th Grade">8th Grade</option>
        <option value="9th Grade">9th Grade</option>
        <option value="10th Grade">10th Grade</option>
        <option value="11th Grade">11th Grade</option>
        <option value="12th Grade">12th Grade</option>
    </select>
</div>
<div>
    <Label>Profile Color</Label>

    <div className="mt-2 flex flex-wrap items-center gap-3">
        <input
            type="color"
            value={newChildColor}
            onChange={(e) =>
                setNewChildColor(e.target.value)
            }
            className="h-11 w-16 cursor-pointer rounded-xl border border-[#ffd166]/30 bg-black p-1"
        />

        <div
            className="h-10 w-10 rounded-full border border-[#ffd166]/30"
            style={{
                backgroundColor: newChildColor,
                boxShadow: `0 0 18px ${newChildColor}66`,
            }}
        />

        <p className="text-xs text-[#8f98b5]">
            This color will identify the child across calendars and profiles.
        </p>
    </div>
</div>
    <div className="md:col-span-2">
        <div className="rounded-2xl border border-[#c084fc]/20 bg-black/30 p-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-sm font-semibold text-[#ffd166]">
                        Child Login
                    </p>

                    <p className="mt-1 text-xs text-[#8f98b5]">
                        Create the child’s own sign-in for their dashboard.
                    </p>
                </div>

                <label className="flex items-center gap-2 text-xs text-[#b0b8d6]">
                    <input
                        type="checkbox"
                        checked={newChildLoginEnabled}
                        onChange={(e) =>
                            setNewChildLoginEnabled(e.target.checked)
                        }
                    />
                    Enable login
                </label>
            </div>

            {newChildLoginEnabled && (
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <Label>Child Username</Label>

                        <Input
                            value={newChildUsername}
                            onChange={(e) =>
                                setNewChildUsername(
                                    e.target.value
                                        .toLowerCase()
                                        .replace(/[^a-z0-9._-]/g, "")
                                )
                            }
                            placeholder="example: marilyn.g"
                            autoComplete="off"
                            className={shadowInput}
                        />

                        <p className="mt-1 text-[11px] text-[#8f98b8]">
                            Use 3–32 lowercase letters, numbers, periods,
                            underscores, or hyphens.
                        </p>
                    </div>

                    <div>
                        <Label>Password</Label>

                        <Input
                            type={showEditChildPassword ? "text" : "password"}
                            value={newChildPassword}
                            onChange={(e) =>
                                setNewChildPassword(e.target.value)
                            }
                            placeholder="At least 6 characters"
                            autoComplete="new-password"
                            className={shadowInput}
                        />
                        <button
                            type="button"
                            onClick={() =>
                                setShowEditChildPassword((current) => !current)
                            }
                            className="mt-2 text-xs font-semibold text-[#c084fc] hover:text-[#d8b4fe]"
                        >
                            {showEditChildPassword
                                ? "Hide New Password"
                                : "Show New Password"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    </div>
        </div>
            <div className="md:col-span-2">
                <div className="rounded-2xl border border-[#ffd166]/25 bg-black/30 p-4">
                    <h4 className="font-display text-lg text-[#ffd166]">
                        What This Child Can See
                    </h4>

                    <p className="mt-1 text-xs text-[#8f98b5]">
                        Choose what appears in the child’s dashboard and academic views.
                    </p>

                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <label className="flex items-start gap-3 rounded-2xl border border-[#c084fc]/20 bg-[#7c3aed]/10 p-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={newShowAcademicRecord}
                                onChange={(e) =>
                                    setNewShowAcademicRecord(e.target.checked)
                                }
                                className="mt-1 h-4 w-4 accent-[#ffd166]"
                            />

                            <span className="text-sm text-[#fff3c4]">
                                Academic Record
                            </span>
                        </label>

                        <label className="flex items-start gap-3 rounded-2xl border border-[#c084fc]/20 bg-[#7c3aed]/10 p-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={newShowCurrentGrades}
                                onChange={(e) =>
                                    setNewShowCurrentGrades(e.target.checked)
                                }
                                className="mt-1 h-4 w-4 accent-[#ffd166]"
                            />

                            <span className="text-sm text-[#fff3c4]">
                                Current Grades
                            </span>
                        </label>

                        <label className="flex items-start gap-3 rounded-2xl border border-[#c084fc]/20 bg-[#7c3aed]/10 p-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={newShowRubricResults}
                                onChange={(e) =>
                                    setNewShowRubricResults(e.target.checked)
                                }
                                className="mt-1 h-4 w-4 accent-[#ffd166]"
                            />

                            <span className="text-sm text-[#fff3c4]">
                                Rubric Results
                            </span>
                        </label>

                        <label className="flex items-start gap-3 rounded-2xl border border-[#c084fc]/20 bg-[#7c3aed]/10 p-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={newShowProgressReports}
                                onChange={(e) =>
                                    setNewShowProgressReports(e.target.checked)
                                }
                                className="mt-1 h-4 w-4 accent-[#ffd166]"
                            />

                            <span className="text-sm text-[#fff3c4]">
                                Progress Reports
                            </span>
                        </label>

                        <label className="flex items-start gap-3 rounded-2xl border border-[#c084fc]/20 bg-[#7c3aed]/10 p-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={newShowReportCards}
                                onChange={(e) =>
                                    setNewShowReportCards(e.target.checked)
                                }
                                className="mt-1 h-4 w-4 accent-[#ffd166]"
                            />

                            <span className="text-sm text-[#fff3c4]">
                                Report Cards
                            </span>
                        </label>

                        <label className="flex items-start gap-3 rounded-2xl border border-[#c084fc]/20 bg-[#7c3aed]/10 p-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={newShowParentComments}
                                onChange={(e) =>
                                    setNewShowParentComments(e.target.checked)
                                }
                                className="mt-1 h-4 w-4 accent-[#ffd166]"
                            />

                            <span className="text-sm text-[#fff3c4]">
                                Parent Comments
                            </span>
                        </label>
                    </div>
                </div>
        </div>
    <div className="md:col-span-2">
    <Label>Profile Picture — Optional</Label>

    <input
        type="file"
        accept="image/*"
        onChange={(e) =>
            fileToDataUrl(
                e.target.files?.[0],
                setNewChildPicture
            )
        }
        className="mt-2 w-full text-sm text-[#b0b8d6]
        file:mr-3
        file:rounded-2xl
        file:border
        file:border-[#ffd166]/30
        file:bg-[#7c3aed]/20
        file:px-4
        file:py-2
        file:text-[#fff3c4]
        hover:file:bg-[#7c3aed]/35"
    />

    {newChildPicture && (
        <div className="mt-3 flex items-center gap-3">
            <img
                src={newChildPicture}
                alt="Child profile preview"
                className="h-20 w-20 rounded-full border border-[#ffd166]/30 object-cover"
            />

            <button
                type="button"
                onClick={() =>
                    setNewChildPicture("")
                }
                className={purpleActionBtn}
            >
                Remove Picture
            </button>
        </div>
    )}
</div>
        <div className={`${purpleActionRow} mt-5`}>
            <button
                type="button"
                onClick={createNewChild}
                disabled={savingNewChild}
                className={`${purpleActionBtn} disabled:cursor-not-allowed disabled:opacity-50`}
            >
                {savingNewChild
                    ? "Adding..."
                    : "Add Child"}
            </button>

            <button
                type="button"
                onClick={() => {
                    setAddChildOpen(false);
                    setNewChildName("");
                    setNewChildBirthday("");
                    setNewChildGradeLevel("");
                    setNewChildColor("#c084fc");
                    setNewChildUsername("");
                    setNewChildPassword("");
                    setNewChildLoginEnabled(true);
                    setNewChildPicture("");
                    setNewShowAcademicRecord(false);
                    setNewShowCurrentGrades(false);
                    setNewShowRubricResults(false);
                    setNewShowProgressReports(false);
                    setNewShowReportCards(false);
                    setNewShowParentComments(false);
                }}
                disabled={savingNewChild}
                className={`${purpleActionBtn} disabled:cursor-not-allowed disabled:opacity-50`}
            >
                Cancel
            </button>
        </div>
    </div>
)}

<div className="space-y-3">
                            {children.length === 0 && (
                                <p className="text-sm text-[#6c7a9c]">
                                    No children yet.
                                </p>
                            )}


{children.map((c) => (
    <div
        key={c.child_id}
        className="rounded-xl bg-white/5 px-3 py-3 space-y-3 border border-[#ffd166]/20"
    >
        <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#7c3aed]/25 border border-[#ffd166]/40 overflow-hidden flex items-center justify-center">
                    {(c.profile_picture || c.picture) ? (
                        <img
                            src={c.profile_picture || c.picture}
                            alt={c.name}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <UserIcon size={20} className="text-[#ffd166]" />
                    )}
                </div>


                <div>
                    <p className="font-semibold">{c.name}</p>
                    <p className="text-xs text-[#b0b8d6]">
                        {c.gradeLevel || (c.age ? `Age ${c.age}` : "Student profile")}
                    </p>


                </div>
            </div>


<div className="flex flex-wrap items-center justify-end gap-2">
    <button
        type="button"
        className={purpleActionBtn}
        onClick={() => {
            const child = ensureChildDefaults(c);

            setEditingChild(child);
            setEditName(child.name || "");
            setEditPicture(
                child.profile_picture || child.picture || ""
            );
            setEditBirthday(child.birthday || "");
            setEditGradeLevel(child.gradeLevel || "");
            setEditProfileColor(
                child.profileColor ||
                child.calendarColor ||
                "#c084fc"
            );
            setEditChildUsername(child.child_username || "");
            setEditChildPassword("");
            setEditChildLoginEnabled(!!child.child_login_enabled);
            setEditBlueberryEnabled(
                !!child.blueberry_behavior_enabled
            );
            setEditShowAcademicRecord(
                !!child.show_academic_record
            );
            setEditShowCurrentGrades(
                !!child.show_current_grades
            );
            setEditShowRubricResults(
                !!child.show_rubric_results
            );
            setEditShowProgressReports(
                !!child.show_progress_reports
            );
            setEditShowReportCards(
                !!child.show_report_cards
            );
            setEditShowParentComments(
                !!child.show_parent_comments
            );
        }}
    >
        Edit
    </button>

   <button
        type="button"
        onClick={() => setArchiveChildTarget(c)}
        className="rounded-2xl border border-red-400/35 bg-red-950/30 px-4 py-2 text-sm font-semibold text-red-200 transition hover:border-red-300/70 hover:bg-red-950/50"
    >
        Archive
    </button>

    <button
        type="button"
        onClick={() => {
            setDeleteChildTarget(c);
        }}
    className="rounded-2xl border border-red-500/60 bg-red-950/50 px-4 py-2 text-sm font-semibold text-red-100 transition hover:border-red-300 hover:bg-red-900/70"
>
    Delete Permanently
    </button>
    </div>
        </div>
    </div>
))}

<div className="mt-6 rounded-[32px] border border-[#ffd166]/30 bg-black p-5 shadow-[0_0_24px_rgba(255,209,102,0.10)]">
    <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
            <h4 className="font-display text-xl text-[#ffd166]">
                Archived Children
            </h4>

            <p className="mt-1 text-xs text-[#b0b8d6]">
                Restore a child profile without losing their saved school records.
            </p>
        </div>

        <button
            type="button"
            onClick={async () => {
                const opening = !archivedChildrenOpen;

                setArchivedChildrenOpen(opening);

                if (opening) {
                    await loadArchivedChildren();
                }
            }}
            className={purpleActionBtn}
        >
            {archivedChildrenOpen
                ? "Hide Archived"
                : "View Archived"}
        </button>
    </div>

    {archivedChildrenOpen && (
        <div className="mt-5 space-y-3">
            {archivedChildren.length === 0 ? (
                <p className="rounded-2xl border border-[#ffd166]/20 bg-white/5 p-4 text-sm text-[#b0b8d6]">
                    No archived child profiles.
                </p>
            ) : (
                archivedChildren.map((child) => (
                    <div
                        key={child.child_id}
                        className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#ffd166]/25 bg-white/5 p-4"
                    >
                        <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-[#ffd166]/40 bg-[#7c3aed]/25">
                                {child.profile_picture ||
                                child.picture ? (
                                    <img
                                        src={
                                            child.profile_picture ||
                                            child.picture
                                        }
                                        alt={child.name}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <UserIcon
                                        size={20}
                                        className="text-[#ffd166]"
                                    />
                                )}
                            </div>

                            <div>
                                <p className="font-semibold text-[#fff3c4]">
                                    {child.name}
                                </p>

                                <p className="text-xs text-[#b0b8d6]">
                                    Archived profile
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() =>
                                restoreArchivedChild(child)
                            }
                            disabled={!!restoringChildId}
                            className={`${purpleActionBtn} disabled:cursor-not-allowed disabled:opacity-50`}
                        >
                            {restoringChildId ===
                                child.child_id
                                    ? "Restoring..."
                                    : "Restore Child"}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setDeleteChildTarget(child);
                                    }}
                                    disabled={!!restoringChildId}
                                    className="rounded-2xl border border-red-500/60 bg-red-950/50 px-4 py-2 text-sm font-semibold text-red-100 transition hover:border-red-300 hover:bg-red-900/70 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Delete Permanently
                                </button>

                                </div>
                ))
            )}
        </div>
    )}
</div>
                        </div>

{archiveChildTarget && (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
        <div className="w-full max-w-lg overflow-hidden rounded-[32px] border border-[#ffd166]/40 bg-black shadow-[0_0_40px_rgba(255,209,102,0.18)]">
            <div className="border-b border-[#ffd166]/25 px-6 py-5">
                <h3 className="font-display text-2xl text-[#ffd166]">
                    Archive Child Profile
                </h3>

                <p className="mt-2 text-sm text-[#b0b8d6]">
                    Archive {archiveChildTarget.name}?
                </p>
            </div>

            <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
                <div className="rounded-2xl border border-red-400/30 bg-red-950/20 p-4">
                    <p className="text-sm text-[#fff3c4]">
                        This removes the child from active dashboards and disables
                        their sign-in.
                    </p>

                    <p className="mt-3 text-xs text-[#b0b8d6]">
                        Their classes, quests, attendance, messages, grades, and
                        other school records will remain stored and recoverable.
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3 border-t border-[#ffd166]/25 px-6 py-5">
                <button
                    type="button"
                    onClick={() => setArchiveChildTarget(null)}
                    disabled={!!archivingChildId}
                    className={`${purpleActionBtn} disabled:cursor-not-allowed disabled:opacity-50`}
                >
                    Cancel
                </button>

                <button
                    type="button"
                    onClick={archiveChild}
                    disabled={!!archivingChildId}
                    className="rounded-2xl border border-red-400/50 bg-red-950/40 px-5 py-2.5 text-sm font-semibold text-red-100 transition hover:border-red-300 hover:bg-red-950/65 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {archivingChildId
                        ? "Archiving..."
                        : `Archive ${archiveChildTarget.name}`}
                </button>
            </div>
        </div>
    </div>
)}

{deleteChildTarget && (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 px-4 backdrop-blur-sm">
        <div className="w-full max-w-lg overflow-hidden rounded-[32px] border border-red-500/40 bg-[#05070d] shadow-[0_0_45px_rgba(239,68,68,0.16)]">
            <div className="border-b border-red-500/25 px-6 py-5">
                <h3 className="font-display text-2xl text-[#ffd166]">
                    Delete Child Permanently
                </h3>

                <p className="mt-2 text-sm text-[#b0b8d6]">
                    This will permanently erase{" "}
                    <span className="font-semibold text-[#fff3c4]">
                        {deleteChildTarget.name}
                    </span>
                    {" "}and their saved school records.
                </p>
            </div>

            <div className="px-6 py-5">
                <div className="rounded-2xl border border-red-500/25 bg-red-950/20 p-4">
                    <p className="text-sm font-semibold text-red-200">
                        This cannot be undone.
                    </p>

                    <p className="mt-2 text-xs leading-5 text-[#b0b8d6]">
                        If you may want these records later, use Archive instead.
                    </p>
                </div>

                <div className="mt-6 flex flex-wrap justify-end gap-3">
                    <button
                        type="button"
                        onClick={() =>
                            setDeleteChildTarget(null)
                        }
                        disabled={!!deletingChildId}
                        className={purpleActionBtn}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        disabled={!!deletingChildId}
                        onClick={async () => {
                            const childToDelete =
                                deleteChildTarget;

                            if (!childToDelete?.child_id) {
                                return;
                            }

                            setDeletingChildId(
                                childToDelete.child_id
                            );

                            try {
                                await api.delete(
                                    `/children/${childToDelete.child_id}`
                                );

                                const updatedChildren =
                                    children.filter(
                                        (currentChild) =>
                                            currentChild.child_id !==
                                            childToDelete.child_id
                                    );

                                setChildren(updatedChildren);

                                localStorage.setItem(
                                    "shadowGroveChildren",
                                    JSON.stringify(
                                        updatedChildren
                                    )
                                );

                                setArchivedChildren(
                                    (current) =>
                                        current.filter(
                                            (archivedChild) =>
                                                archivedChild.child_id !==
                                                childToDelete.child_id
                                        )
                                );

                                setSubjects((current) => {
                                    const updatedSubjects =
                                        current.filter(
                                            (subject) =>
                                                subject.child_id !==
                                                childToDelete.child_id
                                        );

                                    localStorage.setItem(
                                        "shadowGroveSubjects",
                                        JSON.stringify(
                                            updatedSubjects
                                        )
                                    );

                                    return updatedSubjects;
                                });

                                if (
                                    editingChild?.child_id ===
                                    childToDelete.child_id
                                ) {
                                    setEditingChild(null);
                                }

                                toast.success(
                                    `${childToDelete.name} was permanently deleted.`
                                );

                                setDeleteChildTarget(null);
                            } catch (error) {
                                console.error(
                                    "Failed to permanently delete child:",
                                    error
                                );

                                toast.error(
                                    typeof error?.response?.data?.detail ===
                                        "string"
                                        ? error.response.data.detail
                                        : "Could not permanently delete the child."
                                );
                            } finally {
                                setDeletingChildId("");
                            }
                        }}
                        className="rounded-2xl border border-red-500/60 bg-red-950/60 px-5 py-2.5 text-sm font-semibold text-red-100 transition hover:border-red-300 hover:bg-red-900/75 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {deletingChildId
                            ? "Deleting..."
                            : "Delete Permanently"}
                    </button>
                </div>
            </div>
        </div>
    </div>
)}

                        {editingChild && (
                            <div className="mt-6 max-h-[90vh] overflow-y-auto rounded-[32px] border border-[#ffd166]/40 bg-black p-6 space-y-4 shadow-[0_0_35px_rgba(255,209,102,0.12)] backdrop-blur-sm">
                                <h3 className="font-display text-xl text-[#ffd166]">
                                    Edit Child Profile
                                </h3>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        

    
                                        <Label>Name</Label>
                                        <Input
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            className={shadowInput}
                                        />
                                    </div>


                                    <div>
                                        
                                        
                                    <div>
                                        

                                    
                                    </div>

                                    
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) =>
                                                fileToDataUrl(e.target.files?.[0], setEditPicture)
                                            }
                                            className="mt-1 w-full text-sm text-[#b0b8d6] file:mr-3 file:rounded-full file:border-0 file:bg-[#7c3aed] file:text-white file:px-3 file:py-1.5"
                                        />
                                    </div>

<div className="md:col-span-2 rounded-[32px] border border-[#ffd166]/35 bg-black p-5 shadow-[0_0_24px_rgba(255,209,102,0.12)]">
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
            <Label>Birthday</Label>

            <Input
                type="date"
                value={editBirthday}
                onChange={(e) =>
                    setEditBirthday(e.target.value)
                }
                max={new Date().toISOString().split("T")[0]}
                className={shadowInput}
            />
        </div>

        <div>
            <Label>Grade Level</Label>

            <select
                value={editGradeLevel}
                onChange={(e) =>
                    setEditGradeLevel(e.target.value)
                }
                className={shadowInput}
            >
                <option value="">Choose grade level</option>
                <option value="Pre-K">Pre-K</option>
                <option value="Kindergarten">Kindergarten</option>
                <option value="1st Grade">1st Grade</option>
                <option value="2nd Grade">2nd Grade</option>
                <option value="3rd Grade">3rd Grade</option>
                <option value="4th Grade">4th Grade</option>
                <option value="5th Grade">5th Grade</option>
                <option value="6th Grade">6th Grade</option>
                <option value="7th Grade">7th Grade</option>
                <option value="8th Grade">8th Grade</option>
                <option value="9th Grade">9th Grade</option>
                <option value="10th Grade">10th Grade</option>
                <option value="11th Grade">11th Grade</option>
                <option value="12th Grade">12th Grade</option>
            </select>
        </div>

        <div>
            <Label>Profile Color</Label>

            <div className="mt-2 flex items-center gap-3">
                <input
                    type="color"
                    value={editProfileColor}
                    onChange={(e) =>
                        setEditProfileColor(e.target.value)
                    }
                    className="h-11 w-16 cursor-pointer rounded-xl border border-[#ffd166]/30 bg-black p-1"
                />

                <div
                    className="h-10 w-10 rounded-full border border-[#ffd166]/30"
                    style={{
                        backgroundColor: editProfileColor,
                        boxShadow: `0 0 18px ${editProfileColor}66`,
                    }}
                />
            </div>
        </div>
    </div>
    <h4 className="font-display text-lg text-[#ffd166]">
        Child Sign-In
    </h4>

    <p className="mt-1 text-xs text-[#b0b8d6]">
        Create a private username and password for this child’s Atrium.
    </p>

    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
            <Label>Child Username</Label>

            <Input
                value={editChildUsername}
                onChange={(e) =>
                    setEditChildUsername(
                        e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9._-]/g, "")
                    )
                }
                placeholder="example: marilyn.g"
                autoComplete="off"
                className={shadowInput}
            />

            <p className="mt-1 text-[11px] text-[#8f98b8]">
                Use 3–32 lowercase letters, numbers, periods, underscores, or hyphens.
            </p>
        </div>

        <div>
            <Label>
                {editingChild?.child_login_configured
                    ? "New Password"
                    : "Password"}
            </Label>

            {editingChild?.child_login_configured && (
                <p className="mb-2 text-xs text-[#7dffb2]">
                    Password is set. Enter a new one only if you want to change it.
                </p>
            )}

            <Input
                type={showEditChildPassword ? "text" : "password"}
                value={editChildPassword}
                onChange={(e) =>
                    setEditChildPassword(e.target.value)
                }
                placeholder={
                    editingChild?.child_login_configured
                        ? "Leave blank to keep current password"
                        : "At least 6 characters"
                }
                autoComplete="new-password"
                className={shadowInput}
            />
        <button
            type="button"
            onClick={() =>
                setShowEditChildPassword((current) => !current)
            }
            className="mt-2 text-xs font-semibold text-[#c084fc] hover:text-[#d8b4fe]"
        >
            {showEditChildPassword
                ? "Hide New Password"
                : "Show New Password"}
        </button>
        </div>
    </div>

    <div className="mt-5 space-y-3">
        <label className="flex items-start gap-3 rounded-2xl border border-[#ffd166]/25 bg-[#7c3aed]/10 p-4 cursor-pointer">
            <input
                type="checkbox"
                checked={editChildLoginEnabled}
                onChange={(e) =>
                    setEditChildLoginEnabled(e.target.checked)
                }
                className="mt-1 h-4 w-4 accent-[#ffd166]"
            />

            <span>
                <span className="block text-sm font-semibold text-[#fff3c4]">
                    Allow Child Sign-In
                </span>

                <span className="block text-xs text-[#b0b8d6] mt-1">
                    The child can enter the Atrium using their own username and password.
                </span>
            </span>
        </label>

        {user?.genius_access_enabled ? (
    <label className="flex items-start gap-3 rounded-2xl border border-[#ffd166]/25 bg-[#7c3aed]/10 p-4 cursor-pointer">
        <input
            type="checkbox"
            checked={editBlueberryEnabled}
            onChange={(e) =>
                setEditBlueberryEnabled(e.target.checked)
            }
            className="mt-1 h-4 w-4 accent-[#ffd166]"
        />

        <span>
            <span className="block text-sm font-semibold text-[#fff3c4]">
                Show Genius Companion
            </span>

            <span className="block text-xs text-[#b0b8d6] mt-1">
                Adds the optional Genius companion to this child’s dashboard.
            </span>
        </span>
    </label>
) : (
    <div className="rounded-2xl border border-[#ffd166]/20 bg-black p-4 opacity-70">
        <span className="block text-sm font-semibold text-[#fff3c4]">
            Genius Companion
        </span>

        <span className="block text-xs text-[#b0b8d6] mt-1">
            Coming Soon
        </span>
    </div>
)}

<div className="rounded-2xl border border-[#ffd166]/25 bg-black p-4">
    <label className="flex cursor-pointer items-start gap-3">
        <input
            type="checkbox"
            checked={editShowAcademicRecord}
            onChange={(e) =>
                setEditShowAcademicRecord(
                    e.target.checked
                )
            }
            className="mt-1 h-4 w-4 accent-[#ffd166]"
        />

        <span>
            <span className="block text-sm font-semibold text-[#fff3c4]">
                Show Academic Record
            </span>

            <span className="mt-1 block text-xs text-[#b0b8d6]">
                Allows this child to view the academic progress area on their dashboard.
            </span>
        </span>
    </label>

    <div
        className={`
            mt-4
            grid
            gap-3
            md:grid-cols-2
            ${
                editShowAcademicRecord
                    ? ""
                    : "pointer-events-none opacity-45"
            }
        `}
    >
        <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-3">
            <input
                type="checkbox"
                checked={editShowCurrentGrades}
                onChange={(e) =>
                    setEditShowCurrentGrades(
                        e.target.checked
                    )
                }
                className="h-4 w-4 accent-[#ffd166]"
            />

            <span className="text-sm text-[#fff3c4]">
                Current Grades
            </span>
        </label>

        <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-3">
            <input
                type="checkbox"
                checked={editShowRubricResults}
                onChange={(e) =>
                    setEditShowRubricResults(
                        e.target.checked
                    )
                }
                className="h-4 w-4 accent-[#ffd166]"
            />

            <span className="text-sm text-[#fff3c4]">
                Rubric Results
            </span>
        </label>

        <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-3">
            <input
                type="checkbox"
                checked={editShowProgressReports}
                onChange={(e) =>
                    setEditShowProgressReports(
                        e.target.checked
                    )
                }
                className="h-4 w-4 accent-[#ffd166]"
            />

            <span className="text-sm text-[#fff3c4]">
                Progress Reports
            </span>
        </label>

        <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-3">
            <input
                type="checkbox"
                checked={editShowReportCards}
                onChange={(e) =>
                    setEditShowReportCards(
                        e.target.checked
                    )
                }
                className="h-4 w-4 accent-[#ffd166]"
            />

            <span className="text-sm text-[#fff3c4]">
                Report Cards
            </span>
        </label>

        <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-[#ffd166]/20 bg-[#080b14] p-3 md:col-span-2">
            <input
                type="checkbox"
                checked={editShowParentComments}
                onChange={(e) =>
                    setEditShowParentComments(
                        e.target.checked
                    )
                }
                className="h-4 w-4 accent-[#ffd166]"
            />

            <span className="text-sm text-[#fff3c4]">
                Parent Comments and Feedback
            </span>
        </label>
    </div>
</div>  
    </div>
</div>

                                   
                                </div>


                                {editPicture && (
                                    <img
                                        src={editPicture}
                                        alt="Preview"
                                        className="w-20 h-20 rounded-full object-cover border border-[#ffd166]/50"
                                    />
                                )}
                      


                                <div className={purpleActionRow}>
                                    <button
                                        type="button"
                                        onClick={saveChildEdit}
                                        disabled={savingChildAccess}
                                        className={`${purpleActionBtn} disabled:cursor-not-allowed disabled:opacity-50`}
                                    >
                                        {savingChildAccess ? "Saving..." : "Save Child"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setEditingChild(null)}
                                        className={purpleActionBtn}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                       

                    </Panel>


                    <Panel id="quests" openPanel={openPanel}>
                        <h2 className="font-display text-3xl text-[#ffd166] mb-5">
                            Quest Desk
                        </h2>
                        <SchoolDaySettings children={children} />
                    <div className="bb-card p-5 mb-6 border border-[#ffd166]/20">
                <h3 className="font-display text-2xl text-[#d8b4fe] mb-4">
                    Classroom Subjects
                </h3>
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#ffd166]/20 bg-[#05070d] p-3">

                    {/* LEFT — Add New Class */}
                    <button
                        type="button"
                        onClick={() => setShowAddClassPopup(true)}
                        className="rounded-xl border border-[#ffd166]/40 bg-[#ffd166]/10 px-4 py-2 text-sm font-semibold text-[#ffd166] transition hover:bg-[#ffd166]/20"
                    >
                        ＋ Add New Class
                    </button>

                    {/* RIGHT — View Classes */}
                    <div className="flex items-center gap-2">
                        <label className="whitespace-nowrap text-xs font-semibold text-[#d8b4fe]">
                            View Classes:
                        </label>

                        <select
                            value={classStudentFilter}
                            onChange={(e) => setClassStudentFilter(e.target.value)}
                            className="max-w-[180px] rounded-xl border border-[#7c3aed]/40 bg-[#080b14] px-3 py-2 text-sm text-[#f5eaff] outline-none focus:border-[#ffd166]"
                        >
                            <option value="all">All Students</option>

                            {children.map((child) => (
                                <option
                                    key={child.child_id}
                                    value={String(child.child_id)}
                                >
                                    {child.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
{showAddClassPopup && (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 p-4">
        <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[30px] border border-[#ffd166]/50 bg-[#080b14] p-6 shadow-[0_0_40px_rgba(255,209,102,0.2)]">
            <div className="mb-6 flex items-center justify-between">
                <h3 className="font-display text-2xl text-[#ffd166]">
                    Add New Class
                </h3>
                <button
                    type="button"
                    onClick={() => setShowAddClassPopup(false)}
                    className="text-2xl text-[#d8b4fe] hover:text-[#ffd166]"
                >
                    ✕
                </button>
            </div>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
            <Label>Child</Label>
            <select
                value={subjectChildId}
                onChange={(e) => setSubjectChildId(e.target.value)}
                className={shadowInput}
            >
                <option value="">Select child</option>
                {children.map((c) => (
                    <option key={c.child_id} value={c.child_id}>
                        {c.name}
                    </option>
                ))}
            </select>
        </div>


        <div>
            <Label>Subject</Label>
            <Input
                value={subjectName}
                onChange={(e) => setSubjectName(e.target.value)}
                placeholder="Alchemy, Math, History..."
                className={shadowInput}
            />
        </div>


        <div>
            <Label>Class color</Label>
            <Input
                type="color"
                value={subjectColor}
                onChange={(e) => setSubjectColor(e.target.value)}
                className={shadowInput}
            />
        </div>
        <div className="md:col-span-4">
            <div className="rounded-2xl border border-[#ffd166]/25 bg-[#080b14]/80 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <Label>Class Schedule</Label>

                        <p className="mt-1 text-xs text-[#8f98b5]">
                            Choose regular class days with optional times, or use a flexible block schedule.
                        </p>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <button
                        type="button"
                        onClick={() =>
                            setSubjectScheduleMode("days")
                        }
                        className={`rounded-2xl border p-4 text-left transition ${
                            subjectScheduleMode === "days"
                                ? "border-[#ffd166] bg-[#ffd166]/10"
                                : "border-[#c084fc]/20 bg-black/40"
                        }`}
                    >
                        <p className="font-display text-lg text-[#fff3c4]">
                            Class Days
                        </p>

                        <p className="mt-1 text-xs text-[#8f98b5]">
                            Choose days of the week. Start and end times are optional.
                        </p>
                    </button>

                    <button
                        type="button"
                        onClick={() =>
                            setSubjectScheduleMode("block")
                        }
                        className={`rounded-2xl border p-4 text-left transition ${
                            subjectScheduleMode === "block"
                                ? "border-[#ffd166] bg-[#ffd166]/10"
                                : "border-[#c084fc]/20 bg-black/40"
                        }`}
                    >
                        <p className="font-display text-lg text-[#fff3c4]">
                            Block Schedule
                        </p>

                        <p className="mt-1 text-xs text-[#8f98b5]">
                            Use a flexible morning, afternoon, evening, or custom block.
                        </p>
                    </button>
                </div>

                {subjectScheduleMode === "days" && (
                    <div className="mt-5 space-y-5">
                        <div>
                            <Label>Days of Week</Label>

                            <div className="mt-3 flex flex-wrap gap-2">
                                {[
                                    "Monday",
                                    "Tuesday",
                                    "Wednesday",
                                    "Thursday",
                                    "Friday",
                                    "Saturday",
                                    "Sunday",
                                ].map((day) => {
                                    const selected =
                                        subjectScheduleDays.includes(day);

                                    return (
                                        <button
                                            key={day}
                                            type="button"
                                            onClick={() =>
                                                setSubjectScheduleDays(
                                                    (current) =>
                                                        selected
                                                            ? current.filter(
                                                                (entry) =>
                                                                    entry !== day
                                                            )
                                                            : [
                                                                ...current,
                                                                day,
                                                            ]
                                                )
                                            }
                                            className={`rounded-xl border px-3 py-2 text-xs transition ${
                                                selected
                                                    ? "border-[#ffd166] bg-[#ffd166]/15 text-[#fff3c4]"
                                                    : "border-[#c084fc]/20 bg-black text-[#b0b8d6]"
                                            }`}
                                        >
                                            {day}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <Label>Optional Time</Label>

                            <p className="mt-1 text-xs text-[#8f98b5]">
                                Leave these blank if the class does not have a fixed clock time.
                            </p>

                            <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <Label>Start Time</Label>

                                    <Input
                                        type="time"
                                        value={subjectScheduleStartTime}
                                        onChange={(e) =>
                                            setSubjectScheduleStartTime(
                                                e.target.value
                                            )
                                        }
                                        className={shadowInput}
                                    />
                                </div>

                                <div>
                                    <Label>End Time</Label>

                                    <Input
                                        type="time"
                                        value={subjectScheduleEndTime}
                                        onChange={(e) =>
                                            setSubjectScheduleEndTime(
                                                e.target.value
                                            )
                                        }
                                        className={shadowInput}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {subjectScheduleMode === "block" && (
                    <div className="mt-5 space-y-4">
                        <div>
                            <Label>Preferred Block</Label>

                            <select
                                value={subjectScheduleBlock}
                                onChange={(e) =>
                                    setSubjectScheduleBlock(
                                        e.target.value
                                    )
                                }
                                className={shadowInput}
                            >
                                <option value="morning">
                                    Morning
                                </option>

                                <option value="afternoon">
                                    Afternoon
                                </option>

                                <option value="evening">
                                    Evening
                                </option>

                                <option value="custom">
                                    Custom
                                </option>
                            </select>
                        </div>

                        <div>
                            <Label>Estimated Weekly Hours</Label>

                            <Input
                                type="number"
                                min="0"
                                step="0.25"
                                value={subjectScheduleWeeklyHours}
                                onChange={(e) =>
                                    setSubjectScheduleWeeklyHours(
                                        e.target.value
                                    )
                                }
                                placeholder="Optional"
                                className={shadowInput}
                            />
                        </div>

                        {subjectScheduleBlock === "custom" && (
                            <div>
                                <Label>Custom Block Label</Label>

                                <Input
                                    value={
                                        subjectScheduleCustomBlock
                                    }
                                    onChange={(e) =>
                                        setSubjectScheduleCustomBlock(
                                            e.target.value
                                        )
                                    }
                                    placeholder="Late morning, after lunch..."
                                    className={shadowInput}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>

        <div>
            <button
                type="button"
                onClick={addSubject}
                className={purpleActionBtn}
            >
                {editingSubjectId ? "Save Subject Changes" : "Add Subject"}
            </button>
        </div>
        <div className="md:col-span-4">
            <div
                className="
                    rounded-2xl
                    border
                    border-[#ffd166]/25
                    bg-[#080b14]/80
                    p-4
                "
            >
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <Label>Curriculum Sources</Label>

                        <p className="mt-1 text-xs text-[#8f98b5]">
                            Add one or more curricula, textbooks, workbooks,
                            online courses, or custom resources for this class.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => {
                            const newCurriculum = {
                                id: `curriculum-${Date.now()}-${Math.random()
                                    .toString(36)
                                    .slice(2, 8)}`,

                                name: "",
                                publisher: "",
                                edition: "",
                                curriculum_type: "curriculum",
                                is_primary:
                                    subjectCurriculumSources.length === 0,

                                materials: [],
                            };

                            setSubjectCurriculumSources(
                                (current) => [
                                    ...current,
                                    newCurriculum,
                                ]
                            );
                        }}
                        className={purpleActionBtn}
                    >
                        + Add Curriculum Source
                    </button>
                </div>

                {subjectCurriculumSources.length > 0 && (
                    <div className="mt-4 space-y-3">
                        {subjectCurriculumSources.map(
                            (curriculum, index) => (
                                <div
                                    key={curriculum.id}
                                    className="
                                        rounded-2xl
                                        border
                                        border-[#c084fc]/25
                                        bg-black/40
                                        p-4
                                    "
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <p className="text-sm font-semibold text-[#ffd166]">
                                                {curriculum.name ||
                                                    `Curriculum ${index + 1}`}
                                            </p>

                                            {curriculum.is_primary && (
                                                <p className="mt-1 text-[10px] uppercase tracking-[0.16em] text-[#d8b4fe]">
                                                    Primary Curriculum
                                                </p>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSubjectCurriculumSources(
                                                    (current) =>
                                                        current.filter(
                                                            (item) =>
                                                                item.id !==
                                                                curriculum.id
                                                        )
                                                );
                                            }}
                                            className="
                                                rounded-lg
                                                border
                                                border-red-400/25
                                                bg-black
                                                px-2.5
                                                py-1.5
                                                text-[10px]
                                                text-red-200
                                                transition
                                                hover:border-red-400/60
                                            "
                                        >
                                            Remove
                                        </button>
                                    </div>

                                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                                        <div>
                                            <Label>
                                                Curriculum Name
                                            </Label>

                                            <Input
                                                value={
                                                    curriculum.name
                                                }
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;

                                                    setSubjectCurriculumSources(
                                                        (current) =>
                                                            current.map(
                                                                (item) =>
                                                                    item.id ===
                                                                    curriculum.id
                                                                        ? {
                                                                            ...item,
                                                                            name: value,
                                                                        }
                                                                        : item
                                                            )
                                                    );
                                                }}
                                                placeholder="Singapore Math, Saxon, Story of the World..."
                                                className={shadowInput}
                                            />
                                        </div>

                                        <div>
                                            <Label>
                                                Curriculum Type
                                            </Label>

                                            <select
                                                value={
                                                    curriculum.curriculum_type ||
                                                    "curriculum"
                                                }
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;

                                                    setSubjectCurriculumSources(
                                                        (current) =>
                                                            current.map(
                                                                (item) =>
                                                                    item.id ===
                                                                    curriculum.id
                                                                        ? {
                                                                            ...item,
                                                                            curriculum_type:
                                                                                value,
                                                                        }
                                                                        : item
                                                            )
                                                    );
                                                }}
                                                className={shadowInput}
                                            >
                                                <option value="curriculum">
                                                    Full Curriculum
                                                </option>

                                                <option value="textbook_series">
                                                    Textbook Series
                                                </option>

                                                <option value="online_course">
                                                    Online Course
                                                </option>

                                                <option value="custom">
                                                    Custom Curriculum
                                                </option>

                                                <option value="enrichment">
                                                    Enrichment
                                                </option>

                                                <option value="other">
                                                    Other
                                                </option>
                                            </select>
                                        </div>

                                        <div>
                                            <Label>
                                                Publisher
                                            </Label>

                                            <Input
                                                value={
                                                    curriculum.publisher ||
                                                    ""
                                                }
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;

                                                    setSubjectCurriculumSources(
                                                        (current) =>
                                                            current.map(
                                                                (item) =>
                                                                    item.id ===
                                                                    curriculum.id
                                                                        ? {
                                                                            ...item,
                                                                            publisher:
                                                                                value,
                                                                        }
                                                                        : item
                                                            )
                                                    );
                                                }}
                                                placeholder="Optional"
                                                className={shadowInput}
                                            />
                                        </div>

                                        <div>
                                            <Label>
                                                Edition
                                            </Label>

                                            <Input
                                                value={
                                                    curriculum.edition ||
                                                    ""
                                                }
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;

                                                    setSubjectCurriculumSources(
                                                        (current) =>
                                                            current.map(
                                                                (item) =>
                                                                    item.id ===
                                                                    curriculum.id
                                                                        ? {
                                                                            ...item,
                                                                            edition:
                                                                                value,
                                                                        }
                                                                        : item
                                                            )
                                                    );
                                                }}
                                                placeholder="2025, 4th Edition..."
                                                className={shadowInput}
                                            />
                                        </div>
                                    </div>

                                    <label
                                        className="
                                            mt-4
                                            flex
                                            cursor-pointer
                                            items-center
                                            gap-3
                                            rounded-xl
                                            border
                                            border-[#ffd166]/20
                                            bg-[#080b14]
                                            p-3
                                        "
                                    >
                                        <input
                                            type="checkbox"
                                            checked={
                                                Boolean(
                                                    curriculum.is_primary
                                                )
                                            }
                                            onChange={(e) => {
                                                const checked = e.target.checked;

                                                setSubjectCurriculumSources(
                                                    (current) =>
                                                        current.map((item) => ({
                                                            ...item,
                                                            is_primary:
                                                                item.id === curriculum.id
                                                                    ? checked
                                                                    : checked
                                                                    ? false
                                                                    : item.is_primary,
                                                        }))
                                                );
                                            }}
                                            className="h-4 w-4 accent-[#ffd166]"
                                        />

                                        <span className="text-sm text-[#fff3c4]">
                                            Primary curriculum for this class
                                        </span>
                                    </label>
                                    <div className="mt-4 rounded-2xl border border-[#ffd166]/15 bg-[#05070d] p-4">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div>
                                                <p className="text-sm font-semibold text-[#ffd166]">
                                                    Materials
                                                </p>

                                                <p className="mt-1 text-xs text-[#8f98b5]">
                                                    Add textbooks, workbooks, online courses, PDFs,
                                                    teacher guides, or other resources for this curriculum.
                                                </p>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newMaterial = {
                                                        id: `material-${Date.now()}-${Math.random()
                                                            .toString(36)
                                                            .slice(2, 8)}`,

                                                        name: "",
                                                        material_type: "textbook",

                                                        unit: "",
                                                        chapter: "",
                                                        lesson: "",
                                                        section: "",
                                                        pages: "",
                                                        structure_type: "chapter",
                                                        structure: [],

                                                        url: "",
                                                        notes: "",
                                                    };

                                                    setSubjectCurriculumSources(
                                                        (current) =>
                                                            current.map((item) =>
                                                                item.id === curriculum.id
                                                                    ? {
                                                                        ...item,
                                                                        materials: [
                                                                            ...(Array.isArray(item.materials)
                                                                                ? item.materials
                                                                                : []),
                                                                            newMaterial,
                                                                        ],
                                                                    }
                                                                    : item
                                                            )
                                                    );
                                                }}
                                                className={purpleActionBtn}
                                            >
                                                + Add Material
                                            </button>
                                        </div>

    {Array.isArray(curriculum.materials) &&
        curriculum.materials.length > 0 && (
            <div className="mt-4 space-y-3">
                {curriculum.materials.map(
                    (material, materialIndex) => (
                        <div
                            key={material.id}
                            className="
                                rounded-xl
                                border
                                border-[#c084fc]/20
                                bg-black/45
                                p-4
                            "
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <p className="text-sm font-semibold text-[#ffd166]">
                                        {material.name ||
                                            `Resource ${materialIndex + 1}`}
                                    </p>

                                    <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-[#b0b8d6]">
                                        {material.material_type ||
                                            "resource"}
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setSubjectCurriculumSources(
                                            (current) =>
                                                current.map((item) =>
                                                    item.id === curriculum.id
                                                        ? {
                                                            ...item,
                                                            materials:
                                                                item.materials.filter(
                                                                    (entry) =>
                                                                        entry.id !==
                                                                        material.id
                                                                ),
                                                        }
                                                        : item
                                                )
                                        );
                                    }}
                                    className="
                                        rounded-lg
                                        border
                                        border-red-400/25
                                        bg-black
                                        px-2
                                        py-1
                                        text-[10px]
                                        text-red-200
                                        transition
                                        hover:border-red-400/60
                                    "
                                >
                                    Remove
                                </button>
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div>
                                    <Label>Resource Name</Label>

                                    <Input
                                        value={material.name || ""}
                                        onChange={(e) => {
                                            const value =
                                                e.target.value;

                                            setSubjectCurriculumSources(
                                                (current) =>
                                                    current.map(
                                                        (curriculumItem) =>
                                                            curriculumItem.id ===
                                                            curriculum.id
                                                                ? {
                                                                    ...curriculumItem,
                                                                    materials:
                                                                        curriculumItem.materials.map(
                                                                            (resource) =>
                                                                                resource.id ===
                                                                                material.id
                                                                                    ? {
                                                                                            ...resource,
                                                                                            name: value,
                                                                                        }
                                                                                    : resource
                                                                        ),
                                                                }
                                                                : curriculumItem
                                                    )
                                            );
                                        }}
                                        placeholder="Workbook A, Textbook 1..."
                                        className={shadowInput}
                                    />
                                </div>
                            <div className="mt-4 rounded-2xl border border-[#ffd166]/20 bg-[#080b14]/70 p-4">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-semibold text-[#ffd166]">
                                            Class Schedule
                                        </p>

                                        <p className="mt-1 text-xs text-[#8f98b5]">
                                            Set the normal days and times for this class.
                                            The curriculum planner will use this together with
                                            the family school calendar.
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setClassScheduleDraft({
                                                mode:
                                                    material.class_schedule?.mode ||
                                                    "scheduled",

                                                days:
                                                    Array.isArray(
                                                        material.class_schedule?.days
                                                    )
                                                        ? material.class_schedule.days
                                                        : [],

                                                start_time:
                                                    material.class_schedule?.start_time ||
                                                    "",

                                                end_time:
                                                    material.class_schedule?.end_time ||
                                                    "",

                                                weekly_hours:
                                                    material.class_schedule?.weekly_hours ||
                                                    "",

                                                preferred_block:
                                                    material.class_schedule?.preferred_block ||
                                                    "morning",

                                                custom_block_label:
                                                    material.class_schedule?.custom_block_label ||
                                                    "",
                                            });

                                            setClassScheduleModal({
                                                curriculumId:
                                                    curriculum.id,

                                                resourceId:
                                                    material.id,
                                            });
                                        }}
                                        className={purpleActionBtn}
                                    >
                                        Edit Schedule
                                    </button>
                                </div>

                                {material.class_schedule ? (
                                    <div className="mt-3">
                                        {material.class_schedule.mode ===
                                        "flexible" ? (
                                            <>
                                                <p className="text-sm text-[#fff3c4]">
                                                    Flexible / Block Schedule
                                                </p>

                                                <p className="mt-1 text-xs text-[#b0b8d6]">
                                                    {material.class_schedule.weekly_hours
                                                        ? `${material.class_schedule.weekly_hours} hrs/week`
                                                        : "Weekly hours not set"}

                                                    {material.class_schedule.preferred_block
                                                        ? ` · ${material.class_schedule.preferred_block}`
                                                        : ""}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-sm text-[#fff3c4]">
                                                    {Array.isArray(
                                                        material.class_schedule.days
                                                    ) &&
                                                    material.class_schedule.days.length >
                                                        0
                                                        ? material.class_schedule.days.join(
                                                            " · "
                                                        )
                                                        : "No class days selected"}
                                                </p>

                                                <p className="mt-1 text-xs text-[#b0b8d6]">
                                                    {material.class_schedule.start_time ||
                                                        "No start time"}

                                                    {" – "}

                                                    {material.class_schedule.end_time ||
                                                        "No end time"}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <p className="mt-3 text-xs text-[#8f98b5]">
                                        No class schedule configured yet.
                                    </p>
                                )}
                            </div>
                            <div className="mt-4 rounded-2xl border border-[#c084fc]/20 bg-[#080b14]/70 p-4">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-semibold text-[#ffd166]">
                                            Linked Resources
                                        </p>

                                        <p className="mt-1 text-xs text-[#8f98b5]">
                                            Add workbooks, teacher guides, online courses,
                                            videos, PDFs, or other resources used with this
                                            main resource.
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setLinkedResourceDraft({
                                                name: "",
                                                resource_type: "workbook",
                                                has_structure: false,
                                                structure_type: "chapter",
                                                total_pages: "",
                                                content_start_page: "",
                                                structure: [],
                                            });
                                            setCurriculumBuilderResourceId("");

                                            setLinkedResourcesModal({
                                                curriculumId: curriculum.id,
                                                materialId: material.id,
                                            });
                                        }}
                                        className={purpleActionBtn}
                                    >
                                        Open Curriculum Builder
                                    </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCurriculumPlanningWizard((current) => ({
                                            ...current,
                                            step: 0,
                                        }));
                                        const owningSubject =
                                            subjects.find((subject) => {
                                                const curriculumSources =
                                                    Array.isArray(
                                                        subject.curriculum_sources
                                                    )
                                                        ? subject.curriculum_sources
                                                        : [];

                                                return curriculumSources.some(
                                                    (savedCurriculum) =>
                                                        savedCurriculum.id ===
                                                        curriculum.id
                                                );
                                            });

                                        setCurriculumPlanningModal({
                                            subjectId:
                                                curriculumOwnerSubjectId ||
                                                editingSubjectId ||
                                                owningSubject?.subject_id ||
                                                owningSubject?.id ||
                                                "",

                                            curriculumId:
                                                curriculum.id,

                                            materialId:
                                                material.id,
                                        });
                                    }}
                                    className={purpleActionBtn}
                                >
                                    Plan Curriculum
                                </button>
                                </div>

                                {(() => {
    const relatedResources =
        curriculum.materials.filter((resource) => {
            if (resource.id === material.id) {
                return false;
            }

            const linkedFromMain =
                Array.isArray(
                    material.related_resource_ids
                ) &&
                material.related_resource_ids.includes(
                    resource.id
                );

            const linkedBackToMain =
                Array.isArray(
                    resource.linked_to_resource_ids
                ) &&
                resource.linked_to_resource_ids.includes(
                    material.id
                );

            return (
                linkedFromMain ||
                linkedBackToMain
            );
        });

    return relatedResources.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
           {relatedResources.map(
            (linkedResource) => (
                <div
                    key={linkedResource.id}
                    className="
                        rounded-xl
                        border
                        border-[#ffd166]/20
                        bg-black/35
                        px-3
                        py-2
                    "
                >
                    <p className="text-xs font-semibold text-[#fff3c4]">
                        {linkedResource.name ||
                            "Unnamed Resource"}
                    </p>

                    <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-[#8f98b5]">
                        {linkedResource.material_type ||
                            linkedResource.resource_type ||
                            "resource"}

                        {linkedResource.has_structure
                            ? " · Structured"
                            : ""}
                    </p>
                </div>
            )
        )}
        </div>
    ) : (
        <p className="mt-3 text-xs text-[#8f98b5]">
            No linked resources added yet.
        </p>
    );
})()}
                            </div>        
                                <div>
                                    <Label>Resource Type</Label>

                                    <select
                                        value={
                                            material.material_type ||
                                            "textbook"
                                        }
                                        onChange={(e) => {
                                            const value =
                                                e.target.value;

                                            setSubjectCurriculumSources(
                                                (current) =>
                                                    current.map(
                                                        (curriculumItem) =>
                                                            curriculumItem.id ===
                                                            curriculum.id
                                                                ? {
                                                                    ...curriculumItem,
                                                                    materials:
                                                                        curriculumItem.materials.map(
                                                                            (resource) =>
                                                                                resource.id ===
                                                                                material.id
                                                                                    ? {
                                                                                            ...resource,
                                                                                            material_type:
                                                                                                value,
                                                                                        }
                                                                                    : resource
                                                                        ),
                                                                }
                                                                : curriculumItem
                                                    )
                                            );
                                        }}
                                        className={shadowInput}
                                    >
                                        <option value="textbook">
                                            Textbook
                                        </option>
                                        <option value="workbook">
                                            Workbook
                                        </option>
                                        <option value="teacher_guide">
                                            Teacher Guide
                                        </option>
                                        <option value="practice_book">
                                            Practice Book
                                        </option>
                                        <option value="reader">
                                            Reader
                                        </option>
                                        <option value="lab_manual">
                                            Lab Manual
                                        </option>
                                        <option value="online_course">
                                            Online Course
                                        </option>
                                        <option value="video_course">
                                            Video Course
                                        </option>
                                        <option value="pdf">
                                            PDF Resource
                                        </option>
                                        <option value="other">
                                            Other
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <Label>Total Chapters</Label>

                                    <Input
                                        type="number"
                                        min="0"
                                        value={
                                            material.total_chapters ||
                                            ""
                                        }
                                        onChange={(e) => {
                                            const value =
                                                e.target.value;

                                            setSubjectCurriculumSources(
                                                (current) =>
                                                    current.map(
                                                        (curriculumItem) =>
                                                            curriculumItem.id ===
                                                            curriculum.id
                                                                ? {
                                                                    ...curriculumItem,
                                                                    materials:
                                                                        curriculumItem.materials.map(
                                                                            (resource) =>
                                                                                resource.id ===
                                                                                material.id
                                                                                    ? {
                                                                                            ...resource,
                                                                                            total_chapters:
                                                                                                value,
                                                                                        }
                                                                                    : resource
                                                                        ),
                                                                }
                                                                : curriculumItem
                                                    )
                                            );
                                        }}
                                        className={shadowInput}
                                    />
                                </div>

                                
                                

                                <div>
                                    <Label>Total Pages</Label>

                                    <Input
                                        type="number"
                                        min="0"
                                        value={
                                            material.total_pages ||
                                            ""
                                        }
                                        onChange={(e) => {
                                            const value =
                                                e.target.value;

                                            setSubjectCurriculumSources(
                                                (current) =>
                                                    current.map(
                                                        (curriculumItem) =>
                                                            curriculumItem.id ===
                                                            curriculum.id
                                                                ? {
                                                                    ...curriculumItem,
                                                                    materials:
                                                                        curriculumItem.materials.map(
                                                                            (resource) =>
                                                                                resource.id ===
                                                                                material.id
                                                                                    ? {
                                                                                            ...resource,
                                                                                            total_pages:
                                                                                                value,
                                                                                        }
                                                                                    : resource
                                                                        ),
                                                                }
                                                                : curriculumItem
                                                    )
                                            );
                                        }}
                                        className={shadowInput}
                                    />
                                </div>
                            <div>
                                <Label>
                                    Learning Content Starts on Page
                                </Label>

                                <Input
                                    type="number"
                                    min="1"
                                    value={
                                        material.content_start_page ||
                                        ""
                                    }
                                    onChange={(e) => {
                                        const value =
                                            e.target.value;

                                        setSubjectCurriculumSources(
                                            (current) =>
                                                current.map(
                                                    (curriculumItem) =>
                                                        curriculumItem.id ===
                                                        curriculum.id
                                                            ? {
                                                                ...curriculumItem,
                                                                materials:
                                                                    curriculumItem.materials.map(
                                                                        (resource) =>
                                                                            resource.id ===
                                                                            material.id
                                                                                ? {
                                                                                        ...resource,
                                                                                        content_start_page:
                                                                                            value,
                                                                                    }
                                                                                : resource
                                                                    ),
                                                            }
                                                            : curriculumItem
                                                )
                                        );
                                    }}
                                    placeholder="Example: 12"
                                    className={shadowInput}
                                />
                            </div>                
                                
                            </div>
                        <div className="mt-4 rounded-2xl border border-[#ffd166]/15 bg-[#080b14]/70 p-4">
                            <div className="flex flex-wrap items-end justify-between gap-3">
                                <div className="min-w-[220px] flex-1">
                                    <Label>
                                        Resource Structure
                                    </Label>

                                    <select
                                        value={
                                            material.structure_type ||
                                            "chapter"
                                        }
                                        onChange={(e) => {
                                            const value =
                                                e.target.value;

                                            setSubjectCurriculumSources(
                                                (current) =>
                                                    current.map(
                                                        (curriculumItem) =>
                                                            curriculumItem.id ===
                                                            curriculum.id
                                                                ? {
                                                                    ...curriculumItem,
                                                                    materials:
                                                                        curriculumItem.materials.map(
                                                                            (resource) =>
                                                                                resource.id ===
                                                                                material.id
                                                                                    ? {
                                                                                            ...resource,
                                                                                            structure_type:
                                                                                                value,
                                                                                        }
                                                                                    : resource
                                                                        ),
                                                                }
                                                                : curriculumItem
                                                    )
                                            );
                                        }}
                                        className={shadowInput}
                                    >
                                        <option value="chapter">
                                            Chapters
                                        </option>

                                        <option value="unit">
                                            Units
                                        </option>

                                        <option value="module">
                                            Modules
                                        </option>

                                        <option value="part">
                                            Parts
                                        </option>

                                        <option value="week">
                                            Weeks
                                        </option>

                                        <option value="level">
                                            Levels
                                        </option>

                                        <option value="other">
                                            Custom Groups
                                        </option>
                                    </select>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => {
                                        const currentStructure =
                                            Array.isArray(
                                                material.structure
                                            )
                                                ? material.structure
                                                : [];

                                        const nextNumber =
                                            currentStructure.length + 1;

                                        const type =
                                            material.structure_type ||
                                            "chapter";

                                        const labels = {
                                            chapter: "Chapter",
                                            unit: "Unit",
                                            module: "Module",
                                            part: "Part",
                                            week: "Week",
                                            level: "Level",
                                            other: "Group",
                                        };

                                        const newGroup = {
                                            id: `structure-${Date.now()}-${Math.random()
                                                .toString(36)
                                                .slice(2, 8)}`,

                                            title: `${
                                                labels[type] ||
                                                "Group"
                                            } ${nextNumber}`,

                                            structure_type:
                                                type,

                                            order:
                                                nextNumber,

                                            items: [],
                                        };

                                        setSubjectCurriculumSources(
                                            (current) =>
                                                current.map(
                                                    (curriculumItem) =>
                                                        curriculumItem.id ===
                                                        curriculum.id
                                                            ? {
                                                                ...curriculumItem,
                                                                materials:
                                                                    curriculumItem.materials.map(
                                                                        (resource) =>
                                                                            resource.id ===
                                                                            material.id
                                                                                ? {
                                                                                        ...resource,
                                                                                        structure: [
                                                                                            ...currentStructure,
                                                                                            newGroup,
                                                                                        ],
                                                                                    }
                                                                                : resource
                                                                    ),
                                                            }
                                                            : curriculumItem
                                                )
                                        );
                                    }}
                                    className={purpleActionBtn}
                                >
                                    + Add{" "}
                                    {material.structure_type ===
                                    "unit"
                                        ? "Unit"
                                        : material.structure_type ===
                                        "module"
                                        ? "Module"
                                        : material.structure_type ===
                                        "part"
                                        ? "Part"
                                        : material.structure_type ===
                                        "week"
                                        ? "Week"
                                        : material.structure_type ===
                                        "level"
                                        ? "Level"
                                        : material.structure_type ===
                                        "other"
                                        ? "Group"
                                        : "Chapter"}
                                </button>
                            </div>

                            {Array.isArray(
                                material.structure
                            ) &&
                                material.structure.length >
                                    0 && (
                                    <div className="mt-4 space-y-2">
                                        {material.structure.map(
                                            (
                                                group,
                                                groupIndex
                                            ) => (
                                                <div
                                                    key={
                                                        group.id
                                                    }
                                                    className="
                                                        rounded-xl
                                                        border
                                                        border-[#c084fc]/20
                                                        bg-black/40
                                                        p-3
                                                    "
                                                >
                                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                                        <Input
                                                            value={
                                                                group.title ||
                                                                ""
                                                            }
                                                            onChange={(
                                                                e
                                                            ) => {
                                                                const value =
                                                                    e
                                                                        .target
                                                                        .value;

                                                                setSubjectCurriculumSources(
                                                                    (
                                                                        current
                                                                    ) =>
                                                                        current.map(
                                                                            (
                                                                                curriculumItem
                                                                            ) =>
                                                                                curriculumItem.id ===
                                                                                curriculum.id
                                                                                    ? {
                                                                                        ...curriculumItem,

                                                                                        materials:
                                                                                            curriculumItem.materials.map(
                                                                                                (
                                                                                                    resource
                                                                                                ) =>
                                                                                                    resource.id ===
                                                                                                    material.id
                                                                                                        ? {
                                                                                                                ...resource,

                                                                                                                structure:
                                                                                                                    resource.structure.map(
                                                                                                                        (
                                                                                                                            structureGroup
                                                                                                                        ) =>
                                                                                                                            structureGroup.id ===
                                                                                                                            group.id
                                                                                                                                ? {
                                                                                                                                    ...structureGroup,
                                                                                                                                    title: value,
                                                                                                                                }
                                                                                                                                : structureGroup
                                                                                                                    ),
                                                                                                            }
                                                                                                        : resource
                                                                                            ),
                                                                                    }
                                                                                    : curriculumItem
                                                                        )
                                                                );
                                                            }}
                                                            className={`${shadowInput} flex-1`}
                                                        />

                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSubjectCurriculumSources(
                                                                    (
                                                                        current
                                                                    ) =>
                                                                        current.map(
                                                                            (
                                                                                curriculumItem
                                                                            ) =>
                                                                                curriculumItem.id ===
                                                                                curriculum.id
                                                                                    ? {
                                                                                        ...curriculumItem,

                                                                                        materials:
                                                                                            curriculumItem.materials.map(
                                                                                                (
                                                                                                    resource
                                                                                                ) =>
                                                                                                    resource.id ===
                                                                                                    material.id
                                                                                                        ? {
                                                                                                                ...resource,

                                                                                                                structure:
                                                                                                                    resource.structure
                                                                                                                        .filter(
                                                                                                                            (
                                                                                                                                structureGroup
                                                                                                                            ) =>
                                                                                                                                structureGroup.id !==
                                                                                                                                group.id
                                                                                                                        )
                                                                                                                        .map(
                                                                                                                            (
                                                                                                                                structureGroup,
                                                                                                                                index
                                                                                                                            ) => ({
                                                                                                                                ...structureGroup,
                                                                                                                                order:
                                                                                                                                    index +
                                                                                                                                    1,
                                                                                                                            })
                                                                                                                        ),
                                                                                                            }
                                                                                                        : resource
                                                                                            ),
                                                                                    }
                                                                                    : curriculumItem
                                                                        )
                                                                );
                                                            }}
                                                            className="
                                                                rounded-lg
                                                                border
                                                                border-red-400/25
                                                                bg-black
                                                                px-2.5
                                                                py-1.5
                                                                text-[10px]
                                                                text-red-200
                                                            "
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>

                                                    <p className="mt-2 text-[10px] uppercase tracking-[0.14em] text-[#8f98b5]">
                                                        {group.items
                                                            ?.length ||
                                                            0}{" "}
                                                        items
                                                    </p>
                                                <div className="mt-3 flex flex-wrap items-end gap-3">
                                                    <div className="min-w-[160px]">
                                                        <Label>
                                                            Number of Items
                                                        </Label>

                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            value={
                                                                group.item_count ||
                                                                ""
                                                            }
                                                            onChange={(e) => {
                                                                const value =
                                                                    e.target.value;

                                                                setSubjectCurriculumSources(
                                                                    (current) =>
                                                                        current.map(
                                                                            (curriculumItem) =>
                                                                                curriculumItem.id ===
                                                                                curriculum.id
                                                                                    ? {
                                                                                        ...curriculumItem,
                                                                                        materials:
                                                                                            curriculumItem.materials.map(
                                                                                                (resource) =>
                                                                                                    resource.id ===
                                                                                                    material.id
                                                                                                        ? {
                                                                                                                ...resource,
                                                                                                                structure:
                                                                                                                    resource.structure.map(
                                                                                                                        (
                                                                                                                            structureGroup
                                                                                                                        ) =>
                                                                                                                            structureGroup.id ===
                                                                                                                            group.id
                                                                                                                                ? {
                                                                                                                                    ...structureGroup,
                                                                                                                                    item_count:
                                                                                                                                        value,
                                                                                                                                }
                                                                                                                                : structureGroup
                                                                                                                    ),
                                                                                                            }
                                                                                                        : resource
                                                                                            ),
                                                                                    }
                                                                                    : curriculumItem
                                                                        )
                                                                );
                                                            }}
                                                            className={shadowInput}
                                                        />
                                                    </div>

                                                    <button
                                                        type="button"
                                                       onClick={() => {
                                                            const targetCount = Math.max(
                                                                1,
                                                                Number(group.item_count) || 1
                                                            );

                                                            const existingItems = Array.isArray(group.items)
                                                                ? group.items
                                                                : [];

                                                            if (targetCount === existingItems.length) {
                                                                return;
                                                            }

                                                            if (targetCount > existingItems.length) {
                                                                const amountToAdd =
                                                                    targetCount - existingItems.length;

                                                                const newItems = Array.from(
                                                                    { length: amountToAdd },
                                                                    (_, index) => {
                                                                        const nextOrder =
                                                                            existingItems.length +
                                                                            index +
                                                                            1;

                                                                        return {
                                                                            id: `curriculum-item-${Date.now()}-${nextOrder}-${Math.random()
                                                                                .toString(36)
                                                                                .slice(2, 7)}`,

                                                                            title: `Lesson ${nextOrder}`,
                                                                            item_type: "lesson",
                                                                            order: nextOrder,
                                                                            page_start: "",
                                                                            page_end: "",
                                                                            linked_resource_work: [],
                                                                        };
                                                                    }
                                                                );

                                                                setSubjectCurriculumSources(
                                                                    (current) =>
                                                                        current.map((curriculumItem) =>
                                                                            curriculumItem.id === curriculum.id
                                                                                ? {
                                                                                    ...curriculumItem,
                                                                                    materials:
                                                                                        curriculumItem.materials.map(
                                                                                            (resource) =>
                                                                                                resource.id === material.id
                                                                                                    ? {
                                                                                                            ...resource,
                                                                                                            structure:
                                                                                                                resource.structure.map(
                                                                                                                    (structureGroup) =>
                                                                                                                        structureGroup.id ===
                                                                                                                        group.id
                                                                                                                            ? {
                                                                                                                                ...structureGroup,
                                                                                                                                items: [
                                                                                                                                    ...existingItems,
                                                                                                                                    ...newItems,
                                                                                                                                ],
                                                                                                                            }
                                                                                                                            : structureGroup
                                                                                                                ),
                                                                                                        }
                                                                                                    : resource
                                                                                        ),
                                                                                }
                                                                                : curriculumItem
                                                                        )
                                                                );

                                                                return;
                                                            }

                                                            const amountToRemove =
                                                                existingItems.length - targetCount;

                                                            const shouldRemove = window.confirm(
                                                                `This will remove the last ${amountToRemove} curriculum item${
                                                                    amountToRemove === 1 ? "" : "s"
                                                                } from ${group.title || "this group"}. Continue?`
                                                            );

                                                            if (!shouldRemove) {
                                                                return;
                                                            }

                                                            const trimmedItems = existingItems
                                                                .slice(0, targetCount)
                                                                .map((item, index) => ({
                                                                    ...item,
                                                                    order: index + 1,
                                                                }));

                                                            setSubjectCurriculumSources(
                                                                (current) =>
                                                                    current.map((curriculumItem) =>
                                                                        curriculumItem.id === curriculum.id
                                                                            ? {
                                                                                ...curriculumItem,
                                                                                materials:
                                                                                    curriculumItem.materials.map(
                                                                                        (resource) =>
                                                                                            resource.id === material.id
                                                                                                ? {
                                                                                                        ...resource,
                                                                                                        structure:
                                                                                                            resource.structure.map(
                                                                                                                (structureGroup) =>
                                                                                                                    structureGroup.id ===
                                                                                                                    group.id
                                                                                                                        ? {
                                                                                                                            ...structureGroup,
                                                                                                                            items: trimmedItems,
                                                                                                                        }
                                                                                                                        : structureGroup
                                                                                                            ),
                                                                                                    }
                                                                                                : resource
                                                                                    ),
                                                                            }
                                                                            : curriculumItem
                                                                    )
                                                            );
                                                        }}
                                                        className={purpleActionBtn}
                                                    >
                                                        Generate Items
                                                    </button>
                                                </div>
                                            {Array.isArray(group.items) &&
                                                group.items.length > 0 && (
                                                    <div className="mt-4 space-y-2">
                                                        {group.items.map(
                                                            (
                                                                item,
                                                                itemIndex
                                                            ) => (
                                                                <div
                                                                    key={
                                                                        item.id
                                                                    }
                                                                    className="
                                                                        rounded-xl
                                                                        border
                                                                        border-[#ffd166]/15
                                                                        bg-black/35
                                                                        p-3
                                                                    "
                                                                >
                                                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[170px_1fr_120px_120px_auto]">
                                                                        <div>
                                                                            <Label>
                                                                                Type
                                                                            </Label>

                                                                            <select
                                                                                value={
                                                                                    item.item_type ||
                                                                                    "lesson"
                                                                                }
                                                                                onChange={(e) => {
                                                                                    const value =
                                                                                        e.target.value;

                                                                                    setSubjectCurriculumSources(
                                                                                        (
                                                                                            current
                                                                                        ) =>
                                                                                            current.map(
                                                                                                (
                                                                                                    curriculumItem
                                                                                                ) =>
                                                                                                    curriculumItem.id ===
                                                                                                    curriculum.id
                                                                                                        ? {
                                                                                                            ...curriculumItem,
                                                                                                            materials:
                                                                                                                curriculumItem.materials.map(
                                                                                                                    (
                                                                                                                        resource
                                                                                                                    ) =>
                                                                                                                        resource.id ===
                                                                                                                        material.id
                                                                                                                            ? {
                                                                                                                                    ...resource,
                                                                                                                                    structure:
                                                                                                                                        resource.structure.map(
                                                                                                                                            (
                                                                                                                                                structureGroup
                                                                                                                                            ) =>
                                                                                                                                                structureGroup.id ===
                                                                                                                                                group.id
                                                                                                                                                    ? {
                                                                                                                                                        ...structureGroup,
                                                                                                                                                        items:
                                                                                                                                                            structureGroup.items.map(
                                                                                                                                                                (
                                                                                                                                                                    structureItem
                                                                                                                                                                ) =>
                                                                                                                                                                    structureItem.id ===
                                                                                                                                                                    item.id
                                                                                                                                                                        ? {
                                                                                                                                                                                ...structureItem,
                                                                                                                                                                                item_type:
                                                                                                                                                                                    value,
                                                                                                                                                                            }
                                                                                                                                                                        : structureItem
                                                                                                                                                            ),
                                                                                                                                                    }
                                                                                                                                                    : structureGroup
                                                                                                                                        ),
                                                                                                                                }
                                                                                                                            : resource
                                                                                                                ),
                                                                                                        }
                                                                                                        : curriculumItem
                                                                                            )
                                                                                    );
                                                                                }}
                                                                                className={shadowInput}
                                                                            >
                                                                                <option value="lesson">
                                                                                    Lesson
                                                                                </option>

                                                                                <option value="section">
                                                                                    Section
                                                                                </option>

                                                                                <option value="reading">
                                                                                    Reading
                                                                                </option>

                                                                                <option value="practice">
                                                                                    Practice
                                                                                </option>

                                                                                <option value="review">
                                                                                    Review
                                                                                </option>

                                                                                <option value="exercise">
                                                                                    Exercise
                                                                                </option>

                                                                                <option value="activity">
                                                                                    Activity
                                                                                </option>

                                                                                <option value="lab">
                                                                                    Lab
                                                                                </option>

                                                                                <option value="quiz">
                                                                                    Quiz
                                                                                </option>

                                                                                <option value="test">
                                                                                    Test
                                                                                </option>

                                                                                <option value="project">
                                                                                    Project
                                                                                </option>

                                                                                <option value="other">
                                                                                    Other
                                                                                </option>
                                                                            </select>
                                                                        </div>

                                                                        <div>
                                                                            <Label>
                                                                                Name
                                                                            </Label>

                                                                            <Input
                                                                                value={
                                                                                    item.title ||
                                                                                    ""
                                                                                }
                                                                                onChange={(e) => {
                                                                                    const value =
                                                                                        e.target.value;

                                                                                    setSubjectCurriculumSources(
                                                                                        (
                                                                                            current
                                                                                        ) =>
                                                                                            current.map(
                                                                                                (
                                                                                                    curriculumItem
                                                                                                ) =>
                                                                                                    curriculumItem.id ===
                                                                                                    curriculum.id
                                                                                                        ? {
                                                                                                            ...curriculumItem,
                                                                                                            materials:
                                                                                                                curriculumItem.materials.map(
                                                                                                                    (
                                                                                                                        resource
                                                                                                                    ) =>
                                                                                                                        resource.id ===
                                                                                                                        material.id
                                                                                                                            ? {
                                                                                                                                    ...resource,
                                                                                                                                    structure:
                                                                                                                                        resource.structure.map(
                                                                                                                                            (
                                                                                                                                                structureGroup
                                                                                                                                            ) =>
                                                                                                                                                structureGroup.id ===
                                                                                                                                                group.id
                                                                                                                                                    ? {
                                                                                                                                                        ...structureGroup,
                                                                                                                                                        items:
                                                                                                                                                            structureGroup.items.map(
                                                                                                                                                                (
                                                                                                                                                                    structureItem
                                                                                                                                                                ) =>
                                                                                                                                                                    structureItem.id ===
                                                                                                                                                                    item.id
                                                                                                                                                                        ? {
                                                                                                                                                                                ...structureItem,
                                                                                                                                                                                title:
                                                                                                                                                                                    value,
                                                                                                                                                                            }
                                                                                                                                                                        : structureItem
                                                                                                                                                            ),
                                                                                                                                                    }
                                                                                                                                                    : structureGroup
                                                                                                                                        ),
                                                                                                                                }
                                                                                                                            : resource
                                                                                                                ),
                                                                                                        }
                                                                                                        : curriculumItem
                                                                                            )
                                                                                    );
                                                                                }}
                                                                                className={
                                                                                    shadowInput
                                                                                }
                                                                            />
                                                                        </div>

                                                                        <div>
                                                                            <Label>
                                                                                Start Page
                                                                            </Label>

                                                                            <Input
                                                                                type="number"
                                                                                min="1"
                                                                                value={
                                                                                    item.page_start ||
                                                                                    ""
                                                                                }
                                                                                onChange={(e) => {
                                                                                    const value =
                                                                                        e.target.value;

                                                                                    setSubjectCurriculumSources(
                                                                                        (
                                                                                            current
                                                                                        ) =>
                                                                                            current.map(
                                                                                                (
                                                                                                    curriculumItem
                                                                                                ) =>
                                                                                                    curriculumItem.id ===
                                                                                                    curriculum.id
                                                                                                        ? {
                                                                                                            ...curriculumItem,
                                                                                                            materials:
                                                                                                                curriculumItem.materials.map(
                                                                                                                    (
                                                                                                                        resource
                                                                                                                    ) =>
                                                                                                                        resource.id ===
                                                                                                                        material.id
                                                                                                                            ? {
                                                                                                                                    ...resource,
                                                                                                                                    structure:
                                                                                                                                        resource.structure.map(
                                                                                                                                            (
                                                                                                                                                structureGroup
                                                                                                                                            ) =>
                                                                                                                                                structureGroup.id ===
                                                                                                                                                group.id
                                                                                                                                                    ? {
                                                                                                                                                        ...structureGroup,
                                                                                                                                                        items:
                                                                                                                                                            structureGroup.items.map(
                                                                                                                                                                (
                                                                                                                                                                    structureItem
                                                                                                                                                                ) =>
                                                                                                                                                                    structureItem.id ===
                                                                                                                                                                    item.id
                                                                                                                                                                        ? {
                                                                                                                                                                                ...structureItem,
                                                                                                                                                                                page_start:
                                                                                                                                                                                    value,
                                                                                                                                                                            }
                                                                                                                                                                        : structureItem
                                                                                                                                                            ),
                                                                                                                                                    }
                                                                                                                                                    : structureGroup
                                                                                                                                        ),
                                                                                                                                }
                                                                                                                            : resource
                                                                                                                ),
                                                                                                        }
                                                                                                        : curriculumItem
                                                                                            )
                                                                                    );
                                                                                }}
                                                                                className={
                                                                                    shadowInput
                                                                                }
                                                                            />
                                                                        </div>

                                                                        <div>
                                                                            <Label>
                                                                                End Page
                                                                            </Label>

                                                                            <Input
                                                                                type="number"
                                                                                min="1"
                                                                                value={
                                                                                    item.page_end ||
                                                                                    ""
                                                                                }
                                                                                onChange={(e) => {
                                                                                    const value =
                                                                                        e.target.value;

                                                                                    setSubjectCurriculumSources(
                                                                                        (
                                                                                            current
                                                                                        ) =>
                                                                                            current.map(
                                                                                                (
                                                                                                    curriculumItem
                                                                                                ) =>
                                                                                                    curriculumItem.id ===
                                                                                                    curriculum.id
                                                                                                        ? {
                                                                                                            ...curriculumItem,
                                                                                                            materials:
                                                                                                                curriculumItem.materials.map(
                                                                                                                    (
                                                                                                                        resource
                                                                                                                    ) =>
                                                                                                                        resource.id ===
                                                                                                                        material.id
                                                                                                                            ? {
                                                                                                                                    ...resource,
                                                                                                                                    structure:
                                                                                                                                        resource.structure.map(
                                                                                                                                            (
                                                                                                                                                structureGroup
                                                                                                                                            ) =>
                                                                                                                                                structureGroup.id ===
                                                                                                                                                group.id
                                                                                                                                                    ? {
                                                                                                                                                        ...structureGroup,
                                                                                                                                                        items:
                                                                                                                                                            structureGroup.items.map(
                                                                                                                                                                (
                                                                                                                                                                    structureItem
                                                                                                                                                                ) =>
                                                                                                                                                                    structureItem.id ===
                                                                                                                                                                    item.id
                                                                                                                                                                        ? {
                                                                                                                                                                                ...structureItem,
                                                                                                                                                                                page_end:
                                                                                                                                                                                    value,
                                                                                                                                                                            }
                                                                                                                                                                        : structureItem
                                                                                                                                                            ),
                                                                                                                                                    }
                                                                                                                                                    : structureGroup
                                                                                                                                        ),
                                                                                                                                }
                                                                                                                            : resource
                                                                                                                ),
                                                                                                        }
                                                                                                        : curriculumItem
                                                                                            )
                                                                                    );
                                                                                }}
                                                                                className={
                                                                                    shadowInput
                                                                                }
                                                                            />
                                                                        </div>

                                                                        <div className="flex items-end">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setSubjectCurriculumSources(
                                                                                        (
                                                                                            current
                                                                                        ) =>
                                                                                            current.map(
                                                                                                (
                                                                                                    curriculumItem
                                                                                                ) =>
                                                                                                    curriculumItem.id ===
                                                                                                    curriculum.id
                                                                                                        ? {
                                                                                                            ...curriculumItem,
                                                                                                            materials:
                                                                                                                curriculumItem.materials.map(
                                                                                                                    (
                                                                                                                        resource
                                                                                                                    ) =>
                                                                                                                        resource.id ===
                                                                                                                        material.id
                                                                                                                            ? {
                                                                                                                                    ...resource,
                                                                                                                                    structure:
                                                                                                                                        resource.structure.map(
                                                                                                                                            (
                                                                                                                                                structureGroup
                                                                                                                                            ) =>
                                                                                                                                                structureGroup.id ===
                                                                                                                                                group.id
                                                                                                                                                    ? {
                                                                                                                                                        ...structureGroup,
                                                                                                                                                        items:
                                                                                                                                                            structureGroup.items
                                                                                                                                                                .filter(
                                                                                                                                                                    (
                                                                                                                                                                        structureItem
                                                                                                                                                                    ) =>
                                                                                                                                                                        structureItem.id !==
                                                                                                                                                                        item.id
                                                                                                                                                                )
                                                                                                                                                                .map(
                                                                                                                                                                    (
                                                                                                                                                                        structureItem,
                                                                                                                                                                        index
                                                                                                                                                                    ) => ({
                                                                                                                                                                        ...structureItem,
                                                                                                                                                                        order:
                                                                                                                                                                            index +
                                                                                                                                                                            1,
                                                                                                                                                                    })
                                                                                                                                                                ),
                                                                                                                                                    }
                                                                                                                                                    : structureGroup
                                                                                                                                        ),
                                                                                                                                }
                                                                                                                            : resource
                                                                                                                ),
                                                                                                        }
                                                                                                        : curriculumItem
                                                                                            )
                                                                                    );
                                                                                }}
                                                                                className="
                                                                                    rounded-lg
                                                                                    border
                                                                                    border-red-400/25
                                                                                    bg-black
                                                                                    px-2.5
                                                                                    py-2
                                                                                    text-[10px]
                                                                                    text-red-200
                                                                                "
                                                                            >
                                                                                Remove
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    <p className="mt-2 text-[10px] text-[#8f98b5]">
                                                                        Item {itemIndex + 1}
                                                                    </p>
                                                                                {(
                                                                        <div className="mt-3 rounded-xl border border-[#c084fc]/20 bg-[#080b14]/70 p-3">
                                                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                                                <div>
                                                                                    <p className="text-xs font-semibold text-[#ffd166]">
                                                                                        Curriculum Assignments
                                                                                    </p>

                                                                                    <p className="mt-1 text-[10px] text-[#8f98b5]">
                                                                                        Attach textbook work, workbook pages, readings, videos, websites, or other resources to this curriculum item.
                                                                                    </p>
                                                                                </div>

                                                                                <button
                                                                                    type="button"
                                                                                disabled={
                                                                                    !Array.isArray(
                                                                                        material.related_resource_ids
                                                                                    ) ||
                                                                                    material.related_resource_ids.length === 0
                                                                                }
                                                                                    onClick={() => {
                                                                                        const firstLinkedResource =
                                                                                            (
                                                                                                Array.isArray(
                                                                                                    material.related_resource_ids
                                                                                                )
                                                                                                    ? material.related_resource_ids
                                                                                                    : []
                                                                                            )
                                                                                                .map((resourceId) =>
                                                                                                    curriculum.materials.find(
                                                                                                        (resource) =>
                                                                                                            resource.id === resourceId
                                                                                                    )
                                                                                                )
                                                                                                .filter(Boolean)[0];

                                                                                        if (!firstLinkedResource) {
                                                                                            return;
                                                                                        }
                                                                                        const newLinkedWork = {
                                                                                            id: `linked-work-${Date.now()}-${Math.random()
                                                                                                .toString(36)
                                                                                                .slice(2, 7)}`,

                                                                                            resource_id:
                                                                                                firstLinkedResource.id,

                                                                                            assignment_mode:
                                                                                                "structure",

                                                                                            action:
                                                                                                "read",

                                                                                            target_type:
                                                                                                "lesson",

                                                                                            instruction:
                                                                                                "",

                                                                                            page_start:
                                                                                                "",

                                                                                            page_end:
                                                                                                "",

                                                                                            chapter:
                                                                                                "",

                                                                                            lesson:
                                                                                                "",

                                                                                            section:
                                                                                                "",

                                                                                            unit:
                                                                                                "",

                                                                                            module:
                                                                                                "",
                                                                                        };

                                                                                        setSubjectCurriculumSources(
                                                                                            (current) =>
                                                                                                current.map(
                                                                                                    (
                                                                                                        curriculumItem
                                                                                                    ) =>
                                                                                                        curriculumItem.id ===
                                                                                                        curriculum.id
                                                                                                            ? {
                                                                                                                ...curriculumItem,

                                                                                                                materials:
                                                                                                                    curriculumItem.materials.map(
                                                                                                                        (
                                                                                                                            resource
                                                                                                                        ) =>
                                                                                                                            resource.id ===
                                                                                                                            material.id
                                                                                                                                ? {
                                                                                                                                        ...resource,

                                                                                                                                        structure:
                                                                                                                                            resource.structure.map(
                                                                                                                                                (
                                                                                                                                                    structureGroup
                                                                                                                                                ) =>
                                                                                                                                                    structureGroup.id ===
                                                                                                                                                    group.id
                                                                                                                                                        ? {
                                                                                                                                                            ...structureGroup,

                                                                                                                                                            items:
                                                                                                                                                                structureGroup.items.map(
                                                                                                                                                                    (
                                                                                                                                                                        structureItem
                                                                                                                                                                    ) =>
                                                                                                                                                                        structureItem.id ===
                                                                                                                                                                        item.id
                                                                                                                                                                            ? {
                                                                                                                                                                                    ...structureItem,

                                                                                                                                                                                    linked_resource_work: [
                                                                                                                                                                                        ...(
                                                                                                                                                                                            Array.isArray(
                                                                                                                                                                                                structureItem.linked_resource_work
                                                                                                                                                                                            )
                                                                                                                                                                                                ? structureItem.linked_resource_work
                                                                                                                                                                                                : []
                                                                                                                                                                                        ),

                                                                                                                                                                                        newLinkedWork,
                                                                                                                                                                                    ],
                                                                                                                                                                                }
                                                                                                                                                                            : structureItem
                                                                                                                                                                ),
                                                                                                                                                        }
                                                                                                                                                        : structureGroup
                                                                                                                                            ),
                                                                                                                                    }
                                                                                                                                : resource
                                                                                                                    ),
                                                                                                            }
                                                                                                            : curriculumItem
                                                                                                )
                                                                                        );
                                                                                    }}
                                                                                    className={`${purpleActionBtn} disabled:cursor-not-allowed disabled:opacity-40`}
                                                                                >
                                                                                    + Add Curriculum Assignment
                                                                                </button>
                                                                            </div>
                                                                        {(
                                                                            !Array.isArray(material.linked_resources) ||
                                                                            material.linked_resources.length === 0
                                                                        ) && (
                                                                            <div
                                                                                className="
                                                                                    mt-3
                                                                                    rounded-xl
                                                                                    border
                                                                                    border-[#ffd166]/15
                                                                                    bg-black/30
                                                                                    px-3
                                                                                    py-3
                                                                                "
                                                                            >
                                                                                <p className="text-xs text-[#b0b8d6]">
                                                                                    No linked resources are configured for this resource yet.
                                                                                </p>

                                                                                <p className="mt-1 text-[10px] leading-5 text-[#8f98b5]">
                                                                                    Use “Manage Linked Resources” above to add
                                                                                    workbooks, teacher guides, online courses,
                                                                                    videos, PDFs, or other resources that belong
                                                                                    with this main resource.
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                            {Array.isArray(
                                                                                item.linked_resource_work
                                                                            ) &&
                                                                                item.linked_resource_work.length >
                                                                                    0 && (
                                                                                    <div className="mt-3 space-y-3">
                                                                                        {item.linked_resource_work.map(
                                                                                            (linkedWork) => {
                                                                                                const siblingOptions =
                                                                                                    (
                                                                                                        Array.isArray(
                                                                                                            material.related_resource_ids
                                                                                                        )
                                                                                                            ? material.related_resource_ids
                                                                                                            : []
                                                                                                    )
                                                                                                        .map((resourceId) =>
                                                                                                            curriculum.materials.find(
                                                                                                                (resource) =>
                                                                                                                    resource.id === resourceId
                                                                                                            )
                                                                                                        )
                                                                                                        .filter(Boolean);
                                                                                                const selectedSiblingResource =
                                                                                                    siblingOptions.find(
                                                                                                        (resource) =>
                                                                                                            resource.id ===
                                                                                                            linkedWork.resource_id
                                                                                                    ) || null;

                                                                                                const selectedSiblingStructure =
                                                                                                    Array.isArray(
                                                                                                        selectedSiblingResource?.structure
                                                                                                    )
                                                                                                        ? selectedSiblingResource.structure
                                                                                                        : [];
                                                                                                return (
                                                                                                    <div
                                                                                                        key={
                                                                                                            linkedWork.id
                                                                                                        }
                                                                                                        className="
                                                                                                            rounded-xl
                                                                                                            border
                                                                                                            border-[#ffd166]/15
                                                                                                            bg-black/35
                                                                                                            p-3
                                                                                                        "
                                                                                                    >
                                                                                                    <div className="mb-3 flex items-center justify-between gap-3">
                                                                                                        <p className="text-[10px] uppercase tracking-[0.14em] text-[#d8b4fe]">
                                                                                                            Linked Resource
                                                                                                        </p>

                                                                                                        <button
                                                                                                            type="button"
                                                                                                            onClick={() => {
                                                                                                                setSubjectCurriculumSources(
                                                                                                                    (current) =>
                                                                                                                        current.map(
                                                                                                                            (
                                                                                                                                curriculumItem
                                                                                                                            ) =>
                                                                                                                                curriculumItem.id ===
                                                                                                                                curriculum.id
                                                                                                                                    ? {
                                                                                                                                        ...curriculumItem,

                                                                                                                                        materials:
                                                                                                                                            curriculumItem.materials.map(
                                                                                                                                                (
                                                                                                                                                    resource
                                                                                                                                                ) =>
                                                                                                                                                    resource.id ===
                                                                                                                                                    material.id
                                                                                                                                                        ? {
                                                                                                                                                                ...resource,

                                                                                                                                                                structure:
                                                                                                                                                                    resource.structure.map(
                                                                                                                                                                        (
                                                                                                                                                                            structureGroup
                                                                                                                                                                        ) =>
                                                                                                                                                                            structureGroup.id ===
                                                                                                                                                                            group.id
                                                                                                                                                                                ? {
                                                                                                                                                                                    ...structureGroup,

                                                                                                                                                                                    items:
                                                                                                                                                                                        structureGroup.items.map(
                                                                                                                                                                                            (
                                                                                                                                                                                                structureItem
                                                                                                                                                                                            ) =>
                                                                                                                                                                                                structureItem.id ===
                                                                                                                                                                                                item.id
                                                                                                                                                                                                    ? {
                                                                                                                                                                                                            ...structureItem,

                                                                                                                                                                                                            linked_resource_work:
                                                                                                                                                                                                                (
                                                                                                                                                                                                                    structureItem.linked_resource_work ||
                                                                                                                                                                                                                    []
                                                                                                                                                                                                                ).filter(
                                                                                                                                                                                                                    (
                                                                                                                                                                                                                        entry
                                                                                                                                                                                                                    ) =>
                                                                                                                                                                                                                        entry.id !==
                                                                                                                                                                                                                        linkedWork.id
                                                                                                                                                                                                                ),
                                                                                                                                                                                                        }
                                                                                                                                                                                                    : structureItem
                                                                                                                                                                                        ),
                                                                                                                                                                                }
                                                                                                                                                                                : structureGroup
                                                                                                                                                                    ),
                                                                                                                                                            }
                                                                                                                                                        : resource
                                                                                                                                            ),
                                                                                                                                    }
                                                                                                                                    : curriculumItem
                                                                                                                        )
                                                                                                                );
                                                                                                            }}
                                                                                                            className="
                                                                                                                rounded-lg
                                                                                                                border
                                                                                                                border-red-400/25
                                                                                                                bg-black
                                                                                                                px-2.5
                                                                                                                py-1.5
                                                                                                                text-[10px]
                                                                                                                text-red-200
                                                                                                                transition
                                                                                                                hover:border-red-400/60
                                                                                                            "
                                                                                                        >
                                                                                                            Remove
                                                                                                        </button>
                                                                                                    </div>    
                                                                                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                                                                                            <div>
                                                                                                                <Label>
                                                                                                                    Resource
                                                                                                                </Label>

                                                                                                                <select
                                                                                                                    value={
                                                                                                                        linkedWork.resource_id ||
                                                                                                                        ""
                                                                                                                    }
                                                                                                                    onChange={(e) =>
                                                                                                                        updateLinkedWork(
                                                                                                                            curriculum.id,
                                                                                                                            material.id,
                                                                                                                            group.id,
                                                                                                                            item.id,
                                                                                                                            linkedWork.id,
                                                                                                                        {
                                                                                                                            resource_id: e.target.value,

                                                                                                                            assignment_mode:
                                                                                                                                "structure",

                                                                                                                            lesson_mode:
                                                                                                                                "structured",

                                                                                                                            target_type:
                                                                                                                                "lesson",

                                                                                                                            chapter: "",
                                                                                                                            lesson: "",

                                                                                                                            page_start: "",
                                                                                                                            page_end: "",

                                                                                                                            instruction: "",
                                                                                                                        }
                                                                                                                        )
                                                                                                                    }
                                                                                                                    className={
                                                                                                                        shadowInput
                                                                                                                    }
                                                                                                                >
                                                                                                                    {siblingOptions.map(
                                                                                                                        (
                                                                                                                            resource
                                                                                                                        ) => (
                                                                                                                            <option
                                                                                                                                key={
                                                                                                                                    resource.id
                                                                                                                                }
                                                                                                                                value={
                                                                                                                                    resource.id
                                                                                                                                }
                                                                                                                            >
                                                                                                                                {resource.name ||
                                                                                                                                    "Unnamed Resource"}
                                                                                                                            </option>
                                                                                                                        )
                                                                                                                    )}
                                                                                                                </select>
                                                                                                            </div>
                                                                                                        {selectedSiblingStructure.length > 0 && (
                                                                                                            <div className="md:col-span-2 rounded-2xl border border-[#ffd166]/20 bg-[#080b14]/70 p-4">
                                                                                                                <div className="flex flex-wrap items-start justify-between gap-3">
                                                                                                                    <div>
                                                                                                                        <p className="text-sm font-semibold text-[#ffd166]">
                                                                                                                            Use Resource Structure
                                                                                                                        </p>

                                                                                                                        <p className="mt-1 text-xs text-[#8f98b5]">
                                                                                                                            Choose work already entered in this resource.
                                                                                                                            The saved group, item, pages, and instruction
                                                                                                                            will be reused automatically.
                                                                                                                        </p>
                                                                                                                    </div>
                                                                                                                </div>

                                                                                                            <div className="mt-4 space-y-3">
                                                                                                                <div>
                                                                                                                    <Label>
                                                                                                                        Week / Group
                                                                                                                    </Label>

                                                                                                                    <select
                                                                                                                        value={
                                                                                                                            linkedWork.structure_group_id ||
                                                                                                                            linkedWork.chapter ||
                                                                                                                            ""
                                                                                                                        }
                                                                                                                        onChange={(e) => {
                                                                                                                            const selectedGroupId =
                                                                                                                                e.target.value;

                                                                                                                            updateLinkedWork(
                                                                                                                                curriculum.id,
                                                                                                                                material.id,
                                                                                                                                group.id,
                                                                                                                                item.id,
                                                                                                                                linkedWork.id,
                                                                                                                                {
                                                                                                                                    structure_group_id:
                                                                                                                                        selectedGroupId,

                                                                                                                                    chapter:
                                                                                                                                        selectedGroupId,

                                                                                                                                    lesson:
                                                                                                                                        "",

                                                                                                                                    page_start:
                                                                                                                                        "",

                                                                                                                                    page_end:
                                                                                                                                        "",

                                                                                                                                    instruction:
                                                                                                                                        "",

                                                                                                                                    lesson_mode:
                                                                                                                                        "structured",

                                                                                                                                    assignment_mode:
                                                                                                                                        "structure",
                                                                                                                                }
                                                                                                                            );
                                                                                                                        }}
                                                                                                                        className={shadowInput}
                                                                                                                    >
                                                                                                                        <option value="">
                                                                                                                            Choose week / group
                                                                                                                        </option>

                                                                                                                        {selectedSiblingStructure.map(
                                                                                                                            (resourceGroup) => (
                                                                                                                                <option
                                                                                                                                    key={resourceGroup.id}
                                                                                                                                    value={resourceGroup.id}
                                                                                                                                >
                                                                                                                                    {resourceGroup.title ||
                                                                                                                                        "Untitled Group"}
                                                                                                                                </option>
                                                                                                                            )
                                                                                                                        )}
                                                                                                                    </select>
                                                                                                                </div>

                                                                                                                {(linkedWork.structure_group_id ||
                                                                                                                    linkedWork.chapter) && (() => {
                                                                                                                    const selectedResourceGroup =
                                                                                                                        selectedSiblingStructure.find(
                                                                                                                            (resourceGroup) =>
                                                                                                                                resourceGroup.id ===
                                                                                                                                (
                                                                                                                                    linkedWork.structure_group_id ||
                                                                                                                                    linkedWork.chapter
                                                                                                                                )
                                                                                                                        );

                                                                                                                    const resourceItems =
                                                                                                                        Array.isArray(
                                                                                                                            selectedResourceGroup?.items
                                                                                                                        )
                                                                                                                            ? selectedResourceGroup.items
                                                                                                                            : [];

                                                                                                                    return (
                                                                                                                        <div>
                                                                                                                            <p className="text-xs font-semibold text-[#d8b4fe]">
                                                                                                                                {selectedResourceGroup?.title ||
                                                                                                                                    "Items"}
                                                                                                                            </p>

                                                                                                                            {resourceItems.length > 0 ? (
                                                                                                                                <div className="mt-3 flex flex-wrap gap-2">
                                                                                                                                    {resourceItems.map(
                                                                                                                                        (resourceItem) => {
                                                                                                                                            const isSelected =
                                                                                                                                                linkedWork.lesson ===
                                                                                                                                                resourceItem.id;

                                                                                                                                            return (
                                                                                                                                                <button
                                                                                                                                                    key={
                                                                                                                                                        resourceItem.id
                                                                                                                                                    }
                                                                                                                                                    type="button"
                                                                                                                                                    onClick={() => {
                                                                                                                                                        updateLinkedWork(
                                                                                                                                                            curriculum.id,
                                                                                                                                                            material.id,
                                                                                                                                                            group.id,
                                                                                                                                                            item.id,
                                                                                                                                                            linkedWork.id,
                                                                                                                                                            {
                                                                                                                                                                assignment_mode:
                                                                                                                                                                    "structure",

                                                                                                                                                                lesson_mode:
                                                                                                                                                                    "structured",

                                                                                                                                                                structure_group_id:
                                                                                                                                                                    selectedResourceGroup.id,

                                                                                                                                                                target_type:
                                                                                                                                                                    "lesson",

                                                                                                                                                                chapter:
                                                                                                                                                                    selectedResourceGroup.id,

                                                                                                                                                                lesson:
                                                                                                                                                                    resourceItem.id,

                                                                                                                                                                page_start:
                                                                                                                                                                    resourceItem.page_start ||
                                                                                                                                                                    "",

                                                                                                                                                                page_end:
                                                                                                                                                                    resourceItem.page_end ||
                                                                                                                                                                    "",

                                                                                                                                                                instruction:
                                                                                                                                                                    resourceItem.instruction ||
                                                                                                                                                                    resourceItem.description ||
                                                                                                                                                                    "",
                                                                                                                                                            }
                                                                                                                                                        );
                                                                                                                                                    }}
                                                                                                                                                    className={`rounded-xl border px-3 py-2 text-left text-xs transition ${
                                                                                                                                                        isSelected
                                                                                                                                                            ? "border-[#ffd166] bg-[#ffd166]/15 text-[#fff3c4]"
                                                                                                                                                            : "border-[#c084fc]/20 bg-black text-[#b0b8d6] hover:border-[#ffd166]/50"
                                                                                                                                                    }`}
                                                                                                                                                >
                                                                                                                                                    <span className="font-semibold">
                                                                                                                                                        {resourceItem.title ||
                                                                                                                                                            `Item ${
                                                                                                                                                                resourceItem.order ||
                                                                                                                                                                ""
                                                                                                                                                            }`}
                                                                                                                                                    </span>

                                                                                                                                                    {(resourceItem.page_start ||
                                                                                                                                                        resourceItem.page_end) && (
                                                                                                                                                        <span className="ml-2 text-[#ffd166]">
                                                                                                                                                            pp.{" "}
                                                                                                                                                            {resourceItem.page_start ||
                                                                                                                                                                "?"}
                                                                                                                                                            {resourceItem.page_end
                                                                                                                                                                ? `–${resourceItem.page_end}`
                                                                                                                                                                : ""}
                                                                                                                                                        </span>
                                                                                                                                                    )}
                                                                                                                                                </button>
                                                                                                                                            );
                                                                                                                                        }
                                                                                                                                    )}
                                                                                                                                </div>
                                                                                                                            ) : (
                                                                                                                                <p className="mt-2 text-xs text-[#8f98b5]">
                                                                                                                                    No items have been added to this group yet.
                                                                                                                                </p>
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    );
                                                                                                                })()}
                                                                                                            </div>   
                                                                                                            </div>
                                                                                                        )}
                                                                                                        <div>
                                                                                                            <Label>
                                                                                                                Action
                                                                                                            </Label>

                                                                                                            <select
                                                                                                                value={
                                                                                                                    linkedWork.action ||
                                                                                                                    "read"
                                                                                                                }
                                                                                                                onChange={(e) =>
                                                                                                                    updateLinkedWork(
                                                                                                                        curriculum.id,
                                                                                                                        material.id,
                                                                                                                        group.id,
                                                                                                                        item.id,
                                                                                                                        linkedWork.id,
                                                                                                                        {
                                                                                                                            action: e.target.value,
                                                                                                                        }
                                                                                                                    )
                                                                                                                }
                                                                                                                className={shadowInput}
                                                                                                            >
                                                                                                                <option value="read">
                                                                                                                    Read
                                                                                                                </option>

                                                                                                                <option value="complete">
                                                                                                                    Complete
                                                                                                                </option>

                                                                                                                <option value="watch">
                                                                                                                    Watch
                                                                                                                </option>

                                                                                                                <option value="listen">
                                                                                                                    Listen
                                                                                                                </option>

                                                                                                                <option value="study">
                                                                                                                    Study
                                                                                                                </option>

                                                                                                                <option value="practice">
                                                                                                                    Practice
                                                                                                                </option>

                                                                                                                <option value="review">
                                                                                                                    Review
                                                                                                                </option>

                                                                                                                <option value="write">
                                                                                                                    Write
                                                                                                                </option>

                                                                                                                <option value="discuss">
                                                                                                                    Discuss
                                                                                                                </option>

                                                                                                                <option value="other">
                                                                                                                    Other
                                                                                                                </option>
                                                                                                            </select>
                                                                                                        </div>

                                                                                                        <div>
                                                                                                            <Label>
                                                                                                                Reference Type
                                                                                                            </Label>

                                                                                                            <select
                                                                                                                value={
                                                                                                                    linkedWork.target_type ||
                                                                                                                    "pages"
                                                                                                                }
                                                                                                                onChange={(e) =>
                                                                                                                updateLinkedWork(
                                                                                                                    curriculum.id,
                                                                                                                    material.id,
                                                                                                                    group.id,
                                                                                                                    item.id,
                                                                                                                    linkedWork.id,
                                                                                                                    {
                                                                                                                        target_type: e.target.value,
                                                                                                                    }
                                                                                                                )
                                                                                                            }
                                                                                                                className={shadowInput}
                                                                                                            >
                                                                                                                <option value="pages">
                                                                                                                    Pages
                                                                                                                </option>

                                                                                                                <option value="chapter">
                                                                                                                    Chapter
                                                                                                                </option>

                                                                                                                <option value="lesson">
                                                                                                                    Lesson
                                                                                                                </option>

                                                                                                                <option value="section">
                                                                                                                    Section
                                                                                                                </option>

                                                                                                                <option value="unit">
                                                                                                                    Unit
                                                                                                                </option>

                                                                                                                <option value="module">
                                                                                                                    Module
                                                                                                                </option>

                                                                                                                <option value="general">
                                                                                                                    General Instruction
                                                                                                                </option>
                                                                                                            </select>
                                                                                                        </div>                    
                                                                                                            <div>
                                                                                                                <Label>
                                                                                                                    Instruction
                                                                                                                </Label>

                                                                                                                <Input
                                                                                                                    value={
                                                                                                                        linkedWork.instruction ||
                                                                                                                        ""
                                                                                                                    }
                                                                                                                onChange={(e) =>
                                                                                                                    updateLinkedWork(
                                                                                                                        curriculum.id,
                                                                                                                        material.id,
                                                                                                                        group.id,
                                                                                                                        item.id,
                                                                                                                        linkedWork.id,
                                                                                                                        {
                                                                                                                            instruction: e.target.value,
                                                                                                                        }
                                                                                                                    )
                                                                                                                }  
                                                                                                                    placeholder="Read, Complete, Begin, Watch..."
                                                                                                                    className={
                                                                                                                        shadowInput
                                                                                                                    }
                                                                                                                />
                                                                                                            </div>
                                                                                                        {linkedWork.target_type === "pages" && (
    <>
                                                                                                            <div>
                                                                                                                <Label>
                                                                                                                    Start Page
                                                                                                                </Label>

                                                                                                                <Input
                                                                                                                    type="number"
                                                                                                                    min="1"
                                                                                                                    value={
                                                                                                                        linkedWork.page_start ||
                                                                                                                        ""
                                                                                                                    }
                                                                                                                onChange={(e) =>
                                                                                                                    updateLinkedWork(
                                                                                                                        curriculum.id,
                                                                                                                        material.id,
                                                                                                                        group.id,
                                                                                                                        item.id,
                                                                                                                        linkedWork.id,
                                                                                                                        {
                                                                                                                            page_start: e.target.value,
                                                                                                                        }
                                                                                                                    )
                                                                                                                }
                                                                                                                    className={
                                                                                                                        shadowInput
                                                                                                                    }
                                                                                                                />
                                                                                                            </div>

                                                                                                            <div>
                                                                                                                <Label>
                                                                                                                    End Page
                                                                                                                </Label>

                                                                                                                <Input
                                                                                                                    type="number"
                                                                                                                    min="1"
                                                                                                                    value={
                                                                                                                        linkedWork.page_end ||
                                                                                                                        ""
                                                                                                                    }
                                                                                                                    onChange={(e) =>
                                                                                                                        updateLinkedWork(
                                                                                                                            curriculum.id,
                                                                                                                            material.id,
                                                                                                                            group.id,
                                                                                                                            item.id,
                                                                                                                            linkedWork.id,
                                                                                                                            {
                                                                                                                                page_end: e.target.value,
                                                                                                                            }
                                                                                                                        )
                                                                                                                    }
                                                                                                                    className={
                                                                                                                        shadowInput
                                                                                                                    }
                                                                                                                />

                                                                                                            </div>
                                                                                                                </>
                                                                                )}
                                                                                {linkedWork.target_type === "chapter" && (
                                                                                    <div>
                                                                                        <Label>Chapter / Group</Label>

                                                                                        {selectedSiblingStructure.length > 0 ? (
                                                                                            <select
                                                                                                value={linkedWork.chapter || ""}
                                                                                                onChange={(e) =>
                                                                                                    updateLinkedWork(
                                                                                                        curriculum.id,
                                                                                                        material.id,
                                                                                                        group.id,
                                                                                                        item.id,
                                                                                                        linkedWork.id,
                                                                                                        {
                                                                                                            chapter: e.target.value,
                                                                                                        }
                                                                                                    )
                                                                                                }
                                                                                                className={shadowInput}
                                                                                            >
                                                                                                <option value="">
                                                                                                    Choose chapter or group
                                                                                                </option>

                                                                                                {selectedSiblingStructure.map(
                                                                                                    (structureGroup) => (
                                                                                                        <option
                                                                                                            key={structureGroup.id}
                                                                                                            value={structureGroup.id}
                                                                                                        >
                                                                                                            {structureGroup.title ||
                                                                                                                "Untitled Group"}
                                                                                                        </option>
                                                                                                    )
                                                                                                )}
                                                                                            </select>
                                                                                        ) : (
                                                                                            <Input
                                                                                                value={linkedWork.chapter || ""}
                                                                                                onChange={(e) =>
                                                                                                    updateLinkedWork(
                                                                                                        curriculum.id,
                                                                                                        material.id,
                                                                                                        group.id,
                                                                                                        item.id,
                                                                                                        linkedWork.id,
                                                                                                        {
                                                                                                            chapter: e.target.value,
                                                                                                        }
                                                                                                    )
                                                                                                }
                                                                                                placeholder="Chapter 1"
                                                                                                className={shadowInput}
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                )}

                                                                                {linkedWork.target_type === "lesson" && (
                                                                                    <div>
                                                                                        <Label>Lesson / Item</Label>

                                                                                        {selectedSiblingStructure.length > 0 ? (
                                                                                            <>
                                                                                                <select
                                                                                                    value={
                                                                                                        linkedWork.lesson_mode ||
                                                                                                        "structured"
                                                                                                    }
                                                                                                    onChange={(e) =>
                                                                                                        updateLinkedWork(
                                                                                                            curriculum.id,
                                                                                                            material.id,
                                                                                                            group.id,
                                                                                                            item.id,
                                                                                                            linkedWork.id,
                                                                                                            {
                                                                                                                lesson_mode:
                                                                                                                    e.target.value,
                                                                                                                lesson: "",
                                                                                                                chapter: "",
                                                                                                            }
                                                                                                        )
                                                                                                    }
                                                                                                    className={shadowInput}
                                                                                                >
                                                                                                    <option value="structured">
                                                                                                        Choose from curriculum structure
                                                                                                    </option>

                                                                                                    <option value="custom">
                                                                                                        Custom / plan later
                                                                                                    </option>
                                                                                                </select>

                                                                                                {(linkedWork.lesson_mode ||
                                                                                                    "structured") ===
                                                                                                "structured" ? (
                                                                                                    <div className="mt-3 space-y-3">
                                                                                                        <select
                                                                                                            value={
                                                                                                                linkedWork.chapter ||
                                                                                                                ""
                                                                                                            }
                                                                                                            onChange={(e) =>
                                                                                                                            updateLinkedWork(
                                                                                                                                curriculum.id,
                                                                                                                                material.id,
                                                                                                                                group.id,
                                                                                                                                item.id,
                                                                                                                                linkedWork.id,
                                                                                                                                {
                                                                                                                                    chapter: e.target.value,
                                                                                                                                    lesson: "",
                                                                                                                                    page_start: "",
                                                                                                                                    page_end: "",
                                                                                                                                }
                                                                                                                            )
                                                                                                                        }
                                                                                                            className={shadowInput}
                                                                                                        >
                                                                                                            <option value="">
                                                                                                                Choose chapter / unit / module
                                                                                                            </option>

                                                                                                            {selectedSiblingStructure.map(
                                                                                                                (
                                                                                                                    structureGroup
                                                                                                                ) => (
                                                                                                                    <option
                                                                                                                        key={
                                                                                                                            structureGroup.id
                                                                                                                        }
                                                                                                                        value={
                                                                                                                            structureGroup.id
                                                                                                                        }
                                                                                                                    >
                                                                                                                        {structureGroup.title ||
                                                                                                                            "Untitled Group"}
                                                                                                                    </option>
                                                                                                                )
                                                                                                            )}
                                                                                                        </select>

                                                                                                        {linkedWork.chapter && (
                                                                                                            <select
                                                                                                                value={
                                                                                                                    linkedWork.lesson ||
                                                                                                                    ""
                                                                                                                }
                                                                                                                onChange={(e) => {
                                                                                                                    const selectedLessonId =
                                                                                                                        e.target.value;

                                                                                                                    const selectedGroup =
                                                                                                                        selectedSiblingStructure.find(
                                                                                                                            (structureGroup) =>
                                                                                                                                structureGroup.id ===
                                                                                                                                linkedWork.chapter
                                                                                                                        );

                                                                                                                    const selectedLinkedItem =
                                                                                                                        (
                                                                                                                            selectedGroup?.items ||
                                                                                                                            []
                                                                                                                        ).find(
                                                                                                                            (linkedItem) =>
                                                                                                                                linkedItem.id ===
                                                                                                                                selectedLessonId
                                                                                                                        );

                                                                                                                    updateLinkedWork(
                                                                                                                        curriculum.id,
                                                                                                                        material.id,
                                                                                                                        group.id,
                                                                                                                        item.id,
                                                                                                                        linkedWork.id,
                                                                                                                        {
                                                                                                                            lesson:
                                                                                                                                selectedLessonId,

                                                                                                                            page_start:
                                                                                                                                selectedLinkedItem?.page_start ||
                                                                                                                                "",

                                                                                                                            page_end:
                                                                                                                                selectedLinkedItem?.page_end ||
                                                                                                                                "",
                                                                                                                        }
                                                                                                                    );
                                                                                                                }}
                                                                                                                className={shadowInput}
                                                                                                            >
                                                                                                                <option value="">
                                                                                                                    Choose lesson / item
                                                                                                                </option>

                                                                                                                {(
                                                                                                                    selectedSiblingStructure.find(
                                                                                                                        (
                                                                                                                            structureGroup
                                                                                                                        ) =>
                                                                                                                            structureGroup.id ===
                                                                                                                            linkedWork.chapter
                                                                                                                    )?.items || []
                                                                                                                ).map(
                                                                                                                    (
                                                                                                                        siblingItem
                                                                                                                    ) => (
                                                                                                                        <option
                                                                                                                            key={
                                                                                                                                siblingItem.id
                                                                                                                            }
                                                                                                                            value={
                                                                                                                                siblingItem.id
                                                                                                                            }
                                                                                                                        >
                                                                                                                            {siblingItem.title ||
                                                                                                                                "Untitled Item"}
                                                                                                                        </option>
                                                                                                                    )
                                                                                                                )}
                                                                                                            </select>
                                                                                                        )}
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <Input
                                                                                                        value={
                                                                                                            linkedWork.lesson ||
                                                                                                            ""
                                                                                                        }
                                                                                                        onChange={(e) =>
                                                                                                            updateLinkedWork(
                                                                                                                curriculum.id,
                                                                                                                material.id,
                                                                                                                group.id,
                                                                                                                item.id,
                                                                                                                linkedWork.id,
                                                                                                                {
                                                                                                                    lesson:
                                                                                                                        e.target.value,
                                                                                                                }
                                                                                                            )
                                                                                                        }
                                                                                                        placeholder="Optional — plan later in Sessions or Quests"
                                                                                                        className={`${shadowInput} mt-3`}
                                                                                                    />
                                                                                                )}
                                                                                            </>
                                                                                        ) : (
                                                                                            <Input
                                                                                                value={
                                                                                                    linkedWork.lesson || ""
                                                                                                }
                                                                                                onChange={(e) =>
                                                                                                    updateLinkedWork(
                                                                                                        curriculum.id,
                                                                                                        material.id,
                                                                                                        group.id,
                                                                                                        item.id,
                                                                                                        linkedWork.id,
                                                                                                        {
                                                                                                            lesson:
                                                                                                                e.target.value,
                                                                                                        }
                                                                                                    )
                                                                                                }
                                                                                                placeholder="Optional — plan later in Sessions or Quests"
                                                                                                className={shadowInput}
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                )}

                                                                                            {linkedWork.target_type === "section" && (
                                                                                                <div>
                                                                                                    <Label>Section</Label>

                                                                                                    <Input
                                                                                                        value={linkedWork.section || ""}
                                                                                                        onChange={(e) =>
                                                                                                            updateLinkedWork(
                                                                                                                curriculum.id,
                                                                                                                material.id,
                                                                                                                group.id,
                                                                                                                item.id,
                                                                                                                linkedWork.id,
                                                                                                                {
                                                                                                                    section: e.target.value,
                                                                                                                }
                                                                                                            )
                                                                                                        }
                                                                                                        placeholder="Section A"
                                                                                                        className={shadowInput}
                                                                                                    />
                                                                                                </div>
                                                                                            )}

                                                                                            {linkedWork.target_type === "unit" && (
                                                                                                <div>
                                                                                                    <Label>Unit</Label>

                                                                                                    <Input
                                                                                                        value={linkedWork.unit || ""}
                                                                                                        onChange={(e) =>
                                                                                                            updateLinkedWork(
                                                                                                                curriculum.id,
                                                                                                                material.id,
                                                                                                                group.id,
                                                                                                                item.id,
                                                                                                                linkedWork.id,
                                                                                                                {
                                                                                                                    unit: e.target.value,
                                                                                                                }
                                                                                                            )
                                                                                                        }
                                                                                                        placeholder="Unit 2"
                                                                                                        className={shadowInput}
                                                                                                    />
                                                                                                </div>
                                                                                            )}

                                                                                            {linkedWork.target_type === "module" && (
                                                                                                <div>
                                                                                                    <Label>Module</Label>

                                                                                                    <Input
                                                                                                        value={linkedWork.module || ""}
                                                                                                        onChange={(e) =>
                                                                                                            updateLinkedWork(
                                                                                                                curriculum.id,
                                                                                                                material.id,
                                                                                                                group.id,
                                                                                                                item.id,
                                                                                                                linkedWork.id,
                                                                                                                {
                                                                                                                    module: e.target.value,
                                                                                                                }
                                                                                                            )
                                                                                                        }
                                                                                                        placeholder="Module 1"
                                                                                                        className={shadowInput}
                                                                                                    />
                                                                                                </div>
                                                                                            )}

                                                                                            {linkedWork.target_type === "general" && (
                                                                                                <div className="md:col-span-2">
                                                                                                    <Label>General Instruction</Label>

                                                                                                    <Input
                                                                                                        value={linkedWork.instruction || ""}
                                                                                                        onChange={(e) =>
                                                                                                            updateLinkedWork(
                                                                                                                curriculum.id,
                                                                                                                material.id,
                                                                                                                group.id,
                                                                                                                item.id,
                                                                                                                linkedWork.id,
                                                                                                                {
                                                                                                                    instruction:
                                                                                                                        e.target.value,
                                                                                                                }
                                                                                                            )
                                                                                                        }
                                                                                                        placeholder="Read Chapter 1 and begin Caesar's English."
                                                                                                        className={shadowInput}
                                                                                                    />
                                                                                                </div>
                                                                                            )}
                                                                                            <div className="md:col-span-2 mt-2 rounded-xl border border-[#ffd166]/15 bg-[#05070d]/70 px-3 py-2">
                                                                                                <p className="text-[10px] uppercase tracking-[0.14em] text-[#8f98b5]">
                                                                                                    Linked Work Summary
                                                                                                </p>

                                                                                                <p className="mt-1 text-xs text-[#fff3c4]">
                                                                                                    {selectedSiblingResource?.name ||
                                                                                                        "Linked Resource"}

                                                                                                    {" · "}

                                                                                                    {linkedWork.action
                                                                                                        ? linkedWork.action.charAt(0).toUpperCase() +
                                                                                                        linkedWork.action.slice(1)
                                                                                                        : "Work"}

                                                                                                    {linkedWork.chapter &&
                                                                                                        (() => {
                                                                                                            const selectedGroup =
                                                                                                                selectedSiblingStructure.find(
                                                                                                                    (structureGroup) =>
                                                                                                                        structureGroup.id ===
                                                                                                                        linkedWork.chapter
                                                                                                                );

                                                                                                            return selectedGroup?.title
                                                                                                                ? ` · ${selectedGroup.title}`
                                                                                                                : "";
                                                                                                        })()}

                                                                                                    {linkedWork.lesson &&
                                                                                                        (() => {
                                                                                                            const selectedGroup =
                                                                                                                selectedSiblingStructure.find(
                                                                                                                    (structureGroup) =>
                                                                                                                        structureGroup.id ===
                                                                                                                        linkedWork.chapter
                                                                                                                );

                                                                                                            const selectedLesson =
                                                                                                                (
                                                                                                                    selectedGroup?.items ||
                                                                                                                    []
                                                                                                                ).find(
                                                                                                                    (linkedItem) =>
                                                                                                                        linkedItem.id ===
                                                                                                                        linkedWork.lesson
                                                                                                                );

                                                                                                            return selectedLesson?.title
                                                                                                                ? ` · ${selectedLesson.title}`
                                                                                                                : "";
                                                                                                        })()}

                                                                                                    {linkedWork.target_type ===
                                                                                                        "pages" &&
                                                                                                        (linkedWork.page_start ||
                                                                                                            linkedWork.page_end) &&
                                                                                                        ` · pp. ${
                                                                                                            linkedWork.page_start || "?"
                                                                                                        }${
                                                                                                            linkedWork.page_end
                                                                                                                ? `–${linkedWork.page_end}`
                                                                                                                : ""
                                                                                                        }`}

                                                                                                    {linkedWork.target_type ===
                                                                                                        "lesson" &&
                                                                                                        (linkedWork.page_start ||
                                                                                                            linkedWork.page_end) &&
                                                                                                        ` · pp. ${
                                                                                                            linkedWork.page_start || "?"
                                                                                                        }${
                                                                                                            linkedWork.page_end
                                                                                                                ? `–${linkedWork.page_end}`
                                                                                                                : ""
                                                                                                        }`}

                                                                                                    {linkedWork.target_type ===
                                                                                                        "section" &&
                                                                                                        linkedWork.section &&
                                                                                                        ` · ${linkedWork.section}`}

                                                                                                    {linkedWork.target_type ===
                                                                                                        "unit" &&
                                                                                                        linkedWork.unit &&
                                                                                                        ` · ${linkedWork.unit}`}

                                                                                                    {linkedWork.target_type ===
                                                                                                        "module" &&
                                                                                                        linkedWork.module &&
                                                                                                        ` · ${linkedWork.module}`}

                                                                                                    {linkedWork.instruction &&
                                                                                                        ` · ${linkedWork.instruction}`}
                                                                                                </p>
                                                                                            </div>

                                                                                                        </div>
                                                                                                        
                                                                                                    </div>
                                                                                                    
                                                                                                );
                                                                                                
                                                                                            }
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                )}                
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                        </div>    
                        </div>
                    )
                )}
            </div>
        )}
</div>
                                                               </div>
                            )
                        )}
                    </div>
                )}
            </div>
        </div>
    </div>
        </div>
    </div>
)}
{showClassCreatedPopup && (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 p-4">
        <div className="w-full max-w-lg rounded-[30px] border border-[#ffd166]/50 bg-[#080b14] p-8 text-center shadow-[0_0_45px_rgba(255,209,102,0.25)]">
            <div className="mb-4 text-5xl">✨</div>

            <h3 className="mb-3 font-display text-3xl text-[#ffd166]">
                Class Created!
            </h3>

            <p className="mb-8 text-[#d8b4fe]">
                Your new classroom is ready for adventure!
            </p>

            <div className="flex flex-col justify-center gap-3 sm:flex-row">
                <button
                    type="button"
                    onClick={() => setShowClassCreatedPopup(false)}
                    className={purpleActionBtn}
                >
                    I'm Done
                </button>

                <button
                    type="button"
                    onClick={() => {
                        setShowClassCreatedPopup(false);
                        setShowAddClassPopup(true);
                    }}
                    className="rounded-xl border border-[#ffd166]/60 bg-[#ffd166]/10 px-6 py-3 font-display text-[#ffd166] transition hover:bg-[#ffd166]/20"
                >
                    ＋ Add Another Class
                </button>
            </div>
        </div>
    </div>
)}
    <div className="mt-6 space-y-8">
        {children
            .filter(
                (child) =>
                    classStudentFilter === "all" ||
                    String(child.child_id) === classStudentFilter
            )
            .map((child) => {
            const childSubjects = subjects.filter(
                (subject) => subject.child_id === child.child_id
            );
           
            return (
                <div
                    key={child.child_id}
                    className="border-t-2 border-[#ffd166]/60 pt-5 shadow-[0_-10px_24px_rgba(255,209,102,0.08)]"
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full overflow-hidden border border-[#ffd166]/50 bg-[#7c3aed]/25 flex items-center justify-center">
                            <img
                                src={
                                    child.profile_picture ||
                                    child.picture ||
                                    "https://placehold.co/100x100"
                                }
                                alt={child.name}
                                className="w-full h-full object-cover"
                            />
                        </div>


                        <div>
                            <h4 className="font-display text-2xl text-[#ffd166]">
                                {child.name}
                            </h4>
                            <p className="text-xs text-[#b0b8d6]">
                                {child.gradeLevel ||
                                    (getAgeFromBirthday(child.birthday) !== null
                                        ? `Age ${getAgeFromBirthday(child.birthday)}`
                                        : child.age
                                        ? `Age ${child.age}`
                                        : "Student profile")}
                            </p>
                        </div>
                    </div>


                    {childSubjects.length === 0 ? (
                        <p className="text-sm text-[#6c7a9c]">
                            No subjects for this profile yet.
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

                         {childSubjects.map((subject) => (
    <div
        key={subject.subject_id || subject.id}
        className="
            group rounded-2xl p-4 border border-[#ffd166]/45
            shadow-[0_0_18px_rgba(255,209,102,0.12)]
            transition-all duration-200
            hover:border-[#ffd166]
            hover:shadow-[0_0_24px_rgba(255,209,102,0.32)]
            active:border-[#ffd166]
            active:shadow-[0_0_30px_rgba(255,209,102,0.45)]
        "
        style={{
            backgroundColor: `${subject.color}33`,
        }}
    >
        <button
            type="button"
            onClick={() =>
                navigate(`/parent/classroom/${encodeURIComponent(child.child_id)}/${encodeURIComponent(subject.subject_id || subject.id)}`)
            }
            className="
                w-full text-left rounded-xl p-4 border border-[#7c3aed]/40
                bg-[#7c3aed]/20 transition-all duration-200
                hover:border-[#ffd166]/80
                hover:shadow-[0_0_18px_rgba(255,209,102,0.28)]
                focus:outline-none focus:border-[#ffd166]
                focus:shadow-[0_0_22px_rgba(255,209,102,0.4)]
            "
        >
            <h5 className="font-display text-2xl text-[#ffd166]">
                {subject.name}
            </h5>


            <p className="text-xs text-[#f3e8ff] mt-1">
                Open Classroom
            </p>
        </button>


        <div className="flex items-center justify-between gap-3 mt-4">
            <Input
                type="color"
                value={subject.color}
                onChange={(e) => {
                    const updatedSubjects = subjects.map((s) =>
                        s.id === subject.id
                            ? { ...s, color: e.target.value }
                            : s
                    );


                    localStorage.setItem(
                        "shadowGroveSubjects",
                        JSON.stringify(updatedSubjects)
                    );


                    setSubjects(updatedSubjects);
                }}
               className={shadowInput}
            />


            <div className="flex flex-wrap gap-2">
                
                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => {
                        const subjectId =
                            subject.subject_id ||
                            subject.id;

                        setEditingSubjectId(subjectId);
                        setCurriculumOwnerSubjectId(
                            subjectId
                        );
                        setSubjectChildId(subject.child_id);
                        setSubjectName(subject.name);
                        setSubjectColor(
                            subject.color || "#7c3aed"
                        );
                        setSubjectCurriculumSources(
                            Array.isArray(
                                subject.curriculum_sources
                            )
                                ? subject.curriculum_sources
                                : []
                        );
                        const savedSchedule =
                            subject.class_schedule || {};

                        const savedScheduleMode =
                            savedSchedule.mode === "flexible" ||
                            savedSchedule.mode === "block"
                                ? "block"
                                : "days";

                        setSubjectScheduleMode(
                            savedScheduleMode
                        );

                        setSubjectScheduleDays(
                            Array.isArray(savedSchedule.days)
                                ? savedSchedule.days
                                : []
                        );

                        setSubjectScheduleStartTime(
                            savedSchedule.start_time || ""
                        );

                        setSubjectScheduleEndTime(
                            savedSchedule.end_time || ""
                        );

                        setSubjectScheduleBlock(
                            savedSchedule.preferred_block ||
                                "morning"
                        );

                        setSubjectScheduleWeeklyHours(
                            savedSchedule.weekly_hours || ""
                        );

                        setSubjectScheduleCustomBlock(
                            savedSchedule.custom_block_label ||
                                ""
                        );

                        setShowAddClassPopup(true);

                        toast.success(
                            "Subject loaded for editing."
                        );
                    }}
                >
                    Edit
                </button>

<button
    type="button"
    className={purpleActionBtn}
    onClick={() => {
        setDeleteSubjectTarget(
            subject
        );
    }}
>
    Delete
</button>
            </div>
        </div>
    </div>
))}

                        </div>
                    )}
                </div>
            );
        })}
    </div>
</div>

   


                        <div className="space-y-3">
                            {children.map((c) => (
                                <div
                                    key={c.child_id}
                                    className="flex flex-wrap items-center justify-between rounded-xl bg-white/5 px-3 py-3 gap-3 border border-[#ffd166]/20"
                                >
                                    <div>
                                        <span className="text-sm font-semibold">{c.name}</span>
                                        <p className="text-xs text-[#6c7a9c]">
                                            {c.gradeLevel ||
                                                (getAgeFromBirthday(c.birthday) !== null
                                                    ? `Age ${getAgeFromBirthday(c.birthday)}`
                                                    : c.age
                                                    ? `Age ${c.age}`
                                                    : "Student profile")}
                                        </p>
                                    </div>


                                    <div className="flex flex-wrap items-center gap-2">
                                        {pendingByChild[c.child_id] > 0 && (
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#ffd166]/15 text-[#ffd166] inline-flex items-center gap-1">
                                                <Hourglass size={10} />
                                                {pendingByChild[c.child_id]} pending
                                            </span>
                                        )}


                                        <button
                                            type="button"
                                            onClick={() => navigate(`/quests/${c.child_id}`)}
                                            className={purpleActionBtn}
                                        >
                                            Open Desk
                                        </button>


                                        <button
                                            type="button"
                                            onClick={() => { setRequestedDeskGadget({ type: "chores", childId: c.child_id }); setOpenPanel(null); }}
                                            className={purpleActionBtn}
                                        >
                                            Household Duties
                                        </button>


                                        <button
                                            type="button"
                                            onClick={() => navigate(`/codex/${c.child_id}`)}
                                            className={purpleActionBtn}
                                        >
                                            Codex
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Panel>


                  <Panel id="calendar" openPanel={openPanel}>
<div
    className="relative mb-6 rounded-[32px] border border-[#ffd166]/35 bg-black px-8 py-5 shadow-[0_0_28px_rgba(255,209,102,0.12)] overflow-hidden"
    style={
        schoolBannerImage
            ? {
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.82)), url(${schoolBannerImage})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
              }
            : {}
    }
>
    <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
            <h2 className="font-display text-2xl text-[#ffd166]">
            {schoolYearSettings.schoolName?.trim() || BRANDING.appName}
            </h2>

            <p className="mt-1 text-sm text-[#d8b4fe]">
                {schoolYearSettings.academicYearLabel?.trim() ||
                    "School Dashboard"}
                {schoolYearSettings.motto?.trim()
                    ? ` • ${schoolYearSettings.motto}`
                    : ""}
            </p>
        </div>

        <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-[#ffd166]/70">
                Master Calendar
            </div>

            <div className="text-sm text-[#fff3c4]">
                School • Classes • Attendance • Reports
            </div>
        </div>
    </div>
</div>

          <div className="relative h-[92vh] w-full max-w-[1800px] mx-auto">
   
      {calendarDrawerOpen && (
<aside
    className="
        absolute
        top-0
        left-0
        z-40
        h-full
        w-[430px]
        max-w-[95vw]
        space-y-5
        overflow-y-auto
        rounded-2xl
        border-2
        border-[#ffd166]/60
        bg-[#050711]
        p-6
        pb-28
        shadow-[0_0_35px_rgba(255,209,102,0.28)]
    "
>
    <button
        type="button"
        onClick={() => setCalendarDrawerOpen(false)}
        className={`${purpleActionBtn} mb-2`}
    >
        Close Setup
    </button>
           <div className="space-y-4 max-w-[360px] mx-auto">
        <button
    type="button"
    onClick={() => setSchoolSetupOpen((prev) => !prev)}
    className={`${purpleActionBtn} !flex !w-[320px] !mx-auto !justify-between !items-center !self-center !bg-[#7c3aed]/25 !border-[#ffd166]/50 !shadow-[0_0_18px_rgba(255,209,102,0.18)]`}
>
    <span className="font-display text-xl text-[#d8b4fe]">
        School Year Setup
    </span>

    <span>{schoolSetupOpen ? "−" : "+"}</span>
</button>
{schoolSetupOpen && (
    <div className="mt-4 space-y-4">
                    <div>
    <Label>School Name</Label>
    <Input
        value={schoolYearSettings.schoolName || ""}
        onChange={(e) =>
            setSchoolYearSettings((prev) => ({
                ...prev,
                schoolName: e.target.value,
            }))
        }
        placeholder="Shadow Grove Academy"
        className={shadowInput}
    />
</div>

<div>
    <Label>Academic Year Label</Label>
    <Input
        value={schoolYearSettings.academicYearLabel || ""}
        onChange={(e) =>
            setSchoolYearSettings((prev) => ({
                ...prev,
                academicYearLabel: e.target.value,
            }))
        }
        placeholder="2026-2027"
        className={shadowInput}
    />
</div>

<div>
    <Label>School Motto</Label>
    <Input
        value={schoolYearSettings.motto || ""}
        onChange={(e) =>
            setSchoolYearSettings((prev) => ({
                ...prev,
                motto: e.target.value,
            }))
        }
        placeholder="Knowledge Through Wonder"
        className={shadowInput}
    />
</div>
<div>
    <Label>School Banner Image</Label>
    <input
        type="file"
        accept="image/*"
        onChange={(e) =>
            fileToDataUrl(e.target.files?.[0], setSchoolBannerImage)
        }
        className="mt-2 w-full text-sm text-[#b0b8d6]
        file:mr-3
        file:rounded-2xl
        file:border
        file:border-[#ffd166]/30
        file:bg-[#7c3aed]/20
        file:text-[#fff3c4]
        file:px-4
        file:py-2
        file:font-semibold
        hover:file:border-[#ffd166]/75
        hover:file:bg-[#7c3aed]/35"
    />

    {schoolBannerImage && (
        <img
            src={schoolBannerImage}
            alt="School banner preview"
            className="mt-3 h-24 w-full rounded-2xl object-cover border border-[#ffd166]/30"
        />
    )}
</div>
                <div>
                    <Label>Start date</Label>
                    <Input
                        type="date"
                        value={schoolYearSettings.startDate}
                        onChange={(e) =>
                            setSchoolYearSettings((prev) => ({
                                ...prev,
                                startDate: e.target.value,
                            }))
                        }
                        className={shadowInput}
                    />
                </div>

                <div>
                    <Label>End date</Label>
                    <Input
                        type="date"
                        value={schoolYearSettings.endDate}
                        onChange={(e) =>
                            setSchoolYearSettings((prev) => ({
                                ...prev,
                                endDate: e.target.value,
                            }))
                        }
                        className={shadowInput}
                    />
                </div>

                <div>
                    <Label>Divide year by</Label>
                    <select
                        value={schoolYearSettings.division}
                        onChange={(e) =>
                            setSchoolYearSettings((prev) => ({
                                ...prev,
                                division: e.target.value,
                            }))
                        }
                        className={shadowInput}
                    >
                        <option value="semesters">Semesters</option>
                        <option value="quarters">Quarters</option>
                    </select>
                </div>

                <button
                    type="button"
                    onClick={saveSchoolYearSettings}
                    className={purpleActionBtn}
                >
                    Save School Year
                </button>
                    </div>
)}
                   </div>
    

            <div className="space-y-5">
   
                                <div className="space-y-4 max-w-[360px] mx-auto">

    <button
        type="button"
        onClick={() => setCalendarEventOpen((prev) => !prev)}
        className={`${purpleActionBtn} !flex !w-[320px] !mx-auto !justify-between !items-center !self-center !bg-[#7c3aed]/25 !border-[#ffd166]/50 !shadow-[0_0_18px_rgba(255,209,102,0.18)]`}
    >
        <span className="font-display text-xl text-[#d8b4fe]">
            Add Calendar Event
        </span>

        <span>
            {calendarEventOpen ? "−" : "+"}
        </span>
    </button>

    {calendarEventOpen && (
        <div className="mt-4 space-y-4">
            <div>
                <Label>Calendar Owner</Label>

                <select
                    value={calendarItemProfileView}
                    onChange={(e) => setCalendarItemProfileView(e.target.value)}
                    className={shadowInput}
                >
                    <option value="school">School / Shared Calendar</option>

                    {children.map((c) => (
                        <option key={c.child_id} value={c.child_id}>
                            {c.name}
                        </option>
                    ))}
                </select>
                </div>

<div>
    <Label>Type</Label>

    <select
        value={calendarItemType}
        onChange={(e) => {
            const nextType = e.target.value;

            setCalendarItemType(nextType);

            setCalendarItemBlocksSchool(
                ["sick", "appointment"].includes(nextType)
            );
        }}
        className={shadowInput}
    >
        <option value="appointment">Appointment</option>
        <option value="vacation">Vacation</option>
        <option value="sick">Sick Day</option>
        <option value="extracurricular">Extracurricular</option>
        <option value="coop">Co-op Day</option>
        <option value="fieldtrip">Field Trip</option>
        <option value="custom">Custom</option>
    </select>
            </div>
        

        <div>
            <Label>Name</Label>
            <Input
                value={calendarItemName}
                onChange={(e) => setCalendarItemName(e.target.value)}
                placeholder="Dentist appointment, piano, co-op..."
                className={shadowInput}
            />
        </div>

        <div>
            <Label>Description</Label>
            <textarea
                value={calendarItemDescription}
                onChange={(e) => setCalendarItemDescription(e.target.value)}
                placeholder="Notes, details, purpose..."
                className="mt-1 w-full min-h-[80px] bg-[#080b14] border border-[#1f2942] rounded-xl px-4 py-3 text-white text-sm"
            />
        </div>

        <div>
            <Label>Location</Label>
            <Input
                value={calendarItemLocation}
                onChange={(e) => setCalendarItemLocation(e.target.value)}
                placeholder="Library, doctor office, co-op campus..."
                className={shadowInput}
            />
        </div>

        <div className="grid grid-cols-1 gap-4">
            <div>
                <Label>Start date</Label>
                <Input
                    type="date"
                    value={calendarItemStartDate}
                    onChange={(e) => setCalendarItemStartDate(e.target.value)}
                    className={shadowInput}
                />
            </div>

            <div>
                <Label>End date</Label>
                <Input
                    type="date"
                    value={calendarItemEndDate}
                    onChange={(e) => setCalendarItemEndDate(e.target.value)}
                    className={shadowInput}
                />
            </div>
        </div>

        <div>
            <Label>Frequency</Label>
            <select
                value={calendarItemFrequency}
                onChange={(e) => setCalendarItemFrequency(e.target.value)}
                className={shadowInput}
            >
               <option value="once">One time</option>
<option value="through">Through date range</option>
<option value="daily">Daily</option>
<option value="weekly">Weekly</option>
<option value="biweekly">Every two weeks</option>
<option value="monthly">Monthly</option>
<option value="annual">Annual</option>
<option value="custom">Custom</option>
            </select>
        </div>
                {calendarItemFrequency === "custom" && (
    <div className="rounded-2xl border border-[#ffd166]/30 bg-black p-4 shadow-[0_0_18px_rgba(255,209,102,0.10)]">
        <p className="text-sm text-[#ffd166] mb-3">
            Custom Weekly Schedule
        </p>

        <div className="flex flex-wrap gap-2">
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                <button
                    key={day}
                    type="button"
                    onClick={() =>
                        setCalendarItemCustomDays((prev) =>
                            prev.includes(day)
                                ? prev.filter((d) => d !== day)
                                : [...prev, day]
                        )
                    }
                    className={`${purpleActionBtn} ${
                        calendarItemCustomDays.includes(day)
                            ? "!border-[#ffd166] !bg-[#ffd166]/20 !text-[#fff3c4] !shadow-[0_0_18px_rgba(255,209,102,0.55)]"
                            : ""
                    }`}
                >
                    {day}
                </button>
            ))}
        </div>
    </div>
)}
        <div className="grid grid-cols-1 gap-4">
    <div>
        <Label>Start Time</Label>
        <Input
            type="time"
            value={calendarItemTime}
            onChange={(e) => setCalendarItemTime(e.target.value)}
            className={shadowInput}
        />
    </div>

    <div>
        <Label>End Time</Label>
        <Input
            type="time"
            value={calendarItemEndTime}
            onChange={(e) => setCalendarItemEndTime(e.target.value)}
            className={shadowInput}
        />
    </div>
</div>

        <div>
    <Label>Event Color</Label>

    <Input
        type="color"
        value={calendarItemColor}
        onChange={(e) => setCalendarItemColor(e.target.value)}
       className={shadowInput}
    />


   
    <div>
    <Label>Emoji / Symbol</Label>

    <Input
        value={calendarItemEmoji}
        onChange={(e) => setCalendarItemEmoji(e.target.value)}
        placeholder="⭐ 🎵 🏥 📚"
        className={shadowInput}
    />

            <details className="mt-2">
    <summary className="cursor-pointer text-sm text-[#ffd166] hover:text-[#fff3c4]">
        More Emojis
    </summary>

    <div className="flex flex-wrap gap-2 mt-3 max-h-[180px] overflow-y-auto pr-1">
    {[
        "⭐",
        "🎵",
        "📚",
        "🏥",
        "🎨",
        "🧪",
        "🏕️",
        "🚌",
        "⚽",
        "🎭",
        "📝",
        "🍎",
        "🎹",
        "📖",
        "🧙",
        "✨",
        "🌙",
        "🔥",
        "🌲",
        "🎬",
        "🖥️",
        "📐",
        "➕",
        "🔬",
        "🧬",
        "🎤",
        "🎸",
        "🥁",
        "🧵",
        "🍳",
        "🚗",
        "✈️",
        "🌎",
        "🧩",
        "♟️",
        "📜",
        "🕯️",
        "☄️",
    ].map((emoji) => (
        <button
            key={emoji}
            type="button"
            onClick={() => setCalendarItemEmoji(emoji)}
            className="rounded-xl border border-[#ffd166]/25 bg-black px-2 py-1 text-lg shadow-[0_0_10px_rgba(124,58,237,0.12)] hover:border-[#ffd166]/70 hover:shadow-[0_0_16px_rgba(255,209,102,0.20)]"
        >
            {emoji}
        </button>
    ))}
</div>
</details>
</div>
</div>
        <label className="flex items-center gap-2 text-sm text-[#ffd166]">
            <input
                type="checkbox"
                checked={calendarItemBlocksSchool}
                onChange={(e) => setCalendarItemBlocksSchool(e.target.checked)}
            />
            Blocks school / shifts quests
        </label>

     
        <div className="flex flex-wrap gap-2">
    <button
        type="button"
        onClick={saveCalendarItem}
        className={purpleActionBtn}
    >
        {editingCalendarItemId ? "Update Calendar Item" : "Save Calendar Item"}
    </button>

    {editingCalendarItemId && (
        <button
            type="button"
            onClick={() => {
                setEditingCalendarItemId(null);
                setCalendarItemName("");
                setCalendarItemDescription("");
                setCalendarItemLocation("");
                setCalendarItemStartDate("");
                setCalendarItemEndDate("");
                setCalendarItemFrequency("once");
                setCalendarItemTime("");
                setCalendarItemEndTime("");
                setCalendarItemBlocksSchool(false);
                setCalendarItemCustomDays([]);
                setCalendarItemColor("#7c3aed");
                setCalendarItemEmoji("");
            }}
            className={purpleActionBtn}
        >
            Cancel Edit
        </button>
    )}
</div>
           <div className="flex flex-wrap gap-2">
    <button type="button" className={purpleActionBtn}>
        Export Calendar
    </button>

    <button type="button" className={purpleActionBtn}>
        Import Calendar
    </button>
</div>

    </div>
)}

</div>

        <div className="space-y-4 max-w-[360px] mx-auto">
    <button
        type="button"
        onClick={() => setShiftScheduleOpen((prev) => !prev)}
        className={`${purpleActionBtn} !flex !w-[320px] !mx-auto !justify-between !items-center !self-center !bg-[#7c3aed]/25 !border-[#ffd166]/50 !shadow-[0_0_18px_rgba(255,209,102,0.18)]`}
    >
        <span className="font-display text-xl text-[#d8b4fe]">
            Shift Schedule
        </span>

        <span>{shiftScheduleOpen ? "−" : "+"}</span>
    </button>

    {shiftScheduleOpen && (
        <div className="mt-4 space-y-4 rounded-[28px] border border-[#ffd166]/30 bg-black p-4 shadow-[0_0_24px_rgba(255,209,102,0.10)]">
            <p className="text-sm text-[#b0b8d6]">
                Move quests and sessions forward or backward after sickness,
                emergency days, vacations, or schedule changes.
            </p>

            <div>
                <Label>Start shifting from</Label>
                <Input
                    type="date"
                    value={shiftScheduleForm.startDate}
                    onChange={(e) =>
                        setShiftScheduleForm((prev) => ({
                            ...prev,
                            startDate: e.target.value,
                        }))
                    }
                    className={shadowInput}
                />
            </div>

            <div>
                <Label>Shift by days</Label>
                <Input
                    type="number"
                    value={shiftScheduleForm.dayOffset}
                    onChange={(e) =>
                        setShiftScheduleForm((prev) => ({
                            ...prev,
                            dayOffset: e.target.value,
                        }))
                    }
                    className={shadowInput}
                />
            </div>

            <div>
                <Label>Scope</Label>
                <select
                    value={shiftScheduleForm.scope}
                    onChange={(e) =>
                        setShiftScheduleForm((prev) => ({
                            ...prev,
                            scope: e.target.value,
                            childId: "",
                            subjectId: "",
                        }))
                    }
                    className={shadowInput}
                >
                    <option value="school">Whole School</option>
                    <option value="child">One Child</option>
                    <option value="class">One Class</option>
                </select>
            </div>

            {shiftScheduleForm.scope === "child" && (
                <div>
                    <Label>Child</Label>
                    <select
                        value={shiftScheduleForm.childId}
                        onChange={(e) =>
                            setShiftScheduleForm((prev) => ({
                                ...prev,
                                childId: e.target.value,
                            }))
                        }
                        className={shadowInput}
                    >
                        <option value="">Select child</option>

                        {children.map((child) => (
                            <option key={child.child_id} value={child.child_id}>
                                {child.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {shiftScheduleForm.scope === "class" && (
                <div>
                    <Label>Class</Label>
                    <select
                        value={shiftScheduleForm.subjectId}
                        onChange={(e) =>
                            setShiftScheduleForm((prev) => ({
                                ...prev,
                                subjectId: e.target.value,
                            }))
                        }
                        className={shadowInput}
                    >
                        <option value="">Select class</option>

                        {subjects.map((subject) => {
                            const child = children.find(
                                (c) => c.child_id === subject.child_id
                            );

                            return (
                                <option key={subject.id} value={subject.id}>
                                    {child?.name ? `${child.name} · ` : ""}
                                    {subject.name}
                                </option>
                            );
                        })}
                    </select>
                </div>
            )}

            <div>
                <Label>Items to shift</Label>
                <select
                    value={shiftScheduleForm.itemScope}
                    onChange={(e) =>
                        setShiftScheduleForm((prev) => ({
                            ...prev,
                            itemScope: e.target.value,
                        }))
                    }
                    className={shadowInput}
                >
                    <option value="both">Quests and Sessions</option>
                    <option value="quests">Quests Only</option>
                    <option value="sessions">Sessions Only</option>
                </select>
            </div>

            <div>
                <Label>Shift mode</Label>
                <select
                    value={shiftScheduleForm.dateMode}
                    onChange={(e) =>
                        setShiftScheduleForm((prev) => ({
                            ...prev,
                            dateMode: e.target.value,
                        }))
                    }
                    className={shadowInput}
                >
                    <option value="instructional">Instructional Days</option>
                    <option value="calendar">Calendar Days</option>
                </select>
            </div>

            <label className="flex items-center gap-2 text-sm text-[#ffd166]">
                <input
                    type="checkbox"
                    checked={shiftScheduleForm.includeCompleted}
                    onChange={(e) =>
                        setShiftScheduleForm((prev) => ({
                            ...prev,
                            includeCompleted: e.target.checked,
                        }))
                    }
                />
                Include completed items
            </label>

            <div className="rounded-2xl border border-[#ffd166]/20 bg-[#050711] p-3 text-xs text-[#b0b8d6]">
                Preview:{" "}
                <span className="font-semibold text-[#ffd166]">
                    {getProfileShiftPreviewCount()}
                </span>{" "}
                item(s) will move.
            </div>

            <button
                type="button"
                onClick={applyProfileShiftSchedule}
                className={purpleActionBtn}
            >
                Apply Shift
            </button>
        </div>
    )}
</div>
    
          <div className="space-y-4 pb-2">
    <button
    type="button"
    onClick={() => setPeriodAdvanceOpen((prev) => !prev)}
    className={`${purpleActionBtn} !flex !w-[320px] !ml-[12px] !mr-auto !justify-between !items-center !self-center !bg-[#7c3aed]/25 !border-[#ffd166]/50 !shadow-[0_0_18px_rgba(255,209,102,0.18)]`}
>
    <span className="font-display text-xl text-[#d8b4fe]">
        End Period Advancement
    </span>

    <span>{periodAdvanceOpen ? "−" : "+"}</span>
</button>

    {periodAdvanceOpen && (
        <div className="mt-4 space-y-4">
            <p className="text-sm text-[#b0b8d6]">
                Parent confirms when a semester, or two quarters, has ended.
            </p>

            <div>
                <Label>Child</Label>
                <select
                        value={advanceChildId}
                        onChange={(e) => setAdvanceChildId(e.target.value)}
                        className={shadowInput}
                    >
                        <option value="">Select child</option>
                        {children.map((c) => (
                            <option key={c.child_id} value={c.child_id}>
                                {c.name}
                                {c.gradeLevel
                                    ? ` · ${c.gradeLevel}`
                                    : getAgeFromBirthday(c.birthday) !== null
                                        ? ` · Age ${getAgeFromBirthday(c.birthday)}`
                                        : c.age
                                        ? ` · Age ${c.age}`
                                        : ""}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <Label>Period label</Label>
                    <Input
                        value={advancePeriodKey}
                        onChange={(e) => setAdvancePeriodKey(e.target.value)}
                        placeholder="Fall 2026 or Quarter 1"
                        className={shadowInput}
                    />
                </div>

                
                                <div>
    <Label>Grade Promotion</Label>

    <select
        value={advanceType}
        onChange={(e) => setAdvanceType(e.target.value)}
        className={shadowInput}
    >
        <option value="period_only">Period only</option>
        <option value="semester">Semester</option>
        <option value="two_quarters">Two quarters</option>
        <option value="promote_grade">Promote grade</option>
        <option value="promote_grade_and_period">Grade + period</option>
             </select>
                </div>
                           <button
                    type="button"
                    onClick={confirmAdvancePeriod}
                    className={purpleActionBtn}
                >
                    Confirm & Advance
                </button>
                        </div>
    )}
            </div>

        </div>

        </aside>
        

      )}
                  <section
    className={`
        ${shadowCalendarShell}
        h-full
        transition-all
        duration-300
        ${
            calendarDrawerOpen
                ? "ml-[450px] w-[calc(100%-450px)]"
                : "w-full"
        }
    `}
>
    <button
        type="button"
        onClick={() => setCalendarDrawerOpen(true)}
        className={`${purpleActionBtn} mb-4`}
    >
        Open Setup & Controls
    </button>
                <button
    type="button"
    onClick={undoLastCalendarChange}
    className={`${purpleActionBtn} mb-4 ml-2`}
>
    Undo Calendar Change
</button>
           <div className={`${shadowCalendarToolbar} mb-4 flex flex-wrap items-end gap-3`}>
    <div>
        <Label className="text-[#ffd166] text-sm">
            Calendar View
        </Label>

        <select
            value={calendarProfileView}
            onChange={(e) => {
                setCalendarProfileView(e.target.value);
                setCalendarClassView("all");
                loadSavedAttendanceForChild(e.target.value);
            }}
            className={shadowInput}
        >
            <option value="school">All Children / School View</option>

            {children.map((c) => (
                <option key={c.child_id} value={c.child_id}>
                    {c.name}
                </option>
            ))}
        </select>
    </div>

    <div>
        <Label className="text-[#ffd166] text-sm">
            Class View
        </Label>

        <select
            value={calendarClassView}
            onChange={(e) => setCalendarClassView(e.target.value)}
            className={shadowInput}
        >
            <option value="all">All Classes</option>

            {subjects
                .filter((subject) =>
                    calendarProfileView === "school"
                        ? true
                        : subject.child_id === calendarProfileView
                )
                .map((subject) => {
                    const child = children.find(
                        (c) => c.child_id === subject.child_id
                    );

                    return (
                        <option key={subject.subject_id ?? subject.id} value={subject.subject_id ?? subject.id}>
                            {child?.name ? `${child.name} · ` : ""}
                            {subject.name}
                        </option>
                    );
                })}
        </select>
    </div>
</div>
        <div className="flex flex-wrap gap-2 mb-4">
    {[
        { value: "classes", label: "📚 Class Schedule" },
        { value: "assignments", label: "📜 Assignments Due" },
    ].map((view) => (
        <button
            key={view.value}
            type="button"
            onClick={() => setCalendarContentView(view.value)}
            aria-pressed={calendarContentView === view.value}
            className={`${purpleActionBtn} ${
                calendarContentView === view.value
                    ? shadowSelectedGold
                    : ""
            }`}
        >
            {view.label}
        </button>
    ))}
</div>
        <div className="flex flex-wrap gap-2">
            {["month", "week", "day"].map((mode) => (
                <button
                    key={mode}
                    type="button"
                    onClick={() => setCalendarViewMode(mode)}
                    className={`${purpleActionBtn} ${
                        calendarViewMode === mode ? shadowSelectedGold : ""
                    }`}
                >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
            ))}
        </div>


    <div className="flex flex-wrap items-center justify-between gap-3 mb-4 rounded-[28px] border border-[#ffd166]/30 bg-black p-4 shadow-[0_0_24px_rgba(255,209,102,0.10)]">
        <button
            type="button"
            className={purpleActionBtn}
            onClick={() => {
                setSelectedCalendarDate((prev) => {
                    if (calendarViewMode === "month") return addMonths(prev, -1);
                    if (calendarViewMode === "week") return addDays(prev, -7);
                    return addDays(prev, -1);
                });
            }}
        >
            Previous
        </button>

        <h3 className="font-display text-3xl text-[#ffd166] text-center">
            {calendarViewMode === "month" &&
                selectedCalendarDate.toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                })}

            {calendarViewMode === "week" &&
                `${getStartOfWeek(selectedCalendarDate).toLocaleDateString(
                    "default",
                    {
                        month: "short",
                        day: "numeric",
                    }
                )} - ${addDays(
                    getStartOfWeek(selectedCalendarDate),
                    6
                ).toLocaleDateString("default", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                })}`}

            {calendarViewMode === "day" &&
                selectedCalendarDate.toLocaleDateString("default", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                })}
        </h3>

        <button
            type="button"
            className={purpleActionBtn}
            onClick={() => {
                setSelectedCalendarDate((prev) => {
                    if (calendarViewMode === "month") return addMonths(prev, 1);
                    if (calendarViewMode === "week") return addDays(prev, 7);
                    return addDays(prev, 1);
                });
            }}
        >
            Next
        </button>
    </div>

    {calendarViewMode === "month" && (
        <div className="grid grid-cols-7 gap-4 text-center text-xs rounded-2xl border border-[#ffd166]/20 bg-black/70 p-3 mb-3 min-w-[1400px]">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="text-[#ffd166] font-semibold">
                    {day}
                </div>
            ))}
        </div>
    )}
                {calendarViewMode === "day" && (
    <div className="rounded-3xl border border-[#ffd166]/30 bg-[#050711] p-4 mb-4 min-w-[900px] shadow-[0_0_24px_rgba(124,58,237,0.16)]">
        <div
    className="mb-4 rounded-2xl border border-[#ffd166]/20 bg-black/60 p-3"
    onDragOver={(e) => e.preventDefault()}
    onDrop={() => {
        if (!draggedCalendarItemId) return;

        moveCalendarItemToAllDay(
            draggedCalendarItemId,
            selectedCalendarDate.toISOString().slice(0, 10)
        );
    }}
>
            <p className="text-xs text-[#ffd166] mb-2">All Day / Unscheduled</p>

            <div className="flex flex-wrap gap-2">
                    {getCalendarItemsForDay(
                    selectedCalendarDate.getDate(),
                    selectedCalendarDate
                    ).filter((item) => !item.time).length === 0 ? (
                    <div className="rounded-lg border border-[#ffd166]/20 bg-black/60 px-2 py-1 text-[10px] text-[#6c7a9c]">
                        No items
                    </div>
                ) : (
                    getCalendarItemsForDay(
                    selectedCalendarDate.getDate(),
                    selectedCalendarDate
                    ).filter((item) => !item.time).map((item) => (
                        <div
    key={item.id}
    draggable={!item.isVacationRecord && !item.isScheduledClass && !item.isTodoCalendarItem}
    onClick={() => {
        if (item.isScheduledClass) {
            return;
        }

        if (item.time) {
            const wantsEdit = window.confirm(
                "✨ This scheduled event already has a time assigned.\n\nWould you like to open the event editor?"
            );

            if (wantsEdit) {
                setCalendarEditPopupItem({
                    ...item,
                    visibleOccurrenceDate: selectedCalendarDate.toISOString().slice(0, 10),
                });
            }
        }
    }}
    onDragStart={(e) => {
        if (
        item.type === "vacation" ||
        item.isVacationRecord ||
        item.isScheduledClass
    ) {
        e.preventDefault();
        toast.error("Vacation records must be edited, not dragged.");
        setDraggedCalendarItemId(null);
        return;
    }
        setDraggedCalendarSourceDate(
    selectedCalendarDate.toISOString().slice(0, 10)
);
    setDraggedCalendarItemId(item.id);
}}
    onDragEnd={() => setDraggedCalendarItemId(null)}
    className="rounded-lg border px-3 py-2 text-[11px] cursor-grab active:cursor-grabbing w-[220px] max-w-full overflow-hidden"
                            style={{
    borderColor: getCalendarItemBorderColor(item),
    boxShadow: `0 0 14px ${getCalendarItemBorderColor(item)}55`,
    backgroundColor: `${getCalendarItemFillColor(item)}33`,
    color: "#fff3c4",
}}
                        >
{calendarProfileView === "school" &&
    item.isClassCalendarItem && (
        <div
            className="mb-0.5 truncate text-[9px] font-semibold uppercase tracking-[0.12em]"
            style={{
                color: getCalendarItemBorderColor(item),
            }}
        >
            {item.childName}
        </div>
    )}

<div className="font-semibold break-words">
    {item.emoji
        ? `${item.emoji} `
        : ""}
    {item.title}
</div>

                            <div className="text-[9px] opacity-80">
                                {item.type}
                                {item.time ? ` · ${formatCalendarTime(item.time)}` : ""}
                                {item.endTime ? ` - ${formatCalendarTime(item.endTime)}` : ""}
                            </div>
                            <div className="flex gap-1 mt-1">
    <button
        type="button"
        onClick={(e) => {
            e.stopPropagation();
            setCalendarEditPopupItem({
    ...item,
    visibleOccurrenceDate: selectedCalendarDate.toISOString().slice(0, 10),
});
        }}
        className="rounded-md border border-[#ffd166]/40 px-1.5 py-0.5 text-[9px] text-[#ffd166]"
    >
        Edit
    </button>

    <button
        type="button"
        onClick={(e) => {
            e.stopPropagation();

            deleteCalendarItem(
                item.id,
                selectedCalendarDate.toISOString().slice(0, 10)
            );
        }}
        className="rounded-md border border-red-400/40 px-1.5 py-0.5 text-[9px] text-red-300"
    >
        Delete
    </button>
</div>
                        </div>
                    ))
                )}
            </div>
        </div>

<div className="grid grid-cols-[90px_minmax(0,1fr)]">
    {/* Time labels */}
    <div
        className="relative border-r border-[#ffd166]/20"
        style={{
            height: `${calendarTimelineHeight}px`,
        }}
    >
        {calendarTimelineSlots.map((slot, index) => (
            <div
                key={slot.time}
                className="absolute left-0 right-0 pr-3 text-right"
                style={{
                    top: `${index * calendarTimelineSlotHeight}px`,
                    height: `${calendarTimelineSlotHeight}px`,
                }}
            >
                <span
                    className={
                        slot.isHour
                            ? "text-[11px] font-semibold text-[#ffd166]"
                            : "text-[9px] text-[#6c7a9c]"
                    }
                >
                    {slot.label}
                </span>
            </div>
        ))}
    </div>

    {/* Timeline uses five-minute slots for assignments in Day view. */}
<div
    className="relative bg-black/20"
    style={{
        height: `${calendarTimelineHeight}px`,
    }}
    onDragOver={(event) => {
        event.preventDefault();

        if (!draggedCalendarItemId) return;

        const preview =
            getCalendarDragTimeFromPointer(
                event
            );

        setCalendarDragPreview({
            date: formatLocalDate(
                selectedCalendarDate
            ),
            ...preview,
        });
    }}
    onDragLeave={(event) => {
        if (
            !event.currentTarget.contains(
                event.relatedTarget
            )
        ) {
            setCalendarDragPreview(null);
        }
    }}
    onDrop={(event) => {
        event.preventDefault();

        if (!draggedCalendarItemId) {
            return;
        }

        const preview =
            getCalendarDragTimeFromPointer(
                event
            );

        moveCalendarItemToTime(
            draggedCalendarItemId,
            formatLocalDate(
                selectedCalendarDate
            ),
            preview.time
        );

        setCalendarDragPreview(null);
    }}
>
        {calendarTimelineSlots.map((slot, index) => (
            <div
                key={slot.time}
                className={`absolute left-0 right-0 ${
                    slot.isHour
                        ? "border-t border-[#ffd166]/30"
                        : "border-t border-[#ffd166]/10"
                }`}
                style={{
                    top: `${index * calendarTimelineSlotHeight}px`,
                    height: `${calendarTimelineSlotHeight}px`,
                }}
                
            />
        ))}
{calendarDragPreview?.date ===
    formatLocalDate(
        selectedCalendarDate
    ) && (
    <div
        className="pointer-events-none absolute left-0 right-0 z-[1000] border-t-2 border-[#ffd166] bg-[#ffd166]/10"
        style={{
            top: `${
                (calendarDragPreview.minutes -
                    calendarTimelineStartMinutes) *
                (calendarTimelineSlotHeight /
                    calendarTimelineSlotMinutes)
            }px`,
        }}
    >
        <span className="absolute left-2 -top-3 rounded-lg border border-[#ffd166]/60 bg-black px-2 py-0.5 text-[10px] font-bold text-[#ffd166] shadow-[0_0_14px_rgba(255,209,102,0.35)]">
            {formatCalendarTime(
                calendarDragPreview.time
            )}
        </span>
    </div>
)}
{getCalendarTimedLayout(
    getCalendarItemsForDay(
        selectedCalendarDate.getDate(),
        selectedCalendarDate
    ).filter((item) => item.time)
).map(
    ({
        item,
        startMinutes,
        endMinutes,
        laneIndex,
        laneCount,
    }) => {
        const visibleStartMinutes =
            Math.max(
                startMinutes,
                calendarTimelineStartMinutes
            );

        const visibleEndMinutes =
            Math.min(
                endMinutes,
                calendarTimelineEndMinutes
            );

        if (
            visibleStartMinutes >=
                calendarTimelineEndMinutes ||
            visibleEndMinutes <=
                calendarTimelineStartMinutes
        ) {
            return null;
        }

        const pixelsPerMinute =
            calendarTimelineSlotHeight /
            calendarTimelineSlotMinutes;

        const blockTop =
            (visibleStartMinutes -
                calendarTimelineStartMinutes) *
            pixelsPerMinute;

        const blockHeight =
            Math.max(
                (visibleEndMinutes -
                    visibleStartMinutes) *
                    pixelsPerMinute -
                    4,
                calendarTimelineSlotHeight - 4
            );

        const laneWidthPercent =
            100 / Math.max(laneCount, 1);

        const blockLeft =
            laneCount > 1
                ? `calc(${
                      laneIndex *
                      laneWidthPercent
                  }% + 8px)`
                : "8px";

        const blockWidth =
            laneCount > 1
                ? `calc(${laneWidthPercent}% - 12px)`
                : "min(620px, calc(100% - 16px))";

        return (
            <div
                key={item.id}
                draggable={
                    !item.isVacationRecord &&
                    !item.isScheduledClass && !item.isTodoCalendarItem
                }
                title={
                    item.description ||
                    item.title
                }
                onDragStart={(e) => {
                    if (
                        item.isVacationRecord ||
                        item.isScheduledClass
                    ) {
                        e.preventDefault();

                        toast.error(
                            "Vacation records must be edited, not dragged."
                        );

                        return;
                    }

                    setDraggedCalendarSourceDate(
                        formatLocalDate(
                            selectedCalendarDate
                        )
                    );

                    setDraggedCalendarItemId(
                        item.id
                    );
                }}
                onDragEnd={() =>
                    setDraggedCalendarItemId(
                        null
                    )
                }
                onClick={(e) => {
                    e.stopPropagation();

                    setCalendarEditPopupItem({
                        ...item,
                        visibleOccurrenceDate:
                            formatLocalDate(
                                selectedCalendarDate
                            ),
                    });
                }}
            className="absolute z-10 cursor-pointer overflow-hidden rounded-xl border px-3 py-1.5 text-left text-[10px] transition-all duration-200 hover:z-20 hover:brightness-125"
                style={{
                    top: `${blockTop + 2}px`,
                    left: blockLeft,
                    width: blockWidth,
                    height: `${blockHeight}px`,
                    borderColor:
                        getCalendarItemBorderColor(
                            item
                        ),
                    boxShadow: `0 0 14px ${getCalendarItemBorderColor(
                        item
                    )}55`,
                    backgroundColor: `${getCalendarItemFillColor(
                        item
                    )}44`,
                    color: "#fff3c4",
                }}
            >
                {calendarProfileView ===
                    "school" &&
                    item.isClassCalendarItem && (
                        <div
                            className="truncate text-[9px] font-semibold uppercase tracking-[0.12em]"
                            style={{
                                color: getCalendarItemBorderColor(
                                    item
                                ),
                            }}
                        >
                            {item.childName}
                        </div>
                    )}

                <div className="truncate font-semibold">
                    {item.emoji
                        ? `${item.emoji} `
                        : ""}
                    {item.title}
                </div>

                <div className="truncate text-[9px] opacity-85">
                    {formatCalendarTime(
                        item.time
                    )}

                    {item.endTime
                        ? ` – ${formatCalendarTime(
                              item.endTime
                          )}`
                        : ""}
                </div>
            </div>
        );
    }
)}  
    </div>
</div>
    
    </div>
)}
{calendarViewMode === "week" && (
    <div className="mb-4 min-w-[1500px] overflow-hidden rounded-3xl border border-[#ffd166]/30 bg-[#050711] shadow-[0_0_24px_rgba(124,58,237,0.16)]">
        {/* Week day headings */}
        <div className="grid grid-cols-[90px_repeat(7,minmax(180px,1fr))] border-b border-[#ffd166]/25 bg-black/80">
            <div className="border-r border-[#ffd166]/20 p-3 text-center text-xs font-semibold text-[#ffd166]">
                Time
            </div>

            {weekCells.map((date) => (
                <div
                    key={formatLocalDate(date)}
                    className="border-r border-[#ffd166]/15 p-3 text-center last:border-r-0"
                >
                    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[#ffd166]">
                        {date.toLocaleDateString("default", {
                            weekday: "short",
                        })}
                    </div>

                    <div className="mt-1 text-sm text-[#fff3c4]">
                        {date.toLocaleDateString("default", {
                            month: "short",
                            day: "numeric",
                        })}
                    </div>
                </div>
            ))}
        </div>

        {/* All-day and unscheduled items */}
        <div className="grid grid-cols-[90px_repeat(7,minmax(180px,1fr))] border-b border-[#ffd166]/25 bg-black/40">
            <div className="border-r border-[#ffd166]/20 p-3 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-[#ffd166]">
                All Day
            </div>

            {weekCells.map((date) => {
                const dateString =
                    formatLocalDate(date);

                const allDayItems =
                    getCalendarItemsForDay(
                        date.getDate(),
                        date
                    ).filter(
                        (item) => !item.time
                    );

                return (
                    <div
                        key={`all-day-${dateString}`}
                        className="min-h-[70px] border-r border-[#ffd166]/15 p-2 last:border-r-0"
                        onDragOver={(e) =>
                            e.preventDefault()
                        }
                        onDrop={() => {
                            if (
                                !draggedCalendarItemId
                            ) {
                                return;
                            }

                            moveCalendarItemToAllDay(
                                draggedCalendarItemId,
                                dateString
                            );
                        }}
                    >
                        
                        <div className="max-h-[110px] space-y-1 overflow-y-auto pr-1">
                            {allDayItems.length ===
                            0 ? (
                                <div className="rounded-lg border border-[#ffd166]/10 bg-black/30 px-2 py-1 text-[9px] text-[#6c7a9c]">
                                    No items
                                </div>
                            ) : (
                                allDayItems.map(
                                    (item) => (
                                        <div
                                            key={
                                                item.id
                                            }
                                            draggable={
                                                !item.isVacationRecord &&
                                                !item.isScheduledClass && !item.isTodoCalendarItem
                                            }
                                            title={
                                                item.description ||
                                                item.title
                                            }
                                            onDragStart={(e) => {
                                                if (
                                                    item.isVacationRecord ||
                                                    item.isScheduledClass
                                                ) {
                                                    e.preventDefault();

                                                    toast.error(
                                                        "Vacation records must be edited, not dragged."
                                                    );

                                                    return;
                                                }

                                                setDraggedCalendarSourceDate(
                                                    dateString
                                                );

                                                setDraggedCalendarItemId(
                                                    item.id
                                                );
                                            }}
                                            onDragEnd={() =>
                                                setDraggedCalendarItemId(
                                                    null
                                                )
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();

                                           

                                                if (item.isScheduledClass) {
                                                    return;
                                                }

                                                setCalendarEditPopupItem({
                                                    ...item,
                                                    visibleOccurrenceDate: dateString,
                                                });
                                            }}
                                            className="cursor-pointer overflow-hidden rounded-lg border px-2 py-1 text-left text-[9px]"
                                            style={{
                                                borderColor:
                                                    getCalendarItemBorderColor(
                                                        item
                                                    ),
                                                boxShadow: `0 0 10px ${getCalendarItemBorderColor(
                                                    item
                                                )}44`,
                                                backgroundColor: `${getCalendarItemFillColor(
                                                    item
                                                )}33`,
                                                color: "#fff3c4",
                                            }}
                                        >
                                            {calendarProfileView ===
                                                "school" &&
                                                item.isClassCalendarItem && (
                                                    <div
                                                        className="truncate text-[8px] font-semibold uppercase tracking-[0.1em]"
                                                        style={{
                                                            color: getCalendarItemBorderColor(
                                                                item
                                                            ),
                                                        }}
                                                    >
                                                        {
                                                            item.childName
                                                        }
                                                    </div>
                                                )}

                                            <div className="truncate font-semibold">
                                                {item.emoji
                                                    ? `${item.emoji} `
                                                    : ""}
                                                {
                                                    item.title
                                                }
                                            </div>
                                        </div>
                                    )
                                )
                            )}
                        </div>
                    </div>
                );
            })}
        </div>

        {/* Fifteen-minute weekly timeline */}
        <div className="grid grid-cols-[90px_repeat(7,minmax(180px,1fr))]">
            {/* Time labels */}
            <div
                className="relative border-r border-[#ffd166]/20 bg-black/50"
                style={{
                    height: `${calendarTimelineHeight}px`,
                }}
            >
                {calendarTimelineSlots.map(
                    (slot, index) => (
                        <div
                            key={`week-label-${slot.time}`}
                            className="absolute left-0 right-0 pr-3 text-right"
                            style={{
                                top: `${
                                    index *
                                    calendarTimelineSlotHeight
                                }px`,
                                height: `${calendarTimelineSlotHeight}px`,
                            }}
                        >
                            <span
                                className={
                                    slot.isHour
                                        ? "text-[10px] font-semibold text-[#ffd166]"
                                        : "text-[8px] text-[#6c7a9c]"
                                }
                            >
                                {slot.label}
                            </span>
                        </div>
                    )
                )}
            </div>

            {/* Seven timed day columns */}
            {weekCells.map((date) => {
                const dateString =
                    formatLocalDate(date);

                const timedItems =
                    getCalendarItemsForDay(
                        date.getDate(),
                        date
                    ).filter(
                        (item) => item.time
                    );

                const positionedItems =
                    getCalendarTimedLayout(
                        timedItems
                    );

                return (
                    <div
key={`week-timeline-${dateString}`}
className="relative border-r border-[#ffd166]/15 bg-black/20 last:border-r-0"
style={{
    height: `${calendarTimelineHeight}px`,
}}
onDragOver={(event) => {
    event.preventDefault();

    if (!draggedCalendarItemId) return;

    const preview =
        getCalendarDragTimeFromPointer(event);

    setCalendarDragPreview({
        date: dateString,
        ...preview,
    });
}}
onDragLeave={(event) => {
    if (
        !event.currentTarget.contains(
            event.relatedTarget
        )
    ) {
        setCalendarDragPreview(null);
    }
}}
onDrop={(event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!draggedCalendarItemId) return;

    const preview =
        getCalendarDragTimeFromPointer(event);

    moveCalendarItemToTime(
        draggedCalendarItemId,
        dateString,
        preview.time
    );

    setCalendarDragPreview(null);
}}
>
    {calendarTimelineSlots.map(
        (slot, index) => (
            <div
                key={`${dateString}-${slot.time}`}
                className={`absolute left-0 right-0 ${
                    slot.isHour
                        ? "border-t border-[#ffd166]/30"
                        : "border-t border-[#ffd166]/10"
                }`}
                style={{
                    top: `${
                        index *
                        calendarTimelineSlotHeight
                    }px`,
                    height: `${calendarTimelineSlotHeight}px`,
                }}
            />
        )
    )}

    {calendarDragPreview?.date ===
        dateString && (
        <div
            className="pointer-events-none absolute left-0 right-0 z-[1000] border-t-2 border-[#ffd166] bg-[#ffd166]/10"
            style={{
                top: `${
                    (calendarDragPreview.minutes -
                        calendarTimelineStartMinutes) *
                    (calendarTimelineSlotHeight /
                        calendarTimelineSlotMinutes)
                }px`,
            }}
        >
            <span className="absolute left-2 -top-3 rounded-lg border border-[#ffd166]/60 bg-black px-2 py-0.5 text-[10px] font-bold text-[#ffd166] shadow-[0_0_14px_rgba(255,209,102,0.35)]">
                {formatCalendarTime(
                    calendarDragPreview.time
                )}
            </span>
        </div>
    )}

                        {positionedItems.map(
                            ({
                                item,
                                startMinutes,
                                endMinutes,
                                laneIndex,
                                laneCount,
                            }) => {
                                const visibleStartMinutes =
                                    Math.max(
                                        startMinutes,
                                        calendarTimelineStartMinutes
                                    );

                                const visibleEndMinutes =
                                    Math.min(
                                        endMinutes,
                                        calendarTimelineEndMinutes
                                    );

                                if (
                                    visibleStartMinutes >=
                                        calendarTimelineEndMinutes ||
                                    visibleEndMinutes <=
                                        calendarTimelineStartMinutes
                                ) {
                                    return null;
                                }

                                const pixelsPerMinute =
                                    calendarTimelineSlotHeight /
                                    calendarTimelineSlotMinutes;

                                const blockTop =
                                    (visibleStartMinutes -
                                        calendarTimelineStartMinutes) *
                                    pixelsPerMinute;

                                const blockHeight =
                                    Math.max(
                                        (visibleEndMinutes -
                                            visibleStartMinutes) *
                                            pixelsPerMinute -
                                            4,
                                        calendarTimelineSlotHeight -
                                            4
                                    );

                                const laneWidth =
                                    100 /
                                    Math.max(
                                        laneCount,
                                        1
                                    );

                                return (
                                    <div
                                        key={
                                            item.id
                                        }
                                        draggable={
                                            !item.isVacationRecord &&
                                            !item.isScheduledClass && !item.isTodoCalendarItem
                                        }
                                        title={
                                            item.description ||
                                            item.title
                                        }
                                        onDragStart={(e) => {
                                            if (
                                                item.isVacationRecord ||
                                                item.isScheduledClass
                                            ) {
                                                e.preventDefault();
                                                
                                                toast.error(
                                                    item.isScheduledClass
                                                        ? "Class days are managed through the subject schedule."
                                                        : "Vacation records must be edited, not dragged."
                                                );

                                                return;
                                            }

                                            setDraggedCalendarSourceDate(
                                                dateString
                                            );

                                            setDraggedCalendarItemId(
                                                item.id
                                            );
                                        }}
                                        onDragEnd={() =>
                                            setDraggedCalendarItemId(
                                                null
                                            )
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            if (item.isScheduledClass) {
                                                return;
                                            }

                                            setCalendarEditPopupItem({
                                                ...item,
                                                visibleOccurrenceDate: dateString,
                                            });
                                        }}
                                        className="absolute z-10 cursor-pointer overflow-hidden rounded-lg border px-2 py-1 text-left text-[9px] transition-all duration-200 hover:z-30 hover:brightness-125"
                                        style={{
                                            top: `${
                                                blockTop +
                                                2
                                            }px`,
                                            height: `${blockHeight}px`,
                                            left: `calc(${
                                                laneIndex *
                                                laneWidth
                                            }% + 3px)`,
                                            width: `calc(${laneWidth}% - 6px)`,
                                            borderColor:
                                                getCalendarItemBorderColor(
                                                    item
                                                ),
                                            boxShadow: `0 0 12px ${getCalendarItemBorderColor(
                                                item
                                            )}55`,
                                            backgroundColor: `${getCalendarItemFillColor(
                                                item
                                            )}44`,
                                            color: "#fff3c4",
                                        }}
                                    >
                                        {calendarProfileView ===
                                            "school" &&
                                            item.isClassCalendarItem && (
                                                <div
                                                    className="truncate text-[8px] font-semibold uppercase tracking-[0.08em]"
                                                    style={{
                                                        color: getCalendarItemBorderColor(
                                                            item
                                                        ),
                                                    }}
                                                >
                                                    {
                                                        item.childName
                                                    }
                                                </div>
                                            )}

                                        <div className="truncate font-semibold">
                                            {item.emoji
                                                ? `${item.emoji} `
                                                : ""}
                                            {
                                                item.title
                                            }
                                        </div>

                                        <div className="truncate text-[8px] opacity-85">
                                            {formatCalendarTime(
                                                item.time
                                            )}

                                            {item.endTime
                                                ? ` – ${formatCalendarTime(
                                                      item.endTime
                                                  )}`
                                                : ""}
                                        </div>
                                    </div>
                                );
                            }
                        )}
                    </div>
                );
            })}
        </div>
    </div>
)}            
{calendarViewMode === "month" && (
    <div
        className={`grid gap-4 text-center text-xs rounded-3xl border border-[#ffd166]/30 p-5 bg-transparent ${
            calendarViewMode === "day"
                ? "grid-cols-1 min-w-[900px]"
                : "grid-cols-7 min-w-[1400px]"
        }`}
    >
        {(calendarViewMode === "month"
            ? calendarCells
            : calendarViewMode === "week"
            ? weekCells
            : [dayCell]
        ).map((cell, index) => {
            const day = cell instanceof Date ? cell.getDate() : cell;

            const cellDate =
                cell instanceof Date
                    ? cell
                    : day
                    ? new Date(visibleYear, visibleMonth, day)
                    : null;

            const itemsForCell =
    calendarViewMode === "month"
        ? getMonthCalendarItemsForDay(day, cellDate)
        : getCalendarItemsForDay(day, cellDate);
            const cellDateString = cellDate ? formatLocalDate(cellDate) : "";
            const savedAttendance = getSavedAttendanceForDate(cellDateString);

            return (
                <div
                    key={index}
                    onClick={() => {
                        if (cellDate) {
                            setSelectedCalendarDate(cellDate);
                        }
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                        if (!day || !draggedCalendarItemId || !cellDate) return;

                        const targetDate = cellDate.toISOString().slice(0, 10);

                        moveCalendarItemToDate(draggedCalendarItemId, targetDate);
                    }}
                    className={`
                        rounded-2xl
                        border
                        border-[#ffd166]/30
                        bg-[#080b14]/95
                        p-3
                        text-left
                        shadow-[0_0_18px_rgba(124,58,237,0.22)]
                        hover:border-[#ffd166]/70
                        hover:shadow-[0_0_24px_rgba(255,209,102,0.20)]
                        transition-all
                        duration-200
                        ${
                            calendarViewMode === "day"
                                ? "min-h-[900px]"
                                : "min-h-[190px]"
                        }
                    `}
                >
                    {day && (
                        <>
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-[#ffd166] font-semibold">
                                    {day}
                                </span>

                            <div className="flex gap-1">
    <button
        type="button"
        onClick={(e) => {
            e.stopPropagation();

            saveAttendanceForDate(
                calendarProfileView,
                cellDateString,
                "present"
            );
        }}
        className={`rounded-xl border px-2 py-1 text-[10px] font-semibold transition-all duration-200 ${
    savedAttendanceMap[cellDateString]?.status === "present"
        ? "border-[#ffd166] bg-[#ffd166]/20 text-[#ffd166] shadow-[0_0_18px_rgba(255,209,102,0.45)]"
        : "border-[#ffd166]/25 bg-black text-[#fff3c4] hover:border-[#ffd166]/60 hover:bg-[#ffd166]/10"
}`}
    >
        P
    </button>

    <button
        type="button"
        onClick={(e) => {
    e.stopPropagation();

    openAbsenceActionPopup(
        calendarProfileView,
        cellDateString
    );
}}
       className={`rounded-xl border px-2 py-1 text-[10px] font-semibold transition-all duration-200 ${
    savedAttendanceMap[cellDateString]?.status === "absent"
        ? "border-[#60a5fa] bg-[#60a5fa]/20 text-[#93c5fd] shadow-[0_0_18px_rgba(96,165,250,0.45)]"
        : "border-[#ffd166]/25 bg-black text-[#fff3c4] hover:border-[#60a5fa]/60 hover:bg-[#60a5fa]/10"
}`}
    >
        A
    </button>
</div>
                            </div>

                            <div
                                className={`mt-2 pr-1 ${
                                    calendarViewMode === "day"
                                        ? "flex flex-wrap items-start gap-3"
                                        : calendarViewMode === "month"
                                        ? "space-y-1 max-h-[130px] overflow-y-auto"
                                        : "space-y-1 max-h-[110px] overflow-y-auto"
                                }`}
                            >
                                {itemsForCell.length === 0 ? (
                                    <div className="rounded-lg border border-[#ffd166]/20 bg-black/60 px-2 py-1 text-[10px] text-[#6c7a9c]">
                                        No items
                                    </div>
                                ) : (
                                    itemsForCell.map((item) => (
                                        <div
                                            key={item.id}
                                            draggable={
                                                item.type !== "vacation" &&
                                                !item.isVacationRecord &&
                                                !item.isScheduledClass && !item.isTodoCalendarItem
                                            }
                                                onDragStart={(e) => {
                                                    if (item.isScheduledClass) {
                                                        e.preventDefault();
                                                        return;
                                                    }

                                                    if (item.isVacationRecord) {
                                                 setCalendarEditPopupItem({
    ...item,
    visibleOccurrenceDate: selectedCalendarDate.toISOString().slice(0, 10),
});
                                                     return;
                                                     }
                                            setDraggedCalendarSourceDate(
                                              cellDate.toISOString().slice(0, 10)
                                                                    );
                                             setDraggedCalendarItemId(item.id);
                                                    }}
                                            onClick={(e) => {
                                             e.stopPropagation();

                                             if (item.isVacationRecord) {
                                           setCalendarEditPopupItem({
    ...item,
    visibleOccurrenceDate: selectedCalendarDate.toISOString().slice(0, 10),
});
                                             }
                                                }}
                                            className={`rounded-lg border px-2 py-1 text-[10px] cursor-grab active:cursor-grabbing max-w-full overflow-hidden ${
                                                calendarViewMode === "day"
                                                    ? "w-[220px]"
                                                    : "w-full"
                                            }`}
                                           style={{
                                            borderColor: getCalendarItemBorderColor(item),
                                            boxShadow: `0 0 14px ${getCalendarItemBorderColor(item)}55`,
                                            backgroundColor: `${getCalendarItemFillColor(item)}33`,
                                             color: "#fff3c4",
                                                }}
                                        >
                                            <div className="font-semibold break-words">
                                                {item.emoji
                                                    ? `${item.emoji} `
                                                    : ""}
                                                {item.title}
                                            </div>

                                            <div className="text-[9px] opacity-80">
                                                {item.type}
                                                {item.time
                                                    ? ` · ${formatCalendarTime(
                                                          item.time
                                                      )}`
                                                    : ""}
                                                {item.endTime
                                                    ? ` - ${formatCalendarTime(
                                                          item.endTime
                                                      )}`
                                                    : ""}
                                            </div>
                                        {!item.isScheduledClass && (
                                            <div className="flex gap-1 mt-1">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setCalendarEditPopupItem({
                                                            ...item,
                                                            visibleOccurrenceDate: cellDate.toISOString().slice(0, 10),
                                                        });
                                                    }}
                                                    className="rounded-md border border-[#ffd166]/40 px-1.5 py-0.5 text-[9px] text-[#ffd166]"
                                                >
                                                    Edit
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();

                                                        deleteCalendarItem(
                                                            item.id,
                                                            cellDate.toISOString().slice(0, 10)
                                                        );
                                                    }}
                                                    className="rounded-md border border-red-400/40 px-1.5 py-0.5 text-[9px] text-red-300"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                        </div>
                                        ))
                                        )}
                                        </div>

                                        </>
                                        )}
                                        </div>
                                        );
                                        })}
                                        </div>
    )}
</section>
    </div>
    {absenceActionPopup && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
        <div className="w-full max-w-2xl max-h-[86vh] overflow-hidden rounded-[32px] border border-[#ffd166]/50 bg-black shadow-[0_0_45px_rgba(255,209,102,0.24)] flex flex-col">
            <div className="border-b border-[#ffd166]/25 px-6 py-5">
                <h3 className="font-display text-3xl text-[#ffd166] text-center">
                    Absence & Recovery Plan
                </h3>

                <p className="mt-2 text-center text-sm text-[#b0b8d6]">
                    Record today's absence, then choose how Shadow Grove should handle any missed lessons, sessions, and quests.
                </p>
                <div className="mt-4 rounded-2xl border border-[#ffd166]/25 bg-black px-4 py-3 text-center">
    <div className="font-display text-xl text-[#ffd166]">
        {getAbsenceChildName()}
    </div>

    <div className="mt-1 text-sm text-[#fff3c4]">
        {getAbsenceDateLabel()}
    </div>
</div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pt-5 pb-8 space-y-3">
                <div className="rounded-[24px] border border-[#ffd166]/25 bg-[#050711] p-4">
    <div className="mb-4 rounded-2xl border border-[#ffd166]/25 bg-black p-4">
    <Label>Absence Duration</Label>

    <div className="mt-3 flex flex-wrap gap-2">
        <button
            type="button"
            onClick={() =>
                setAbsenceRecoveryForm((prev) => ({
                    ...prev,
                    multiDay: false,
                    endDate: "",
                }))
            }
            className={`${purpleActionBtn} ${
                !absenceRecoveryForm.multiDay
                    ? "!border-[#ffd166] !bg-[#ffd166]/20 !text-[#ffd166]"
                    : ""
            }`}
        >
            Single Day
        </button>

        <button
            type="button"
            onClick={() =>
                setAbsenceRecoveryForm((prev) => ({
                    ...prev,
                    multiDay: true,
                    endDate: prev.endDate || absenceActionPopup?.dateString || "",
                }))
            }
            className={`${purpleActionBtn} ${
                absenceRecoveryForm.multiDay
                    ? "!border-[#ffd166] !bg-[#ffd166]/20 !text-[#ffd166]"
                    : ""
            }`}
        >
            Multiple Days
        </button>
    </div>

    {absenceRecoveryForm.multiDay && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
                <Label>From Date</Label>
                <Input
                    type="date"
                    value={absenceActionPopup?.dateString || ""}
                    disabled
                    className={shadowInput}
                />
            </div>

            <div>
                <Label>To Date</Label>
                <Input
                    type="date"
                    value={absenceRecoveryForm.endDate}
                    onChange={(e) =>
                        setAbsenceRecoveryForm((prev) => ({
                            ...prev,
                            endDate: e.target.value,
                        }))
                    }
                    className={shadowInput}
                />
            </div>
        </div>
    )}
</div>

    <select
        value={absenceRecoveryForm.reason}
        onChange={(e) =>
            setAbsenceRecoveryForm((prev) => ({
                ...prev,
                reason: e.target.value,
            }))
        }
        className={shadowInput}
    >
        <option value="sick">🤒 Sick</option>
        <option value="appointment">🩺 Appointment</option>
        <option value="vacation">🏖 Vacation</option>
        <option value="family_emergency">🚨 Family Emergency</option>
        <option value="mental_health">🌿 Mental Health Day</option>
        <option value="other">📌 Other</option>
    </select>

    <Label className="mt-4 block">Parent Notes</Label>

    <textarea
        value={absenceRecoveryForm.note}
        onChange={(e) =>
            setAbsenceRecoveryForm((prev) => ({
                ...prev,
                note: e.target.value,
            }))
        }
        placeholder="Optional notes for attendance records..."
        className="mt-2 w-full min-h-[90px] rounded-2xl border border-[#ffd166]/25 bg-black p-3 text-[#fff3c4] outline-none focus:border-[#ffd166]/70 focus:shadow-[0_0_16px_rgba(255,209,102,0.25)]"
    />

    <Label className="mt-4 block">Grading Treatment</Label>

    <select
        value={absenceRecoveryForm.grading}
        onChange={(e) =>
            setAbsenceRecoveryForm((prev) => ({
                ...prev,
                grading: e.target.value,
            }))
        }
        className={shadowInput}
    >
        <option value="leave_ungraded">Leave Ungraded</option>
        <option value="keep_existing">Keep Existing Grades</option>
        <option value="exempt">Mark as Exempt</option>
        <option value="zero_due_work">Record Zero</option>
    </select>
</div>
            <div className="rounded-[24px] border border-[#ffd166]/25 bg-[#050711] p-4">
    <h4 className="font-display text-2xl text-[#ffd166]">
        📋 Recovery Preview
    </h4>

    <div className="mt-4 space-y-2">
        <div className="flex justify-between">
            <span className="text-[#b0b8d6]">Absence Days</span>
            <span className="text-[#fff3c4]">
                {getAbsencePreview().dates.length}
            </span>
        </div>

        <div className="flex justify-between">
            <span className="text-[#b0b8d6]">Sessions Affected</span>
            <span className="text-[#fff3c4]">
                {getAbsencePreview().sessions.length}
            </span>
        </div>

        <div className="flex justify-between">
            <span className="text-[#b0b8d6]">Quests Affected</span>
            <span className="text-[#fff3c4]">
                {getAbsencePreview().quests.length}
            </span>
        </div>
    </div>
</div>
              <button
    type="button"
    onClick={(e) => {
        e.stopPropagation();
        setAbsenceRecoveryForm((prev) => ({
            ...prev,
            recoveryPlan: "absent_only",
        }));
    }}
    className={`w-full rounded-2xl border p-4 text-left transition-all duration-200 ${
        absenceRecoveryForm.recoveryPlan === "absent_only"
            ? "border-[#ffd166] bg-[#ffd166]/15 text-[#fff3c4] shadow-[0_0_22px_rgba(255,209,102,0.35)]"
            : "border-[#60a5fa]/50 bg-black text-[#fff3c4] shadow-[0_0_18px_rgba(96,165,250,0.18)] hover:bg-[#60a5fa]/10"
    }`}
>
    <div className="flex items-start justify-between gap-3">
        <div>
            <div className="font-display text-xl text-[#93c5fd]">
                {absenceRecoveryForm.recoveryPlan === "absent_only" ? "✓ " : ""}
                🌙 Just Record the Absence
            </div>

            <p className="mt-1 text-sm text-[#b0b8d6]">
                Attendance is recorded. Nothing on the schedule moves.
            </p>
        </div>

        <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
                e.stopPropagation();
                setAbsenceInfoOpen((prev) =>
                    prev === "absent_only" ? "" : "absent_only"
                );
            }}
            className="rounded-full border border-[#ffd166]/40 bg-black px-2 py-1 text-xs text-[#ffd166] hover:bg-[#ffd166]/10"
        >
            ⓘ
        </span>
    </div>

    <div className="mt-3 flex flex-wrap gap-2">
        <span className="rounded-full border border-[#60a5fa]/40 bg-[#60a5fa]/10 px-3 py-1 text-xs text-[#93c5fd]">
            Attendance Only
        </span>

        <span className="rounded-full border border-[#ffd166]/30 bg-[#ffd166]/10 px-3 py-1 text-xs text-[#ffd166]">
            Schedule Unchanged
        </span>
    </div>

    {absenceInfoOpen === "absent_only" && (
        <div className="mt-3 rounded-2xl border border-[#ffd166]/25 bg-black p-3 text-sm text-[#b0b8d6]">
            The child is marked absent for the selected date or date range.
            Lessons, sessions, quests, grades, and due dates stay where they are.
            Use this when you only want attendance records updated.
        </div>
    )}
</button>  
             <button
    type="button"
    onClick={(e) => {
        e.stopPropagation();
        setAbsenceRecoveryForm((prev) => ({
            ...prev,
            recoveryPlan: "move_today",
        }));
    }}
    className={`w-full rounded-2xl border p-4 text-left transition-all duration-200 ${
        absenceRecoveryForm.recoveryPlan === "move_today"
            ? "border-[#ffd166] bg-[#ffd166]/15 text-[#fff3c4] shadow-[0_0_22px_rgba(255,209,102,0.35)]"
            : "border-[#ffd166]/35 bg-black text-[#fff3c4] hover:bg-[#ffd166]/10"
    }`}
>
    <div className="flex items-start justify-between gap-3">
        <div>
            <div className="font-display text-xl text-[#ffd166]">
                {absenceRecoveryForm.recoveryPlan === "move_today" ? "✓ " : ""}
                🔁 Move Only Today's Lessons
            </div>

            <p className="mt-1 text-sm text-[#b0b8d6]">
                Move only the lessons, sessions, and quests scheduled for the absent day to the next available instructional day.
            </p>
        </div>

        <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
                e.stopPropagation();
                setAbsenceInfoOpen((prev) =>
                    prev === "move_today" ? "" : "move_today"
                );
            }}
            className="rounded-full border border-[#ffd166]/40 bg-black px-2 py-1 text-xs text-[#ffd166] hover:bg-[#ffd166]/10"
        >
            ⓘ
        </span>
    </div>

    <div className="mt-3 flex flex-wrap gap-2">
        <span className="rounded-full border border-[#ffd166]/30 bg-[#ffd166]/10 px-3 py-1 text-xs text-[#ffd166]">
            Missed Day Only
        </span>

        <span className="rounded-full border border-[#ffd166]/30 bg-[#ffd166]/10 px-3 py-1 text-xs text-[#ffd166]">
            Next Class Day
        </span>

        <span className="rounded-full border border-[#60a5fa]/35 bg-[#60a5fa]/10 px-3 py-1 text-xs text-[#93c5fd]">
            Future Schedule Stays
        </span>
    </div>

    {absenceInfoOpen === "move_today" && (
        <div className="mt-3 rounded-2xl border border-[#ffd166]/25 bg-black p-3 text-sm text-[#b0b8d6]">
            Example: If Monday is missed, Monday's work moves to the next valid class day.
            Tuesday's original work stays on Tuesday. The rest of the school year does not shift.
        </div>
    )}
</button>  

         <button
    type="button"
    onClick={(e) => {
        e.stopPropagation();
        setAbsenceRecoveryForm((prev) => ({
            ...prev,
            recoveryPlan: "shift_future",
        }));
    }}
    className={`w-full rounded-2xl border p-4 text-left transition-all duration-200 ${
        absenceRecoveryForm.recoveryPlan === "shift_future"
            ? "border-[#ffd166] bg-[#ffd166]/15 text-[#fff3c4] shadow-[0_0_22px_rgba(255,209,102,0.35)]"
            : "border-[#ffd166]/35 bg-black text-[#fff3c4] hover:bg-[#ffd166]/10"
    }`}
>
    <div className="flex items-start justify-between gap-3">
        <div>
            <div className="font-display text-xl text-[#ffd166]">
                {absenceRecoveryForm.recoveryPlan === "shift_future" ? "✓ " : ""}
                📅 Shift the Entire Schedule
            </div>

            <p className="mt-1 text-sm text-[#b0b8d6]">
                Move the rest of the school year forward. Shadow Grove will skip holidays, vacations, sick days, and non-class days.
            </p>
        </div>

        <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
                e.stopPropagation();
                setAbsenceInfoOpen((prev) =>
                    prev === "shift_future" ? "" : "shift_future"
                );
            }}
            className="rounded-full border border-[#ffd166]/40 bg-black px-2 py-1 text-xs text-[#ffd166] hover:bg-[#ffd166]/10"
        >
            ⓘ
        </span>
    </div>

    <div className="mt-3 flex flex-wrap gap-2">
        <span className="rounded-full border border-[#ffd166]/30 bg-[#ffd166]/10 px-3 py-1 text-xs text-[#ffd166]">
            Entire Schedule
        </span>

        <span className="rounded-full border border-[#60a5fa]/35 bg-[#60a5fa]/10 px-3 py-1 text-xs text-[#93c5fd]">
            Skips Holidays
        </span>

        <span className="rounded-full border border-[#60a5fa]/35 bg-[#60a5fa]/10 px-3 py-1 text-xs text-[#93c5fd]">
            Skips Vacations
        </span>

        <span className="rounded-full border border-[#60a5fa]/35 bg-[#60a5fa]/10 px-3 py-1 text-xs text-[#93c5fd]">
            Instructional Days
        </span>
    </div>

    {absenceInfoOpen === "shift_future" && (
        <div className="mt-3 rounded-2xl border border-[#ffd166]/25 bg-black p-3 text-sm text-[#b0b8d6]">
            Example: If Monday is missed, Tuesday becomes Monday's lesson day,
            Wednesday becomes Tuesday's lesson day, and the rest of the plan moves forward.
            Shadow Grove automatically skips blocked days.
        </div>
    )}
</button>      
            </div>

            <div className="shrink-0 border-t border-[#ffd166]/25 bg-black px-6 py-4 flex flex-wrap justify-end gap-3">
    <button
    type="button"
    onClick={applyAbsenceRecoveryPlan}
    className={purpleActionBtn}
>
    {absenceRecoveryForm.recoveryPlan === "absent_only" && "Record Absence"}
    {absenceRecoveryForm.recoveryPlan === "move_today" && "Move Today's Lessons"}
    {absenceRecoveryForm.recoveryPlan === "shift_future" && "Shift Schedule"}
</button>

    <button
        type="button"
        onClick={() => setAbsenceActionPopup(null)}
        className={purpleActionBtn}
    >
        Cancel
    </button>
</div>
        </div>
    </div>
)}

{sessionMovePopup && (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 px-4 backdrop-blur-sm">
        <div className="w-full max-w-xl rounded-[32px] border border-[#ffd166]/50 bg-black p-6 shadow-[0_0_45px_rgba(255,209,102,0.22)]">
            <h3 className="mb-4 text-center font-display text-3xl text-[#ffd166]">
                Move Session Block?
            </h3>

            <p className="mb-6 text-center text-sm text-[#d8dee9]">
                This block belongs to a complete lesson session. Move only
                this block, or move every block in the session together?
            </p>

            <div className="flex flex-col gap-3">
                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => {
                        const storedEvents =
                            getShadowGroveEvents();

                        const updatedEvents =
                            storedEvents.map((event) => {
                                if (
                                    String(event.id) !==
                                    String(
                                        sessionMovePopup.sourceEvent.id
                                    )
                                ) {
                                    return event;
                                }

                                return updateShadowGroveEventTime(
                                    updateShadowGroveEventDate(
                                        event,
                                        sessionMovePopup.targetDate
                                    ),
                                    sessionMovePopup.targetTime
                                );
                            });

                        saveShadowGroveEvents(
                            updatedEvents
                        );

                        setSchoolCalendar(
                            (currentCalendar) => ({
                                ...currentCalendar,
                            })
                        );

                        setSessionMovePopup(null);
                        setDraggedCalendarItemId(null);
                        setDraggedCalendarSourceDate("");

                        toast.success(
                            "Session block moved."
                        );
                    }}
                >
                    Move Only This Block
                </button>

                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => {
                        const storedEvents =
                            getShadowGroveEvents();

                        const sourceEvent =
                            sessionMovePopup.sourceEvent;

                        const sessionEvents =
                            getShadowGroveSessionFlowEvents(
                                sourceEvent,
                                storedEvents
                            );

                        const sourceStartTime =
                            sourceEvent.scheduled_start_time ||
                            sourceEvent.start_time ||
                            sourceEvent.due_time ||
                            "";

                        const sourceStartMinutes =
                            timeToMinutes(
                                sourceStartTime
                            );

                        const targetStartMinutes =
                            timeToMinutes(
                                sessionMovePopup.targetTime
                            );

                        if (
                            sourceStartMinutes === null ||
                            targetStartMinutes === null
                        ) {
                            toast.error(
                                "Could not calculate the session time."
                            );
                            return;
                        }

                        const minuteOffset =
                            targetStartMinutes -
                            sourceStartMinutes;

                        const sessionEventIds =
                            new Set(
                                sessionEvents.map(
                                    (event) =>
                                        String(event.id)
                                )
                            );

                        const toTimeString = (
                            totalMinutes
                        ) => {
                            const normalizedMinutes =
                                ((totalMinutes % 1440) +
                                    1440) %
                                1440;

                            const hours =
                                Math.floor(
                                    normalizedMinutes /
                                        60
                                );

                            const minutes =
                                normalizedMinutes % 60;

                            return `${String(
                                hours
                            ).padStart(
                                2,
                                "0"
                            )}:${String(
                                minutes
                            ).padStart(2, "0")}`;
                        };

                        const updatedEvents =
                            storedEvents.map((event) => {
                                if (
                                    !sessionEventIds.has(
                                        String(event.id)
                                    )
                                ) {
                                    return event;
                                }

                                const eventStartTime =
                                    event.scheduled_start_time ||
                                    event.start_time ||
                                    event.due_time ||
                                    "";

                                const eventStartMinutes =
                                    timeToMinutes(
                                        eventStartTime
                                    );

                                const eventWithNewDate =
                                    updateShadowGroveEventDate(
                                        event,
                                        sessionMovePopup.targetDate
                                    );

                                if (
                                    eventStartMinutes === null
                                ) {
                                    return eventWithNewDate;
                                }

                                const shiftedStartTime =
                                    toTimeString(
                                        eventStartMinutes +
                                            minuteOffset
                                    );

                                return updateShadowGroveEventTime(
                                    eventWithNewDate,
                                    shiftedStartTime
                                );
                            });

                        saveShadowGroveEvents(
                            updatedEvents
                        );

                        setSchoolCalendar(
                            (currentCalendar) => ({
                                ...currentCalendar,
                            })
                        );

                        setSessionMovePopup(null);
                        setDraggedCalendarItemId(null);
                        setDraggedCalendarSourceDate("");

                        toast.success(
                            "Entire session moved."
                        );
                    }}
                >
                    Move Entire Session
                </button>

                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => {
                        setSessionMovePopup(null);
                        setDraggedCalendarItemId(null);
                        setDraggedCalendarSourceDate("");
                    }}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}
{sessionDeletePopup && (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 px-4 backdrop-blur-sm">
        <div className="w-full max-w-xl rounded-[32px] border border-red-400/50 bg-black p-6 shadow-[0_0_45px_rgba(248,113,113,0.22)]">
            <h3 className="mb-4 text-center font-display text-3xl text-red-300">
                Delete Session Block?
            </h3>

            <p className="mb-6 text-center text-sm text-[#d8dee9]">
                This block belongs to a complete lesson session. Delete only
                this block, or delete every block in this session?
            </p>

            <div className="flex flex-col gap-3">
                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => {
                        const storedEvents =
                            getShadowGroveEvents();

                        const updatedEvents =
                            storedEvents.filter(
                                (event) =>
                                    String(event.id) !==
                                    String(
                                        sessionDeletePopup.originalEventId
                                    )
                            );

                        saveShadowGroveEvents(
                            updatedEvents
                        );

                        setSchoolCalendar(
                            (currentCalendar) => ({
                                ...currentCalendar,
                            })
                        );

                        setSessionDeletePopup(null);
                        setDraggedCalendarItemId(null);
                        setDraggedCalendarSourceDate("");

                        toast.success(
                            "Session block deleted."
                        );
                    }}
                >
                    Delete Only This Block
                </button>

                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => {
                        const storedEvents =
                            getShadowGroveEvents();

                        const sessionEvents =
                            getShadowGroveSessionFlowEvents(
                                sessionDeletePopup.sourceEvent,
                                storedEvents
                            );

                        const sessionEventIds =
                            new Set(
                                sessionEvents.map(
                                    (event) =>
                                        String(event.id)
                                )
                            );

                        const updatedEvents =
                            storedEvents.filter(
                                (event) =>
                                    !sessionEventIds.has(
                                        String(event.id)
                                    )
                            );

                        saveShadowGroveEvents(
                            updatedEvents
                        );

                        setSchoolCalendar(
                            (currentCalendar) => ({
                                ...currentCalendar,
                            })
                        );

                        setSessionDeletePopup(null);
                        setDraggedCalendarItemId(null);
                        setDraggedCalendarSourceDate("");

                        toast.success(
                            "Entire session deleted."
                        );
                    }}
                >
                    Delete Entire Session
                </button>

                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => {
                        setSessionDeletePopup(null);
                        setDraggedCalendarItemId(null);
                        setDraggedCalendarSourceDate("");
                    }}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}
            {recurringMovePopup && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
        <div className="w-full max-w-xl rounded-[32px] border border-[#ffd166]/50 bg-black p-6 shadow-[0_0_45px_rgba(255,209,102,0.22)]">
           <h3 className="font-display text-3xl text-[#ffd166] text-center mb-4">
    {recurringMovePopup.mode === "time"
        ? "Move Recurring Time Slot?"
        : recurringMovePopup.mode === "allDay"
        ? "Move Recurring Event to All Day?"
        : "Move Recurring Schedule?"}
</h3>

           <p className="text-sm text-[#b0b8d6] text-center mb-6">
    {recurringMovePopup.mode === "time"
        ? "Do you want to change only this scheduled occurrence, or update the entire repeating schedule time?"
        : recurringMovePopup.mode === "allDay"
        ? "Do you want to move only this occurrence into the all-day area, or move the full repeating schedule?"
        : "Do you want to move only this one occurrence, or reschedule the entire repeating event?"}
</p>

            <div className="flex flex-wrap justify-center gap-3">
                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => applyRecurringMoveChoice("one_time")}
                >
                    One Time Only
                </button>

              <button
    type="button"
    className={`${purpleActionBtn} !border-[#ff8a65]/60 !bg-[#ff8a65]/10 hover:!bg-[#ff8a65]/20`}
    onClick={() => applyRecurringMoveChoice("weekly")}
>
   This And Future Events
</button>

                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => setRecurringMovePopup(null)}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}
            {recurringDeletePopup && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
        <div className="w-full max-w-xl rounded-[32px] border border-red-400/50 bg-black p-6 shadow-[0_0_45px_rgba(248,113,113,0.22)]">
            <h3 className="font-display text-3xl text-red-300 text-center mb-4">
                Delete Recurring Event?
            </h3>

            <p className="text-sm text-[#b0b8d6] text-center mb-6">
                Do you want to delete only this occurrence, or delete the entire repeating schedule?
            </p>

            <div className="flex flex-wrap justify-center gap-3">
                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => applyRecurringDeleteChoice("one_time")}
                >
                    Delete This Occurrence
                </button>

                <button
                    type="button"
                    className={`${purpleActionBtn} !border-red-400/60 !bg-red-500/10 hover:!bg-red-500/20`}
                    onClick={() => applyRecurringDeleteChoice("all")}
                >
                    Delete Entire Schedule
                </button>

                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => setRecurringDeletePopup(null)}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}

             {recurringEditPopup && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
        <div className="w-full max-w-xl rounded-[32px] border border-[#ffd166]/50 bg-black p-6 shadow-[0_0_45px_rgba(255,209,102,0.22)]">
            <h3 className="font-display text-3xl text-[#ffd166] text-center mb-4">
                Edit Recurring Event?
            </h3>

            <p className="text-sm text-[#b0b8d6] text-center mb-6">
                Do you want to edit only this occurrence, this and future events, or the entire series?
            </p>

            <div className="flex flex-wrap justify-center gap-3">
                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => applyRecurringEditChoice("one_time")}
                >
                    This Occurrence
                </button>

                <button
                    type="button"
                    className={`${purpleActionBtn} !border-[#ff8a65]/60 !bg-[#ff8a65]/10 hover:!bg-[#ff8a65]/20`}
                    onClick={() => applyRecurringEditChoice("future")}
                >
                    This And Future Events
                </button>

                <button
                    type="button"
                    className={`${purpleActionBtn} !border-red-400/60 !bg-red-500/10 hover:!bg-red-500/20`}
                    onClick={() => applyRecurringEditChoice("all")}
                >
                    Entire Series
                </button>

                <button
                    type="button"
                    className={purpleActionBtn}
                    onClick={() => setRecurringEditPopup(null)}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}
            {calendarEditPopupItem?.isTodoCalendarItem && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"><div className="max-w-lg space-y-4 rounded-2xl border border-[#ffd166]/40 bg-black p-6 text-white"><h3 className="font-display text-xl text-[#ffd166]">{calendarEditPopupItem.title}</h3><p>{calendarEditPopupItem.description}</p><p className="text-sm text-[#b0b8d6]">Scheduled {calendarEditPopupItem.start}. Edit the date, title, or assignment in the dashboard to-do list.</p><button className={purpleActionBtn} onClick={() => { setCalendarEditPopupItem(null); setOpenPanel(null); }}>Open dashboard</button><button className={purpleActionBtn} onClick={() => setCalendarEditPopupItem(null)}>Close</button></div></div>}
            {pendingAssignmentDelete && <DeleteAssignmentDialog event={pendingAssignmentDelete} onClose={() => setPendingAssignmentDelete(null)} onDelete={async (scope) => {
    await deleteSharedAssignment(pendingAssignmentDelete, scope);
    setSchoolCalendar((current) => ({ ...current }));
    setDraggedCalendarItemId(null); setDraggedCalendarSourceDate("");
    toast.success(scope === "series" ? "All assignments in this loop deleted." : "Assignment deleted.");
    return true;
}} />}
            {calendarEditPopupItem && (calendarEditPopupItem.lesson_id || calendarEditPopupItem.lesson_sections?.length) && <div role="dialog" aria-modal="true" aria-label="Class lesson details" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"><div className="max-h-[90vh] w-full max-w-3xl space-y-4 overflow-auto rounded-[32px] border border-[#ffd166]/50 bg-black p-6 shadow-[0_0_45px_rgba(255,209,102,0.22)]"><h3 className="font-display text-3xl text-[#ffd166]">{calendarEditPopupItem.title}</h3><LessonCalendarDetails event={calendarEditPopupItem} /><div className="flex gap-2"><button type="button" className={purpleActionBtn} onClick={() => navigate(`/parent/classroom/${calendarEditPopupItem.child_id}/${calendarEditPopupItem.subject_id}?panel=lessonStage&date=${calendarEditPopupItem.date_start || calendarEditPopupItem.start}`)}>Open Lesson Stage</button><button type="button" className={purpleActionBtn} onClick={() => setCalendarEditPopupItem(null)}>Close</button></div></div></div>}
            {calendarEditPopupItem && !calendarEditPopupItem.isTodoCalendarItem &&
                !calendarEditPopupItem.lesson_id && !calendarEditPopupItem.lesson_sections?.length &&
                !calendarEditPopupItem.isScheduledClass &&
                calendarEditPopupItem.type !== "scheduled_class" && (
   <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
    <div className="w-full max-w-3xl rounded-[32px] border border-[#ffd166]/50 bg-black p-6 shadow-[0_0_45px_rgba(255,209,102,0.22)]">
            
                    <h3 className="font-display text-3xl text-[#ffd166] text-center mb-6">
    Edit Calendar Item
</h3>
<div>
    <Label>Emoji / Symbol</Label>     

</div>
<div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
    <div>
        <Label>Name</Label>
        <Input
            value={calendarEditPopupItem.title || ""}
            onChange={(e) => updateCalendarPopupItem("title", e.target.value)}
          className={shadowInput}
        />
    </div>

    <div>
        <Label>Type</Label>
        <select
            value={calendarEditPopupItem.type || "appointment"}
            onChange={(e) => updateCalendarPopupItem("type", e.target.value)}
            className={shadowInput}
        >
            <option value="appointment">Appointment</option>
            <option value="holiday">Personal Holiday</option>
            <option value="vacation">Vacation</option>
            <option value="sick">Sick Day</option>
            <option value="extracurricular">Extracurricular</option>
            <option value="coop">Co-op Day</option>
        </select>
    </div>

    <div className="md:col-span-2">
        <LessonCalendarDetails event={calendarEditPopupItem} />
        <Label>Description</Label>
        <textarea
            value={calendarEditPopupItem.description || ""}
            onChange={(e) =>
                updateCalendarPopupItem("description", e.target.value)
            }
            className="mt-1 w-full min-h-[80px] bg-[#080b14] border border-[#1f2942] rounded-xl px-4 py-3 text-white text-sm"
        />
    </div>

    <div>
        <Label>Location</Label>
        <Input
            value={calendarEditPopupItem.location || ""}
            onChange={(e) => updateCalendarPopupItem("location", e.target.value)}
            className={shadowInput}
        />
    </div>

            <div>
    <Label>Start Time</Label>
    <Input
        type="time"
        value={calendarEditPopupItem.time || ""}
        onChange={(e) => updateCalendarPopupItem("time", e.target.value)}
        className={shadowInput}
    />
</div>

<div>
    <Label>End Time</Label>
    <Input
        type="time"
        value={calendarEditPopupItem.endTime || ""}
        onChange={(e) => updateCalendarPopupItem("endTime", e.target.value)}
        className={shadowInput}
    />
</div>

    <div>
        <Label>Start date</Label>
        <Input
            type="date"
            value={calendarEditPopupItem.start || ""}
            onChange={(e) => updateCalendarPopupItem("start", e.target.value)}
            className={shadowInput}
        />
    </div>

    <div>
        <Label>End date</Label>
        <Input
            type="date"
            value={calendarEditPopupItem.end || ""}
            onChange={(e) => updateCalendarPopupItem("end", e.target.value)}
            className={shadowInput}
        />
    </div>

    <div>
        <Label>Frequency</Label>
        <select
            value={calendarEditPopupItem.frequency || "once"}
            onChange={(e) =>
                updateCalendarPopupItem("frequency", e.target.value)
            }
            className={shadowInput}
        >
           <option value="once">One time</option>
            <option value="through">Through date range</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Every two weeks</option>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
            <option value="custom">Custom</option>
               </select>
                </div>

    <div>
        <Label>Event Color</Label>
        <Input
            type="color"
            value={calendarEditPopupItem.color || "#7c3aed"}
            onChange={(e) => updateCalendarPopupItem("color", e.target.value)}
            className={shadowInput}
        />
    </div>
            <div>
    <Label>Emoji / Symbol</Label>

    <Input
        value={calendarEditPopupItem.emoji || ""}
        onChange={(e) =>
            updateCalendarPopupItem("emoji", e.target.value)
        }
        placeholder="⭐"
        className={shadowInput}
    />
    <details className="mt-2">
    <summary className="cursor-pointer text-sm text-[#ffd166] hover:text-[#fff3c4]">
        More Emojis
    </summary>

    <div className="flex flex-wrap gap-2 mt-3 max-h-[180px] overflow-y-auto pr-1">
        {[
            "⭐",
            "🎵",
            "📚",
            "🏥",
            "🎨",
            "🧪",
            "🏕️",
            "🚌",
            "⚽",
            "🎭",
            "📝",
            "🍎",
            "🎹",
            "📖",
            "🧙",
            "✨",
            "🌙",
            "🔥",
            "🌲",
            "🎬",
            "🖥️",
            "📐",
            "➕",
            "🔬",
            "🧬",
            "🎤",
            "🎸",
            "🥁",
            "🧵",
            "🍳",
            "🚗",
            "✈️",
            "🌎",
            "🧩",
            "♟️",
            "📜",
            "🕯️",
            "☄️",
        ].map((emoji) => (
            <button
                key={emoji}
                type="button"
                onClick={() =>
                    updateCalendarPopupItem("emoji", emoji)
                }
                className="rounded-xl border border-[#ffd166]/25 bg-black px-2 py-1 text-lg shadow-[0_0_10px_rgba(124,58,237,0.12)] hover:border-[#ffd166]/70 hover:shadow-[0_0_16px_rgba(255,209,102,0.20)]"
            >
                {emoji}
            </button>
        ))}
    </div>
</details>
</div>
    {calendarEditPopupItem.frequency === "custom" && (
        <div className="md:col-span-2 rounded-2xl border border-[#ffd166]/30 bg-black p-4">
            <p className="text-sm text-[#ffd166] mb-3">
                Custom Weekly Schedule
            </p>

            <div className="flex flex-wrap gap-2">
                {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                ].map((day) => (
                    <button
                        key={day}
                        type="button"
                        onClick={() => {
                            const currentDays =
                                calendarEditPopupItem.customDays || [];

                            updateCalendarPopupItem(
                                "customDays",
                                currentDays.includes(day)
                                    ? currentDays.filter((d) => d !== day)
                                    : [...currentDays, day]
                            );
                        }}
                        className={`${purpleActionBtn} ${
                            calendarEditPopupItem.customDays?.includes(day)
                                ? "!border-[#ffd166] !bg-[#ffd166]/20 !text-[#fff3c4]"
                                : ""
                        }`}
                    >
                        {day}
                    </button>
                ))}
            </div>
        </div>
    )}

    <label className="md:col-span-2 flex items-center gap-2 text-sm text-[#ffd166]">
        <input
            type="checkbox"
            checked={!!calendarEditPopupItem.blocksSchool}
            onChange={(e) =>
                updateCalendarPopupItem("blocksSchool", e.target.checked)
            }
        />
        Blocks school / shifts quests
    </label>
</div>

<div className="flex flex-wrap gap-2 mt-5">
    <button
        type="button"
        className={purpleActionBtn}
        onClick={saveCalendarPopupItem}
    >
        Save Changes
    </button>

    <button
        type="button"
        className={purpleActionBtn}
        onClick={() => {
            deleteCalendarItem(calendarEditPopupItem.id);
            setCalendarEditPopupItem(null);
        }}
    >
        Delete
    </button>

    <button
        type="button"
        className={purpleActionBtn}
        onClick={() => setCalendarEditPopupItem(null)}
    >
        Cancel
    </button>
</div>


        </div>
    </div>
)}
</Panel>
                  


                     


                {classScheduleModal && (
                    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/90 px-4">
                        <div className="w-full max-w-2xl rounded-[30px] border border-[#ffd166]/40 bg-[#05070d] p-6 shadow-[0_0_40px_rgba(255,209,102,0.18)]">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h3 className="font-display text-2xl text-[#ffd166]">
                                        Class Schedule
                                    </h3>

                                    <p className="mt-1 text-sm text-[#b0b8d6]">
                                        Choose a regular weekly schedule or a flexible block.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setClassScheduleModal(null)
                                    }
                                    className={purpleActionBtn}
                                >
                                    Close
                                </button>
                            </div>

                            <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setClassScheduleDraft((current) => ({
                                            ...current,
                                            mode: "scheduled",
                                        }))
                                    }
                                    className={`rounded-2xl border p-4 text-left transition ${
                                        classScheduleDraft.mode === "scheduled"
                                            ? "border-[#ffd166] bg-[#ffd166]/10"
                                            : "border-[#c084fc]/20 bg-black/40"
                                    }`}
                                >
                                    <p className="font-display text-lg text-[#fff3c4]">
                                        Scheduled Days & Times
                                    </p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setClassScheduleDraft((current) => ({
                                            ...current,
                                            mode: "flexible",
                                        }))
                                    }
                                    className={`rounded-2xl border p-4 text-left transition ${
                                        classScheduleDraft.mode === "flexible"
                                            ? "border-[#ffd166] bg-[#ffd166]/10"
                                            : "border-[#c084fc]/20 bg-black/40"
                                    }`}
                                >
                                    <p className="font-display text-lg text-[#fff3c4]">
                                        Flexible / Block Schedule
                                    </p>
                                </button>
                            </div>

                            {classScheduleDraft.mode === "scheduled" && (
                                <div className="mt-6 space-y-5">
                                    <div>
                                        <Label>Days of Week</Label>

                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {[
                                                "Monday",
                                                "Tuesday",
                                                "Wednesday",
                                                "Thursday",
                                                "Friday",
                                                "Saturday",
                                                "Sunday",
                                            ].map((day) => {
                                                const selected =
                                                    classScheduleDraft.days.includes(day);

                                                return (
                                                    <button
                                                        key={day}
                                                        type="button"
                                                        onClick={() =>
                                                            setClassScheduleDraft(
                                                                (current) => ({
                                                                    ...current,
                                                                    days: selected
                                                                        ? current.days.filter(
                                                                            (entry) =>
                                                                                entry !== day
                                                                        )
                                                                        : [
                                                                            ...current.days,
                                                                            day,
                                                                        ],
                                                                })
                                                            )
                                                        }
                                                        className={`rounded-xl border px-3 py-2 text-xs transition ${
                                                            selected
                                                                ? "border-[#ffd166] bg-[#ffd166]/15 text-[#fff3c4]"
                                                                : "border-[#c084fc]/20 bg-black text-[#b0b8d6]"
                                                        }`}
                                                    >
                                                        {day}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <Label>Start Time</Label>

                                            <Input
                                                type="time"
                                                value={classScheduleDraft.start_time}
                                                onChange={(e) =>
                                                    setClassScheduleDraft(
                                                        (current) => ({
                                                            ...current,
                                                            start_time:
                                                                e.target.value,
                                                        })
                                                    )
                                                }
                                                className={shadowInput}
                                            />
                                        </div>

                                        <div>
                                            <Label>End Time</Label>

                                            <Input
                                                type="time"
                                                value={classScheduleDraft.end_time}
                                                onChange={(e) =>
                                                    setClassScheduleDraft(
                                                        (current) => ({
                                                            ...current,
                                                            end_time:
                                                                e.target.value,
                                                        })
                                                    )
                                                }
                                                className={shadowInput}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {classScheduleDraft.mode === "flexible" && (
                                <div className="mt-6 space-y-4">
                                    <div>
                                        <Label>Estimated Weekly Hours</Label>

                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.25"
                                            value={classScheduleDraft.weekly_hours}
                                            onChange={(e) =>
                                                setClassScheduleDraft(
                                                    (current) => ({
                                                        ...current,
                                                        weekly_hours:
                                                            e.target.value,
                                                    })
                                                )
                                            }
                                            className={shadowInput}
                                        />
                                    </div>

                                    <div>
                                        <Label>Preferred Block</Label>

                                        <select
                                            value={classScheduleDraft.preferred_block}
                                            onChange={(e) =>
                                                setClassScheduleDraft(
                                                    (current) => ({
                                                        ...current,
                                                        preferred_block:
                                                            e.target.value,
                                                    })
                                                )
                                            }
                                            className={shadowInput}
                                        >
                                            <option value="morning">
                                                Morning
                                            </option>
                                            <option value="afternoon">
                                                Afternoon
                                            </option>
                                            <option value="evening">
                                                Evening
                                            </option>
                                            <option value="custom">
                                                Custom
                                            </option>
                                        </select>
                                    </div>

                                    {classScheduleDraft.preferred_block ===
                                        "custom" && (
                                        <div>
                                            <Label>Custom Block Label</Label>

                                            <Input
                                                value={
                                                    classScheduleDraft.custom_block_label
                                                }
                                                onChange={(e) =>
                                                    setClassScheduleDraft(
                                                        (current) => ({
                                                            ...current,
                                                            custom_block_label:
                                                                e.target.value,
                                                        })
                                                    )
                                                }
                                                placeholder="Late morning, after lunch..."
                                                className={shadowInput}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="mt-6 flex flex-wrap justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setClassScheduleModal(null)
                                    }
                                    className={purpleActionBtn}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setSubjectCurriculumSources(
                                            (current) =>
                                                current.map(
                                                    (curriculumItem) =>
                                                        curriculumItem.id ===
                                                        classScheduleModal.curriculumId
                                                            ? {
                                                                ...curriculumItem,

                                                                materials:
                                                                    curriculumItem.materials.map(
                                                                        (resource) =>
                                                                            resource.id ===
                                                                            classScheduleModal.resourceId
                                                                                ? {
                                                                                        ...resource,

                                                                                        class_schedule: {
                                                                                            ...classScheduleDraft,
                                                                                        },
                                                                                    }
                                                                                : resource
                                                                    ),
                                                            }
                                                            : curriculumItem
                                                )
                                        );

                                        setClassScheduleModal(null);

                                        toast.success(
                                            "Class schedule saved."
                                        );
                                    }}
                                    className={purpleActionBtn}
                                >
                                    Save Schedule
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {resourceDeleteConfirm && (
                    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/90 px-4">
                        <div className="w-full max-w-lg rounded-[30px] border border-red-400/35 bg-[#05070d] p-6 shadow-[0_0_40px_rgba(248,113,113,0.16)]">
                            <h3 className="font-display text-2xl text-[#ffd166]">
                                Delete Resource?
                            </h3>

                            <p className="mt-3 text-sm leading-6 text-[#b0b8d6]">
                                You are about to delete{" "}
                                <span className="font-semibold text-[#fff3c4]">
                                    {resourceDeleteConfirm.resourceName}
                                </span>
                                .
                            </p>

                            <p className="mt-2 text-sm leading-6 text-[#b0b8d6]">
                                This will remove it from the curriculum and remove future
                                curriculum assignments that reference it.
                            </p>

                            <p className="mt-2 text-xs leading-5 text-[#8f98b5]">
                                Completed work, grades, attendance, and historical progress
                                should remain separate and are not changed by this action.
                            </p>

                            <div className="mt-6 flex flex-wrap justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setResourceDeleteConfirm(null)
                                    }
                                    className={purpleActionBtn}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        const resourceIdToDelete =
                                            resourceDeleteConfirm.resourceId;

                                        setSubjectCurriculumSources(
                                            (current) =>
                                                current.map(
                                                    (curriculumItem) => {
                                                        if (
                                                            curriculumItem.id !==
                                                            resourceDeleteConfirm.curriculumId
                                                        ) {
                                                            return curriculumItem;
                                                        }

                                                        return {
                                                            ...curriculumItem,

                                                            materials:
                                                                (
                                                                    curriculumItem.materials ||
                                                                    []
                                                                )
                                                                    .filter(
                                                                        (resource) =>
                                                                            resource.id !==
                                                                            resourceIdToDelete
                                                                    )
                                                                    .map((resource) => ({
                                                                        ...resource,

                                                                        related_resource_ids:
                                                                            (
                                                                                Array.isArray(
                                                                                    resource.related_resource_ids
                                                                                )
                                                                                    ? resource.related_resource_ids
                                                                                    : []
                                                                            ).filter(
                                                                                (id) =>
                                                                                    id !==
                                                                                    resourceIdToDelete
                                                                            ),

                                                                        linked_to_resource_ids:
                                                                            (
                                                                                Array.isArray(
                                                                                    resource.linked_to_resource_ids
                                                                                )
                                                                                    ? resource.linked_to_resource_ids
                                                                                    : []
                                                                            ).filter(
                                                                                (id) =>
                                                                                    id !==
                                                                                    resourceIdToDelete
                                                                            ),

                                                                        structure:
                                                                            (
                                                                                Array.isArray(
                                                                                    resource.structure
                                                                                )
                                                                                    ? resource.structure
                                                                                    : []
                                                                            ).map(
                                                                                (structureGroup) => ({
                                                                                    ...structureGroup,

                                                                                    items:
                                                                                        (
                                                                                            Array.isArray(
                                                                                                structureGroup.items
                                                                                            )
                                                                                                ? structureGroup.items
                                                                                                : []
                                                                                        ).map(
                                                                                            (structureItem) => ({
                                                                                                ...structureItem,

                                                                                                linked_resource_work:
                                                                                                    (
                                                                                                        Array.isArray(
                                                                                                            structureItem.linked_resource_work
                                                                                                        )
                                                                                                            ? structureItem.linked_resource_work
                                                                                                            : []
                                                                                                    ).filter(
                                                                                                        (linkedWork) =>
                                                                                                            linkedWork.resource_id !==
                                                                                                            resourceIdToDelete
                                                                                                    ),
                                                                                            })
                                                                                        ),
                                                                                })
                                                                            ),
                                                                    })),
                                                        };
                                                    }
                                                )
                                        );

                                        setCurriculumBuilderResourceId("");

                                        setLinkedResourceDraft({
                                            name: "",
                                            resource_type: "workbook",
                                            has_structure: false,
                                            structure_type: "chapter",
                                            total_pages: "",
                                            content_start_page: "",
                                            structure: [],
                                        });

                                        setResourceDeleteConfirm(null);

                                        toast.success(
                                            "Resource deleted."
                                        );
                                    }}
                                    className="
                                        rounded-xl
                                        border
                                        border-red-400/40
                                        bg-red-950/25
                                        px-4
                                        py-2
                                        text-sm
                                        font-semibold
                                        text-red-200
                                        transition
                                        hover:border-red-400/80
                                        hover:bg-red-950/40
                                    "
                                >
                                    Delete Resource
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {curriculumPlanningModal && (
                    <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/90 px-4">
                        <div className="w-full max-w-3xl rounded-[30px] border border-[#ffd166]/40 bg-[#05070d] p-6 shadow-[0_0_40px_rgba(255,209,102,0.18)]">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h3 className="font-display text-2xl text-[#ffd166]">
                                        Plan Curriculum
                                    </h3>

                                    <p className="mt-1 text-sm text-[#b0b8d6]">
                                        Choose how far ahead you want to plan this class.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setCurriculumPlanningModal(null)
                                    }
                                    className={purpleActionBtn}
                                >
                                    Close
                                </button>
                            </div>

                            {curriculumPlanningWizard.step === 0 && (
                                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCurriculumPlanningWizard((current) => ({
                                            ...current,
                                            step: 1,
                                        }));
                                    }}
                                    className="
                                        rounded-2xl
                                        border
                                        border-[#ffd166]/30
                                        bg-black/40
                                        p-5
                                        text-left
                                        transition
                                        hover:border-[#ffd166]/70
                                        hover:bg-[#1a1328]
                                    "
                                >
                                    <p className="font-display text-xl text-[#ffd166]">
                                        Plan Year / Term
                                    </p>

                                    <p className="mt-2 text-sm leading-6 text-[#b0b8d6]">
                                        Spread curriculum across the school year,
                                        semester, quarter, or custom term.
                                    </p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        toast.success(
                                            "Weekly curriculum planning will be connected next."
                                        );
                                    }}
                                    className="
                                        rounded-2xl
                                        border
                                        border-[#c084fc]/30
                                        bg-black/40
                                        p-5
                                        text-left
                                        transition
                                        hover:border-[#c084fc]/70
                                        hover:bg-[#1a1328]
                                    "
                                >
                                    <p className="font-display text-xl text-[#d8b4fe]">
                                        Plan This Week
                                    </p>

                                    <p className="mt-2 text-sm leading-6 text-[#b0b8d6]">
                                        Choose upcoming lessons and place them across
                                        this week's available class days.
                                    </p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        toast.success(
                                            "As-you-go planning will connect to the classroom next."
                                        );
                                    }}
                                    className="
                                        rounded-2xl
                                        border
                                        border-[#7dd3fc]/30
                                        bg-black/40
                                        p-5
                                        text-left
                                        transition
                                        hover:border-[#7dd3fc]/70
                                        hover:bg-[#1a1328]
                                    "
                                >
                                    <p className="font-display text-xl text-[#7dd3fc]">
                                        Plan As You Go
                                    </p>

                                    <p className="mt-2 text-sm leading-6 text-[#b0b8d6]">
                                        Choose the next curriculum lesson directly
                                        from the classroom when you're ready to teach.
                                    </p>
                                </button>
                            </div>
                            )}
                        {curriculumPlanningWizard.step === 1 && (
                            <div className="mt-6">
                                <div className="rounded-[24px] border border-[#ffd166]/25 bg-[#080b14]/80 p-5">
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#ffd166]/70">
                                        Planning Wizard · Step 1
                                    </p>

                                    <h4 className="mt-2 font-display text-2xl text-[#ffd166]">
                                        How would you like to plan?
                                    </h4>

                                    <p className="mt-2 text-sm text-[#b0b8d6]">
                                        Choose how much control you want while the curriculum is placed onto the school calendar.
                                    </p>

                                    <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                                        {[
                                            {
                                                value: "automatic",
                                                title: "Automatic",
                                                description:
                                                    "Build the plan from the curriculum structure and available school days.",
                                            },
                                            {
                                                value: "assisted",
                                                title: "Assisted",
                                                description:
                                                    "Build the plan with you, letting you review and adjust before saving.",
                                            },
                                            {
                                                value: "manual",
                                                title: "Manual",
                                                description:
                                                    "You choose and place curriculum work yourself.",
                                            },
                                        ].map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() =>
                                                    setCurriculumPlanningWizard((current) => ({
                                                        ...current,
                                                        planningStyle: option.value,
                                                    }))
                                                }
                                                className={`rounded-2xl border p-4 text-left transition ${
                                                    curriculumPlanningWizard.planningStyle === option.value
                                                        ? "border-[#ffd166] bg-[#ffd166]/10"
                                                        : "border-[#c084fc]/20 bg-black/40 hover:border-[#c084fc]/60"
                                                }`}
                                            >
                                                <p className="font-display text-lg text-[#fff3c4]">
                                                    {option.title}
                                                </p>

                                                <p className="mt-2 text-xs leading-5 text-[#b0b8d6]">
                                                    {option.description}
                                                </p>
                                            </button>
                                        ))}
                                    </div>

                                    <div className="mt-6 flex flex-wrap justify-between gap-3">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setCurriculumPlanningWizard((current) => ({
                                                    ...current,
                                                    step: 0,
                                                }))
                                            }
                                            className={purpleActionBtn}
                                        >
                                            Back
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                setCurriculumPlanningWizard((current) => ({
                                                    ...current,
                                                    step: 2,
                                                }))
                                            }
                                            className={purpleActionBtn}
                                        >
                                            Continue
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}    
                    {curriculumPlanningWizard.step === 2 && (
                        <div className="mt-6">
                            <div className="rounded-[24px] border border-[#ffd166]/25 bg-[#080b14]/80 p-5">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#ffd166]/70">
                                    Planning Wizard · Step 2
                                </p>

                                <h4 className="mt-2 font-display text-2xl text-[#ffd166]">
                                    How far ahead do you want to plan?
                                </h4>

                                <p className="mt-2 text-sm text-[#b0b8d6]">
                                    The planner will use the existing school-year calendar dates and division settings.
                                </p>

                                <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                                    {[
                                        {
                                            value: "year",
                                            title: "Entire Year",
                                        },
                                        {
                                            value: "semester",
                                            title: "Semester",
                                        },
                                        {
                                            value: "quarter",
                                            title: "Quarter",
                                        },
                                        {
                                            value: "month",
                                            title: "Month",
                                        },
                                        {
                                            value: "week",
                                            title: "Week",
                                        },
                                    ].map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() =>
                                                setCurriculumPlanningWizard((current) => ({
                                                    ...current,
                                                    planningRange: option.value,
                                                }))
                                            }
                                            className={`rounded-2xl border p-4 text-left transition ${
                                                curriculumPlanningWizard.planningRange === option.value
                                                    ? "border-[#ffd166] bg-[#ffd166]/10"
                                                    : "border-[#c084fc]/20 bg-black/40 hover:border-[#c084fc]/60"
                                            }`}
                                        >
                                            <p className="font-display text-lg text-[#fff3c4]">
                                                {option.title}
                                            </p>
                                        </button>
                                    ))}
                                </div>

                                <div className="mt-5 rounded-2xl border border-[#c084fc]/20 bg-black/35 p-4">
                                    <p className="text-xs text-[#b0b8d6]">
                                        School year:
                                    </p>

                                    <p className="mt-1 text-sm text-[#fff3c4]">
                                        {schoolYearSettings.startDate || "No start date set"}
                                        {" → "}
                                        {schoolYearSettings.endDate || "No end date set"}
                                    </p>

                                    <p className="mt-2 text-xs text-[#8f98b5]">
                                        Calendar division:{" "}
                                        {schoolYearSettings.division || "Not set"}
                                    </p>
                                </div>

                                <div className="mt-6 flex flex-wrap justify-between gap-3">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setCurriculumPlanningWizard((current) => ({
                                                ...current,
                                                step: 1,
                                            }))
                                        }
                                        className={purpleActionBtn}
                                    >
                                        Back
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setCurriculumPlanningWizard((current) => ({
                                                ...current,
                                                step: 3,
                                            }))
                                        }
                                        className={purpleActionBtn}
                                    >
                                        Continue
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {curriculumPlanningWizard.step === 3 && (
                        <div className="mt-6">
                            <div className="rounded-[24px] border border-[#ffd166]/25 bg-[#080b14]/80 p-5">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#ffd166]/70">
                                    Planning Wizard · Step 3
                                </p>

                                <h4 className="mt-2 font-display text-2xl text-[#ffd166]">
                                    When should curriculum work become a Quest?
                                </h4>

                                <p className="mt-2 text-sm text-[#b0b8d6]">
                                    Regular readings, workbook work, and lesson tasks can stay as
                                    checklist items. Quests are reserved for work you want treated
                                    as a larger assignment.
                                </p>

                                <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
                                    {[
                                        {
                                            value: "never",
                                            title: "Never Automatically",
                                            description:
                                                "Keep curriculum work as lesson and session checklist items unless I create a Quest myself.",
                                        },
                                        {
                                            value: "parent_choice",
                                            title: "Let Me Choose",
                                            description:
                                                "Show an option while planning each lesson so I can decide whether something becomes a Quest.",
                                        },
                                        {
                                            value: "curriculum_defined",
                                            title: "Use Curriculum Types",
                                            description:
                                                "Automatically treat curriculum items such as tests, projects, essays, or other marked major work as Quests.",
                                        },
                                        {
                                            value: "tests_projects",
                                            title: "Tests & Projects Only",
                                            description:
                                                "Only automatically create Quests for tests, major projects, essays, and similar high-value work.",
                                        },
                                    ].map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() =>
                                                setCurriculumPlanningWizard((current) => ({
                                                    ...current,
                                                    questMode: option.value,
                                                }))
                                            }
                                            className={`rounded-2xl border p-4 text-left transition ${
                                                curriculumPlanningWizard.questMode === option.value
                                                    ? "border-[#ffd166] bg-[#ffd166]/10"
                                                    : "border-[#c084fc]/20 bg-black/40 hover:border-[#c084fc]/60"
                                            }`}
                                        >
                                            <p className="font-display text-lg text-[#fff3c4]">
                                                {option.title}
                                            </p>

                                            <p className="mt-2 text-xs leading-5 text-[#b0b8d6]">
                                                {option.description}
                                            </p>
                                        </button>
                                    ))}
                                </div>

                                <div className="mt-5 rounded-2xl border border-[#c084fc]/20 bg-black/35 p-4">
                                    <p className="text-xs text-[#8f98b5]">
                                        This does not permanently convert the curriculum item itself
                                        into a Quest. It controls what the planner creates from that
                                        curriculum item when work is scheduled.
                                    </p>
                                </div>

                                <div className="mt-6 flex flex-wrap justify-between gap-3">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setCurriculumPlanningWizard((current) => ({
                                                ...current,
                                                step: 2,
                                            }))
                                        }
                                        className={purpleActionBtn}
                                    >
                                        Back
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setCurriculumPlanningWizard((current) => ({
                                                ...current,
                                                step: 4,
                                            }))
                                        }
                                        className={purpleActionBtn}
                                    >
                                        Continue
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {curriculumPlanningWizard.step === 4 && (
                        <div className="mt-6">
                            <div className="rounded-[24px] border border-[#ffd166]/25 bg-[#080b14]/80 p-5">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#ffd166]/70">
                                    Planning Wizard · Step 4
                                </p>

                                <h4 className="mt-2 font-display text-2xl text-[#ffd166]">
                                    How should Sessions be created?
                                </h4>

                                <p className="mt-2 text-sm text-[#b0b8d6]">
                                    Sessions can be generated from curriculum lessons automatically,
                                    created with your approval, or left for you to build manually.
                                </p>

                                <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                                    {[
                                        {
                                            value: "automatic",
                                            title: "Generate Automatically",
                                            description:
                                                "Create Sessions from planned curriculum lessons and their assignments automatically.",
                                        },
                                        {
                                            value: "assisted",
                                            title: "Review Before Creating",
                                            description:
                                                "Prepare each Session from the curriculum and let me review or edit it before saving.",
                                        },
                                        {
                                            value: "manual",
                                            title: "I'll Build Sessions",
                                            description:
                                                "Place curriculum work on the plan, but do not create Sessions automatically.",
                                        },
                                    ].map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() =>
                                                setCurriculumPlanningWizard((current) => ({
                                                    ...current,
                                                    sessionMode: option.value,
                                                }))
                                            }
                                            className={`rounded-2xl border p-4 text-left transition ${
                                                curriculumPlanningWizard.sessionMode === option.value
                                                    ? "border-[#ffd166] bg-[#ffd166]/10"
                                                    : "border-[#c084fc]/20 bg-black/40 hover:border-[#c084fc]/60"
                                            }`}
                                        >
                                            <p className="font-display text-lg text-[#fff3c4]">
                                                {option.title}
                                            </p>

                                            <p className="mt-2 text-xs leading-5 text-[#b0b8d6]">
                                                {option.description}
                                            </p>
                                        </button>
                                    ))}
                                </div>

                                <div className="mt-5 rounded-2xl border border-[#c084fc]/20 bg-black/35 p-4">
                                    <p className="text-xs text-[#8f98b5]">
                                        Generated Sessions will later read the family school calendar,
                                        class days, holidays, vacations, absences, field trips, and
                                        curriculum assignments before choosing actual dates.
                                    </p>
                                </div>

                                <div className="mt-6 flex flex-wrap justify-between gap-3">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setCurriculumPlanningWizard((current) => ({
                                                ...current,
                                                step: 3,
                                            }))
                                        }
                                        className={purpleActionBtn}
                                    >
                                        Back
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setCurriculumPlanningWizard((current) => ({
                                                ...current,
                                                step: 5,
                                            }))
                                        }
                                        className={purpleActionBtn}
                                    >
                                        Continue
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {curriculumPlanningWizard.step === 5 && (
                        <div className="mt-6">
                            <div className="rounded-[24px] border border-[#ffd166]/25 bg-[#080b14]/80 p-5">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#ffd166]/70">
                                    Planning Wizard · Step 5
                                </p>

                                <h4 className="mt-2 font-display text-2xl text-[#ffd166]">
                                    Review Your Plan
                                </h4>

                                <p className="mt-2 text-sm text-[#b0b8d6]">
                                    Nothing will be scheduled until you confirm.
                                </p>

                                <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
                                    <div className="rounded-2xl border border-[#c084fc]/20 bg-black/35 p-4">
                                        <p className="text-xs text-[#8f98b5]">
                                            Planning Style
                                        </p>

                                        <p className="mt-1 text-sm text-[#fff3c4]">
                                            {curriculumPlanningWizard.planningStyle === "automatic"
                                                ? "Automatic"
                                                : curriculumPlanningWizard.planningStyle === "manual"
                                                ? "Manual"
                                                : "Assisted"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl border border-[#c084fc]/20 bg-black/35 p-4">
                                        <p className="text-xs text-[#8f98b5]">
                                            Planning Range
                                        </p>

                                        <p className="mt-1 text-sm text-[#fff3c4]">
                                            {curriculumPlanningWizard.planningRange}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl border border-[#c084fc]/20 bg-black/35 p-4">
                                        <p className="text-xs text-[#8f98b5]">
                                            Quest Behavior
                                        </p>

                                        <p className="mt-1 text-sm text-[#fff3c4]">
                                            {curriculumPlanningWizard.questMode}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl border border-[#c084fc]/20 bg-black/35 p-4">
                                        <p className="text-xs text-[#8f98b5]">
                                            Session Behavior
                                        </p>

                                        <p className="mt-1 text-sm text-[#fff3c4]">
                                            {curriculumPlanningWizard.sessionMode}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-5 rounded-2xl border border-[#ffd166]/20 bg-black/35 p-4">
                                    <p className="text-xs text-[#8f98b5]">
                                        School Calendar
                                    </p>

                                    <p className="mt-1 text-sm text-[#fff3c4]">
                                        {schoolYearSettings.startDate || "No start date set"}
                                        {" → "}
                                        {schoolYearSettings.endDate || "No end date set"}
                                    </p>

                                    <p className="mt-2 text-xs text-[#8f98b5]">
                                        Division: {schoolYearSettings.division || "Not set"}
                                    </p>
                                </div>

                                <div className="mt-6 flex flex-wrap justify-between gap-3">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setCurriculumPlanningWizard((current) => ({
                                                ...current,
                                                step: 4,
                                            }))
                                        }
                                        className={purpleActionBtn}
                                    >
                                        Back
                                    </button>

                                    <button
                                        type="button"
                                        onClick={async () => {
                                            const subjectId =
                                                curriculumPlanningModal?.subjectId ||
                                                curriculumOwnerSubjectId ||
                                                editingSubjectId ||
                                                "";

                                            if (!subjectId) {
                                                toast.error(
                                                    "Save or open this classroom for editing before saving its curriculum plan."
                                                );
                                                return;
                                            }

                                            const planningData = {
                                                planning_style:
                                                    curriculumPlanningWizard.planningStyle,

                                                planning_range:
                                                    curriculumPlanningWizard.planningRange,

                                                quest_mode:
                                                    curriculumPlanningWizard.questMode,

                                                session_mode:
                                                    curriculumPlanningWizard.sessionMode,

                                                calendar_start:
                                                    schoolYearSettings.startDate || "",

                                                calendar_end:
                                                    schoolYearSettings.endDate || "",

                                                calendar_division:
                                                    schoolYearSettings.division || "",

                                                updated_at:
                                                    new Date().toISOString(),
                                            };

                                            try {
                                                const response = await api.put(
                                                    `/subjects/${subjectId}`,
                                                    {
                                                        curriculum_planning:
                                                            planningData,
                                                    }
                                                );

                                                const savedSubject =
                                                    response.data;

                                                const updatedSubjects =
                                                    subjects.map((subject) =>
                                                        (
                                                            subject.subject_id ||
                                                            subject.id
                                                        ) === subjectId
                                                            ? savedSubject
                                                            : subject
                                                    );

                                                setSubjects(
                                                    updatedSubjects
                                                );

                                                localStorage.setItem(
                                                    "shadowGroveSubjects",
                                                    JSON.stringify(
                                                        updatedSubjects
                                                    )
                                                );

                                                toast.success(
                                                    "Curriculum plan saved."
                                                );

                                                setCurriculumPlanningModal(
                                                    null
                                                );
                                            } catch (error) {
                                                console.error(
                                                    "Failed to save curriculum plan:",
                                                    error
                                                );

                                                toast.error(
                                                    error?.response?.data?.detail ||
                                                        "Could not save the curriculum plan."
                                                );
                                            }
                                        }}
                                        className={purpleActionBtn}
                                    >
                                        Confirm Plan
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                        </div>
                    
                    </div>
                
                )}
                {linkedResourcesModal && (
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 px-4">
                        <div
                            className="
                                max-h-[90vh]
                                w-full
                                max-w-6xl
                                overflow-y-auto
                                rounded-[30px]
                                border
                                border-[#ffd166]/40
                                bg-[#05070d]
                                p-6
                                shadow-[0_0_40px_rgba(255,209,102,0.18)]
                            "
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h3 className="font-display text-2xl text-[#ffd166]">
                                        Curriculum Builder
                                    </h3>

                                    <p className="mt-1 text-sm text-[#b0b8d6]">
                                        Build the resources, lesson structures, and
                                        relationships used by this curriculum.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setLinkedResourcesModal(null)
                                    }
                                    className={purpleActionBtn}
                                >
                                    Close
                                </button>
                            </div>
                        <div className="mt-6 rounded-[24px] border border-[#ffd166]/20 bg-[#080b14]/80 p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="font-display text-xl text-[#ffd166]">
                                        Curriculum Resources
                                    </h4>

                                    <p className="mt-1 text-xs text-[#8f98b5]">
                                        Every textbook, workbook, teacher guide, online course,
                                        PDF, video, and supplemental resource belongs here.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setCurriculumBuilderResourceId("");

                                        setLinkedResourceDraft({
                                            name: "",
                                            resource_type: "workbook",
                                            has_structure: false,
                                            structure_type: "chapter",
                                            total_pages: "",
                                            content_start_page: "",
                                            structure: [],
                                        });
                                    }}
                                    className={purpleActionBtn}
                                >
                                    + New Resource
                                </button>
                            </div>

                            <div className="mt-5 flex flex-wrap gap-3">
                                {(subjectCurriculumSources.find(
                                    (curriculum) =>
                                        curriculum.id === linkedResourcesModal.curriculumId
                                )?.materials || []).map((resource) => (
                                    <button
                                        key={resource.id}
                                        type="button"
                                        onClick={() => {
                                            setCurriculumBuilderResourceId(
                                                resource.id
                                            );

                                            setLinkedResourceDraft({
                                                name:
                                                    resource.name || "",

                                                resource_type:
                                                    resource.material_type ||
                                                    resource.resource_type ||
                                                    "workbook",

                                                has_structure:
                                                    Boolean(
                                                        resource.has_structure ||
                                                        (
                                                            Array.isArray(
                                                                resource.structure
                                                            ) &&
                                                            resource.structure.length > 0
                                                        )
                                                    ),

                                                structure_type:
                                                    resource.structure_type ||
                                                    "chapter",

                                                total_pages:
                                                    resource.total_pages || "",

                                                content_start_page:
                                                    resource.content_start_page ||
                                                    "",

                                                structure:
                                                    Array.isArray(
                                                        resource.structure
                                                    )
                                                        ? resource.structure
                                                        : [],
                                            });
                                        }}
                                        className={`rounded-2xl border px-4 py-3 text-left transition ${
                                            curriculumBuilderResourceId === resource.id
                                                ? "border-[#ffd166] bg-[#1a1328]"
                                                : "border-[#c084fc]/20 bg-black/40"
                                        }`}
                                    >
                                        <div className="font-semibold text-[#fff3c4]">
                                            {resource.name || "Unnamed Resource"}
                                        </div>

                                        <div className="mt-1 text-xs text-[#8f98b5]">
                                            {resource.material_type || resource.resource_type}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <Label>
                                        Linked Resource Name
                                    </Label>

                                    <Input
                                        value={
                                            linkedResourceDraft.name
                                        }
                                        onChange={(e) =>
                                            setLinkedResourceDraft(
                                                (current) => ({
                                                    ...current,
                                                    name: e.target.value,
                                                })
                                            )
                                        }
                                        placeholder="Workbook A, Teacher Guide..."
                                        className={shadowInput}
                                    />
                                </div>

                                <div>
                                    <Label>
                                        Resource Type
                                    </Label>

                                    <select
                                        value={
                                            linkedResourceDraft.resource_type
                                        }
                                        onChange={(e) =>
                                            setLinkedResourceDraft(
                                                (current) => ({
                                                    ...current,
                                                    resource_type:
                                                        e.target.value,
                                                })
                                            )
                                        }
                                        className={shadowInput}
                                    >
                                        <option value="workbook">
                                            Workbook
                                        </option>
                                        <option value="textbook">
                                            Textbook
                                        </option>
                                        <option value="teacher_guide">
                                            Teacher Guide
                                        </option>
                                        <option value="practice_book">
                                            Practice Book
                                        </option>
                                        <option value="reader">
                                            Reader
                                        </option>
                                        <option value="lab_manual">
                                            Lab Manual
                                        </option>
                                        <option value="online_course">
                                            Online Course
                                        </option>
                                        <option value="video_course">
                                            Video Course
                                        </option>
                                        <option value="pdf">
                                            PDF Resource
                                        </option>
                                        <option value="website">
                                            Website
                                        </option>
                                        <option value="other">
                                            Other
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <label
                                className="
                                    mt-4
                                    flex
                                    cursor-pointer
                                    items-center
                                    gap-3
                                    rounded-2xl
                                    border
                                    border-[#c084fc]/20
                                    bg-black/35
                                    p-4
                                "
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        linkedResourceDraft.has_structure
                                    }
                                    onChange={(e) =>
                                        setLinkedResourceDraft(
                                            (current) => ({
                                                ...current,
                                                has_structure:
                                                    e.target.checked,
                                            })
                                        )
                                    }
                                    className="h-4 w-4 accent-[#ffd166]"
                                />

                                <div>
                                    <p className="text-sm font-semibold text-[#fff3c4]">
                                        This resource has its own lesson structure
                                    </p>

                                    <p className="mt-1 text-xs text-[#8f98b5]">
                                        Use this for workbooks, courses, or books
                                        with their own chapters, lessons, units,
                                        modules, or page progression.
                                    </p>
                                </div>
                            </label>

                            {linkedResourceDraft.has_structure && (
                                <div
                                    className="
                                        mt-4
                                        rounded-2xl
                                        border
                                        border-[#ffd166]/20
                                        bg-[#080b14]/70
                                        p-4
                                    "
                                >
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <Label>
                                                Structure Type
                                            </Label>

                                            <select
                                                value={
                                                    linkedResourceDraft.structure_type
                                                }
                                                onChange={(e) =>
                                                    setLinkedResourceDraft(
                                                        (current) => ({
                                                            ...current,
                                                            structure_type:
                                                                e.target.value,
                                                        })
                                                    )
                                                }
                                                className={shadowInput}
                                            >
                                                <option value="chapter">
                                                    Chapters
                                                </option>
                                                <option value="unit">
                                                    Units
                                                </option>
                                                <option value="module">
                                                    Modules
                                                </option>
                                                <option value="week">
                                                    Weeks
                                                </option>
                                                <option value="part">
                                                    Parts
                                                </option>
                                                <option value="other">
                                                    Custom Groups
                                                </option>
                                            </select>
                                        </div>

                                        <div>
                                            <Label>
                                                Total Pages
                                            </Label>

                                            <Input
                                                type="number"
                                                min="1"
                                                value={
                                                    linkedResourceDraft.total_pages
                                                }
                                                onChange={(e) =>
                                                    setLinkedResourceDraft(
                                                        (current) => ({
                                                            ...current,
                                                            total_pages:
                                                                e.target.value,
                                                        })
                                                    )
                                                }
                                                className={shadowInput}
                                            />
                                        </div>

                                        <div>
                                            <Label>
                                                Learning Content Starts on Page
                                            </Label>

                                            <Input
                                                type="number"
                                                min="1"
                                                value={
                                                    linkedResourceDraft.content_start_page
                                                }
                                                onChange={(e) =>
                                                    setLinkedResourceDraft(
                                                        (current) => ({
                                                            ...current,
                                                            content_start_page:
                                                                e.target.value,
                                                        })
                                                    )
                                                }
                                                className={shadowInput}
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div>
                                                <p className="text-sm font-semibold text-[#ffd166]">
                                                    Resource Structure
                                                </p>

                                                <p className="mt-1 text-xs text-[#8f98b5]">
                                                    Add the chapters, units, modules, or other groups
                                                    used by this linked resource.
                                                </p>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const currentStructure =
                                                        Array.isArray(
                                                            linkedResourceDraft.structure
                                                        )
                                                            ? linkedResourceDraft.structure
                                                            : [];

                                                    const nextNumber =
                                                        currentStructure.length + 1;

                                                    const labels = {
                                                        chapter: "Chapter",
                                                        unit: "Unit",
                                                        module: "Module",
                                                        week: "Week",
                                                        part: "Part",
                                                        other: "Group",
                                                    };

                                                    const type =
                                                        linkedResourceDraft.structure_type ||
                                                        "chapter";

                                                    const newGroup = {
                                                        id: `linked-structure-${Date.now()}-${Math.random()
                                                            .toString(36)
                                                            .slice(2, 8)}`,

                                                        title: `${
                                                            labels[type] || "Group"
                                                        } ${nextNumber}`,

                                                        structure_type:
                                                            type,

                                                        order:
                                                            nextNumber,

                                                        items: [],
                                                    };

                                                    setLinkedResourceDraft(
                                                        (current) => ({
                                                            ...current,

                                                            structure: [
                                                                ...(
                                                                    Array.isArray(
                                                                        current.structure
                                                                    )
                                                                        ? current.structure
                                                                        : []
                                                                ),

                                                                newGroup,
                                                            ],
                                                        })
                                                    );
                                                }}
                                                className={purpleActionBtn}
                                            >
                                                + Add{" "}
                                                {linkedResourceDraft.structure_type === "unit"
                                                    ? "Unit"
                                                    : linkedResourceDraft.structure_type === "module"
                                                    ? "Module"
                                                    : linkedResourceDraft.structure_type === "week"
                                                    ? "Week"
                                                    : linkedResourceDraft.structure_type === "part"
                                                    ? "Part"
                                                    : linkedResourceDraft.structure_type === "other"
                                                    ? "Group"
                                                    : "Chapter"}
                                            </button>
                                        </div>

                                        {Array.isArray(
                                            linkedResourceDraft.structure
                                        ) &&
                                            linkedResourceDraft.structure.length > 0 && (
                                                <div className="mt-4 space-y-3">
                                                    {linkedResourceDraft.structure.map(
                                                        (group, groupIndex) => (
                                                            <div
                                                                key={group.id}
                                                                className="
                                                                    rounded-xl
                                                                    border
                                                                    border-[#c084fc]/20
                                                                    bg-black/35
                                                                    p-3
                                                                "
                                                            >
                                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                                    <Input
                                                                        value={
                                                                            group.title || ""
                                                                        }
                                                                        onChange={(e) => {
                                                                            const value =
                                                                                e.target.value;

                                                                            setLinkedResourceDraft(
                                                                                (current) => ({
                                                                                    ...current,

                                                                                    structure:
                                                                                        (
                                                                                            current.structure ||
                                                                                            []
                                                                                        ).map(
                                                                                            (
                                                                                                structureGroup
                                                                                            ) =>
                                                                                                structureGroup.id ===
                                                                                                group.id
                                                                                                    ? {
                                                                                                        ...structureGroup,
                                                                                                        title:
                                                                                                            value,
                                                                                                    }
                                                                                                    : structureGroup
                                                                                        ),
                                                                                })
                                                                            );
                                                                        }}
                                                                        className={`${shadowInput} flex-1`}
                                                                    />

                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setLinkedResourceDraft(
                                                                                (current) => ({
                                                                                    ...current,

                                                                                    structure:
                                                                                        (
                                                                                            current.structure ||
                                                                                            []
                                                                                        )
                                                                                            .filter(
                                                                                                (
                                                                                                    structureGroup
                                                                                                ) =>
                                                                                                    structureGroup.id !==
                                                                                                    group.id
                                                                                            )
                                                                                            .map(
                                                                                                (
                                                                                                    structureGroup,
                                                                                                    index
                                                                                                ) => ({
                                                                                                    ...structureGroup,
                                                                                                    order:
                                                                                                        index +
                                                                                                        1,
                                                                                                })
                                                                                            ),
                                                                                })
                                                                            );
                                                                        }}
                                                                        className="
                                                                            rounded-lg
                                                                            border
                                                                            border-red-400/25
                                                                            bg-black
                                                                            px-2.5
                                                                            py-1.5
                                                                            text-[10px]
                                                                            text-red-200
                                                                        "
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>

                                                                <p className="mt-2 text-[10px] uppercase tracking-[0.14em] text-[#8f98b5]">
                                                                    Group {groupIndex + 1}
                                                                </p>
                                                            <div className="mt-3 flex flex-wrap items-end gap-3">
                                                                <div className="min-w-[160px]">
                                                                    <Label>
                                                                        Number of Items
                                                                    </Label>

                                                                    <Input
                                                                        type="number"
                                                                        min="1"
                                                                        value={group.item_count || ""}
                                                                        onChange={(e) => {
                                                                            const value =
                                                                                e.target.value;

                                                                            setLinkedResourceDraft(
                                                                                (current) => ({
                                                                                    ...current,

                                                                                    structure:
                                                                                        (
                                                                                            current.structure ||
                                                                                            []
                                                                                        ).map(
                                                                                            (
                                                                                                structureGroup
                                                                                            ) =>
                                                                                                structureGroup.id ===
                                                                                                group.id
                                                                                                    ? {
                                                                                                        ...structureGroup,
                                                                                                        item_count:
                                                                                                            value,
                                                                                                    }
                                                                                                    : structureGroup
                                                                                        ),
                                                                                })
                                                                            );
                                                                        }}
                                                                        className={shadowInput}
                                                                    />
                                                                </div>

                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const targetCount =
                                                                            Math.max(
                                                                                1,
                                                                                Number(
                                                                                    group.item_count
                                                                                ) || 1
                                                                            );

                                                                        const existingItems =
                                                                            Array.isArray(
                                                                                group.items
                                                                            )
                                                                                ? group.items
                                                                                : [];

                                                                        let nextItems =
                                                                            existingItems;

                                                                        if (
                                                                            targetCount >
                                                                            existingItems.length
                                                                        ) {
                                                                            const amountToAdd =
                                                                                targetCount -
                                                                                existingItems.length;

                                                                            const newItems =
                                                                                Array.from(
                                                                                    {
                                                                                        length:
                                                                                            amountToAdd,
                                                                                    },
                                                                                    (_, index) => {
                                                                                        const nextOrder =
                                                                                            existingItems.length +
                                                                                            index +
                                                                                            1;

                                                                                        return {
                                                                                            id: `linked-item-${Date.now()}-${nextOrder}-${Math.random()
                                                                                                .toString(36)
                                                                                                .slice(2, 7)}`,

                                                                                            title: `Lesson ${nextOrder}`,

                                                                                            item_type:
                                                                                                "lesson",

                                                                                            order:
                                                                                                nextOrder,

                                                                                            page_start:
                                                                                                "",

                                                                                            page_end:
                                                                                                "",
                                                                                        };
                                                                                    }
                                                                                );

                                                                            nextItems = [
                                                                                ...existingItems,
                                                                                ...newItems,
                                                                            ];
                                                                        }

                                                                        if (
                                                                            targetCount <
                                                                            existingItems.length
                                                                        ) {
                                                                            const amountToRemove =
                                                                                existingItems.length -
                                                                                targetCount;

                                                                            const shouldRemove =
                                                                                window.confirm(
                                                                                    `This will remove the last ${amountToRemove} item${
                                                                                        amountToRemove ===
                                                                                        1
                                                                                            ? ""
                                                                                            : "s"
                                                                                    } from ${
                                                                                        group.title ||
                                                                                        "this group"
                                                                                    }. Continue?`
                                                                                );

                                                                            if (!shouldRemove) {
                                                                                return;
                                                                            }

                                                                            nextItems =
                                                                                existingItems
                                                                                    .slice(
                                                                                        0,
                                                                                        targetCount
                                                                                    )
                                                                                    .map(
                                                                                        (
                                                                                            currentItem,
                                                                                            index
                                                                                        ) => ({
                                                                                            ...currentItem,
                                                                                            order:
                                                                                                index +
                                                                                                1,
                                                                                        })
                                                                                    );
                                                                        }

                                                                        setLinkedResourceDraft(
                                                                            (current) => ({
                                                                                ...current,

                                                                                structure:
                                                                                    (
                                                                                        current.structure ||
                                                                                        []
                                                                                    ).map(
                                                                                        (
                                                                                            structureGroup
                                                                                        ) =>
                                                                                            structureGroup.id ===
                                                                                            group.id
                                                                                                ? {
                                                                                                    ...structureGroup,
                                                                                                    items:
                                                                                                        nextItems,
                                                                                                }
                                                                                                : structureGroup
                                                                                    ),
                                                                            })
                                                                        );
                                                                    }}
                                                                    className={purpleActionBtn}
                                                                >
                                                                    Generate Items
                                                                </button>
                                                            </div>

                                                            {Array.isArray(group.items) &&
                                                                group.items.length > 0 && (
                                                                    <div className="mt-4 space-y-2">
                                                                        {group.items.map(
                                                                            (
                                                                                linkedItem,
                                                                                linkedItemIndex
                                                                            ) => (
                                                                                <div
                                                                                    key={
                                                                                        linkedItem.id
                                                                                    }
                                                                                    className="
                                                                                        rounded-xl
                                                                                        border
                                                                                        border-[#ffd166]/15
                                                                                        bg-black/30
                                                                                        p-3
                                                                                    "
                                                                                >
                                                                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[150px_1fr_110px_110px]">
                                                                                        <div>
                                                                                            <Label>
                                                                                                Type
                                                                                            </Label>

                                                                                            <select
                                                                                                value={
                                                                                                    linkedItem.item_type ||
                                                                                                    "lesson"
                                                                                                }
                                                                                                onChange={(e) => {
                                                                                                    const value =
                                                                                                        e.target.value;

                                                                                                    setLinkedResourceDraft(
                                                                                                        (current) => ({
                                                                                                            ...current,

                                                                                                            structure:
                                                                                                                (
                                                                                                                    current.structure ||
                                                                                                                    []
                                                                                                                ).map(
                                                                                                                    (
                                                                                                                        structureGroup
                                                                                                                    ) =>
                                                                                                                        structureGroup.id ===
                                                                                                                        group.id
                                                                                                                            ? {
                                                                                                                                ...structureGroup,

                                                                                                                                items:
                                                                                                                                    (
                                                                                                                                        structureGroup.items ||
                                                                                                                                        []
                                                                                                                                    ).map(
                                                                                                                                        (
                                                                                                                                            structureItem
                                                                                                                                        ) =>
                                                                                                                                            structureItem.id ===
                                                                                                                                            linkedItem.id
                                                                                                                                                ? {
                                                                                                                                                        ...structureItem,
                                                                                                                                                        item_type:
                                                                                                                                                            value,
                                                                                                                                                    }
                                                                                                                                                : structureItem
                                                                                                                                    ),
                                                                                                                            }
                                                                                                                            : structureGroup
                                                                                                                ),
                                                                                                        })
                                                                                                    );
                                                                                                }}
                                                                                                className={shadowInput}
                                                                                            >
                                                                                                <option value="lesson">
                                                                                                    Lesson
                                                                                                </option>
                                                                                                <option value="section">
                                                                                                    Section
                                                                                                </option>
                                                                                                <option value="reading">
                                                                                                    Reading
                                                                                                </option>
                                                                                                <option value="practice">
                                                                                                    Practice
                                                                                                </option>
                                                                                                <option value="review">
                                                                                                    Review
                                                                                                </option>
                                                                                                <option value="exercise">
                                                                                                    Exercise
                                                                                                </option>
                                                                                                <option value="activity">
                                                                                                    Activity
                                                                                                </option>
                                                                                                <option value="lab">
                                                                                                    Lab
                                                                                                </option>
                                                                                                <option value="quiz">
                                                                                                    Quiz
                                                                                                </option>
                                                                                                <option value="test">
                                                                                                    Test
                                                                                                </option>
                                                                                                <option value="project">
                                                                                                    Project
                                                                                                </option>
                                                                                                <option value="other">
                                                                                                    Other
                                                                                                </option>
                                                                                            </select>
                                                                                        </div>

                                                                                        <div>
                                                                                            <Label>
                                                                                                Name
                                                                                            </Label>

                                                                                            <Input
                                                                                                value={
                                                                                                    linkedItem.title ||
                                                                                                    ""
                                                                                                }
                                                                                                onChange={(e) => {
                                                                                                    const value =
                                                                                                        e.target.value;

                                                                                                    setLinkedResourceDraft(
                                                                                                        (current) => ({
                                                                                                            ...current,

                                                                                                            structure:
                                                                                                                (
                                                                                                                    current.structure ||
                                                                                                                    []
                                                                                                                ).map(
                                                                                                                    (
                                                                                                                        structureGroup
                                                                                                                    ) =>
                                                                                                                        structureGroup.id ===
                                                                                                                        group.id
                                                                                                                            ? {
                                                                                                                                ...structureGroup,

                                                                                                                                items:
                                                                                                                                    (
                                                                                                                                        structureGroup.items ||
                                                                                                                                        []
                                                                                                                                    ).map(
                                                                                                                                        (
                                                                                                                                            structureItem
                                                                                                                                        ) =>
                                                                                                                                            structureItem.id ===
                                                                                                                                            linkedItem.id
                                                                                                                                                ? {
                                                                                                                                                        ...structureItem,
                                                                                                                                                        title:
                                                                                                                                                            value,
                                                                                                                                                    }
                                                                                                                                                : structureItem
                                                                                                                                    ),
                                                                                                                            }
                                                                                                                            : structureGroup
                                                                                                                ),
                                                                                                        })
                                                                                                    );
                                                                                                }}
                                                                                                className={shadowInput}
                                                                                            />
                                                                                        </div>

                                                                                        <div>
                                                                                            <Label>
                                                                                                Start Page
                                                                                            </Label>

                                                                                            <Input
                                                                                                type="number"
                                                                                                min="1"
                                                                                                value={
                                                                                                    linkedItem.page_start ||
                                                                                                    ""
                                                                                                }
                                                                                                onChange={(e) => {
                                                                                                    const value =
                                                                                                        e.target.value;

                                                                                                    setLinkedResourceDraft(
                                                                                                        (current) => ({
                                                                                                            ...current,

                                                                                                            structure:
                                                                                                                (
                                                                                                                    current.structure ||
                                                                                                                    []
                                                                                                                ).map(
                                                                                                                    (
                                                                                                                        structureGroup
                                                                                                                    ) =>
                                                                                                                        structureGroup.id ===
                                                                                                                        group.id
                                                                                                                            ? {
                                                                                                                                ...structureGroup,

                                                                                                                                items:
                                                                                                                                    (
                                                                                                                                        structureGroup.items ||
                                                                                                                                        []
                                                                                                                                    ).map(
                                                                                                                                        (
                                                                                                                                            structureItem
                                                                                                                                        ) =>
                                                                                                                                            structureItem.id ===
                                                                                                                                            linkedItem.id
                                                                                                                                                ? {
                                                                                                                                                        ...structureItem,
                                                                                                                                                        page_start:
                                                                                                                                                            value,
                                                                                                                                                    }
                                                                                                                                                : structureItem
                                                                                                                                    ),
                                                                                                                            }
                                                                                                                            : structureGroup
                                                                                                                ),
                                                                                                        })
                                                                                                    );
                                                                                                }}
                                                                                                className={shadowInput}
                                                                                            />
                                                                                        </div>

                                                                                        <div>
                                                                                            <Label>
                                                                                                End Page
                                                                                            </Label>

                                                                                            <Input
                                                                                                type="number"
                                                                                                min="1"
                                                                                                value={
                                                                                                    linkedItem.page_end ||
                                                                                                    ""
                                                                                                }
                                                                                                onChange={(e) => {
                                                                                                    const value =
                                                                                                        e.target.value;

                                                                                                    setLinkedResourceDraft(
                                                                                                        (current) => ({
                                                                                                            ...current,

                                                                                                            structure:
                                                                                                                (
                                                                                                                    current.structure ||
                                                                                                                    []
                                                                                                                ).map(
                                                                                                                    (
                                                                                                                        structureGroup
                                                                                                                    ) =>
                                                                                                                        structureGroup.id ===
                                                                                                                        group.id
                                                                                                                            ? {
                                                                                                                                ...structureGroup,

                                                                                                                                items:
                                                                                                                                    (
                                                                                                                                        structureGroup.items ||
                                                                                                                                        []
                                                                                                                                    ).map(
                                                                                                                                        (
                                                                                                                                            structureItem
                                                                                                                                        ) =>
                                                                                                                                            structureItem.id ===
                                                                                                                                            linkedItem.id
                                                                                                                                                ? {
                                                                                                                                                        ...structureItem,
                                                                                                                                                        page_end:
                                                                                                                                                            value,
                                                                                                                                                    }
                                                                                                                                                : structureItem
                                                                                                                                    ),
                                                                                                                            }
                                                                                                                            : structureGroup
                                                                                                                ),
                                                                                                        })
                                                                                                    );
                                                                                                }}
                                                                                                className={shadowInput}
                                                                                            />
                                                                                        </div>
                                                                                    </div>

                                                                                    <p className="mt-2 text-[10px] text-[#8f98b5]">
                                                                                        Item {linkedItemIndex + 1}
                                                                                    </p>
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                )}   
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                </div>
                            )}

                            <div className="mt-6 flex flex-wrap justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!linkedResourceDraft.name.trim()) {
                                            toast.error(
                                                "Enter a resource name."
                                            );
                                            return;
                                        }

                                        const resourceId =
                                            curriculumBuilderResourceId ||
                                            `resource-${Date.now()}-${Math.random()
                                                .toString(36)
                                                .slice(2, 8)}`;

                                        const resourceToSave = {
                                            id: resourceId,

                                            name:
                                                linkedResourceDraft.name.trim(),

                                            material_type:
                                                linkedResourceDraft.resource_type,

                                            resource_type:
                                                linkedResourceDraft.resource_type,

                                            has_structure:
                                                Boolean(
                                                    linkedResourceDraft.has_structure
                                                ),

                                            structure_type:
                                                linkedResourceDraft.structure_type,

                                            total_pages:
                                                linkedResourceDraft.total_pages,

                                            content_start_page:
                                                linkedResourceDraft.content_start_page,

                                            structure:
                                                Array.isArray(
                                                    linkedResourceDraft.structure
                                                )
                                                    ? linkedResourceDraft.structure
                                                    : [],

                                            related_resource_ids: [],
                                        };

                                        setSubjectCurriculumSources(
                                            (current) =>
                                                current.map(
                                                    (curriculumItem) => {
                                                        if (
                                                            curriculumItem.id !==
                                                            linkedResourcesModal.curriculumId
                                                        ) {
                                                            return curriculumItem;
                                                        }

                                                        const existingResources =
                                                            Array.isArray(
                                                                curriculumItem.materials
                                                            )
                                                                ? curriculumItem.materials
                                                                : [];

                                                        if (
                                                            curriculumBuilderResourceId
                                                        ) {
                                                            return {
                                                                ...curriculumItem,

                                                                materials:
                                                                    existingResources.map(
                                                                        (resource) =>
                                                                            resource.id ===
                                                                            curriculumBuilderResourceId
                                                                                ? {
                                                                                    ...resource,
                                                                                    ...resourceToSave,

                                                                                    related_resource_ids:
                                                                                        Array.isArray(
                                                                                            resource.related_resource_ids
                                                                                        )
                                                                                            ? resource.related_resource_ids
                                                                                            : [],
                                                                                }
                                                                                : resource
                                                                    ),
                                                            };
                                                        }

                                                        const mainResourceId =
                                                            linkedResourcesModal.materialId;

                                                        return {
                                                            ...curriculumItem,

                                                            materials: [
                                                                ...existingResources.map(
                                                                    (resource) =>
                                                                        resource.id ===
                                                                        mainResourceId
                                                                            ? {
                                                                                ...resource,

                                                                                related_resource_ids: [
                                                                                    ...new Set([
                                                                                        ...(
                                                                                            Array.isArray(
                                                                                                resource.related_resource_ids
                                                                                            )
                                                                                                ? resource.related_resource_ids
                                                                                                : []
                                                                                        ),

                                                                                        resourceId,
                                                                                    ]),
                                                                                ],
                                                                            }
                                                                            : resource
                                                                ),

                                                                {
                                                                    ...resourceToSave,

                                                                    linked_to_resource_ids: [
                                                                        mainResourceId,
                                                                    ],
                                                                },
                                                            ],
                                                        };
                                                    }
                                                )
                                        );

                                        setCurriculumBuilderResourceId(
                                            resourceId
                                        );

                                        toast.success(
                                            curriculumBuilderResourceId
                                                ? "Resource updated."
                                                : "Resource added."
                                        );
                                    }}
                                    className={purpleActionBtn}
                                >
                                    {curriculumBuilderResourceId
                                        ? "Save Resource Changes"
                                        : "Add Resource"}
                                </button>
                            {curriculumBuilderResourceId && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setResourceDeleteConfirm({
                                            curriculumId:
                                                linkedResourcesModal.curriculumId,

                                            resourceId:
                                                curriculumBuilderResourceId,

                                            resourceName:
                                                linkedResourceDraft.name ||
                                                "this resource",
                                        });
                                    }}
                                    className="
                                        rounded-xl
                                        border
                                        border-red-400/30
                                        bg-black
                                        px-4
                                        py-2
                                        text-sm
                                        text-red-200
                                        transition
                                        hover:border-red-400/70
                                    "
                                >
                                    Delete Resource
                                </button>
                            )}
                                <button
                                    type="button"
                                    onClick={() =>
                                        setLinkedResourcesModal(null)
                                    }
                                    className={purpleActionBtn}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                </main>
            )}
        </div>
    );
};


export default ParentProfile;
